import { sectionRenderer } from '@theme/section-renderer';

const TARGET_PATHS = new Set([
  '/products/morning-bundle',
  '/products/weekend-bundle',
]);
const TARGET_PRODUCT_HANDLES = new Set([
  'morning-bundle',
  'weekend-bundle',
]);
const ACTIVE_CLASS = 'bc-bundle-cart-flow-active';
const BUNDLE_PENDING_ADD_EVENT = 'bc:bundle-cart-flow:pending-add';
const BUNDLE_ADD_SUCCEEDED_EVENT = 'bc:bundle-cart-flow:add-succeeded';
const BUNDLE_ADD_FAILED_EVENT = 'bc:bundle-cart-flow:add-failed';
const BUNDLE_CART_FLOW_READY_EVENT = 'bc:bundle-cart-flow:ready';
const STANDARD_SUBSCRIPTION_DISCOUNT_RATE = 0.25;
const STANDARD_SUBSCRIPTION_DISCOUNT_CODES = new Set(['STAYIN', 'FRANKIE25', 'FIRST25', 'PASTRIES25']);
const CART_ADD_PATHS = new Set(['/cart/add', '/cart/add.js']);
const CART_GIFT_RECONCILE_MUTATION_PATHS = new Set([
  '/cart/change',
  '/cart/change.js',
  '/cart/update',
  '/cart/update.js',
  '/cart/clear',
  '/cart/clear.js',
]);
const FLARE_ENHANCEMENT_DELAYS_MS = [0, 250, 750, 1500];
const DRAWER_OPEN_DEDUPE_MS = 1500;
const DRAWER_OPEN_DELAY_MS = 90;
const FLARE_UI_READINESS_CHECK_DELAYS_MS = [250, 900];
const BUNDLE_DRAWER_LOADING_MIN_MS = 180;
const BUNDLE_DRAWER_LOADING_MAX_MS = 2400;
// Last-resort window for the instant tap-time shell open: the normal post-add
// scheduleDrawerOpen always re-arms the standard MAX_MS window once the add lands and
// clears this one, so it only ever fires if the add never completes (no POST, no
// failure event). Generous so a slow add/reconcile never force-hides the skeleton early.
const BUNDLE_DRAWER_SHELL_SAFETY_MS = 9000;
const BUNDLE_DRAWER_LOADING_POST_RECOVERY_SETTLE_DELAY_MS = Math.max(...FLARE_ENHANCEMENT_DELAYS_MS);
const BUNDLE_DRAWER_LOADING_MAX_RECOVERY_ATTEMPTS = 2;
const GB_COUNTRY_MATCHER = /^(gb|uk)$/i;
const GB_COUNTRY_LABEL_MATCHER = /united kingdom|great britain|\buk\b/i;
const CHECKOUT_INTENT_RESET_DELAY_MS = 3000;
const RECHARGE_FINAL_ADD_TO_CART_TEXT_MATCHER = /add(?:\s+bundle)?\s+to\s+cart/i;
const PRIMED_SESSION_RESET_DELAY_MS = 6000;
const BUNDLE_STATE_SNAPSHOT_STORAGE_KEY = 'bcMorningBundleStateSnapshot';
const BUNDLE_HYBRID_DRAFT_SESSION_STORAGE_KEY = 'bcMorningBundleHybridDraft:v1';
const BUNDLE_HYBRID_DRAFT_LOCAL_STORAGE_KEY = 'bcMorningBundleHybridDraftBackup:v1';
const BUNDLE_HYBRID_DRAFT_TAB_ID_STORAGE_KEY = 'bcMorningBundleHybridDraftTabId:v1';
const BUNDLE_HYBRID_SCHEMA_VERSION = 1;
const BUNDLE_HYBRID_FEATURE_VERSION = 'hybrid-1';
const BUNDLE_HYBRID_DRAFT_TTL_MS = 2 * 60 * 60 * 1000;
const BUNDLE_HYBRID_CHECKOUT_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
const BUNDLE_HYBRID_CAPTURE_DEBOUNCE_MS = 120;
const BUNDLE_HYBRID_CAPTURE_SETTLE_DELAYS_MS = [300];
const BUNDLE_HYBRID_RESTORE_DELAYS_MS = [0, 100, 300, 750, 1500, 2500];
const BUNDLE_HYBRID_RESTORE_MASK_DELAY_MS = 150;
const BUNDLE_HYBRID_EMPTY_CLEAR_SUPPRESS_MS = 12 * 1000;
const BUNDLE_HYBRID_QUERY_KEY = 'bc_bundle_persist';
const BUNDLE_HYBRID_DEBUG_QUERY_KEY = 'bc_bundle_persist_debug';
const BUNDLE_HYBRID_DEBUG_PREFIX = '[BC bundle persist]';
const BUNDLE_CHECKOUT_AB_QUERY_KEY = 'bc_bundle_checkout_ab';
const BUNDLE_CHECKOUT_AB_DEBUG_QUERY_KEY = 'bc_bundle_checkout_ab_debug';
const BUNDLE_CHECKOUT_AB_DRY_QUERY_KEY = 'bc_bundle_checkout_ab_dry';
const BUNDLE_CHECKOUT_AB_STORAGE_KEY = 'bcBundleCheckoutAb:v1';
const BUNDLE_CHECKOUT_AB_SCHEMA_VERSION = 1;
const BUNDLE_CHECKOUT_AB_DEBUG_PREFIX = '[BC bundle checkout AB]';
const BUNDLE_CHECKOUT_AB_ASSIGNMENT_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const BUNDLE_DIRECT_CHECKOUT_VERIFY_ATTEMPTS = 2;
const BUNDLE_DIRECT_CHECKOUT_VERIFY_RETRY_DELAY_MS = 220;
const BUNDLE_DIRECT_CHECKOUT_REDIRECT_DELAY_MS = 120;
const BUNDLE_DIRECT_CHECKOUT_PENDING_CLASS = 'bc-bundle-direct-checkout-pending';
const BUNDLE_DIRECT_CHECKOUT_ONLY_CLASS = 'bc-bundle-direct-checkout-only';
const STORED_POSTCODE_COOKIE_NAME = 'rbz-zipcode';
const STORED_SAFE_PLACE_COOKIE_NAME = 'bc-bundle-safe-place';
const FLARE_POSTCODE_PLACEHOLDER_TEXT = 'Please enter your full postcode';
const FLARE_POSTCODE_PLACEHOLDER_STYLE_ID = 'bc-flare-postcode-placeholder-style';
const FLARE_DRAWER_FIELD_STYLE_ID = 'bc-flare-drawer-field-style';
const FLARE_POSTCODE_EMPTY_CLASS = 'bc-flare-postcode-is-empty';
const FLARE_POSTCODE_VALID_CLASS = 'bc-flare-postcode-has-valid-value';
const FLARE_DATE_EMPTY_CLASS = 'bc-flare-date-is-empty';
const FLARE_DATE_VALID_CLASS = 'bc-flare-date-has-value';
const FLARE_HIDDEN_PLACEHOLDER_CLASS = 'bc-flare-value-placeholder-hidden';
const POSTCODE_GATE_INPUT_SELECTOR = '#input_postcode, input.bc-dc__input, #delivery-zip-code, input.zip-code-input';
const POSTCODE_GATE_MESSAGE_ID = 'bc-postcode-gate-message';
const POSTCODE_GATE_BLOCKED_MESSAGE = 'Sorry, we do not deliver to that postcode yet.';
const POSTCODE_GATE_EMPTY_MESSAGE = 'Enter your full postcode.';
const POSTCODE_GATE_INVALID_MESSAGE = 'Enter a valid UK postcode.';
// Generated from Postcode Sectors-All Launched.csv via bc-native-delivery-zones.js.
// Exact postcode-sector matches only; no district fallback.
const DELIVERY_ZONE_DISTRICT_TOKENS = [];
const DELIVERY_ZONE_SECTOR_TOKENS = `
  BR14 BR15 BR31 BR34 BR35 CR78 E10 E11 E12 E13 E14 E15 E16 E17 E18 E19 E1W1 E1W2
  E1W3 E1W9 E20 E22 E26 E27 E28 E29 E35 E50 E55 E58 E59 E81 E82 E83 E84 E89
  E95 E96 E97 E99 EC1A1 EC1A2 EC1A4 EC1A7 EC1A9 EC1M3 EC1M4 EC1M5 EC1M6 EC1M7 EC1N2 EC1N6 EC1N7 EC1N8
  EC1P1 EC1R0 EC1R1 EC1R3 EC1R4 EC1R5 EC1V0 EC1V1 EC1V2 EC1V3 EC1V4 EC1V7 EC1V8 EC1V9 EC1Y0 EC1Y1 EC1Y2 EC1Y4
  EC1Y7 EC1Y8 EC2A1 EC2A2 EC2A3 EC2A4 EC2M1 EC2M2 EC2M3 EC2M4 EC2M5 EC2M6 EC2M7 EC2N1 EC2N2 EC2N3 EC2N4 EC2P2
  EC2R5 EC2R6 EC2R7 EC2R8 EC2V5 EC2V6 EC2V7 EC2V8 EC2Y5 EC2Y8 EC2Y9 EC3A1 EC3A2 EC3A3 EC3A4 EC3A5 EC3A6 EC3A7
  EC3A8 EC3B0 EC3M1 EC3M2 EC3M3 EC3M4 EC3M5 EC3M6 EC3M7 EC3M8 EC3N1 EC3N2 EC3N3 EC3N4 EC3P3 EC3R5 EC3R6 EC3R7
  EC3R8 EC3V0 EC3V1 EC3V3 EC3V4 EC3V9 EC4A1 EC4A2 EC4A3 EC4A4 EC4M4 EC4M5 EC4M6 EC4M7 EC4M8 EC4M9 EC4N1 EC4N4
  EC4N5 EC4N6 EC4N7 EC4N8 EC4P4 EC4R0 EC4R1 EC4R2 EC4R3 EC4R9 EC4V2 EC4V3 EC4V4 EC4V5 EC4V6 EC4Y0 EC4Y1 EC4Y7
  EC4Y8 EC4Y9 EC500 EC501 EC503 EC504 EC505 EC506 EC509 EC881 EC882 EC883 EC884 EC885 EC886 EC887 EC888 EC889
  N10 N11 N12 N13 N14 N15 N16 N17 N18 N19 N1C4 N41 N42 N43 N44 N49 N51 N52
  N59 N64 N65 N66 N69 N70 N71 N76 N77 N78 N79 N80 N81 N87 N88 N89 N153 N155
  N156 N160 N161 N165 N166 N167 N168 N169 N193 N194 N195 N199 NW10 NW11 NW12 NW13 NW14 NW17
  NW18 NW19 NW51 NW52 NW53 NW54 NW59 SE10 SE11 SE12 SE13 SE14 SE15 SE16 SE17 SE18 SE19 SE30
  SE33 SE37 SE39 SE41 SE42 SE49 SE50 SE55 SE57 SE58 SE59 SE61 SE62 SE63 SE64 SE69 SE81 SE83
  SE84 SE85 SE89 SE101 SE108 SE109 SE114 SE115 SE116 SE119 SE120 SE122 SE128 SE129 SE135 SE136 SE137 SE139
  SE145 SE146 SE149 SE151 SE152 SE153 SE154 SE155 SE156 SE159 SE161 SE162 SE163 SE164 SE165 SE166 SE167 SE169
  SE171 SE172 SE173 SE179 SE191 SE192 SE193 SE199 SE207 SE208 SE209 SE217 SE218 SE219 SE220 SE222 SE228 SE229
  SE231 SE232 SE233 SE239 SE240 SE244 SE249 SE254 SE255 SE256 SE259 SE264 SE265 SE266 SE269 SE270 SE277 SE279
  SW21 SW22 SW23 SW24 SW25 SW29 SW90 SW91 SW96 SW97 SW98 SW99 WC1A1 WC1A2 WC1A5 WC1A9 WC1B3 WC1B4
  WC1B5 WC1E6 WC1E7 WC1H0 WC1H8 WC1H9 WC1N1 WC1N2 WC1N3 WC1R4 WC1R5 WC1V6 WC1V7 WC1X0 WC1X8 WC1X9 WC2A1 WC2A2
  WC2A3 WC2B4 WC2B5 WC2B6 WC2B8 WC2E7 WC2E8 WC2E9 WC2H0 WC2H7 WC2H8 WC2H9 WC2N4 WC2N5 WC2N6 WC2R0 WC2R1 WC2R2
  WC2R3 WC2R4 CR41 CR42 CR43 CR44 CR46 CR47 CR48 CR49 CR76 KT11 KT12 KT13 KT14 KT19 KT22 KT25
  KT26 KT27 KT33 KT34 KT35 KT36 KT39 NW15 NW16 NW21 NW22 NW23 NW24 NW25 NW26 NW27 NW28 NW29
  NW31 NW32 NW33 NW34 NW35 NW36 NW37 NW39 NW61 NW62 NW63 NW64 NW65 NW66 NW67 NW69 NW80 NW81
  NW86 NW87 NW88 NW89 NW101 NW102 NW103 NW104 NW105 NW106 NW109 NW110 NW111 NW116 NW117 NW118 NW119 SM44
  SM45 SM46 SM49 SW1A0 SW1A1 SW1A2 SW1E5 SW1E6 SW1H0 SW1H9 SW1P1 SW1P2 SW1P3 SW1P4 SW1P9 SW1V1 SW1V2 SW1V3
  SW1V4 SW1W0 SW1W8 SW1W9 SW1X0 SW1X1 SW1X7 SW1X8 SW1X9 SW1Y4 SW1Y5 SW1Y6 SW31 SW32 SW33 SW34 SW35 SW36
  SW39 SW40 SW44 SW46 SW47 SW48 SW49 SW50 SW55 SW59 SW61 SW62 SW63 SW64 SW65 SW66 SW67 SW69
  SW71 SW72 SW73 SW74 SW75 SW77 SW79 SW81 SW82 SW83 SW84 SW85 SW89 SW100 SW101 SW109 SW111 SW112
  SW113 SW114 SW115 SW116 SW117 SW118 SW119 SW120 SW122 SW128 SW129 SW130 SW131 SW133 SW138 SW139 SW147 SW148
  SW149 SW151 SW152 SW153 SW154 SW155 SW156 SW159 SW161 SW162 SW163 SW164 SW165 SW166 SW169 SW170 SW171 SW176
  SW177 SW178 SW179 SW181 SW182 SW183 SW184 SW185 SW189 SW191 SW192 SW193 SW194 SW195 SW196 SW197 SW198 SW199
  SW200 SW202 SW208 SW209 TW80 TW81 TW88 TW89 TW91 TW92 TW93 TW94 TW99 TW103 TW105 TW106 TW107 W13
  W1A0 W1A1 W1A2 W1A3 W1A4 W1A5 W1A6 W1A7 W1A8 W1A9 W1B1 W1B2 W1B3 W1B4 W1B5 W1C1 W1C2 W1D1
  W1D2 W1D3 W1D4 W1D5 W1D6 W1D7 W1E5 W1F0 W1F7 W1F8 W1F9 W1G0 W1G6 W1G7 W1G8 W1G9 W1H0 W1H1
  W1H2 W1H3 W1H4 W1H5 W1H6 W1H7 W1H8 W1H9 W1J0 W1J5 W1J6 W1J7 W1J8 W1J9 W1K1 W1K2 W1K3 W1K4
  W1K5 W1K6 W1K7 W1M0 W1M1 W1M2 W1M3 W1M4 W1M5 W1M6 W1M7 W1M8 W1M9 W1N0 W1N1 W1N2 W1N3 W1N4
  W1N5 W1N6 W1N7 W1N8 W1N9 W1P0 W1P1 W1P2 W1P3 W1P4 W1P5 W1P6 W1P7 W1P8 W1P9 W1R0 W1R1 W1R2
  W1R3 W1R4 W1R5 W1R6 W1R7 W1R8 W1R9 W1S1 W1S2 W1S3 W1S4 W1T1 W1T2 W1T3 W1T4 W1T5 W1T6 W1T7
  W1U1 W1U2 W1U3 W1U4 W1U5 W1U6 W1U7 W1U8 W1V0 W1V1 W1V2 W1V3 W1V4 W1V5 W1V6 W1V7 W1V8 W1V9
  W1W5 W1W6 W1W7 W1W8 W1X0 W1X1 W1X2 W1X3 W1X4 W1X5 W1X6 W1X7 W1X8 W1X9 W1Y0 W1Y1 W1Y2 W1Y3
  W1Y4 W1Y5 W1Y6 W1Y7 W1Y8 W1Y9 W21 W22 W23 W24 W25 W26 W27 W30 W33 W36 W37 W38
  W39 W41 W42 W43 W44 W45 W49 W51 W52 W53 W54 W55 W59 W60 W66 W67 W68 W69
  W72 W84 W85 W86 W87 W89 W91 W92 W93 W94 W104 W105 W106 W109 W111 W112 W113 W114
  W119 W120 W122 W126 W127 W128 W129 W130 W133 W138 W139 W140 W144 W148 W149
`.trim().split(/\s+/);
const DELIVERY_ZONE_DISTRICTS = new Set(DELIVERY_ZONE_DISTRICT_TOKENS);
const DELIVERY_ZONE_SECTORS = new Set(DELIVERY_ZONE_SECTOR_TOKENS);
const BUNDLE_DRAWER_LOADING_STYLE_ID = 'bc-bundle-drawer-loading-polish-style';
const BUNDLE_DRAWER_LOADING_LABEL = 'Setting up your delivery...';
const PERSISTED_BUNDLE_STATE_RESTORE_DELAYS_MS = [100, 300, 750, 1500, 3000, 5000];
const PERSISTED_FLARE_FIELD_SYNC_DELAYS_MS = [0, ...PERSISTED_BUNDLE_STATE_RESTORE_DELAYS_MS];
const BUNDLE_MOTION_VARIANT_QUERY_KEY = 'bc_motion_variant';
const BUNDLE_MOTION_VARIANT_FREEZE = 'freeze';
const BUNDLE_MOTION_VARIANT_CLONE = 'clone';
const FETCH_LISTENER_INSTALL_FLAG = '__bcBundleCartFlowFetchListenerInstalled';
const NATIVE_FLARE_APP_BLOCK_SCRIPT_SRC_ATTR = 'data-bc-native-flare-app-block-script-src';
const NATIVE_FLARE_APP_BLOCK_SCRIPT_SRC_MATCHER = /flare_cart_date_picker(?:\.min)?\.js/i;
const NATIVE_FLARE_APP_BLOCK_SELECTOR = '[data-bc-bundle-app-blocks]';
const NATIVE_DELIVERY_QUERY_KEY = 'bc_native_delivery';
const NATIVE_DELIVERY_ACTIVE_CLASS = 'bc-native-delivery-active';
const NATIVE_DELIVERY_STYLE_ID = 'bc-native-delivery-style';
const NATIVE_DELIVERY_FLARE_CHECKOUT_IGNORE_ATTR = 'data-flare-ignore-checkout-guard';
const NATIVE_DELIVERY_DATE_COUNT = 8;
const nativeDeliveryAlternativesOpen = new WeakMap();
const nativeDeliveryBoundRoots = new WeakSet();
const nativeDeliveryEmailInputs = new WeakSet();
const nativeDeliveryInstructionInputs = new WeakSet();
const NATIVE_DELIVERY_BUYER_IDENTITY_TIMEOUT_MS = 3000;
const NATIVE_DELIVERY_KLAVIYO_PUBLIC_KEY = 'VQeQ2Z';
const NATIVE_DELIVERY_KLAVIYO_NEWSLETTER_LIST_ID = 'Wdn7Bs';
const NATIVE_DELIVERY_KLAVIYO_REVISION = '2026-07-15';
const NATIVE_DELIVERY_CART_UPDATE_TIMEOUT_MS = 8000;
// Wave 1 (2026-10-08): the drawer email is remembered on the Shopify cart, like the
// postcode/date attributes and the cart note, so a returning visitor's email is prefilled.
// Single underscore: hidden from buyers at checkout but readable from Liquid and /cart.js
// (Shopify hides double-underscore attributes from both, which would break the prefill).
const NATIVE_DELIVERY_EMAIL_ATTRIBUTE_NAME = '_bc_delivery_email';
const NATIVE_DELIVERY_EMAIL_PERSIST_DELAY_MS = 600;
const NATIVE_DELIVERY_EMAIL_PERSIST_RETRY_MS = 1000;
const NATIVE_DELIVERY_EMAIL_PERSIST_MAX_RETRIES = 20;
const nativeDeliveryEmailPersistTimers = new WeakMap();
// Wave 1: GA4 add_to_cart for builder adds. The builders add through the captured native
// fetch (BC_CART_TOP_LEVEL_NATIVE_FETCH), so Shopify's web-pixel add-to-cart observer never
// sees them and the Google & YouTube pixel never sends add_to_cart. If bundle adds are ever
// moved onto the patched window.fetch, remove this to avoid double counting.
const BUNDLE_GA4_FALLBACK_TAG_ID = 'G-L1MX20WZNP';
const BUNDLE_GA4_GTAG_WAIT_MS = 10000;
const BUNDLE_GA4_GTAG_RETRY_MS = 250;
const NATIVE_DELIVERY_STALE_CLEAR_TIMEOUT_MS = 8000;
const NATIVE_DELIVERY_ATTRIBUTE_NAMES = {
  postcode: 'Delivery Postcode',
  date: 'Delivery Date',
  day: 'Delivery Day',
  sector: 'Delivery Sector',
  flareDate: '__flare_delivery_date',
  flareProfile: '__flare_shipping_profile',
  flareProfileName: '__shipping_profile_name',
};
const nativeDeliveryPersistTimers = new WeakMap();
const nativeDeliveryPersistChains = new WeakMap();
const nativeDeliveryDeferredPersistRoots = new WeakSet();
const nativeDeliveryNoRootPreparationPromises = new WeakMap();
let nativeDeliveryCheckoutGuardObserver = null;
let nativeDeliveryCheckoutGuardObserverTarget = null;
let nativeDeliveryCheckoutGuardReapplyTimer = 0;
let nativeDeliveryFinalSubmitInFlight = false;
const nativeDeliveryEmptyAttributes = Object.fromEntries(
  Object.values(NATIVE_DELIVERY_ATTRIBUTE_NAMES).map((attributeName) => [attributeName, ''])
);
const XHR_LISTENER_INSTALL_FLAG = '__bcBundleCartFlowXhrListenerInstalled';
const CART_ENTRYPOINT_INSTALL_FLAG = '__bcBundleCartFlowCartEntrypointInstalled';
const BUNDLE_REPLACEMENT_DEBUG_PREFIX = '[BC bundle replace debug]';
const BREAD_BAG_GIFT_DEFAULT_THRESHOLD_PENCE = 4000;
const BREAD_BAG_GIFT_MARKER_VALUE = 'cotton-bread-bag';
const BREAD_BAG_GIFT_RULE = 'morning-bundle-4000';
const BREAD_BAG_GIFT_SOURCE = 'bundle-drawer';
const BUNDLE_CHOICE_GIFT_MARKER_PREFIX = 'bundle-choice:';
const FIRST_REWARD_CHOICE_SLOT = '3000';
const SECOND_REWARD_CHOICE_SLOT = '4000';
const THIRD_REWARD_CHOICE_SLOT = '5000';
const BUNDLE_CHOICE_GIFT_RULE = 'morning-bundle-4000-choice';
const BUNDLE_CHOICE_GIFT_SOURCE = 'bundle-gift-picker';
const BREAD_BAG_GIFT_DEBUG_PREFIX = '[BC bread bag sync]';
const BREAD_BAG_GIFT_SYNC_QUERY_KEY = 'bc_bread_bag_sync';
const BREAD_BAG_GIFT_DEBUG_QUERY_KEY = 'bc_bread_bag_debug';
const BREAD_BAG_GIFT_REQUEST_TIMEOUT_MS = 1800;
const BREAD_BAG_GIFT_RECONCILE_TIMEOUT_MS = 9000;
const GIFT_RECONCILE_TIMEOUT_RETRY_LIMIT = 2;
const CHECKOUT_RETURN_GIFT_RETRY_COOLDOWN_MS = 1000;
const BREAD_BAG_GIFT_CART_MUTATION_RECONCILE_DELAY_MS = 180;
const GUINNESS_CAKES_GIFT_DEFAULT_THRESHOLD_PENCE = 3000;
const GUINNESS_CAKES_GIFT_MARKER_VALUE = 'guinness-cakes';
const GUINNESS_CAKES_GIFT_RULE = 'morning-bundle-guinness-cakes-2000';
const GUINNESS_CAKES_GIFT_SOURCE = 'bundle-query-experiment';
const GUINNESS_CAKES_GIFT_DEBUG_PREFIX = '[BC Guinness cakes gift sync]';
const GUINNESS_CAKES_GIFT_SYNC_QUERY_KEY = 'bc_guinness_gift';
const GUINNESS_CAKES_GIFT_DEBUG_QUERY_KEY = 'bc_guinness_gift_debug';
const GUINNESS_CAKES_GIFT_TARGET_PATHS = new Set([
  '/products/morning-bundle',
  '/products/weekend-bundle',
]);

let openTimer;
let flareEnhancerTimers = [];
let hybridBundleDraftCaptureTimer = 0;
let hybridBundleDraftCaptureSettleTimers = [];
let hybridBundleDraftRestoreTimers = [];
let hybridBundleDraftRestoreMaskTimer = 0;
let hybridBundleDraftRestoreMaskAllowed = false;
let hybridBundleDraftRestoreActive = false;
let hybridBundleDraftRestoreStartedAt = 0;
let hybridBundleDraftRestoredThisLifecycle = false;
let hybridBundleDraftLastCaptureSignature = '';
let hybridBundleLastUserInteractionAt = 0;
let hybridBundleDraftEmptyClearSuppressedUntil = 0;
let hybridBundleFreshCartPrimeRequired = false;
let pendingBundleStateSnapshot = null;
let flareUiReadinessTimers = [];
let flareCalendarStateObserver = null;
let flareCalendarObservedNode = null;
let flareCalendarPortaledNode = null;
let flareCalendarPortalHost = null;
let flareCalendarOriginalParent = null;
let flareCalendarOriginalNextSibling = null;
let flareCalendarScrollContainer = null;
let flareCalendarPositionRaf = 0;
let flareCalendarPortalDocumentHandlerInstalled = false;
let flareFieldSyncRaf = 0;
let flarePostcodePlaceholderTimers = [];
let bundleDrawerLoadingHideTimer;
let bundleDrawerLoadingFallbackTimer;
let bundleDrawerFieldSyncTimers = [];
let bundleDrawerLoadingRecoveryAttempts = 0;
let lastDrawerOpenScheduledAt = 0;
let lastRenderedDrawerCartSignature = '';
let hasAttemptedFlareUiRecoveryForCurrentOpen = false;
let hasAttemptedMissingFlareBlockReopenForCurrentOpen = false;
let hasAttemptedDisabledCheckoutRecoveryForCurrentOpen = false;
let isBundleDrawerLoading = false;
let isBundleDrawerCartReconciliationPending = false;
let optimisticNativeDeliveryRoot = null;
let pendingBundleAddPipelineCount = 0;
let hasPendingBundleAddPipeline = false;
let bundleDrawerLoadingShownAt = 0;
let nativeFlareAppBlockScriptSuppressionDepth = 0;
let nativeDOMParserParseFromString = null;
let latestCartSnapshot = null;
let latestCartSnapshotVersion = 0;
let latestCartSnapshotPromise = null;
let historyRestoreCartSnapshotPromise = null;
let needsHistoryRestoreCartResync = false;
let lastHistoryRestoreCartResyncAttemptAt = 0;
let pendingCartSnapshotPromise = null;
let pendingBeforeCartSnapshot = null;
let latestExpectedBundleManifest = null;
let pendingBundleSessionState = 'idle';
let isProceedingToCheckout = false;
let isPreparingPendingBundleSession = false;
let allowNativeRechargeSubmitClick = false;
let checkoutIntentResetTimer;
let primedPendingBundleSessionResetTimer;
let isBundleSubmitHandlerInstalled = false;
let isBundleCheckoutGiftGateBypassActive = false;
let checkoutGiftFinalizationPromise = null;
let checkoutLifecycleGeneration = 0;
let checkoutReturnGiftRetryPromise = null;
let lastCheckoutReturnGiftRetryAt = 0;
let cartFlowNativeFetch = null;
let isBundleDirectCheckoutPending = false;
let suppressBundleRollbackOnNextDrawerClose = false;
let breadBagGiftReconcilePromise = null;
let guinnessCakesGiftReconcilePromise = null;
let giftReconcileChainPromise = null;
let giftReconcileTimeoutRetryCount = 0;
let breadBagGiftCartMutationReconcileTimer = 0;
let pendingBreadBagGiftCartMutation = null;
let breadBagGiftSyncState = {
  inFlight: false,
  lastAction: 'idle',
  lastError: null,
  lastPlan: null,
  lastCartSignature: '',
  lastSyncedAt: null,
};
let guinnessCakesGiftSyncState = {
  inFlight: false,
  lastAction: 'idle',
  lastError: null,
  lastPlan: null,
  lastCartSignature: '',
  lastSyncedAt: null,
};
let bundleMotionMaskElement = null;
let bundleMotionCloneElement = null;
let bundleMotionVariant = null;
let bundleMotionHostMinHeight = '';
let bundleInteractionUnlockTimers = [];

bundleMotionVariant = readBundleMotionVariant();

if (isActiveBundleFlow()) {
  document.documentElement.classList.add(ACTIVE_CLASS);
  if (isDirectCheckoutOnlyBundleFlow()) {
    document.documentElement.classList.add(BUNDLE_DIRECT_CHECKOUT_ONLY_CLASS);
  }
  installBreadBagGiftDebugHelpers();
  installGuinnessCakesGiftDebugHelpers();
  installHybridBundlePersistenceDebugHelpers();
  installBundleCheckoutAbDebugHelpers();
  installDeliveryPostcodeGatekeeperDebugHelpers();
  installBundleCheckoutReturnFallbackStatus();
  if (!isDirectCheckoutOnlyBundleFlow()) {
    installBundleDrawerLifecycleHandlers();
    installBundleCartEntrypointHandlers();
    installBundleDrawerLoadingPolishStyle();
    if (isNativeDeliveryEnabled()) {
      setNativeDeliveryFlareCheckoutGuardIgnoredForCurrentSurfaces(true);
      installNativeDeliveryCartObserver();
    } else {
      installFlareCalendarPortalHandlers();
    }
  }
  installBundlePendingAddEventHandler();
  installBundleAddToCartInstantFeedback();
  installRechargeSubmitClickInterceptor();
  installRechargeAddToCartHook();
  guardRechargeBundleReassignment();
  installFetchListener();
  installXhrListener();
  installBundlePageResumeHandler();
  installPersistedBundleFieldListeners();
  installFlarePostcodePlaceholderHandlers();
  installHybridBundleDraftLifecycleHandlers();
  initializePersistedBundleStateRestore();
} else if (isCartPage()) {
  installBreadBagGiftDebugHelpers();
  installGuinnessCakesGiftDebugHelpers();
  installFetchListener();
  installXhrListener();
  installBundleDrawerLifecycleHandlers();
  if (isNativeDeliveryEnabled()) {
    installNativeDeliveryCartObserver();
    enhanceBundleDeliveryUi();
  }
  initializeCartPageBreadBagGiftSync();
}

if (isActiveBundleFlow() || isCartPage()) {
  window.BC_BUNDLE_CART_FLOW_READY = true;
  document.dispatchEvent(new CustomEvent(BUNDLE_CART_FLOW_READY_EVENT));
}

function isActiveBundleFlow() {
  const normalizedPath = getStorefrontPath(window.location.pathname);
  return TARGET_PATHS.has(normalizedPath) || Boolean(document.querySelector('[data-bc-custom-bundle-mount]'));
}

function isCartPage() {
  return getStorefrontPath(window.location.pathname) === '/cart';
}

function isBundleReplacementDebugEnabled() {
  if (window.BC_BUNDLE_REPLACEMENT_DEBUG === true) {
    return true;
  }

  const requestedDebug = new URLSearchParams(window.location.search)
    .get('bc_bundle_replacement_debug')
    ?.trim()
    .toLowerCase();

  return ['1', 'on', 'true'].includes(requestedDebug);
}

function summarizeBundleReplacementCart(cart) {
  // Call sites build these summaries eagerly as log arguments; skip the per-item work
  // whenever debug logging is off.
  if (!isBundleReplacementDebugEnabled()) {
    return null;
  }

  const items = Array.isArray(cart?.items)
    ? cart.items.map((item) => ({
        key: item?.key ?? null,
        quantity: Number(item?.quantity) || 0,
        title: item?.title ?? null,
        bundleParent: item?.properties?._rc_bundle_parent ?? null,
        bundleId: item?.properties?._rc_bundle ?? null,
      }))
    : [];

  return {
    itemCount: Number(cart?.item_count) || items.reduce((count, item) => count + item.quantity, 0),
    items,
  };
}

function logBundleReplacementDebug(step, details = {}) {
  if (!isBundleReplacementDebugEnabled()) {
    return;
  }

  try {
    console.warn(BUNDLE_REPLACEMENT_DEBUG_PREFIX, step, details);
  } catch (error) {
    // Ignore debug logging failures.
  }
}

function getHybridBundlePersistenceMode() {
  if (isHybridBundlePersistenceBlockedOnLiveTheme()) {
    return 'off';
  }

  const requestedMode = new URLSearchParams(window.location.search)
    .get(BUNDLE_HYBRID_QUERY_KEY)
    ?.trim()
    .toLowerCase();

  if (requestedMode) {
    if (['0', 'off', 'false', 'disabled'].includes(requestedMode)) return 'off';
    if (['clear', 'reset'].includes(requestedMode)) return 'clear';
    if (['debug'].includes(requestedMode)) return 'debug';
    if (['1', 'on', 'true', 'live'].includes(requestedMode)) return 'on';
  }

  if (window.BC_BUNDLE_PERSISTENCE?.enabled === false) {
    return 'off';
  }

  return 'on';
}

function isHybridBundlePersistenceBlockedOnLiveTheme() {
  return window.BC_BUNDLE_PERSISTENCE?.disableOnLiveTheme === true && window.Shopify?.theme?.role === 'live';
}

function isHybridBundlePersistenceEnabled() {
  const mode = getHybridBundlePersistenceMode();
  return mode !== 'off';
}

function isHybridBundlePersistenceDebugEnabled() {
  const requestedDebug = new URLSearchParams(window.location.search)
    .get(BUNDLE_HYBRID_DEBUG_QUERY_KEY)
    ?.trim()
    .toLowerCase();

  return (
    getHybridBundlePersistenceMode() === 'debug' ||
    ['1', 'true', 'debug'].includes(requestedDebug || '') ||
    window.BC_BUNDLE_PERSISTENCE?.debug === true
  );
}

function logHybridBundlePersistenceDebug(step, details = {}) {
  if (!isHybridBundlePersistenceDebugEnabled()) {
    return;
  }

  try {
    console.warn(BUNDLE_HYBRID_DEBUG_PREFIX, step, details);
  } catch (error) {
    // Ignore debug logging failures.
  }
}

function installBreadBagGiftDebugHelpers() {
  window.bcBreadBagSyncStatus = function bcBreadBagSyncStatus() {
    const config = readBreadBagGiftConfig();

    return {
      enabled: isBreadBagGiftSyncEnabled(config),
      mode: getBreadBagGiftSyncMode(config),
      dryRun: isBreadBagGiftDryRunMode(config),
      config,
      ...breadBagGiftSyncState,
    };
  };

  window.bcBreadBagPlan = async function bcBreadBagPlan() {
    const cart = await fetchCartState();
    return buildCottonBreadBagGiftPlan(cart, { source: 'manual-plan' });
  };
}

function installGuinnessCakesGiftDebugHelpers() {
  window.bcGuinnessGiftSyncStatus = function bcGuinnessGiftSyncStatus() {
    const config = readGuinnessCakesGiftConfig();

    return {
      enabled: isGuinnessCakesGiftSyncEnabled(config),
      mode: getGuinnessCakesGiftSyncMode(config),
      dryRun: isGuinnessCakesGiftDryRunMode(config),
      queryAllowed: isGuinnessCakesGiftQueryAllowed(),
      config,
      ...guinnessCakesGiftSyncState,
    };
  };

  window.bcGuinnessGiftPlan = async function bcGuinnessGiftPlan() {
    const cart = await fetchCartState();
    return buildGuinnessCakesGiftPlan(cart, { source: 'manual-plan' });
  };
}

function installHybridBundlePersistenceDebugHelpers() {
  window.bcBundlePersistStatus = function bcBundlePersistStatus() {
    const draft = readHybridBundleDraft({ allowLegacy: true, logInvalid: false });

    return {
      enabled: isHybridBundlePersistenceEnabled(),
      mode: getHybridBundlePersistenceMode(),
      debug: isHybridBundlePersistenceDebugEnabled(),
      hasDraft: Boolean(draft),
      draft: summarizeHybridBundleDraft(draft),
      restoredThisLifecycle: hybridBundleDraftRestoredThisLifecycle,
      restoreActive: hybridBundleDraftRestoreActive,
    };
  };

  window.bcBundlePersistDump = function bcBundlePersistDump() {
    return readHybridBundleDraft({ allowLegacy: true, logInvalid: false });
  };

  window.bcBundlePersistCapture = function bcBundlePersistCapture(reason = 'manual') {
    return captureAndPersistHybridBundleDraft(String(reason || 'manual'), { force: true });
  };

  window.bcBundlePersistRestore = function bcBundlePersistRestore(reason = 'manual') {
    requestHybridBundleDraftRestore(String(reason || 'manual'), { force: true, allowMask: false });
    return window.bcBundlePersistStatus();
  };

  window.bcBundlePersistClear = function bcBundlePersistClear(reason = 'manual') {
    clearHybridBundleDraft(String(reason || 'manual'), { clearLegacy: true });
    return window.bcBundlePersistStatus();
  };
}

function installBundleCheckoutAbDebugHelpers() {
  window.bcBundleCheckoutAbStatus = function bcBundleCheckoutAbStatus() {
    const config = readBundleCheckoutAbConfig();
    const decision = getBundleCheckoutAbDecision({ persist: false });

    return {
      config,
      decision,
      pending: isBundleDirectCheckoutPending,
      theme: getBundleCheckoutAbThemeDescriptor(config),
    };
  };
}

function readBundleCheckoutAbConfig() {
  const rawConfig = window.BC_BUNDLE_CHECKOUT_AB ?? {};
  const rawAllocation = Number(rawConfig.allocation);
  const normalizedAllocation = Number.isFinite(rawAllocation)
    ? rawAllocation > 1
      ? rawAllocation / 100
      : rawAllocation
    : 0;
  const configuredThemeIds = Array.isArray(rawConfig.allowedThemeIds) ? rawConfig.allowedThemeIds : [];
  const allowedThemeIds = configuredThemeIds
    .map((themeId) => String(themeId ?? '').trim())
    .filter(Boolean);
  const configuredDirectCheckoutHandles = Array.isArray(rawConfig.directCheckoutProductHandles)
    ? rawConfig.directCheckoutProductHandles
    : [];
  const directCheckoutProductHandles = configuredDirectCheckoutHandles
    .map((handle) => String(handle ?? '').trim())
    .filter(Boolean);

  return {
    enabled: rawConfig.enabled === true,
    experimentId: String(rawConfig.experimentId || 'bundle-direct-checkout-v1').trim() || 'bundle-direct-checkout-v1',
    allocation: clampNumber(normalizedAllocation, 0, 1),
    disableOnLiveTheme: rawConfig.disableOnLiveTheme !== false,
    allowedThemeIds,
    directCheckoutProductHandles,
    themeId: String(rawConfig.themeId ?? '').trim(),
    themeRole: String(rawConfig.themeRole ?? '').trim(),
    dryRun: rawConfig.dryRun === true,
    debug: rawConfig.debug === true,
  };
}

function getBundleCheckoutAbThemeDescriptor(config = readBundleCheckoutAbConfig()) {
  const shopifyTheme = window.Shopify?.theme ?? {};
  const themeId = String(shopifyTheme.id ?? config.themeId ?? '').trim();
  const themeRole = String(shopifyTheme.role ?? config.themeRole ?? '').trim().toLowerCase();

  return {
    id: themeId,
    role: themeRole,
  };
}

function isBundleCheckoutAbThemeAllowed(config = readBundleCheckoutAbConfig()) {
  const theme = getBundleCheckoutAbThemeDescriptor(config);

  if (config.disableOnLiveTheme && theme.role === 'live') {
    return false;
  }

  if (config.allowedThemeIds.length === 0) {
    return true;
  }

  return Boolean(theme.id && config.allowedThemeIds.includes(theme.id));
}

function readBundleCheckoutAbQueryMode() {
  const requestedMode = new URLSearchParams(window.location.search)
    .get(BUNDLE_CHECKOUT_AB_QUERY_KEY)
    ?.trim()
    .toLowerCase();

  if (['checkout', 'direct', 'variant', 'test', '1', 'true', 'on'].includes(requestedMode)) {
    return 'checkout';
  }

  if (['control', 'drawer', 'flare'].includes(requestedMode)) {
    return 'control';
  }

  if (['0', 'off', 'false', 'disabled'].includes(requestedMode)) {
    return 'off';
  }

  if (requestedMode === 'clear') {
    return 'clear';
  }

  return null;
}

function isBundleCheckoutAbDebugEnabled(config = readBundleCheckoutAbConfig()) {
  const requestedDebug = new URLSearchParams(window.location.search)
    .get(BUNDLE_CHECKOUT_AB_DEBUG_QUERY_KEY)
    ?.trim()
    .toLowerCase();

  return config.debug || ['1', 'on', 'true'].includes(requestedDebug);
}

function isBundleCheckoutAbDryRun(config = readBundleCheckoutAbConfig()) {
  const requestedDryRun = new URLSearchParams(window.location.search)
    .get(BUNDLE_CHECKOUT_AB_DRY_QUERY_KEY)
    ?.trim()
    .toLowerCase();

  return config.dryRun || ['1', 'on', 'true'].includes(requestedDryRun);
}

function getBundleCheckoutAbDecision({ persist = true } = {}) {
  const config = readBundleCheckoutAbConfig();
  const queryMode = readBundleCheckoutAbQueryMode();
  const theme = getBundleCheckoutAbThemeDescriptor(config);
  const dryRun = isBundleCheckoutAbDryRun(config);

  if (!config.enabled) {
    return buildBundleCheckoutAbDecision(config, theme, {
      enabled: false,
      cohort: 'control',
      dryRun,
      reason: 'config-disabled',
    });
  }

  if (!isBundleCheckoutAbThemeAllowed(config)) {
    return buildBundleCheckoutAbDecision(config, theme, {
      enabled: false,
      cohort: 'control',
      dryRun,
      reason: theme.role === 'live' ? 'live-theme-disabled' : 'theme-not-allowed',
    });
  }

  if (queryMode === 'clear') {
    clearBundleCheckoutAbStoredAssignment();
    return buildBundleCheckoutAbDecision(config, theme, {
      enabled: false,
      cohort: 'control',
      dryRun,
      forced: true,
      reason: 'query-clear',
    });
  }

  if (queryMode === 'off') {
    return buildBundleCheckoutAbDecision(config, theme, {
      enabled: false,
      cohort: 'control',
      dryRun,
      forced: true,
      reason: 'query-off',
    });
  }

  if (queryMode === 'checkout' || queryMode === 'control') {
    return buildBundleCheckoutAbDecision(config, theme, {
      enabled: true,
      cohort: queryMode,
      dryRun,
      forced: true,
      reason: `query-${queryMode}`,
    });
  }

  if (isCurrentBundleProductForcedToCheckout(config)) {
    return buildBundleCheckoutAbDecision(config, theme, {
      enabled: true,
      cohort: 'checkout',
      dryRun,
      forced: true,
      reason: 'product-direct-checkout',
    });
  }

  if (config.allocation <= 0) {
    return buildBundleCheckoutAbDecision(config, theme, {
      enabled: true,
      cohort: 'control',
      dryRun,
      reason: 'allocation-zero',
    });
  }

  const storedAssignment = readBundleCheckoutAbStoredAssignment(config, theme);

  if (storedAssignment) {
    return buildBundleCheckoutAbDecision(config, theme, {
      enabled: true,
      cohort: storedAssignment.cohort,
      dryRun,
      reason: 'stored-assignment',
    });
  }

  const cohort = Math.random() < config.allocation ? 'checkout' : 'control';

  if (persist) {
    persistBundleCheckoutAbStoredAssignment(config, theme, cohort);
  }

  return buildBundleCheckoutAbDecision(config, theme, {
    enabled: true,
    cohort,
    dryRun,
    reason: 'new-assignment',
  });
}

function isCurrentBundleProductForcedToCheckout(config = readBundleCheckoutAbConfig()) {
  return config.directCheckoutProductHandles.includes(getCurrentBundleProductHandle());
}

function isDirectCheckoutOnlyBundleFlow(config = readBundleCheckoutAbConfig()) {
  return isBundleCheckoutAbThemeAllowed(config) && isCurrentBundleProductForcedToCheckout(config);
}

function buildBundleCheckoutAbDecision(config, theme, overrides = {}) {
  return {
    experimentId: config.experimentId,
    enabled: overrides.enabled === true,
    cohort: overrides.cohort === 'checkout' ? 'checkout' : 'control',
    dryRun: overrides.dryRun === true,
    forced: overrides.forced === true,
    reason: String(overrides.reason || 'unknown'),
    themeId: theme.id,
    themeRole: theme.role,
  };
}

function readBundleCheckoutAbStoredAssignment(config, theme) {
  try {
    const rawAssignment = getBrowserStorage('localStorage')?.getItem(BUNDLE_CHECKOUT_AB_STORAGE_KEY);

    if (!rawAssignment) {
      return null;
    }

    const assignment = JSON.parse(rawAssignment);

    if (
      assignment?.schemaVersion !== BUNDLE_CHECKOUT_AB_SCHEMA_VERSION ||
      assignment?.experimentId !== config.experimentId ||
      assignment?.themeId !== theme.id ||
      !['control', 'checkout'].includes(assignment?.cohort) ||
      Number(assignment?.expiresAt) <= Date.now()
    ) {
      clearBundleCheckoutAbStoredAssignment();
      return null;
    }

    return assignment;
  } catch (error) {
    return null;
  }
}

function persistBundleCheckoutAbStoredAssignment(config, theme, cohort) {
  try {
    getBrowserStorage('localStorage')?.setItem(
      BUNDLE_CHECKOUT_AB_STORAGE_KEY,
      JSON.stringify({
        schemaVersion: BUNDLE_CHECKOUT_AB_SCHEMA_VERSION,
        experimentId: config.experimentId,
        cohort,
        themeId: theme.id,
        themeRole: theme.role,
        assignedAt: Date.now(),
        expiresAt: Date.now() + BUNDLE_CHECKOUT_AB_ASSIGNMENT_TTL_MS,
      })
    );
  } catch (error) {
    // Ignore storage failures; query overrides still work.
  }
}

function clearBundleCheckoutAbStoredAssignment() {
  try {
    getBrowserStorage('localStorage')?.removeItem(BUNDLE_CHECKOUT_AB_STORAGE_KEY);
  } catch (error) {
    // Ignore storage failures.
  }
}

function logBundleCheckoutAbDebug(step, details = {}) {
  if (!isBundleCheckoutAbDebugEnabled()) {
    return;
  }

  try {
    console.info(BUNDLE_CHECKOUT_AB_DEBUG_PREFIX, step, details);
  } catch (error) {
    // Ignore debug logging failures.
  }
}

function emitBundleCheckoutAbEvent(step, details = {}) {
  const config = readBundleCheckoutAbConfig();
  const decision = getBundleCheckoutAbDecision({ persist: false });
  const payload = {
    event: 'bc_bundle_checkout_ab',
    step,
    experimentId: config.experimentId,
    cohort: decision.cohort,
    dryRun: decision.dryRun,
    reason: decision.reason,
    themeId: decision.themeId,
    themeRole: decision.themeRole,
    ...details,
  };

  try {
    window.dispatchEvent(new CustomEvent('bc:bundle-checkout-ab', { detail: payload }));
  } catch (error) {
    // Ignore CustomEvent failures.
  }

  try {
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(payload);
    }
  } catch (error) {
    // Ignore analytics failures.
  }

  logBundleCheckoutAbDebug(step, payload);
}

function getRewardChoiceMarker(slot, choiceId) {
  return `${BUNDLE_CHOICE_GIFT_MARKER_PREFIX}${slot}:${choiceId}`;
}

function getRewardChoiceIdFromMarker(marker, slot) {
  const normalized = String(marker || '');
  const scopedPrefix = `${BUNDLE_CHOICE_GIFT_MARKER_PREFIX}${slot}:`;
  if (normalized.startsWith(scopedPrefix)) return normalized.slice(scopedPrefix.length);

  // Legacy GBP 40 markers were `bundle-choice:<id>` with no slot.
  if (slot === SECOND_REWARD_CHOICE_SLOT && normalized.startsWith(BUNDLE_CHOICE_GIFT_MARKER_PREFIX)) {
    const legacyChoiceId = normalized.slice(BUNDLE_CHOICE_GIFT_MARKER_PREFIX.length);
    return legacyChoiceId.includes(':') ? '' : legacyChoiceId;
  }

  return '';
}

function readBreadBagGiftConfig(cart = null, requestedRewardSlot = SECOND_REWARD_CHOICE_SLOT) {
  const rawConfig = window.BC_BREAD_BAG_GIFT ?? {};
  const rewardSlot = requestedRewardSlot === THIRD_REWARD_CHOICE_SLOT
    ? THIRD_REWARD_CHOICE_SLOT
    : SECOND_REWARD_CHOICE_SLOT;
  const variantId = Number(rawConfig.variantId);
  const thresholdPence = Number(
    rewardSlot === THIRD_REWARD_CHOICE_SLOT
      ? rawConfig.thirdThresholdPence
      : rawConfig.thresholdPence
  );
  const configuredParents = Array.isArray(rawConfig.targetBundleParents) ? rawConfig.targetBundleParents : [];
  const targetBundleParents = configuredParents
    .map((handle) => String(handle || '').trim())
    .filter(Boolean);
  const choices = (Array.isArray(rawConfig.choices) ? rawConfig.choices : [])
    .map((choice) => {
      const choiceVariantId = Number(choice?.variantId);
      const id = String(choice?.id || '').trim();
      const productHandle = String(choice?.productHandle || '').trim();
      if (!id || !productHandle || !Number.isFinite(choiceVariantId) || choiceVariantId <= 0) return null;
      return { id, productHandle, variantId: choiceVariantId };
    })
    .filter(Boolean);
  const retiredChoices = (Array.isArray(rawConfig.retiredChoices) ? rawConfig.retiredChoices : [])
    .map((choice) => {
      const id = String(choice?.id || '').trim();
      if (!id) return null;
      return { id };
    })
    .filter(Boolean);

  const selectedChoice = Array.isArray(cart?.items)
    ? cart.items
      .map((item) => {
        if (!isTargetBundleLineForGiftConfig(item, { targetBundleParents })) return null;
        const choiceId = String(
          item?.properties?.[`_bc_reward_choice_${rewardSlot}`] ||
          (rewardSlot === SECOND_REWARD_CHOICE_SLOT ? item?.properties?._bc_final_reward_choice : '') ||
          ''
        ).trim();
        return choices.find((choice) => choice.id === choiceId) || null;
      })
      .find(Boolean)
    : null;
  const skipped = Array.isArray(cart?.items) && cart.items.some((item) => (
    isTargetBundleLineForGiftConfig(item, { targetBundleParents }) &&
    String(item?.properties?.[`_bc_reward_skipped_${rewardSlot}`] || '').trim().toLowerCase() === 'true'
  ));

  return {
    enabled: rawConfig.enabled === true,
    rewardSlot,
    thresholdPence: Number.isFinite(thresholdPence) && thresholdPence > 0
      ? thresholdPence
      : rewardSlot === THIRD_REWARD_CHOICE_SLOT ? 5000 : BREAD_BAG_GIFT_DEFAULT_THRESHOLD_PENCE,
    productHandle: selectedChoice?.productHandle || String(rawConfig.productHandle || BREAD_BAG_GIFT_MARKER_VALUE).trim() || BREAD_BAG_GIFT_MARKER_VALUE,
    variantId: selectedChoice?.variantId || (Number.isFinite(variantId) && variantId > 0 ? variantId : null),
    markerValue: selectedChoice ? getRewardChoiceMarker(rewardSlot, selectedChoice.id) : BREAD_BAG_GIFT_MARKER_VALUE,
    rule: selectedChoice ? `morning-bundle-${rewardSlot}-choice` : BREAD_BAG_GIFT_RULE,
    source: selectedChoice ? BUNDLE_CHOICE_GIFT_SOURCE : BREAD_BAG_GIFT_SOURCE,
    choiceId: selectedChoice?.id || null,
    choiceMode: choices.length > 0,
    skipped,
    choices,
    retiredChoices,
    targetBundleParents: targetBundleParents.length > 0 ? targetBundleParents : Array.from(TARGET_PRODUCT_HANDLES),
  };
}

function getBreadBagGiftSyncMode(config = readBreadBagGiftConfig()) {
  if (window.BC_BREAD_BAG_SYNC_DISABLED === true) {
    return 'off';
  }

  const requestedMode = new URLSearchParams(window.location.search)
    .get(BREAD_BAG_GIFT_SYNC_QUERY_KEY)
    ?.trim()
    .toLowerCase();

  if (['0', 'off', 'false', 'disabled'].includes(requestedMode)) {
    return 'off';
  }

  if (['dry', 'dry-run', 'dryrun'].includes(requestedMode)) {
    return 'dry';
  }

  if (['1', 'on', 'true', 'live'].includes(requestedMode)) {
    return 'live';
  }

  return config.enabled ? 'live' : 'off';
}

function isBreadBagGiftSyncEnabled(config = readBreadBagGiftConfig()) {
  return getBreadBagGiftSyncMode(config) !== 'off';
}

function isBreadBagGiftDryRunMode(config = readBreadBagGiftConfig()) {
  return getBreadBagGiftSyncMode(config) === 'dry';
}

function isBreadBagGiftDebugEnabled() {
  const requestedDebug = new URLSearchParams(window.location.search)
    .get(BREAD_BAG_GIFT_DEBUG_QUERY_KEY)
    ?.trim()
    .toLowerCase();

  return window.BC_BREAD_BAG_DEBUG === true || ['1', 'on', 'true'].includes(requestedDebug);
}

function logBreadBagGiftDebug(step, details = {}) {
  if (!isBreadBagGiftDebugEnabled()) {
    return;
  }

  try {
    console.info(BREAD_BAG_GIFT_DEBUG_PREFIX, step, details);
  } catch (error) {
    // Ignore debug logging failures.
  }
}

function getBreadBagGiftLineProperties(config = readBreadBagGiftConfig()) {
  return {
    _bc_free_gift: config.markerValue,
    _bc_free_gift_rule: config.rule,
    _bc_free_gift_source: config.source,
    ...(config.choiceId ? { _bc_final_reward_choice: config.choiceId } : {}),
    ...(config.choiceId ? {
      _bc_reward_slot: config.rewardSlot || SECOND_REWARD_CHOICE_SLOT,
      _bc_reward_choice: config.choiceId,
    } : {}),
  };
}

function isMarkedBreadBagGiftLine(item, config = readBreadBagGiftConfig()) {
  const properties = item?.properties || {};

  // Match on the marker value + product only. Requiring the exact rule string made
  // promo lines written under any other rule version invisible to removal — they
  // would survive a one-time switch and be charged.
  return properties._bc_free_gift === config.markerValue && isBreadBagProductLine(item, config);
}

function isKnownFinalRewardGiftLine(item, config = readBreadBagGiftConfig()) {
  const marker = String(item?.properties?._bc_free_gift || '');
  if (marker === BREAD_BAG_GIFT_MARKER_VALUE) return true;
  const choiceId = getRewardChoiceIdFromMarker(marker, config.rewardSlot || SECOND_REWARD_CHOICE_SLOT);
  if (!choiceId) return false;
  const knownChoices = Array.isArray(config.choices) ? config.choices : [];
  const retiredChoices = Array.isArray(config.retiredChoices) ? config.retiredChoices : [];
  return [...knownChoices, ...retiredChoices].some((choice) => choice.id === choiceId);
}

function isBreadBagProductLine(item, config = readBreadBagGiftConfig()) {
  if (!item) return false;

  const variantId = Number(item.variant_id);

  if (config.variantId && variantId === config.variantId) {
    return true;
  }

  if (item.handle && item.handle === config.productHandle) {
    return true;
  }

  if (typeof item.url === 'string' && item.url.includes(`/products/${config.productHandle}`)) {
    return true;
  }

  return false;
}

function isTargetBundleLineForGiftConfig(item, config) {
  const bundleHandle = item?.properties?._rc_bundle_parent;
  const bundleId = item?.properties?._rc_bundle;

  return Boolean(bundleId && bundleHandle && config?.targetBundleParents?.includes(bundleHandle));
}

function isSubscriptionCartLine(item) {
  return Boolean(
    item?.selling_plan_allocation ||
      item?.selling_plan ||
      item?.selling_plan_id ||
      item?.properties?.selling_plan ||
      item?.properties?._selling_plan
  );
}

function getNativeDeliveryPurchaseModeFromCart(cart) {
  if (!Array.isArray(cart?.items)) return 'unknown';

  const bundleLines = cart.items.filter((item) => (
    item?.properties?._rc_bundle &&
    !isAnyMarkedFreeGiftLine(item) &&
    item?.gift_card !== true
  ));

  if (bundleLines.length === 0) return 'unknown';

  const subscriptionLineCount = bundleLines.filter(isSubscriptionCartLine).length;
  if (subscriptionLineCount === bundleLines.length) return 'subscription';
  if (subscriptionLineCount === 0) return 'one-time';
  return 'mixed';
}

function normalizeNativeDeliveryPurchaseMode(purchaseMode) {
  if (purchaseMode === 'onetime' || purchaseMode === 'one-time') return 'one-time';
  if (purchaseMode === 'subscription') return 'subscription';
  if (purchaseMode === 'mixed') return 'mixed';
  return 'unknown';
}

function getTrackedBundleGiftPurchaseMode(cart, config) {
  if (!Array.isArray(cart?.items)) return 'unknown';

  const trackedChargeableLines = cart.items.filter((item) => {
    if (!isTargetBundleLineForGiftConfig(item, config)) {
      return false;
    }

    if (isAnyMarkedFreeGiftLine(item) || item?.gift_card === true) {
      return false;
    }

    return true;
  });

  if (trackedChargeableLines.length === 0) {
    return 'unknown';
  }

  const subscriptionLineCount = trackedChargeableLines.filter(isSubscriptionCartLine).length;
  const oneTimeLineCount = trackedChargeableLines.length - subscriptionLineCount;

  if (subscriptionLineCount > 0 && oneTimeLineCount > 0) {
    return 'mixed';
  }

  return subscriptionLineCount > 0 ? 'subscription' : 'one-time';
}

function shouldRemovePromoGiftForPurchaseMode(purchaseMode) {
  return purchaseMode === 'one-time' || purchaseMode === 'mixed';
}

function getTrackedBundleGiftProductLines(cart, config, isGiftProductLine) {
  if (!Array.isArray(cart?.items) || typeof isGiftProductLine !== 'function') return [];

  return cart.items.filter((item) => {
    return isTargetBundleLineForGiftConfig(item, config) &&
      isAnyMarkedFreeGiftLine(item) &&
      isGiftProductLine(item, config);
  });
}

function getUniqueCartLineKeys(items) {
  const keys = [];
  const seen = new Set();

  items.forEach((item) => {
    const key = item?.key;
    if (!key || seen.has(key)) return;
    seen.add(key);
    keys.push(key);
  });

  return keys;
}

function getManualBreadBagLines(cart, config = readBreadBagGiftConfig()) {
  if (!Array.isArray(cart?.items)) return [];

  return cart.items.filter((item) => (
    isBreadBagProductLine(item, config) &&
    !isMarkedBreadBagGiftLine(item, config) &&
    !isAnyMarkedFreeGiftLine(item)
  ));
}

function getMarkedBreadBagGiftLines(cart, config = readBreadBagGiftConfig()) {
  if (!Array.isArray(cart?.items)) return [];

  return cart.items.filter((item) => isMarkedBreadBagGiftLine(item, config));
}

function getSupersededFinalRewardGiftLines(cart, config = readBreadBagGiftConfig()) {
  if (!Array.isArray(cart?.items)) return [];
  return cart.items.filter((item) => isKnownFinalRewardGiftLine(item, config) && !isMarkedBreadBagGiftLine(item, config));
}

function isBreadBagTargetBundleParent(bundleHandle, config = readBreadBagGiftConfig()) {
  if (!bundleHandle) return false;

  return config.targetBundleParents.includes(bundleHandle);
}

function getNonNegativeCartPence(value) {
  if (value === null || value === undefined || value === '') return null;
  const pence = Number(value);
  return Number.isFinite(pence) && pence >= 0 ? pence : null;
}

function getActivePreOrderDiscountLinePrice(item) {
  const sellingPlanPrice = getNonNegativeCartPence(item?.selling_plan_allocation?.price);
  const quantity = getNonNegativeCartPence(item?.quantity);

  if (sellingPlanPrice !== null && quantity !== null) {
    return sellingPlanPrice * quantity;
  }

  const candidates = [
    item?.original_line_price,
    item?.line_price,
    item?.final_line_price,
  ];

  for (const candidate of candidates) {
    const price = getNonNegativeCartPence(candidate);
    if (price !== null) return price;
  }

  return 0;
}

function getBreadBagEligibleBundleSubtotal(cart, config = readBreadBagGiftConfig()) {
  if (!Array.isArray(cart?.items)) return 0;

  return cart.items.reduce((subtotal, item) => {
    const bundleHandle = item?.properties?._rc_bundle_parent;
    const bundleId = item?.properties?._rc_bundle;

    if (!bundleId || !isBreadBagTargetBundleParent(bundleHandle, config)) {
      return subtotal;
    }

    if (isAnyMarkedFreeGiftLine(item) || item?.gift_card === true) {
      return subtotal;
    }

    return subtotal + getActivePreOrderDiscountLinePrice(item);
  }, 0);
}

function buildCottonBreadBagGiftPlan(cart, { source = 'unknown', rewardSlot = SECOND_REWARD_CHOICE_SLOT } = {}) {
  const config = readBreadBagGiftConfig(cart, rewardSlot);
  const mode = getBreadBagGiftSyncMode(config);
  const cartSignature = buildCartSnapshotSignature(cart);
  const markedGiftLines = getMarkedBreadBagGiftLines(cart, config);
  const supersededGiftLines = getSupersededFinalRewardGiftLines(cart, config);
  const allFinalRewardGiftLines = [...markedGiftLines, ...supersededGiftLines];
  const manualBreadBagLines = getManualBreadBagLines(cart, config);
  const purchaseMode = getTrackedBundleGiftPurchaseMode(cart, config, isBreadBagProductLine);
  const trackedBundleGiftProductLines = getTrackedBundleGiftProductLines(cart, config, isBreadBagProductLine);
  const eligibleBundleSubtotal = getBreadBagEligibleBundleSubtotal(cart, config);
  const qualifies = eligibleBundleSubtotal >= config.thresholdPence;
  const invalidReasons = [];

  if (!config.variantId) {
    invalidReasons.push('missing-gift-variant-id');
  }

  if (!Array.isArray(cart?.items)) {
    invalidReasons.push('missing-cart-items');
  }

  const plan = {
    source,
    rewardSlot: config.rewardSlot,
    mode,
    enabled: mode !== 'off',
    dryRun: mode === 'dry',
    valid: invalidReasons.length === 0,
    invalidReasons,
    action: 'noop',
    reason: '',
    cartSignature,
    thresholdPence: config.thresholdPence,
    eligibleBundleSubtotal,
    qualifies,
    giftVariantId: config.variantId,
    giftLineProperties: getBreadBagGiftLineProperties(config),
    choiceId: config.choiceId,
    skipped: config.skipped,
    targetBundleParents: config.targetBundleParents,
    purchaseMode,
    markedGiftLineKeys: markedGiftLines.map((item) => item?.key).filter(Boolean),
    supersededGiftLineKeys: supersededGiftLines.map((item) => item?.key).filter(Boolean),
    manualBreadBagLineKeys: manualBreadBagLines.map((item) => item?.key).filter(Boolean),
    trackedBundleGiftProductLineKeys: trackedBundleGiftProductLines.map((item) => item?.key).filter(Boolean),
    removeLineKeys: [],
    normalizeLineKey: null,
    addGift: false,
  };

  // Removal decisions come before the off/invalid gates: protecting customers from
  // being charged for a promo gift must not depend on the promo being enabled or on
  // add-side config (the gift variant id) being present.
  if (shouldRemovePromoGiftForPurchaseMode(purchaseMode)) {
    const removeLineKeys = getUniqueCartLineKeys([...allFinalRewardGiftLines, ...trackedBundleGiftProductLines]);

    if (removeLineKeys.length > 0) {
      plan.action = 'remove';
      plan.reason = `bundle-purchase-mode-${purchaseMode}`;
      plan.removeLineKeys = removeLineKeys;
    } else {
      plan.reason = `bundle-purchase-mode-${purchaseMode}-no-gift`;
    }

    return plan;
  }

  if (!qualifies && allFinalRewardGiftLines.length > 0) {
    plan.action = 'remove';
    plan.reason = 'bundle-subtotal-below-threshold';
    plan.removeLineKeys = allFinalRewardGiftLines.map((item) => item?.key).filter(Boolean);
    return plan;
  }

  if (config.skipped) {
    if (allFinalRewardGiftLines.length > 0) {
      plan.action = 'remove';
      plan.reason = 'gift-skipped-remove-existing';
      plan.removeLineKeys = allFinalRewardGiftLines.map((item) => item?.key).filter(Boolean);
    } else {
      plan.reason = 'gift-skipped';
    }
    return plan;
  }

  if (config.choiceMode && !config.choiceId) {
    if (allFinalRewardGiftLines.length > 0) {
      plan.action = 'remove';
      plan.reason = 'choice-required-remove-legacy-gift';
      plan.removeLineKeys = getUniqueCartLineKeys(allFinalRewardGiftLines);
    } else {
      plan.reason = 'choice-required';
    }
    return plan;
  }

  if (markedGiftLines.length > 0) {
    const [keeperGiftLine, ...extraGiftLines] = markedGiftLines;
    const keeperQuantity = Number(keeperGiftLine?.quantity) || 0;

    if (keeperQuantity !== 1 || extraGiftLines.length > 0 || supersededGiftLines.length > 0) {
      plan.action = 'normalize';
      plan.reason = 'marked-gift-quantity-or-duplicate';
      plan.normalizeLineKey = keeperQuantity !== 1 ? keeperGiftLine?.key ?? null : null;
      plan.removeLineKeys = getUniqueCartLineKeys([...extraGiftLines, ...supersededGiftLines]);
      return plan;
    }
  }

  if (mode === 'off') {
    plan.reason = 'sync-disabled';
    return plan;
  }

  if (!plan.valid) {
    plan.action = 'invalid-config';
    plan.reason = invalidReasons.join(',');
    return plan;
  }

  if (!qualifies) {
    plan.reason = 'bundle-subtotal-below-threshold-no-gift';
    return plan;
  }

  if (!config.choiceMode && manualBreadBagLines.length > 0) {
    if (markedGiftLines.length > 0) {
      plan.action = 'remove';
      plan.reason = 'manual-bread-bag-present-remove-promo-gift';
      plan.removeLineKeys = markedGiftLines.map((item) => item?.key).filter(Boolean);
      return plan;
    }

    plan.reason = 'manual-bread-bag-present';
    return plan;
  }

  if (markedGiftLines.length === 0) {
    plan.action = supersededGiftLines.length > 0 ? 'replace' : 'add';
    plan.reason = supersededGiftLines.length > 0 ? 'gift-choice-changed' : 'bundle-subtotal-qualified';
    plan.removeLineKeys = supersededGiftLines.map((item) => item?.key).filter(Boolean);
    plan.addGift = true;
    return plan;
  }

  plan.reason = 'gift-already-correct';
  return plan;
}

function readGuinnessCakesGiftConfig(cart = null) {
  const rawConfig = window.BC_GUINNESS_CAKES_GIFT ?? {};
  const thresholdPence = Number(rawConfig.thresholdPence);
  const configuredParents = Array.isArray(rawConfig.targetBundleParents) ? rawConfig.targetBundleParents : [];
  const targetBundleParents = configuredParents
    .map((handle) => String(handle || '').trim())
    .filter(Boolean);
  const choices = (Array.isArray(rawConfig.choices) ? rawConfig.choices : [])
    .map((choice) => {
      const id = String(choice?.id || '').trim();
      const productHandle = String(choice?.productHandle || '').trim();
      const productId = Number(choice?.productId);
      const variantId = Number(choice?.variantId);
      if (!id || !productHandle || !Number.isFinite(variantId) || variantId <= 0) return null;
      return {
        id,
        productHandle,
        productId: Number.isFinite(productId) && productId > 0 ? productId : null,
        variantId,
      };
    })
    .filter(Boolean);
  const retiredChoices = (Array.isArray(rawConfig.retiredChoices) ? rawConfig.retiredChoices : [])
    .map((choice) => {
      const id = String(choice?.id || '').trim();
      if (!id) return null;
      return { id };
    })
    .filter(Boolean);
  const selectedChoice = Array.isArray(cart?.items)
    ? cart.items
      .map((item) => {
        if (!isTargetBundleLineForGiftConfig(item, { targetBundleParents })) return null;
        const choiceId = String(item?.properties?._bc_reward_choice_3000 || '').trim();
        return choices.find((choice) => choice.id === choiceId) || null;
      })
      .find(Boolean)
    : null;
  const skipped = Array.isArray(cart?.items) && cart.items.some((item) => (
    isTargetBundleLineForGiftConfig(item, { targetBundleParents }) &&
    String(item?.properties?._bc_reward_skipped_3000 || '').trim().toLowerCase() === 'true'
  ));

  return {
    enabled: rawConfig.enabled === true,
    thresholdPence:
      Number.isFinite(thresholdPence) && thresholdPence > 0
        ? thresholdPence
        : GUINNESS_CAKES_GIFT_DEFAULT_THRESHOLD_PENCE,
    productHandle: selectedChoice?.productHandle ||
      String(rawConfig.productHandle || GUINNESS_CAKES_GIFT_MARKER_VALUE).trim() ||
      GUINNESS_CAKES_GIFT_MARKER_VALUE,
    productId: selectedChoice?.productId || (Number(rawConfig.productId) || null),
    variantId: selectedChoice?.variantId || (Number(rawConfig.variantId) || null),
    markerValue: selectedChoice
      ? getRewardChoiceMarker(FIRST_REWARD_CHOICE_SLOT, selectedChoice.id)
      : GUINNESS_CAKES_GIFT_MARKER_VALUE,
    rule: selectedChoice ? 'morning-bundle-3000-choice' : GUINNESS_CAKES_GIFT_RULE,
    source: selectedChoice ? BUNDLE_CHOICE_GIFT_SOURCE : GUINNESS_CAKES_GIFT_SOURCE,
    choiceId: selectedChoice?.id || null,
    choiceMode: choices.length > 0,
    skipped,
    choices,
    retiredChoices,
    targetBundleParents:
      targetBundleParents.length > 0
        ? targetBundleParents
        : ['morning-bundle', 'weekend-bundle'],
  };
}

function getGuinnessCakesGiftSyncMode(config = readGuinnessCakesGiftConfig()) {
  const requestedMode = new URLSearchParams(window.location.search)
    .get(GUINNESS_CAKES_GIFT_SYNC_QUERY_KEY)
    ?.trim()
    .toLowerCase();

  if (['0', 'off', 'false', 'disabled'].includes(requestedMode)) {
    return 'off';
  }

  if (isGuinnessCakesGiftQueryAllowed() && ['dry', 'dry-run', 'dryrun'].includes(requestedMode)) {
    return 'dry';
  }

  if (isGuinnessCakesGiftQueryAllowed() && ['1', 'on', 'true', 'live'].includes(requestedMode)) {
    return 'live';
  }

  // Liquid only enables this on gift-aware bundle/cart surfaces. Once enabled,
  // the storefront should add missing gifts; the query kill switch still wins.
  return config.enabled ? 'live' : 'off';
}

function isGuinnessCakesGiftSyncEnabled(config = readGuinnessCakesGiftConfig()) {
  return getGuinnessCakesGiftSyncMode(config) !== 'off';
}

function isGuinnessCakesGiftDryRunMode(config = readGuinnessCakesGiftConfig()) {
  return getGuinnessCakesGiftSyncMode(config) === 'dry';
}

function isGuinnessCakesGiftQueryAllowed() {
  return GUINNESS_CAKES_GIFT_TARGET_PATHS.has(getStorefrontPath(window.location.pathname));
}

function isGuinnessCakesGiftDebugEnabled() {
  const requestedDebug = new URLSearchParams(window.location.search)
    .get(GUINNESS_CAKES_GIFT_DEBUG_QUERY_KEY)
    ?.trim()
    .toLowerCase();

  return window.BC_GUINNESS_CAKES_GIFT_DEBUG === true || ['1', 'on', 'true'].includes(requestedDebug);
}

function logGuinnessCakesGiftDebug(step, details = {}) {
  if (!isGuinnessCakesGiftDebugEnabled()) {
    return;
  }

  try {
    console.info(GUINNESS_CAKES_GIFT_DEBUG_PREFIX, step, details);
  } catch (error) {
    // Ignore debug logging failures.
  }
}

function getGuinnessCakesGiftLineProperties(config = readGuinnessCakesGiftConfig()) {
  return {
    _bc_free_gift: config.markerValue,
    _bc_free_gift_rule: config.rule,
    _bc_free_gift_source: config.source,
    ...(config.choiceId ? {
      _bc_reward_slot: FIRST_REWARD_CHOICE_SLOT,
      _bc_reward_choice: config.choiceId,
    } : {}),
  };
}

function isAnyMarkedFreeGiftLine(item) {
  const properties = item?.properties || {};
  return typeof properties._bc_free_gift === 'string' && properties._bc_free_gift.trim() !== '';
}

function isMarkedGuinnessCakesGiftLine(item, config = readGuinnessCakesGiftConfig()) {
  const properties = item?.properties || {};

  // Match on the marker value + product only — see isMarkedBreadBagGiftLine.
  return properties._bc_free_gift === config.markerValue && isGuinnessCakesProductLine(item, config);
}

function isKnownFirstRewardGiftLine(item, config = readGuinnessCakesGiftConfig()) {
  const marker = String(item?.properties?._bc_free_gift || '');
  if (marker === GUINNESS_CAKES_GIFT_MARKER_VALUE) return true;
  const choiceId = getRewardChoiceIdFromMarker(marker, FIRST_REWARD_CHOICE_SLOT);
  const knownChoices = Array.isArray(config.choices) ? config.choices : [];
  const retiredChoices = Array.isArray(config.retiredChoices) ? config.retiredChoices : [];
  return Boolean(
    choiceId &&
    [...knownChoices, ...retiredChoices].some((choice) => choice.id === choiceId)
  );
}

function isGuinnessCakesProductLine(item, config = readGuinnessCakesGiftConfig()) {
  if (!item) return false;

  const productId = Number(item.product_id);
  const variantId = Number(item.variant_id);

  if (config.productId && productId === config.productId) {
    return true;
  }

  if (config.variantId && variantId === config.variantId) {
    return true;
  }

  if (item.handle && item.handle === config.productHandle) {
    return true;
  }

  if (typeof item.url === 'string' && item.url.includes(`/products/${config.productHandle}`)) {
    return true;
  }

  return false;
}

function getManualGuinnessCakesLines(cart, config = readGuinnessCakesGiftConfig()) {
  if (!Array.isArray(cart?.items)) return [];

  return cart.items.filter(
    (item) => isGuinnessCakesProductLine(item, config) &&
      !isMarkedGuinnessCakesGiftLine(item, config) &&
      !isAnyMarkedFreeGiftLine(item)
  );
}

function getMarkedGuinnessCakesGiftLines(cart, config = readGuinnessCakesGiftConfig()) {
  if (!Array.isArray(cart?.items)) return [];

  return cart.items.filter((item) => isMarkedGuinnessCakesGiftLine(item, config));
}

function getSupersededFirstRewardGiftLines(cart, config = readGuinnessCakesGiftConfig()) {
  if (!Array.isArray(cart?.items)) return [];
  return cart.items.filter((item) => (
    isKnownFirstRewardGiftLine(item, config) && !isMarkedGuinnessCakesGiftLine(item, config)
  ));
}

function isGuinnessCakesTargetBundleParent(bundleHandle, config = readGuinnessCakesGiftConfig()) {
  if (!bundleHandle) return false;

  return config.targetBundleParents.includes(bundleHandle);
}

function getGuinnessCakesEligibleBundleSubtotal(cart, config = readGuinnessCakesGiftConfig()) {
  if (!Array.isArray(cart?.items)) return 0;

  return cart.items.reduce((subtotal, item) => {
    const bundleHandle = item?.properties?._rc_bundle_parent;
    const bundleId = item?.properties?._rc_bundle;

    if (!bundleId || !isGuinnessCakesTargetBundleParent(bundleHandle, config)) {
      return subtotal;
    }

    if (isAnyMarkedFreeGiftLine(item) || item?.gift_card === true) {
      return subtotal;
    }

    return subtotal + getActivePreOrderDiscountLinePrice(item);
  }, 0);
}

function buildGuinnessCakesGiftPlan(cart, { source = 'unknown' } = {}) {
  const config = readGuinnessCakesGiftConfig(cart);
  const mode = getGuinnessCakesGiftSyncMode(config);
  const canAddGift = mode === 'live' || mode === 'dry';
  const cartSignature = buildCartSnapshotSignature(cart);
  const markedGiftLines = getMarkedGuinnessCakesGiftLines(cart, config);
  const supersededGiftLines = getSupersededFirstRewardGiftLines(cart, config);
  const allFirstRewardGiftLines = [...markedGiftLines, ...supersededGiftLines];
  const manualGiftLines = getManualGuinnessCakesLines(cart, config);
  const purchaseMode = getTrackedBundleGiftPurchaseMode(cart, config, isGuinnessCakesProductLine);
  const trackedBundleGiftProductLines = getTrackedBundleGiftProductLines(cart, config, isGuinnessCakesProductLine);
  const eligibleBundleSubtotal = getGuinnessCakesEligibleBundleSubtotal(cart, config);
  const qualifies = eligibleBundleSubtotal >= config.thresholdPence;
  const invalidReasons = [];
  const hasMarkedGift = allFirstRewardGiftLines.length > 0;

  if (!config.variantId) {
    invalidReasons.push('missing-gift-variant-id');
  }

  if (!Array.isArray(cart?.items)) {
    invalidReasons.push('missing-cart-items');
  }

  const plan = {
    source,
    mode,
    enabled: mode !== 'off' && (canAddGift || hasMarkedGift),
    dryRun: mode === 'dry',
    valid: invalidReasons.length === 0,
    invalidReasons,
    action: 'noop',
    reason: '',
    cartSignature,
    thresholdPence: config.thresholdPence,
    eligibleBundleSubtotal,
    qualifies,
    giftProductId: config.productId,
    giftVariantId: config.variantId,
    giftLineProperties: getGuinnessCakesGiftLineProperties(config),
    choiceId: config.choiceId,
    skipped: config.skipped,
    targetBundleParents: config.targetBundleParents,
    purchaseMode,
    markedGiftLineKeys: markedGiftLines.map((item) => item?.key).filter(Boolean),
    supersededGiftLineKeys: supersededGiftLines.map((item) => item?.key).filter(Boolean),
    manualGiftLineKeys: manualGiftLines.map((item) => item?.key).filter(Boolean),
    trackedBundleGiftProductLineKeys: trackedBundleGiftProductLines.map((item) => item?.key).filter(Boolean),
    removeLineKeys: [],
    normalizeLineKey: null,
    addGift: false,
    canAddGift,
  };

  // Removal decisions come before the enabled/invalid gates: protecting customers from
  // being charged for a promo gift must not depend on the promo being enabled or on
  // add-side config (gift variant/product ids) being valid.
  if (shouldRemovePromoGiftForPurchaseMode(purchaseMode)) {
    const removeLineKeys = getUniqueCartLineKeys([...allFirstRewardGiftLines, ...trackedBundleGiftProductLines]);

    if (removeLineKeys.length > 0) {
      plan.action = 'remove';
      plan.reason = `bundle-purchase-mode-${purchaseMode}`;
      plan.removeLineKeys = removeLineKeys;
    } else {
      plan.reason = `bundle-purchase-mode-${purchaseMode}-no-gift`;
    }

    return plan;
  }

  if (!qualifies && allFirstRewardGiftLines.length > 0) {
    plan.action = 'remove';
    plan.reason = 'bundle-subtotal-below-threshold';
    plan.removeLineKeys = allFirstRewardGiftLines.map((item) => item?.key).filter(Boolean);
    return plan;
  }

  if (config.skipped) {
    if (allFirstRewardGiftLines.length > 0) {
      plan.action = 'remove';
      plan.reason = 'gift-skipped-remove-existing';
      plan.removeLineKeys = allFirstRewardGiftLines.map((item) => item?.key).filter(Boolean);
    } else {
      plan.reason = 'gift-skipped';
    }
    return plan;
  }

  if (config.choiceMode && !config.choiceId) {
    if (allFirstRewardGiftLines.length > 0) {
      plan.action = 'remove';
      plan.reason = 'choice-required-remove-legacy-gift';
      plan.removeLineKeys = getUniqueCartLineKeys(allFirstRewardGiftLines);
    } else {
      plan.reason = 'choice-required';
    }
    return plan;
  }

  if (markedGiftLines.length > 0) {
    const [keeperGiftLine, ...extraGiftLines] = markedGiftLines;
    const keeperQuantity = Number(keeperGiftLine?.quantity) || 0;

    if (keeperQuantity !== 1 || extraGiftLines.length > 0 || supersededGiftLines.length > 0) {
      plan.action = 'normalize';
      plan.reason = 'marked-gift-quantity-or-duplicate';
      plan.normalizeLineKey = keeperQuantity !== 1 ? keeperGiftLine?.key ?? null : null;
      plan.removeLineKeys = getUniqueCartLineKeys([...extraGiftLines, ...supersededGiftLines]);
      return plan;
    }
  }

  if (!plan.enabled) {
    plan.reason = mode === 'off' ? 'sync-disabled' : 'query-not-enabled-no-marked-gift';
    return plan;
  }

  if (!plan.valid) {
    plan.action = 'invalid-config';
    plan.reason = invalidReasons.join(',');
    return plan;
  }

  if (!qualifies) {
    plan.reason = 'bundle-subtotal-below-threshold-no-gift';
    return plan;
  }

  if (!config.choiceMode && manualGiftLines.length > 0) {
    if (markedGiftLines.length > 0) {
      plan.action = 'remove';
      plan.reason = 'manual-guinness-cakes-present-remove-promo-gift';
      plan.removeLineKeys = allFirstRewardGiftLines.map((item) => item?.key).filter(Boolean);
      return plan;
    }

    plan.reason = 'manual-guinness-cakes-present';
    return plan;
  }

  if (markedGiftLines.length === 0) {
    if (canAddGift) {
      plan.action = supersededGiftLines.length > 0 ? 'replace' : 'add';
      plan.reason = supersededGiftLines.length > 0 ? 'gift-choice-changed' : 'bundle-subtotal-qualified';
      plan.removeLineKeys = supersededGiftLines.map((item) => item?.key).filter(Boolean);
      plan.addGift = true;
    } else {
      plan.reason = 'query-not-enabled-gift-missing';
    }

    return plan;
  }

  plan.reason = 'gift-already-correct';
  return plan;
}

async function reconcileCottonBreadBagGift(cart, {
  source = 'unknown',
  forceFresh = false,
  retryCount = 0,
  signal = null,
  rewardSlot = SECOND_REWARD_CHOICE_SLOT,
} = {}) {
  const previousReconcilePromise = breadBagGiftReconcilePromise;
  let waitedForActiveReconcile = false;

  let activeReconcilePromise;
  activeReconcilePromise = (async () => {
    if (previousReconcilePromise) {
      waitedForActiveReconcile = true;

      try {
        await previousReconcilePromise;
      } catch (error) {
        // The active reconcile handles its own failure state; this call still needs a fresh pass.
      }
    }

    return runCottonBreadBagGiftReconcile(cart, {
      source,
      forceFresh: forceFresh || waitedForActiveReconcile,
      retryCount,
      signal: waitedForActiveReconcile ? null : signal,
      rewardSlot,
    });
  })().finally(() => {
    if (breadBagGiftReconcilePromise === activeReconcilePromise) {
      breadBagGiftReconcilePromise = null;
    }
  });

  breadBagGiftReconcilePromise = activeReconcilePromise;

  return activeReconcilePromise;
}

async function reconcileCottonBreadBagGiftBounded(cart, { source = 'unknown', forceFresh = false } = {}) {
  // One global chain serialises entire bread-bag -> Guinness sequences. Without it,
  // two overlapping bounded calls can interleave (call B's bread-bag pass mutating the
  // cart while call A's Guinness pass still plans against A's stale read).
  const previousChainPromise = giftReconcileChainPromise;

  let chainPromise;
  chainPromise = (async () => {
    let waitedForActiveChain = false;

    if (previousChainPromise) {
      waitedForActiveChain = true;

      try {
        await previousChainPromise;
      } catch (error) {
        // The previous chain reports its own failures; this call still runs.
      }
    }

    return runBothGiftReconcilesBounded(cart, {
      source,
      forceFresh: forceFresh || waitedForActiveChain,
    });
  })().finally(() => {
    if (giftReconcileChainPromise === chainPromise) {
      giftReconcileChainPromise = null;
    }
  });

  giftReconcileChainPromise = chainPromise;

  return chainPromise;
}

async function runBothGiftReconcilesBounded(cart, { source = 'unknown', forceFresh = false } = {}) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => {
    controller.abort();
  }, BREAD_BAG_GIFT_RECONCILE_TIMEOUT_MS);

  try {
    const secondRewardCart = await reconcileCottonBreadBagGift(cart, {
      source,
      forceFresh,
      signal: controller.signal,
      rewardSlot: SECOND_REWARD_CHOICE_SLOT,
    });
    const afterSecondReward = secondRewardCart ?? cart;

    if (breadBagGiftSyncState.lastError || controller.signal.aborted) {
      return afterSecondReward;
    }

    const thirdRewardCart = await reconcileCottonBreadBagGift(afterSecondReward, {
      source,
      forceFresh: false,
      signal: controller.signal,
      rewardSlot: THIRD_REWARD_CHOICE_SLOT,
    });
    const nextCart = thirdRewardCart ?? afterSecondReward;

    if (breadBagGiftSyncState.lastError || controller.signal.aborted) {
      return nextCart;
    }

    if (!shouldRunGuinnessCakesGiftReconcile(nextCart)) {
      return nextCart;
    }

    return await reconcileGuinnessCakesGift(nextCart, { source, forceFresh: false, signal: controller.signal });
  } finally {
    window.clearTimeout(timeoutId);

    if (!controller.signal.aborted) {
      giftReconcileTimeoutRetryCount = 0;
    } else if (giftReconcileTimeoutRetryCount < GIFT_RECONCILE_TIMEOUT_RETRY_LIMIT) {
      // The bounded window expired mid-pass. The cart may hold a half-applied gift
      // state, and gift mutations bypass the wrapped fetch, so nothing else would
      // re-trigger a pass — schedule a deferred retry instead of going silent.
      giftReconcileTimeoutRetryCount += 1;
      scheduleBreadBagGiftCartMutationReconcile(null, { source: `${source}:timeout-retry` });
    }
  }
}

function shouldRunGuinnessCakesGiftReconcile(cart) {
  const config = readGuinnessCakesGiftConfig();
  const mode = getGuinnessCakesGiftSyncMode(config);

  if (mode === 'live' || mode === 'dry') {
    return true;
  }

  // Cleanup and off modes still run whenever promo gift lines (or gift product lines
  // inside the tracked bundle) exist, so removal protection works even with the promo
  // disabled or its config missing.
  return (
    getMarkedGuinnessCakesGiftLines(cart, config).length > 0 ||
    getTrackedBundleGiftProductLines(cart, config, isGuinnessCakesProductLine).length > 0
  );
}

async function reconcileGuinnessCakesGift(cart, { source = 'unknown', forceFresh = false, retryCount = 0, signal = null } = {}) {
  const previousReconcilePromise = guinnessCakesGiftReconcilePromise;
  let waitedForActiveReconcile = false;

  let activeReconcilePromise;
  activeReconcilePromise = (async () => {
    if (previousReconcilePromise) {
      waitedForActiveReconcile = true;

      try {
        await previousReconcilePromise;
      } catch (error) {
        // The active reconcile handles its own failure state; this call still needs a fresh pass.
      }
    }

    return runGuinnessCakesGiftReconcile(cart, {
      source,
      forceFresh: forceFresh || waitedForActiveReconcile,
      retryCount,
      signal: waitedForActiveReconcile ? null : signal,
    });
  })().finally(() => {
    if (guinnessCakesGiftReconcilePromise === activeReconcilePromise) {
      guinnessCakesGiftReconcilePromise = null;
    }
  });

  guinnessCakesGiftReconcilePromise = activeReconcilePromise;

  return activeReconcilePromise;
}

async function runGuinnessCakesGiftReconcile(cart, { source = 'unknown', forceFresh = false, retryCount = 0, signal = null } = {}) {
  const startedAt = Date.now();
  const baseCart =
    forceFresh || !Array.isArray(cart?.items)
      ? (await fetchCartStateForBreadBagGift(signal)) ?? cart ?? getLatestCartSnapshot()
      : cart;
  const plan = buildGuinnessCakesGiftPlan(baseCart, { source });

  guinnessCakesGiftSyncState = {
    ...guinnessCakesGiftSyncState,
    inFlight: true,
    lastAction: plan.action,
    lastError: null,
    lastPlan: plan,
    lastCartSignature: plan.cartSignature,
  };

  logGuinnessCakesGiftDebug('sync:start', {
    action: plan.action,
    reason: plan.reason,
    source,
    retryCount,
    eligibleBundleSubtotal: plan.eligibleBundleSubtotal,
    thresholdPence: plan.thresholdPence,
    qualifies: plan.qualifies,
    purchaseMode: plan.purchaseMode,
    markedGiftLineKeys: plan.markedGiftLineKeys,
    manualGiftLineKeys: plan.manualGiftLineKeys,
    trackedBundleGiftProductLineKeys: plan.trackedBundleGiftProductLineKeys,
    canAddGift: plan.canAddGift,
  });

  try {
    // Remove/normalize plans execute even when the promo is disabled or its add-side
    // config is invalid — they only need line keys and protect against wrong charges.
    const isRemovalAction = plan.action === 'remove' || plan.action === 'normalize';

    if (plan.action === 'noop' || plan.action === 'invalid-config' || (!isRemovalAction && (!plan.enabled || !plan.valid))) {
      logGuinnessCakesGiftDebug('sync:decision:no-op', { action: plan.action, reason: plan.reason });
      return baseCart;
    }

    if (plan.dryRun) {
      logGuinnessCakesGiftDebug('sync:decision:dry-run', plan);
      return baseCart;
    }

    const updatedCart = await executeGuinnessCakesGiftPlan(plan, signal);

    if (updatedCart?.items) {
      setLatestCartSnapshot(updatedCart);
    }

    guinnessCakesGiftSyncState = {
      ...guinnessCakesGiftSyncState,
      lastAction: plan.action,
      lastError: null,
      lastSyncedAt: new Date(startedAt).toISOString(),
      lastCartSignature: buildCartSnapshotSignature(updatedCart ?? baseCart),
    };

    logGuinnessCakesGiftDebug('sync:complete', {
      action: plan.action,
      source,
      cartSignature: guinnessCakesGiftSyncState.lastCartSignature,
    });

    return updatedCart ?? baseCart;
  } catch (error) {
    if (signal?.aborted) {
      guinnessCakesGiftSyncState = {
        ...guinnessCakesGiftSyncState,
        lastAction: 'timeout',
        lastError: `Timed out after ${BREAD_BAG_GIFT_RECONCILE_TIMEOUT_MS}ms`,
        lastSyncedAt: new Date(startedAt).toISOString(),
      };

      logGuinnessCakesGiftDebug('sync:timeout', {
        source,
        timeoutMs: BREAD_BAG_GIFT_RECONCILE_TIMEOUT_MS,
      });

      return baseCart;
    }

    logGuinnessCakesGiftDebug('sync:request:error', {
      action: plan.action,
      source,
      retryCount,
      message: error instanceof Error ? error.message : String(error),
    });

    if (retryCount < 1) {
      const freshCart = (await fetchCartStateForBreadBagGift(signal)) ?? getLatestCartSnapshot() ?? baseCart;

      logGuinnessCakesGiftDebug('sync:retry', { source, retryCount: retryCount + 1 });
      return runGuinnessCakesGiftReconcile(freshCart, {
        source: `${source}:retry`,
        forceFresh: false,
        retryCount: retryCount + 1,
        signal,
      });
    }

    guinnessCakesGiftSyncState = {
      ...guinnessCakesGiftSyncState,
      lastAction: plan.action,
      lastError: error instanceof Error ? error.message : String(error),
      lastSyncedAt: new Date(startedAt).toISOString(),
    };

    console.error('Failed to sync the Guinness cakes gift.', error);
    return baseCart;
  } finally {
    guinnessCakesGiftSyncState = {
      ...guinnessCakesGiftSyncState,
      inFlight: false,
    };
  }
}

async function runCottonBreadBagGiftReconcile(cart, {
  source = 'unknown',
  forceFresh = false,
  retryCount = 0,
  signal = null,
  rewardSlot = SECOND_REWARD_CHOICE_SLOT,
} = {}) {
  const startedAt = Date.now();
  const baseCart =
    forceFresh || !Array.isArray(cart?.items)
      ? (await fetchCartStateForBreadBagGift(signal)) ?? cart ?? getLatestCartSnapshot()
      : cart;
  const plan = buildCottonBreadBagGiftPlan(baseCart, { source, rewardSlot });

  breadBagGiftSyncState = {
    ...breadBagGiftSyncState,
    inFlight: true,
    lastAction: plan.action,
    lastError: null,
    lastPlan: plan,
    lastCartSignature: plan.cartSignature,
  };

  logBreadBagGiftDebug('sync:start', {
    action: plan.action,
    reason: plan.reason,
    source,
    rewardSlot: plan.rewardSlot,
    retryCount,
    eligibleBundleSubtotal: plan.eligibleBundleSubtotal,
    thresholdPence: plan.thresholdPence,
    qualifies: plan.qualifies,
    purchaseMode: plan.purchaseMode,
    markedGiftLineKeys: plan.markedGiftLineKeys,
    manualBreadBagLineKeys: plan.manualBreadBagLineKeys,
    trackedBundleGiftProductLineKeys: plan.trackedBundleGiftProductLineKeys,
  });

  try {
    // Remove/normalize plans execute even when the promo is disabled or its add-side
    // config is invalid — they only need line keys and protect against wrong charges.
    const isRemovalAction = plan.action === 'remove' || plan.action === 'normalize';

    if (plan.action === 'noop' || plan.action === 'invalid-config' || (!isRemovalAction && (!plan.enabled || !plan.valid))) {
      logBreadBagGiftDebug('sync:decision:no-op', { action: plan.action, reason: plan.reason });
      return baseCart;
    }

    if (plan.dryRun) {
      logBreadBagGiftDebug('sync:decision:dry-run', plan);
      return baseCart;
    }

    const updatedCart = await executeCottonBreadBagGiftPlan(plan, signal);

    if (updatedCart?.items) {
      setLatestCartSnapshot(updatedCart);
    }

    breadBagGiftSyncState = {
      ...breadBagGiftSyncState,
      lastAction: plan.action,
      lastError: null,
      lastSyncedAt: new Date(startedAt).toISOString(),
      lastCartSignature: buildCartSnapshotSignature(updatedCart ?? baseCart),
    };

    logBreadBagGiftDebug('sync:complete', {
      action: plan.action,
      source,
      cartSignature: breadBagGiftSyncState.lastCartSignature,
    });

    return updatedCart ?? baseCart;
  } catch (error) {
    if (signal?.aborted) {
      breadBagGiftSyncState = {
        ...breadBagGiftSyncState,
        lastAction: 'timeout',
        lastError: `Timed out after ${BREAD_BAG_GIFT_RECONCILE_TIMEOUT_MS}ms`,
        lastSyncedAt: new Date(startedAt).toISOString(),
      };

      logBreadBagGiftDebug('sync:timeout', {
        source,
        timeoutMs: BREAD_BAG_GIFT_RECONCILE_TIMEOUT_MS,
      });

      return baseCart;
    }

    logBreadBagGiftDebug('sync:request:error', {
      action: plan.action,
      source,
      retryCount,
      message: error instanceof Error ? error.message : String(error),
    });

    if (retryCount < 1) {
      const freshCart = (await fetchCartStateForBreadBagGift(signal)) ?? getLatestCartSnapshot() ?? baseCart;

      logBreadBagGiftDebug('sync:retry', { source, retryCount: retryCount + 1 });
      return runCottonBreadBagGiftReconcile(freshCart, {
        source: `${source}:retry`,
        forceFresh: false,
        retryCount: retryCount + 1,
        signal,
        rewardSlot,
      });
    }

    breadBagGiftSyncState = {
      ...breadBagGiftSyncState,
      lastAction: plan.action,
      lastError: error instanceof Error ? error.message : String(error),
      lastSyncedAt: new Date(startedAt).toISOString(),
    };

    console.error('Failed to sync the Cotton Bread Bag gift.', error);
    return baseCart;
  } finally {
    breadBagGiftSyncState = {
      ...breadBagGiftSyncState,
      inFlight: false,
    };
  }
}

async function executeCottonBreadBagGiftPlan(plan, signal = null) {
  let currentCart = null;

  if ((plan.action === 'add' || plan.action === 'replace') && plan.addGift) {
    currentCart = await addCottonBreadBagGiftToCart(plan.giftVariantId, plan.giftLineProperties, signal);
  }

  currentCart = (await applyGiftPlanLineUpdates(plan, signal)) ?? currentCart;

  return currentCart ?? (await fetchCartStateForBreadBagGift(signal));
}

// All of a plan's quantity corrections go through one atomic /cart/update.js so a
// timeout or network failure can never leave the cart with some gift lines removed
// and others still present.
async function applyGiftPlanLineUpdates(plan, signal = null) {
  const updates = {};

  if (plan.action === 'normalize' && plan.normalizeLineKey) {
    updates[plan.normalizeLineKey] = 1;
  }

  for (const lineKey of plan.removeLineKeys) {
    updates[lineKey] = 0;
  }

  if (Object.keys(updates).length === 0) {
    return null;
  }

  const response = await fetchBreadBagGiftCartRequest(`${window.Shopify?.routes?.root ?? '/'}cart/update.js`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ updates }),
  }, signal);

  if (!response.ok) {
    throw new Error(`Gift line updates failed with status ${response.status}`);
  }

  return response.json();
}

async function addCottonBreadBagGiftToCart(variantId, properties, signal = null) {
  const response = await fetchBreadBagGiftCartRequest(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      items: [
        {
          id: variantId,
          quantity: 1,
          properties,
        },
      ],
    }),
  }, signal);

  if (!response.ok) {
    throw new Error(`Cotton Bread Bag gift add failed with status ${response.status}`);
  }

  return (await fetchCartStateForBreadBagGift(signal)) ?? getLatestCartSnapshot();
}

async function executeGuinnessCakesGiftPlan(plan, signal = null) {
  let currentCart = null;

  if ((plan.action === 'add' || plan.action === 'replace') && plan.addGift) {
    currentCart = await addGuinnessCakesGiftToCart(plan.giftVariantId, plan.giftLineProperties, signal);
  }

  currentCart = (await applyGiftPlanLineUpdates(plan, signal)) ?? currentCart;

  return currentCart ?? (await fetchCartStateForBreadBagGift(signal));
}

async function addGuinnessCakesGiftToCart(variantId, properties, signal = null) {
  const response = await fetchBreadBagGiftCartRequest(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      items: [
        {
          id: variantId,
          quantity: 1,
          properties,
        },
      ],
    }),
  }, signal);

  if (!response.ok) {
    throw new Error(`Guinness cakes gift add failed with status ${response.status}`);
  }

  return (await fetchCartStateForBreadBagGift(signal)) ?? getLatestCartSnapshot();
}


async function fetchCartStateForBreadBagGift(signal = null) {
  try {
    const response = await fetchBreadBagGiftCartRequest(`${window.Shopify?.routes?.root ?? '/'}cart.js`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    }, signal);

    if (!response.ok) return null;

    return response.json();
  } catch (error) {
    return null;
  }
}

async function fetchBreadBagGiftCartRequest(url, options = {}, parentSignal = null) {
  const controller = new AbortController();
  const abortFromParent = () => {
    controller.abort();
  };
  const timeoutId = window.setTimeout(() => {
    controller.abort();
  }, BREAD_BAG_GIFT_REQUEST_TIMEOUT_MS);

  if (parentSignal?.aborted) {
    controller.abort();
  } else if (parentSignal) {
    parentSignal.addEventListener('abort', abortFromParent, { once: true });
  }

  try {
    return await getCartMutationFetch()(url, {
      ...options,
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timeoutId);
    parentSignal?.removeEventListener?.('abort', abortFromParent);
  }
}

function getCartMutationFetch() {
  const topLevelNativeFetch = window.BC_CART_TOP_LEVEL_NATIVE_FETCH;
  if (typeof topLevelNativeFetch === 'function') {
    return topLevelNativeFetch;
  }

  return cartFlowNativeFetch || window.fetch.bind(window);
}

function getBundleDrawer() {
  return document.querySelector('cart-drawer-component[data-bc-bundle-flow="true"]');
}

function readBundleMotionVariant() {
  const rawVariant = new URLSearchParams(window.location.search).get(BUNDLE_MOTION_VARIANT_QUERY_KEY)?.trim().toLowerCase();

  if (!rawVariant) {
    return null;
  }

  if (rawVariant === '1' || rawVariant === 'freeze') {
    return BUNDLE_MOTION_VARIANT_FREEZE;
  }

  if (rawVariant === '2' || rawVariant === 'clone') {
    return BUNDLE_MOTION_VARIANT_CLONE;
  }

  return null;
}

function getBundleMotionHost() {
  const bundleHost = document.getElementById('recharge-bundles');

  if (bundleHost instanceof HTMLElement) {
    return bundleHost;
  }

  const bundleApp = document.getElementById('bundling-app');
  return bundleApp?.parentElement instanceof HTMLElement ? bundleApp.parentElement : null;
}

function activateBundleMotionMask() {
  if (!bundleMotionVariant) {
    return;
  }

  const host = getBundleMotionHost();
  const bundleApp = document.getElementById('bundling-app');

  if (!(host instanceof HTMLElement) || !(bundleApp instanceof HTMLElement)) {
    return;
  }

  const appRect = bundleApp.getBoundingClientRect();

  if (appRect.height > 0) {
    bundleMotionHostMinHeight = host.style.minHeight;
    host.style.minHeight = `${appRect.height}px`;
  }

  host.dataset.bcMotionVariant = bundleMotionVariant;
  host.dataset.bcMotionMaskActive = 'true';
  ensureBundleMotionClone(host, bundleApp);

  if (bundleMotionVariant === BUNDLE_MOTION_VARIANT_FREEZE) {
    ensureBundleFreezeMask(host);
    return;
  }

  if (bundleMotionVariant === BUNDLE_MOTION_VARIANT_CLONE) {
    removeBundleMotionMask();
  }
}

function deactivateBundleMotionMask() {
  const host = getBundleMotionHost();

  removeBundleMotionClone();
  removeBundleMotionMask();

  if (!(host instanceof HTMLElement)) {
    return;
  }

  host.removeAttribute('data-bc-motion-mask-active');
  host.removeAttribute('data-bc-motion-variant');

  if (bundleMotionHostMinHeight) {
    host.style.minHeight = bundleMotionHostMinHeight;
  } else {
    host.style.removeProperty('min-height');
  }

  bundleMotionHostMinHeight = '';
}

function ensureBundleFreezeMask(host) {
  if (!(host instanceof HTMLElement)) {
    return;
  }

  if (bundleMotionMaskElement instanceof HTMLElement && bundleMotionMaskElement.parentElement === host) {
    return;
  }

  removeBundleMotionMask();

  const mask = document.createElement('div');
  mask.className = 'bc-bundle-motion-mask';
  mask.setAttribute('aria-hidden', 'true');
  host.appendChild(mask);
  bundleMotionMaskElement = mask;
}

function removeBundleMotionMask() {
  if (bundleMotionMaskElement instanceof HTMLElement) {
    bundleMotionMaskElement.remove();
  }

  bundleMotionMaskElement = null;
}

function ensureBundleMotionClone(host, bundleApp) {
  if (!(host instanceof HTMLElement) || !(bundleApp instanceof HTMLElement)) {
    return;
  }

  if (bundleMotionCloneElement instanceof HTMLElement && bundleMotionCloneElement.parentElement === host) {
    return;
  }

  removeBundleMotionClone();

  const clone = bundleApp.cloneNode(true);
  clone.classList.add('bc-bundle-motion-clone');
  clone.removeAttribute('id');
  clone.setAttribute('aria-hidden', 'true');
  clone.setAttribute('inert', '');
  disableBundleMotionCloneInteractivity(clone);
  host.appendChild(clone);
  bundleMotionCloneElement = clone;
}

function removeBundleMotionClone() {
  if (bundleMotionCloneElement instanceof HTMLElement) {
    bundleMotionCloneElement.remove();
  }

  bundleMotionCloneElement = null;
}

function disableBundleMotionCloneInteractivity(cloneRoot) {
  if (!(cloneRoot instanceof HTMLElement)) {
    return;
  }

  cloneRoot.querySelectorAll('[id]').forEach((element) => {
    element.removeAttribute('id');
  });

  cloneRoot.querySelectorAll('a, button, input, select, textarea, summary, [role="button"]').forEach((element) => {
    element.setAttribute('tabindex', '-1');
    element.setAttribute('aria-hidden', 'true');

    if ('disabled' in element) {
      try {
        element.disabled = true;
      } catch (error) {
        // Ignore read-only DOM API failures on cloned third-party controls.
      }
    }
  });
}

function getBundleDrawerSectionId() {
  const drawer = getBundleDrawer();
  const cartItems = drawer?.querySelector('cart-items-component');

  if (cartItems?.dataset.sectionId) {
    return cartItems.dataset.sectionId;
  }

  return drawer?.closest('.shopify-section')?.id || null;
}

function getBundleDrawerDialog() {
  return getBundleDrawer()?.querySelector('.cart-drawer__dialog') || null;
}

function getBundleDrawerContent() {
  return getBundleDrawer()?.querySelector('.cart-drawer__content--bundle-flow') || null;
}

function isNativeFlareDrawer(drawer = getBundleDrawer()) {
  return drawer instanceof HTMLElement && drawer.classList.contains('cart-drawer--native-flare');
}

function isNativeFlareAppBlockScript(script) {
  if (!(script instanceof HTMLScriptElement)) {
    return false;
  }

  const src = script.getAttribute('src') || script.getAttribute(NATIVE_FLARE_APP_BLOCK_SCRIPT_SRC_ATTR) || '';
  return NATIVE_FLARE_APP_BLOCK_SCRIPT_SRC_MATCHER.test(src);
}

function suppressNativeFlareAppBlockScripts(root = document) {
  if (!(root instanceof Document || root instanceof DocumentFragment || root instanceof Element)) {
    return;
  }

  root.querySelectorAll('.cart-drawer--bundle-flow .shopify-app-block script[src]').forEach((script) => {
    if (!isNativeFlareAppBlockScript(script)) {
      return;
    }

    const src = script.getAttribute('src');
    if (src && !script.hasAttribute(NATIVE_FLARE_APP_BLOCK_SCRIPT_SRC_ATTR)) {
      script.setAttribute(NATIVE_FLARE_APP_BLOCK_SCRIPT_SRC_ATTR, src);
    }

    script.removeAttribute('src');
  });
}

function suppressNativeFlareAppBlockScriptReplay() {
  suppressNativeFlareAppBlockScripts(document);

  if (typeof DOMParser === 'undefined' || !DOMParser.prototype) {
    return () => {};
  }

  nativeFlareAppBlockScriptSuppressionDepth += 1;

  if (!nativeDOMParserParseFromString) {
    nativeDOMParserParseFromString = DOMParser.prototype.parseFromString;
    // Horizon replays app-block scripts after section morphs; Flare's drawer script is not idempotent.
    DOMParser.prototype.parseFromString = function parseBundleDrawerSection(markup, type) {
      const parsed = nativeDOMParserParseFromString.call(this, markup, type);

      if (nativeFlareAppBlockScriptSuppressionDepth > 0) {
        suppressNativeFlareAppBlockScripts(parsed);
      }

      return parsed;
    };
  }

  return () => {
    nativeFlareAppBlockScriptSuppressionDepth = Math.max(0, nativeFlareAppBlockScriptSuppressionDepth - 1);

    if (nativeFlareAppBlockScriptSuppressionDepth === 0 && nativeDOMParserParseFromString) {
      DOMParser.prototype.parseFromString = nativeDOMParserParseFromString;
      nativeDOMParserParseFromString = null;
    }
  };
}

function setBundleDirectCheckoutPending(pending) {
  isBundleDirectCheckoutPending = pending === true;
  document.documentElement.classList.toggle(BUNDLE_DIRECT_CHECKOUT_PENDING_CLASS, isBundleDirectCheckoutPending);

  const drawer = getBundleDrawer();

  if (!(drawer instanceof HTMLElement)) {
    return;
  }

  if (isBundleDirectCheckoutPending) {
    window.clearTimeout(openTimer);

    if (drawer.hasAttribute('auto-open')) {
      drawer.dataset.bcDirectCheckoutAutoOpenSuppressed = 'true';
      drawer.removeAttribute('auto-open');
    }

    return;
  }

  if (drawer.dataset.bcDirectCheckoutAutoOpenSuppressed === 'true') {
    drawer.setAttribute('auto-open', '');
    delete drawer.dataset.bcDirectCheckoutAutoOpenSuppressed;
  }
}

function scheduleDrawerOpen({ force = false, cartSnapshot = null } = {}) {
  if (isBundleDirectCheckoutPending) {
    return;
  }

  const now = Date.now();

  if (!force && now - lastDrawerOpenScheduledAt < DRAWER_OPEN_DEDUPE_MS) {
    return;
  }

  lastDrawerOpenScheduledAt = now;
  window.clearTimeout(openTimer);
  openTimer = window.setTimeout(async () => {
    showBundleDrawerLoadingState();

    if (Array.isArray(cartSnapshot?.items)) {
      applyVerifiedCartSnapshotToBundleDrawer(cartSnapshot);
    }

    hasAttemptedFlareUiRecoveryForCurrentOpen = false;
    hasAttemptedMissingFlareBlockReopenForCurrentOpen = false;
    hasAttemptedDisabledCheckoutRecoveryForCurrentOpen = false;

    // Phase 2 split drawer: the delivery-form region is rendered statically in the page
    // (it survives the items morph via data-skip-subtree-update), so open the drawer
    // FIRST — the form is visible and usable almost immediately, with the volatile cart
    // region masked behind its skeleton. The items section render (which re-pulls
    // ReCharge bundle-data, ~1.5s+) then runs in the BACKGROUND and only updates the
    // items region. This is the core of the perf win: form visible ~drawer-open time
    // instead of after the ~3s section render.
    const drawer = getBundleDrawer();

    if (typeof drawer?.open === 'function') {
      drawer.open();
      beginBundleDrawerLoadingWindow();
    }

    // Kick Flare immediately so postcode/date populate into the already-visible form.
    runFlareEnhancementSequence();

    const resolvedCartSnapshot = Array.isArray(cartSnapshot?.items)
      ? cartSnapshot
      : await primeCartSnapshot({ force: true });
    const cartSignature = buildCartSnapshotSignature(resolvedCartSnapshot ?? getLatestCartSnapshot());
    const shouldRefreshDrawer = force || cartSignature !== lastRenderedDrawerCartSignature;

    if (shouldRefreshDrawer) {
      // refreshBundleDrawer records the post-refresh cart signature itself; overwriting
      // it with the pre-refresh value here could skip a needed re-render next open.
      const drawerRefreshed = await refreshBundleDrawer(resolvedCartSnapshot);

      if (!drawerRefreshed && Array.isArray(resolvedCartSnapshot?.items)) {
        applyVerifiedCartSnapshotToBundleDrawer(resolvedCartSnapshot);
      }
    }

    isBundleDrawerCartReconciliationPending = false;

    // The morph may have rebuilt the items region; make sure the drawer is still open
    // and re-run the Flare enhancement (the form region itself is preserved by
    // data-skip-subtree-update, but Flare-state syncing is cheap and idempotent).
    const refreshedDrawer = getBundleDrawer();

    if (typeof refreshedDrawer?.open === 'function' && !isBundleDrawerCurrentlyOpen()) {
      refreshedDrawer.open();
    }

    // The volatile cart region (items peek, totals, checkout) is now reconciled — lift
    // its skeleton. The form region was never masked, so it has been usable the whole
    // time. beginBundleDrawerLoadingWindow() still arms a safety timer in case the
    // refresh stalls.
    hideBundleDrawerLoadingState({ force: true });

    runFlareEnhancementSequence();
    scheduleFlareUiReadinessCheck(cartSignature);
  }, DRAWER_OPEN_DELAY_MS);
}

// Instant feedback at tap time. Called from the tap-time click listener
// (installBundleAddToCartInstantFeedback), this pops the Phase 2 split drawer the moment
// the customer taps Add to cart, instead of leaving the page frozen until the whole add +
// gift-reconcile pipeline finishes ~2s later (handleSuccessfulCartAdd -> scheduleDrawerOpen).
// Native mode materializes the dormant delivery form before opening the drawer. It is
// preserved across morphs via data-skip-subtree-update while the volatile items region
// stays masked until the verified post-add section render lands.
function openBundleDrawerShellImmediately({ ignoreCheckoutAb = false, purchaseMode = 'unknown' } = {}) {
  if (isBundleDirectCheckoutPending) {
    return;
  }

  // The direct-checkout A/B cohort redirects to /checkout after the add instead of
  // showing the drawer, so popping it here would be a wrong flash for that cohort.
  const abDecision = getBundleCheckoutAbDecision();

  if (!ignoreCheckoutAb && abDecision.enabled && abDecision.cohort === 'checkout') {
    return;
  }

  const nativeDeliveryRoot = materializeDormantNativeDeliveryRoot(purchaseMode);
  if (isNativeDeliveryEnabled() && nativeDeliveryRoot instanceof HTMLElement) {
    isBundleDrawerCartReconciliationPending = true;
  }
  const drawer = getBundleDrawer();

  if (typeof drawer?.open !== 'function') {
    return;
  }

  showBundleDrawerLoadingState();
  drawer.open();
  // Generous safety window — the normal post-add scheduleDrawerOpen re-arms the standard
  // window and clears this one as soon as the add lands; this only fires if the add never
  // completes at all, so the customer can never get stuck staring at the skeleton.
  beginBundleDrawerLoadingWindow({ maxMs: BUNDLE_DRAWER_SHELL_SAFETY_MS });
  // Kick Flare now so postcode/date populate into the already-visible form.
  runFlareEnhancementSequence();
}

function runFlareEnhancementSequence() {
  clearFlareEnhancementSequence();

  FLARE_ENHANCEMENT_DELAYS_MS.forEach((delay) => {
    const timer = window.setTimeout(() => {
      enhanceBundleDeliveryUi();
    }, delay);

    flareEnhancerTimers.push(timer);
  });
}

function clearFlareEnhancementSequence() {
  flareEnhancerTimers.forEach((timer) => {
    window.clearTimeout(timer);
  });

  flareEnhancerTimers = [];
}

function scheduleFlareUiReadinessCheck(expectedCartSignature = '') {
  clearFlareUiReadinessCheck();

  FLARE_UI_READINESS_CHECK_DELAYS_MS.forEach((delay) => {
    const timer = window.setTimeout(async () => {
      const drawer = getBundleDrawer();
      const dialog = drawer?.querySelector('.cart-drawer__dialog[open]');
      const currentCartSignature = buildCartSnapshotSignature(getLatestCartSnapshot());

      if (!(dialog instanceof HTMLDialogElement)) {
        return;
      }

      if (expectedCartSignature && currentCartSignature && expectedCartSignature !== currentCartSignature) {
        return;
      }

      if (isBundleDrawerLoading) {
        runFlareEnhancementSequence();
        return;
      }

      if (isFlareDeliveryUiReady()) {
        runFlareEnhancementSequence();
        return;
      }

      if (hasAttemptedFlareUiRecoveryForCurrentOpen) {
        return;
      }

      if (isBundleDrawerOpenWithoutFlareBlock() || isBundleCheckoutBlockedForVisiblePrefill()) {
        hasAttemptedFlareUiRecoveryForCurrentOpen = true;
        await recoverFlareUiForCurrentOpen(currentCartSignature);
        return;
      }

      runFlareEnhancementSequence();
    }, delay);

    flareUiReadinessTimers.push(timer);
  });
}

function clearFlareUiReadinessCheck() {
  flareUiReadinessTimers.forEach((timer) => {
    window.clearTimeout(timer);
  });

  flareUiReadinessTimers = [];
}

async function recoverFlareUiForCurrentOpen(
  cartSignature = buildCartSnapshotSignature(getLatestCartSnapshot()),
  { restartLoadingWindow = true } = {}
) {
  await refreshBundleDrawer();
  lastRenderedDrawerCartSignature = cartSignature;

  const refreshedDrawer = getBundleDrawer();

  if (typeof refreshedDrawer?.open === 'function') {
    refreshedDrawer.open();

    if (restartLoadingWindow) {
      beginBundleDrawerLoadingWindow();
    }
  }

  runFlareEnhancementSequence();
}

function showBundleDrawerLoadingState() {
  isBundleDrawerLoading = true;
  bundleDrawerLoadingShownAt = 0;
  bundleDrawerLoadingRecoveryAttempts = 0;
  applyBundleDrawerLoadingState();
  clearBundleDrawerLoadingTimers();
}

function beginBundleDrawerLoadingWindow({ maxMs = BUNDLE_DRAWER_LOADING_MAX_MS } = {}) {
  if (!isBundleDrawerLoading) {
    return;
  }

  bundleDrawerLoadingShownAt = Date.now();
  clearBundleDrawerLoadingTimers();
  // Phase 2 split drawer: the loading state now masks ONLY the volatile items region
  // (peek/totals/checkout) behind its skeleton; the delivery form is already visible.
  // The skeleton is lifted deterministically when the background items refresh resolves
  // (hideBundleDrawerLoadingState in scheduleDrawerOpen). This timer is just the safety
  // net: if that refresh never lands, force the items region visible at the deadline so
  // the customer never gets stuck on a skeleton.
  scheduleBundleDrawerLoadingRecoveryCheck(maxMs);
}

function hideBundleDrawerLoadingState({ force = false } = {}) {
  if (!isBundleDrawerLoading && !force) {
    return;
  }

  clearBundleDrawerLoadingTimers();

  const elapsed = bundleDrawerLoadingShownAt ? Date.now() - bundleDrawerLoadingShownAt : 0;
  const delay = force ? 0 : Math.max(0, BUNDLE_DRAWER_LOADING_MIN_MS - elapsed);

  bundleDrawerLoadingHideTimer = window.setTimeout(() => {
    try {
      if (isNativeDeliveryEnabled()) {
        enhanceNativeDeliveryUi();
      } else {
        const flareBlock = getFlareBlock();
        applyPersistedFlareFieldPrefills(flareBlock, { allowPostcode: canPrefillFlarePostcode(flareBlock) });
        syncFlareFieldValueStates(flareBlock);
      }
    } catch (error) {
      console.error('Failed to finish the bundle drawer enhancement.', error);
    } finally {
      isBundleDrawerLoading = false;
      applyBundleDrawerLoadingState();
      schedulePersistedFlareFieldSyncSequence();
    }
  }, delay);
}

function clearBundleDrawerLoadingTimers() {
  window.clearTimeout(bundleDrawerLoadingHideTimer);
  window.clearTimeout(bundleDrawerLoadingFallbackTimer);
  window.cancelAnimationFrame(flareFieldSyncRaf);
  flareFieldSyncRaf = 0;
  bundleDrawerFieldSyncTimers.forEach((timer) => {
    window.clearTimeout(timer);
  });
  bundleDrawerFieldSyncTimers = [];
}

function installBundleCartEntrypointHandlers() {
  if (window[CART_ENTRYPOINT_INSTALL_FLAG]) {
    return;
  }

  window[CART_ENTRYPOINT_INSTALL_FLAG] = true;
  document.addEventListener('click', handleBundleCartEntrypointClick, true);
}

function handleBundleCartEntrypointClick(event) {
  if (event.defaultPrevented) {
    return;
  }

  if (
    event instanceof MouseEvent &&
    (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
  ) {
    return;
  }

  const target = event.target;

  if (!(target instanceof Element)) {
    return;
  }

  const link = target.closest('a[href]');

  if (!isBundleCartEntrypointLink(link)) {
    return;
  }

  if (link.closest('cart-drawer-component')) {
    return;
  }

  const drawer = getBundleDrawer();

  if (typeof drawer?.open !== 'function') {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  openBundleDrawerFromCartEntrypoint(drawer);
}

function isBundleCartEntrypointLink(link) {
  if (!(link instanceof HTMLAnchorElement)) {
    return false;
  }

  if (link.hasAttribute('download')) {
    return false;
  }

  const target = String(link.getAttribute('target') ?? '').trim().toLowerCase();

  if (target && target !== '_self') {
    return false;
  }

  const href = link.getAttribute('href') || link.href;
  const url = normalizeUrl(href);

  if (!url || url.origin !== window.location.origin) {
    return false;
  }

  return getStorefrontPath(url.pathname) === '/cart';
}

function openBundleDrawerFromCartEntrypoint(drawer = getBundleDrawer()) {
  if (isBundleDirectCheckoutPending || typeof drawer?.open !== 'function') {
    return;
  }

  showBundleDrawerLoadingState();
  drawer.open();
  beginBundleDrawerLoadingWindow();
  runFlareEnhancementSequence();
  scheduleDrawerOpen({ force: true });
}

function getBundleGa4TagId() {
  const layer = Array.isArray(window.dataLayer) ? window.dataLayer : [];

  for (const entry of layer) {
    const tagId = entry && entry[0] === 'config' ? String(entry[1] || '') : '';
    if (/^G-[A-Z0-9]+$/.test(tagId)) {
      return tagId;
    }
  }

  return BUNDLE_GA4_FALLBACK_TAG_ID;
}

function buildBundleGa4AddToCartPayload(detail) {
  const bundleId = String(detail?.dynamicBundleId || '');
  const cart = detail?.verifiedCart || getLatestCartSnapshot();
  const lines = (Array.isArray(cart?.items) ? cart.items : [])
    .filter((item) => bundleId && String(item?.properties?._rc_bundle || '') === bundleId);

  if (!lines.length) {
    return null;
  }

  const items = lines.map((item) => {
    const quantity = Math.max(1, Number(item?.quantity) || 1);
    const linePence = Number(item?.final_line_price ?? item?.line_price) || 0;

    return {
      id: `shopify_US_${item?.product_id}_${item?.variant_id}`,
      name: String(item?.product_title || item?.title || ''),
      brand: String(item?.vendor || 'Butter & Crust'),
      category: String(item?.product_type || ''),
      variant: item?.variant_title || null,
      variant_id: String(item?.variant_id || ''),
      price: Math.round(linePence / quantity) / 100,
      quantity,
    };
  });
  const valuePence = lines.reduce((sum, item) => sum + (Number(item?.final_line_price ?? item?.line_price) || 0), 0);

  return {
    currency: String(cart?.currency || 'GBP'),
    value: Math.round(valuePence) / 100,
    items,
  };
}

function trackBundleAddToCartForGa4(detail) {
  const bundleId = String(detail?.dynamicBundleId || '');
  const trackedIds = window.__bcBundleGa4TrackedAddIds instanceof Set
    ? window.__bcBundleGa4TrackedAddIds
    : (window.__bcBundleGa4TrackedAddIds = new Set());

  if (!bundleId || trackedIds.has(bundleId)) {
    return;
  }

  const payload = buildBundleGa4AddToCartPayload(detail);
  if (!payload) {
    return;
  }

  trackedIds.add(bundleId);
  const startedAt = Date.now();
  const send = () => {
    if (typeof window.gtag !== 'function') {
      if (Date.now() - startedAt < BUNDLE_GA4_GTAG_WAIT_MS) {
        window.setTimeout(send, BUNDLE_GA4_GTAG_RETRY_MS);
      }
      return;
    }

    try {
      window.gtag('event', 'add_to_cart', {
        send_to: getBundleGa4TagId(),
        ...payload,
        bc_add_source: String(detail?.source || 'bundle-builder'),
      });
      document.documentElement.dataset.bcBundleGa4AddCount = String(trackedIds.size);
    } catch (error) {
      // Ignore analytics failures.
    }
  };

  send();
}

function installBundlePendingAddEventHandler() {
  document.addEventListener(
    BUNDLE_PENDING_ADD_EVENT,
    (event) => {
      const detail = event instanceof CustomEvent ? event.detail : null;
      hasPendingBundleAddPipeline = true;
      if (isNativeDeliveryEnabled()) {
        const nativeDeliveryRoot = materializeDormantNativeDeliveryRoot(detail?.purchaseMode);
        if (nativeDeliveryRoot instanceof HTMLElement) {
          setNativeDeliveryPurchaseMode(nativeDeliveryRoot, detail?.purchaseMode);
          isBundleDrawerCartReconciliationPending = true;
        }
      }

      const optimisticTotalPence = calculateOptimisticSubscriptionDiscountTotal(detail);
      if (optimisticTotalPence !== null) {
        applyOptimisticCartTotalToBundleDrawer(optimisticTotalPence, detail);
      }
      if (isNativeDeliveryEnabled() && !isBundleDrawerCurrentlyOpen()) {
        openBundleDrawerShellImmediately();
      }
    },
    true
  );
  document.addEventListener(
    BUNDLE_ADD_SUCCEEDED_EVENT,
    (event) => {
      const detail = event instanceof CustomEvent ? event.detail : null;
      const addResponsePayload = detail?.addResponsePayload;

      setNativeDeliveryPurchaseMode(getNativeDeliveryRoot(), detail?.purchaseMode);

      if (addResponsePayload) {
        detail.completion = handleSuccessfulCartAdd(addResponsePayload, {
          verifiedCart: detail.verifiedCart,
          expectedBundleId: detail.dynamicBundleId,
          expectedItems: detail.expectedItems,
        });
        void Promise.resolve(detail.completion).then(
          () => trackBundleAddToCartForGa4(detail),
          () => {}
        );
        return;
      }

      if (isBundleDirectCheckoutPending) {
        return;
      }

      showBundleDrawerLoadingState();
      if (!isBundleDrawerCurrentlyOpen()) {
        openBundleDrawerShellImmediately({ ignoreCheckoutAb: true });
      }
      scheduleDrawerOpen({ force: true });
    },
    true
  );
  document.addEventListener(
    BUNDLE_ADD_FAILED_EVENT,
    () => {
      if (pendingBundleAddPipelineCount === 0) {
        hasPendingBundleAddPipeline = false;
      }
      rollbackOptimisticNativeDeliveryRoot();
      hideBundleDrawerLoadingState({ force: true });
    },
    true
  );
}

function scheduleBundleDrawerLoadingRecoveryCheck(delay) {
  bundleDrawerLoadingFallbackTimer = window.setTimeout(async () => {
    if (isFlareVisualStateReady()) {
      hideBundleDrawerLoadingState({ force: true });
      return;
    }

    if (bundleDrawerLoadingRecoveryAttempts < BUNDLE_DRAWER_LOADING_MAX_RECOVERY_ATTEMPTS) {
      bundleDrawerLoadingRecoveryAttempts += 1;

      if (isBundleDrawerOpenWithoutFlareBlock()) {
        // The Flare block is provably missing — only a full drawer re-render can
        // restore it.
        await recoverFlareUiForCurrentOpen(undefined, { restartLoadingWindow: false });
      } else {
        // The block exists but isn't ready yet. A full drawer refresh forces ReCharge
        // to re-fetch its bundle data (~1.5s a pass) and amplifies slow loads into a
        // long blank modal, so re-run the enhancement sequence and wait instead.
        runFlareEnhancementSequence();
      }

      if (isFlareVisualStateReady()) {
        hideBundleDrawerLoadingState({ force: true });
        return;
      }

      scheduleBundleDrawerLoadingRecoveryCheck(BUNDLE_DRAWER_LOADING_POST_RECOVERY_SETTLE_DELAY_MS);
      return;
    }

    if (!hasAttemptedMissingFlareBlockReopenForCurrentOpen && isBundleDrawerOpenWithoutFlareBlock()) {
      hasAttemptedMissingFlareBlockReopenForCurrentOpen = true;
      await silentlyReopenBundleDrawerAfterMissingFlareBlock();
      return;
    }

    if (!hasAttemptedDisabledCheckoutRecoveryForCurrentOpen && isBundleCheckoutBlockedForVisiblePrefill()) {
      hasAttemptedDisabledCheckoutRecoveryForCurrentOpen = true;
      await silentlyReopenBundleDrawerAfterMissingFlareBlock();
      return;
    }

    // Out of recovery options: a degraded but interactive drawer beats items and
    // summary stuck at opacity 0 until the customer closes it.
    hideBundleDrawerLoadingState({ force: true });
  }, delay);
}

function schedulePersistedFlareFieldSyncSequence() {
  bundleDrawerFieldSyncTimers.forEach((timer) => {
    window.clearTimeout(timer);
  });

  bundleDrawerFieldSyncTimers = PERSISTED_FLARE_FIELD_SYNC_DELAYS_MS.map((delay) => {
    return window.setTimeout(() => {
      const flareBlock = getFlareBlock();
      applyPersistedFlareFieldPrefills(flareBlock, { allowPostcode: canPrefillFlarePostcode(flareBlock) });
      syncFlareFieldValueStates(flareBlock);
    }, delay);
  });
}

function applyBundleDrawerLoadingState() {
  const drawer = getBundleDrawer();

  if (!(drawer instanceof HTMLElement)) {
    return;
  }

  // Phase 2 split drawer: the loading state masks ONLY the volatile cart region
  // (.cart-drawer__items: items peek, totals, checkout, skeleton). The delivery-form
  // region (.cart-drawer__summary) is never masked here — it is revealed as soon as it
  // is laid out so the customer can start filling it in within ~1s. The skeleton lives
  // inside the items region, so the CSS keyed off [data-bc-loading="true"] handles its
  // visibility; we keep the inline opacity toggle scoped to the items region only.
  const loadingOverlay = drawer.querySelector('.bc-bundle-drawer-loading');
  const loadingLabel = drawer.querySelector('.bc-bundle-drawer-loading__label');
  const drawerItems = drawer.querySelector('.cart-drawer__items');

  if (isBundleDrawerLoading) {
    drawer.setAttribute('data-bc-loading', 'true');
    const hasPreparedEstimate =
      drawer.getAttribute('data-bc-cart-price-source') === 'estimate' &&
      drawer.getAttribute('data-bc-cart-snapshot-ready') === 'true';
    if (!hasPreparedEstimate) {
      drawer.removeAttribute('data-bc-cart-snapshot-ready');
    }

    if (loadingLabel instanceof HTMLElement) {
      loadingLabel.textContent = BUNDLE_DRAWER_LOADING_LABEL;
    }

    if (loadingOverlay instanceof HTMLElement) {
      loadingOverlay.style.display = 'flex';
    }

    if (drawerItems instanceof HTMLElement) {
      drawerItems.style.pointerEvents = 'none';
    }
  } else {
    drawer.removeAttribute('data-bc-loading');

    if (loadingOverlay instanceof HTMLElement) {
      loadingOverlay.style.display = '';
    }

    if (drawerItems instanceof HTMLElement) {
      drawerItems.style.pointerEvents = '';
    }
  }
}

function installBundleDrawerLoadingPolishStyle() {
  if (document.getElementById(BUNDLE_DRAWER_LOADING_STYLE_ID)) {
    return;
  }

  const style = document.createElement('style');
  style.id = BUNDLE_DRAWER_LOADING_STYLE_ID;
  style.textContent = `
    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow[data-bc-loading="true"] .cart-drawer__items {
      min-height: 8.75rem;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow[data-bc-loading="true"]:not([data-bc-cart-snapshot-ready="true"]) .bc-bundle-peek-shell {
      visibility: hidden;
      pointer-events: none;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow .bc-bundle-drawer-loading {
      align-items: flex-start;
      padding-top: 1.125rem;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow .bc-bundle-drawer-loading__inner {
      font-size: 0.875rem;
      line-height: 1.35;
    }

    @media screen and (min-width: 750px) {
      html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow[data-bc-loading="true"] .cart-drawer__header {
        justify-content: flex-start;
        padding-right: 4.75rem;
        text-align: left;
      }

      html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow[data-bc-loading="true"] .cart-drawer__heading-group {
        align-items: flex-start;
        flex: 1 1 auto;
        margin: 0;
        min-width: 0;
        text-align: left;
      }

      html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow[data-bc-loading="true"] .cart-drawer__heading {
        justify-content: flex-start;
        max-width: 100%;
        text-align: left;
      }

      html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow[data-bc-loading="true"] .cart-drawer__subheading {
        text-align: left;
      }
    }

    @media screen and (max-width: 749px) {
      html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow[data-bc-loading="true"] .cart-drawer__items {
        min-height: 7.75rem;
      }

      html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow .bc-bundle-drawer-loading {
        padding-top: 0.875rem;
      }
    }
  `;
  document.head.appendChild(style);
}

function installFlareDrawerFieldStyle() {
  return;

  if (document.getElementById(FLARE_DRAWER_FIELD_STYLE_ID)) {
    return;
  }

  const style = document.createElement('style');
  style.id = FLARE_DRAWER_FIELD_STYLE_ID;
  style.textContent = `
    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow .cart-drawer__summary {
      gap: 1rem;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block {
      display: grid !important;
      gap: 1rem;
      width: 100%;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block > :is(h1, h2, h3, h4) {
      display: none !important;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block .bc-flare-delivery-heading-hidden {
      display: none !important;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow .bc-bundle-peek__summary {
      align-items: stretch;
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      gap: 0.5rem;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow .bc-bundle-peek__summary-copy {
      align-items: center;
      flex-direction: row;
      justify-content: space-between;
      gap: 1rem;
      width: 100%;
      white-space: nowrap;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow .bc-bundle-peek__summary-meta {
      justify-content: space-between;
      gap: 0.75rem;
      width: 100%;
      white-space: nowrap;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block .bc-bundle-step-section,
    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow .cart-note--bundle-flow .cart-note {
      display: grid;
      gap: 0.625rem;
      margin: 0;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block .bc-bundle-step-label {
      align-items: center !important;
      color: #111111 !important;
      display: grid !important;
      font-size: 1rem !important;
      font-weight: 700 !important;
      gap: 0.75rem !important;
      grid-template-columns: 2rem minmax(0, 1fr);
      line-height: 1.2 !important;
      margin: 0 !important;
      opacity: 1 !important;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block .bc-bundle-step-label::before {
      align-items: center;
      background: #111111;
      border-radius: 999px;
      color: #ffffff;
      content: attr(data-bc-step-number);
      display: inline-flex;
      font-size: 0.875rem;
      font-weight: 700;
      height: 2rem;
      justify-content: center;
      line-height: 1;
      width: 2rem;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block .bc-bundle-step-label .step-number {
      display: none !important;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block .bc-bundle-step-label .bc-step-label-text {
      min-width: 0;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block .date-selector,
    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block #date-selector-wrapper,
    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow .cart-note--bundle-flow .cart-note__instructions {
      margin-top: 0 !important;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block #date-selector-wrapper {
      align-items: center !important;
      background: #ffffff !important;
      border: 1.5px solid #c5bfb7 !important;
      border-radius: 0.75rem !important;
      box-shadow: none !important;
      box-sizing: border-box !important;
      display: flex !important;
      gap: 0.75rem !important;
      min-height: 3.25rem !important;
      opacity: 1 !important;
      padding: 0 0.875rem !important;
      width: 100% !important;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block #date-selector-wrapper .date-content {
      align-items: center !important;
      background: transparent !important;
      border: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      box-sizing: border-box !important;
      display: flex !important;
      flex: 1 1 auto !important;
      height: auto !important;
      min-height: 0 !important;
      min-width: 0 !important;
      padding: 0 !important;
      width: auto !important;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block :is(#delivery-zip-code, input.zip-code-input, #input_postcode, input.bc-dc__input, #ship-date),
    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow .cart-note--bundle-flow textarea {
      font-size: 16px !important;
      line-height: 1.25 !important;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block #date-selector-wrapper svg,
    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block #date-selector-wrapper .calendar-icon,
    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block #date-selector-wrapper .date-icon {
      color: #76716b !important;
      flex: 0 0 1.5rem !important;
      height: 1.5rem !important;
      margin: 0 !important;
      max-height: 1.5rem !important;
      max-width: 1.5rem !important;
      opacity: 0.8 !important;
      width: 1.5rem !important;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block #date-selector-wrapper #ship-date {
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      box-sizing: border-box !important;
      color: #111111 !important;
      font-family: inherit !important;
      font-size: 16px !important;
      line-height: 1.25 !important;
      height: auto !important;
      min-width: 0 !important;
      padding: 0 !important;
      width: 100% !important;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block #date-selector-wrapper #ship-date::placeholder {
      color: #76716b !important;
      opacity: 0.55 !important;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block .${FLARE_DATE_VALID_CLASS} #ship-date::placeholder,
    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block .zip-code-input-wrapper.${FLARE_POSTCODE_VALID_CLASS} input::placeholder,
    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block .flare-floating-field.${FLARE_POSTCODE_VALID_CLASS} input::placeholder {
      color: transparent !important;
      opacity: 0 !important;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block .${FLARE_HIDDEN_PLACEHOLDER_CLASS} {
      display: none !important;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block .zip-code-input-wrapper.${FLARE_POSTCODE_EMPTY_CLASS},
    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block .flare-floating-field.${FLARE_POSTCODE_EMPTY_CLASS},
    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block .zip-code-input-wrapper.${FLARE_POSTCODE_VALID_CLASS},
    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block .flare-floating-field.${FLARE_POSTCODE_VALID_CLASS} {
      border-color: #c5bfb7 !important;
      box-shadow: none !important;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block .zip-code-input-wrapper.${FLARE_POSTCODE_EMPTY_CLASS} input,
    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block .flare-floating-field.${FLARE_POSTCODE_EMPTY_CLASS} input {
      border-color: transparent !important;
      box-shadow: none !important;
    }

    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block .zip-code-input-wrapper.${FLARE_POSTCODE_EMPTY_CLASS}:focus-within,
    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block .flare-floating-field.${FLARE_POSTCODE_EMPTY_CLASS}:focus-within,
    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block .zip-code-input-wrapper.${FLARE_POSTCODE_VALID_CLASS}:focus-within,
    html.bc-bundle-cart-flow-active .cart-drawer--bundle-flow #flare-date-picker-block .flare-floating-field.${FLARE_POSTCODE_VALID_CLASS}:focus-within {
      border-color: #111111 !important;
      box-shadow: none !important;
    }
  `;
  document.head.appendChild(style);
}

function isNativeDeliveryEnabled() {
  const value = new URLSearchParams(window.location.search || '').get(NATIVE_DELIVERY_QUERY_KEY);
  const normalizedValue = String(value || '').trim().toLowerCase();
  if (['0', 'false', 'off', 'no', 'disabled'].includes(normalizedValue)) return false;
  if (['1', 'true', 'on', 'yes'].includes(normalizedValue)) return true;
  return window.__BC_NATIVE_DELIVERY_DEFAULT_ENABLED__ === true;
}

function getNativeDeliveryRoot() {
  return (
    document.querySelector('[data-bc-native-delivery-context="cart"]') ||
    getBundleDrawer()?.querySelector('[data-bc-native-delivery]') ||
    null
  );
}

function materializeDormantNativeDeliveryRoot(purchaseMode = 'unknown') {
  if (!isNativeDeliveryEnabled()) {
    return null;
  }

  const drawer = getBundleDrawer();
  const existingRoot = drawer?.querySelector('[data-bc-native-delivery]');

  if (existingRoot instanceof HTMLElement) {
    return existingRoot;
  }

  const slot = drawer?.querySelector('[data-bc-native-delivery-slot]');
  const template = slot?.querySelector('template[data-bc-native-delivery-dormant-template]');
  const templateRoot = template instanceof HTMLTemplateElement
    ? template.content.querySelector('[data-bc-native-delivery]')
    : null;

  if (!(slot instanceof HTMLElement) || !(templateRoot instanceof HTMLElement)) {
    return null;
  }

  const root = templateRoot.cloneNode(true);
  const initialPurchaseMode = normalizeNativeDeliveryPurchaseMode(purchaseMode);
  if (initialPurchaseMode !== 'unknown') {
    root.dataset.bcNativeDeliveryPurchaseMode = initialPurchaseMode;
  }
  root.dataset.bcNativeDeliveryOptimistic = 'true';
  slot.insertBefore(root, template);
  optimisticNativeDeliveryRoot = root;
  isBundleDrawerCartReconciliationPending = true;
  enhanceNativeDeliveryUi({ purchaseMode: initialPurchaseMode });
  return root;
}

function commitOptimisticNativeDeliveryRoot() {
  if (optimisticNativeDeliveryRoot instanceof HTMLElement) {
    delete optimisticNativeDeliveryRoot.dataset.bcNativeDeliveryOptimistic;
  }

  optimisticNativeDeliveryRoot = null;
}

function rollbackOptimisticNativeDeliveryRoot() {
  const root = optimisticNativeDeliveryRoot;
  optimisticNativeDeliveryRoot = null;
  isBundleDrawerCartReconciliationPending = false;

  if (!(root instanceof HTMLElement) || !root.isConnected) {
    return false;
  }

  root.remove();
  const drawer = getBundleDrawer();

  if (typeof drawer?.open === 'function' && isBundleDrawerCurrentlyOpen()) {
    void refreshBundleDrawer().then(() => {
      enhanceBundleDeliveryUi();
    });
  }

  return true;
}

function getNativeDeliveryRootForElement(element) {
  const scope = isNativeDeliveryElement(element)
    ? element.closest('cart-drawer-component, cart-items-component')
    : null;

  return scope?.querySelector('[data-bc-native-delivery]') || null;
}

function getNativeDeliveryCheckoutScope(root = getNativeDeliveryRoot()) {
  return isNativeDeliveryElement(root)
    ? root.closest('cart-drawer-component, cart-items-component')
    : null;
}

function getNativeDeliveryCheckoutButton(root = getNativeDeliveryRoot()) {
  return getNativeDeliveryCheckoutScope(root)?.querySelector('#checkout') || null;
}

function getNativeDeliveryCheckoutForm(root = getNativeDeliveryRoot()) {
  return getNativeDeliveryCheckoutScope(root)?.querySelector('form#cart-form') || null;
}

function getNativeDeliveryInput(root = getNativeDeliveryRoot()) {
  return root?.querySelector('[data-bc-native-delivery-postcode]') || null;
}

function getNativeDeliveryEmailInput(root = getNativeDeliveryRoot()) {
  return root?.querySelector('[data-bc-native-delivery-email]') || null;
}

function normalizeNativeDeliveryEmail(rawEmail) {
  return String(rawEmail || '').trim().toLowerCase();
}

function isValidNativeDeliveryEmail(rawEmail) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeNativeDeliveryEmail(rawEmail));
}

function getNativeDeliveryEmailValue(root = getNativeDeliveryRoot()) {
  const input = getNativeDeliveryEmailInput(root);
  const email = isNativeDeliveryInput(input) ? normalizeNativeDeliveryEmail(input.value) : '';
  return isValidNativeDeliveryEmail(email) ? email : '';
}

function getNativeDeliveryInstructionsInput(root = getNativeDeliveryRoot()) {
  const scope = getNativeDeliveryCheckoutScope(root);

  return (
    root?.querySelector('[data-bc-native-delivery-instructions]') ||
    scope?.querySelector('textarea[name="note"]') ||
    null
  );
}

function getNativeDeliveryInstructionsValue(root = getNativeDeliveryRoot()) {
  const input = getNativeDeliveryInstructionsInput(root);
  return isNativeDeliveryTextarea(input) ? String(input.value || '').trim() : '';
}

function validateNativeDeliveryEmail(root = getNativeDeliveryRoot(), options) {
  const { showError = false, focus = false } = options || {};
  const input = getNativeDeliveryEmailInput(root);
  const message = root?.querySelector('[data-bc-native-delivery-email-message]');

  if (!isNativeDeliveryInput(input)) {
    return false;
  }

  const complete = isValidNativeDeliveryEmail(input.value);
  if (complete) {
    input.value = normalizeNativeDeliveryEmail(input.value);
    delete input.dataset.bcNativeDeliveryEmailTouched;
  }
  const hasError = !complete && (showError || input.dataset.bcNativeDeliveryEmailTouched === 'true');
  input.setAttribute('aria-invalid', hasError ? 'true' : 'false');

  if (isNativeDeliveryElement(message)) {
    message.textContent = hasError
      ? (String(input.value || '').trim() ? 'Please enter a valid email address.' : 'Please enter your email address.')
      : '';
    if (hasError) {
      message.dataset.tone = 'error';
    } else {
      delete message.dataset.tone;
    }
  }

  if (hasError && focus && typeof input.focus === 'function') {
    input.focus({ preventScroll: true });
    input.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }

  return complete;
}

function bindNativeDeliveryEmail(root, input = getNativeDeliveryEmailInput(root)) {
  if (!isNativeDeliveryInput(input) || nativeDeliveryEmailInputs.has(input)) {
    return;
  }

  input.addEventListener('input', () => {
    validateNativeDeliveryEmail(root);
    applyNativeDeliveryCheckoutGuard(root);
    scheduleNativeDeliveryEmailPersist(root);
  });
  input.addEventListener('blur', () => {
    input.dataset.bcNativeDeliveryEmailTouched = 'true';
    validateNativeDeliveryEmail(root, { showError: true });
    applyNativeDeliveryCheckoutGuard(root);
    scheduleNativeDeliveryEmailPersist(root, 0);
  });
  nativeDeliveryEmailInputs.add(input);
  input.dataset.bcNativeDeliveryEmailBound = 'true';
}

function prefillNativeDeliveryEmail(root, input = getNativeDeliveryEmailInput(root)) {
  if (!isNativeDeliveryInput(input) || input.dataset.bcNativeDeliveryEmailPrefillChecked === 'true') {
    return;
  }

  input.dataset.bcNativeDeliveryEmailPrefillChecked = 'true';
  const remembered = normalizeNativeDeliveryEmail(
    getLatestCartSnapshot()?.attributes?.[NATIVE_DELIVERY_EMAIL_ATTRIBUTE_NAME] || input.defaultValue || ''
  );

  if (!isValidNativeDeliveryEmail(remembered)) {
    return;
  }

  root.dataset.bcNativeDeliveryEmailPersisted = remembered;
  if (!String(input.value || '').trim()) {
    input.value = remembered;
  }
}

async function persistNativeDeliveryEmail(email) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), NATIVE_DELIVERY_CART_UPDATE_TIMEOUT_MS);

  try {
    const response = await getCartMutationFetch()(`${window.Shopify?.routes?.root ?? '/'}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        attributes: {
          [NATIVE_DELIVERY_EMAIL_ATTRIBUTE_NAME]: email,
        },
      }),
      keepalive: true,
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Native delivery email cart update failed with status ${response.status}`);
    }
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function scheduleNativeDeliveryEmailPersist(root, delay = NATIVE_DELIVERY_EMAIL_PERSIST_DELAY_MS, attempt = 0) {
  if (!isNativeDeliveryElement(root)) {
    return;
  }

  window.clearTimeout(nativeDeliveryEmailPersistTimers.get(root));
  nativeDeliveryEmailPersistTimers.set(root, window.setTimeout(() => {
    nativeDeliveryEmailPersistTimers.delete(root);
    const email = getNativeDeliveryEmailValue(root);

    if (!email || root.dataset.bcNativeDeliveryEmailPersisted === email) {
      return;
    }

    if (!isNativeDeliveryCheckoutCartSettled()) {
      if (attempt < NATIVE_DELIVERY_EMAIL_PERSIST_MAX_RETRIES) {
        scheduleNativeDeliveryEmailPersist(root, NATIVE_DELIVERY_EMAIL_PERSIST_RETRY_MS, attempt + 1);
      }
      return;
    }

    void persistNativeDeliveryEmail(email)
      .then(() => {
        root.dataset.bcNativeDeliveryEmailPersisted = email;
      })
      .catch((error) => {
        console.warn('Native delivery email could not be remembered on the cart.', error);
      });
  }, Math.max(0, delay)));
}

function isNativeDeliveryElement(node) {
  return Boolean(node && node.nodeType === 1);
}

function isNativeDeliveryInput(node) {
  return isNativeDeliveryElement(node) && node.tagName === 'INPUT';
}

function isNativeDeliveryTextarea(node) {
  return isNativeDeliveryElement(node) && node.tagName === 'TEXTAREA';
}

function isNativeDeliveryButton(node) {
  return isNativeDeliveryElement(node) && node.tagName === 'BUTTON';
}

function isNativeDeliverySubmitter(node) {
  return isNativeDeliveryButton(node) || isNativeDeliveryInput(node);
}

function selectNativeDeliveryDate(root, dateValue) {
  if (!isNativeDeliveryElement(root) || !dateValue) {
    return;
  }

  root.dataset.bcDeliveryDate = dateValue;
  invalidateNativeDeliveryCheckoutPersistence(root);
  renderNativeDeliveryDates(root, root.dataset.bcDeliveryDay, root.dataset.bcDeliveryDate);
  syncNativeDeliveryAttributeInputs(root);
  setNativeDeliveryMessage(root, '');
  applyNativeDeliveryCheckoutGuard(root);
  scheduleNativeDeliveryCartAttributePersist(root);
}

function getNativeDeliveryZones() {
  const zones = window.BC_NATIVE_DELIVERY_ZONES?.sectors;
  return zones && typeof zones === 'object' ? zones : {};
}

function getNativeDeliveryPostcodeResult(rawPostcode) {
  const hasValue = Boolean(String(rawPostcode ?? '').trim());
  const parsed = parseDeliveryZonePostcode(rawPostcode, { allowAmbiguousCorrections: true });

  if (!hasValue) {
    return { allowed: false, reason: 'empty', message: '', ...parsed };
  }

  if (!parsed.valid) {
    return { allowed: false, reason: 'invalid-format', message: 'Enter a valid UK postcode.', ...parsed };
  }

  if (!Object.keys(getNativeDeliveryZones()).length) {
    return {
      allowed: false,
      reason: 'zones-unavailable',
      message: 'Delivery checker is still loading. Please try again.',
      ...parsed,
    };
  }

  const zone = getNativeDeliveryZones()[parsed.sector];

  if (!zone) {
    return {
      allowed: false,
      reason: 'unsupported-zone',
      message: "We're not delivering to this postcode just yet.",
      ...parsed,
    };
  }

  return {
    allowed: true,
    reason: 'allowed',
    message: `${zone.day} delivery is available for ${parsed.outcode} ${parsed.incode.charAt(0)}.`,
    deliveryDay: zone.day,
    district: zone.district || parsed.outcode,
    sectorDisplay: `${parsed.outcode} ${parsed.incode.charAt(0)}`,
    ...parsed,
  };
}

function enhanceNativeDeliveryUi(options) {
  const purchaseMode = options?.purchaseMode || 'unknown';
  const root = getNativeDeliveryRoot();

  if (!isNativeDeliveryElement(root)) {
    prepareNativeDeliveryCheckoutWithoutRootForCurrentSurfaces();
    return;
  }

  installNativeDeliveryStyle();
  disconnectFlareCalendarStateObserver();
  document.documentElement.classList.add(NATIVE_DELIVERY_ACTIVE_CLASS);
  root.hidden = false;
  root.removeAttribute('hidden');
  root.removeAttribute('aria-hidden');
  root.removeAttribute('inert');
  const initialPurchaseMode = normalizeNativeDeliveryPurchaseMode(purchaseMode);
  setNativeDeliveryPurchaseMode(
    root,
    initialPurchaseMode !== 'unknown'
      ? initialPurchaseMode
      : getNativeDeliveryPurchaseModeFromCart(getLatestCartSnapshot())
  );
  if (root.dataset.bcNativeDeliveryContext !== 'cart') {
    hideNativeDeliveryFlareLayer();
  }
  scheduleNativeDeliveryFlareLayerSuppression();
  prefillNativeDeliveryEmail(root);
  bindNativeDeliveryEmail(root);
  validateNativeDeliveryEmail(root);
  enhanceNativeDeliveryInstructions(root);
  bindNativeDeliveryUi(root);
  applyNativeDeliveryCheckoutGuard(root);
  flushDeferredNativeDeliveryCartAttributePersist(root);

  if (isNativeDeliveryVisualStateReady(root) && !isBundleDrawerCartReconciliationPending) {
    hideBundleDrawerLoadingState();
  }
}

function installNativeDeliveryCartObserver() {
  const target = document.getElementById('MainContent');

  if (!(target instanceof HTMLElement) || target.dataset.bcNativeDeliveryObserved === 'true') {
    return;
  }

  let frame = 0;
  const observer = new MutationObserver(() => {
    window.cancelAnimationFrame(frame);
    frame = window.requestAnimationFrame(() => {
      enhanceBundleDeliveryUi();
    });
  });

  observer.observe(target, { childList: true, subtree: true });
  target.dataset.bcNativeDeliveryObserved = 'true';
}

function hideNativeDeliveryFlareLayer() {
  const flareSurfaces = document.querySelectorAll(`
    ${NATIVE_FLARE_APP_BLOCK_SELECTOR},
    #flare-date-picker-block,
    [data-block-type="flare-app-embed"],
    .bc-bundle-flare-anchor
  `);

  flareSurfaces?.forEach((surface) => {
    if (!(surface instanceof HTMLElement)) return;
    surface.dataset.bcNativeDeliveryHidden = 'true';
    surface.setAttribute('aria-hidden', 'true');
    surface.setAttribute('inert', '');
  });
}

function scheduleNativeDeliveryFlareLayerSuppression() {
  [0, 500, 1000, 2000].forEach((delay) => {
    window.setTimeout(() => {
      if (isNativeDeliveryEnabled()) {
        hideNativeDeliveryFlareLayer();
      }
    }, delay);
  });
}

function installNativeDeliveryStyle() {
  if (document.getElementById(NATIVE_DELIVERY_STYLE_ID)) {
    return;
  }

  const style = document.createElement('style');
  style.id = NATIVE_DELIVERY_STYLE_ID;
  style.textContent = `
    html.${NATIVE_DELIVERY_ACTIVE_CLASS} .cart-drawer--bundle-flow [data-bc-bundle-app-blocks],
    html.${NATIVE_DELIVERY_ACTIVE_CLASS} .cart-drawer--bundle-flow #flare-date-picker-block,
    html.${NATIVE_DELIVERY_ACTIVE_CLASS} .cart-drawer--bundle-flow [data-block-type="flare-app-embed"],
    html.${NATIVE_DELIVERY_ACTIVE_CLASS} .cart-drawer--bundle-flow .bc-bundle-flare-anchor,
    html.${NATIVE_DELIVERY_ACTIVE_CLASS} .cart-page #flare-date-picker-block,
    html.${NATIVE_DELIVERY_ACTIVE_CLASS} .cart-page [data-block-type="flare-app-embed"] {
      display: none !important;
      visibility: hidden !important;
      pointer-events: none !important;
    }

    .bc-native-delivery {
      display: grid;
      gap: 14px;
      margin: 0;
      padding: 0;
    }

    .cart-page .cart__ctas > .bc-native-delivery {
      width: 100%;
      text-align: left;
    }

    html.${NATIVE_DELIVERY_ACTIVE_CLASS} .cart-drawer--bundle-flow .cart__summary-totals {
      display: none !important;
    }

    html.${NATIVE_DELIVERY_ACTIVE_CLASS} .cart__checkout-button[data-bc-delivery-ready="true"]:not(:disabled):not([aria-disabled="true"]) {
      cursor: pointer !important;
      opacity: 1 !important;
      pointer-events: auto !important;
    }

    html.${NATIVE_DELIVERY_ACTIVE_CLASS} [data-bc-native-delivery-state="blocked"] .cart__checkout-button {
      cursor: not-allowed !important;
      opacity: 0.5 !important;
    }

    html.${NATIVE_DELIVERY_ACTIVE_CLASS} [data-bc-native-delivery-state="incomplete"] .cart__checkout-button {
      cursor: pointer !important;
      opacity: 1 !important;
      pointer-events: auto !important;
    }

    .bc-native-delivery__checkout-hint {
      margin: 0 0 8px;
      color: #555;
      font-size: 14px;
      line-height: 1.35;
      text-align: center;
    }

    .bc-native-delivery__checkout-hint[hidden] {
      display: none !important;
    }

    .bc-native-delivery__checkout-hint[data-tone="error"] {
      color: #9f1d1d;
      font-weight: 600;
    }

    .bc-native-delivery__section {
      display: grid;
      gap: 8px;
    }

    .bc-native-delivery__section[hidden] {
      display: none !important;
    }

    .bc-native-delivery__label {
      display: flex;
      align-items: center;
      gap: 0;
      margin: 0;
      color: #111;
      font-size: 14px;
      font-weight: 600;
      line-height: 1.2;
    }

    .bc-native-delivery__step {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: var(--bc-bundle-step-badge-size, 24px);
      min-width: var(--bc-bundle-step-badge-size, 24px);
      height: var(--bc-bundle-step-badge-size, 24px);
      margin-right: var(--bc-bundle-step-label-gap, 12px);
      border-radius: 999px;
      background: #111;
      color: #fff;
      font-size: 12px;
      font-weight: 700;
      line-height: 1;
    }

    .bc-native-delivery__input {
      width: 100%;
      min-height: 48px;
      border: 1px solid #a5a5a5;
      border-radius: 8px;
      background: #fff;
      color: #111;
      font-size: 16px;
      padding: 12px 14px;
    }

    .bc-native-delivery__input::placeholder {
      color: #77726c;
      opacity: 0.62;
    }

    .bc-native-delivery__input[aria-invalid="true"] {
      border-color: #a64a3f;
      box-shadow: 0 0 0 1px #a64a3f;
    }

    .bc-native-delivery__message {
      margin: 0;
      color: #555;
      font-size: 13px;
      line-height: 1.35;
    }

    .bc-native-delivery__message:empty {
      display: none;
    }

    .bc-native-delivery__message[data-tone="error"] {
      color: #9f1d1d;
    }

    .bc-native-delivery__message[data-tone="unsupported"] {
      display: flex;
      align-items: flex-start;
      gap: 9px;
      padding: 10px 12px;
      border: 1px solid #dfc4bd;
      border-radius: 8px;
      background: #fff7f5;
      color: #662f28;
      font-size: 13px;
      line-height: 1.4;
    }

    .bc-native-delivery__message[data-tone="unsupported"]::before {
      display: inline-flex;
      flex: 0 0 18px;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      margin-top: 1px;
      border: 1px solid currentColor;
      border-radius: 50%;
      content: "!";
      font-family: Arial, sans-serif;
      font-size: 12px;
      font-weight: 700;
      line-height: 1;
    }

    .bc-native-delivery__default {
      display: grid;
      gap: 4px;
      min-height: 62px;
      padding: 12px 14px;
      border: 1px solid #111;
      border-radius: 8px;
      background: #fff;
      color: #111;
    }

    .bc-native-delivery__default[aria-disabled="true"] {
      border-color: #d1ccc6;
      background: #f7f3ef;
      color: #85807a;
    }

    .bc-native-delivery__default-date {
      font-size: 18px;
      font-weight: 700;
      line-height: 1.25;
    }

    @media (max-width: 749px) {
      .cart-drawer--bundle-flow .bc-native-delivery > .bc-native-delivery__section:first-child {
        padding-top: 6px;
      }

      .bc-native-delivery__default-date {
        font-size: 15px;
        font-weight: 400;
      }
    }

    .bc-native-delivery__default-context {
      color: #69645f;
      font-size: 13px;
      line-height: 1.3;
    }

    .bc-native-delivery__date-actions {
      display: flex;
      align-items: center;
      min-height: 20px;
    }

    .bc-native-delivery__alternatives {
      display: grid;
      gap: 6px;
    }

    .bc-native-delivery__link {
      appearance: none;
      border: 0;
      background: transparent;
      color: #555;
      cursor: pointer;
      font: inherit;
      font-size: 13px;
      padding: 0;
      text-decoration: underline;
    }

    .bc-native-delivery__link:disabled,
    .bc-native-delivery__link[aria-disabled="true"] {
      color: #999;
      cursor: default;
      text-decoration: none;
    }

    .bc-native-delivery__summary {
      display: inline-flex;
      align-items: center;
      justify-content: flex-start;
      gap: 5px;
      width: fit-content;
      min-height: 0;
      padding: 0;
      border: 0;
      border-radius: 0;
      background: transparent;
      color: #555;
      font-size: 13px;
      line-height: 1.3;
      list-style: none;
      text-decoration: none;
    }

    .bc-native-delivery__summary::-webkit-details-marker {
      display: none;
    }

    .bc-native-delivery__summary > span {
      text-decoration: underline;
      text-underline-offset: 3px;
    }

    .bc-native-delivery__summary::after {
      content: "";
      display: inline-block;
      width: 7px;
      height: 7px;
      margin-top: -3px;
      border-right: 1.5px solid currentColor;
      border-bottom: 1.5px solid currentColor;
      transform: rotate(45deg);
      transform-origin: center;
      transition: transform 160ms ease;
    }

    .bc-native-delivery__alternatives[open] .bc-native-delivery__summary::after {
      transform: rotate(225deg);
    }

    .bc-native-delivery__dates {
      display: grid;
      gap: 8px;
    }

    .bc-native-delivery__options {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;
    }

    .bc-native-delivery__options[hidden] {
      display: none !important;
    }

    .bc-native-delivery__date {
      min-height: 44px;
      border: 1px solid #a5a5a5;
      border-radius: 8px;
      background: #fff;
      color: #111;
      font: inherit;
      font-size: 14px;
      cursor: pointer;
    }

    .bc-native-delivery__date[hidden] {
      display: none !important;
    }

    .bc-native-delivery__date[aria-checked="true"] {
      border-color: #c8c3be;
      background: #ece9e6;
      color: #77726d;
      cursor: default;
      opacity: 1;
    }

    .bc-native-delivery__instructions-slot {
      min-width: 0;
    }

    .bc-native-delivery__instructions-slot[hidden] {
      display: none !important;
    }

    .bc-native-delivery__instructions-slot cart-note,
    .bc-native-delivery__instructions-slot .cart-note,
    .bc-native-delivery__instructions-slot .cart-note__inner {
      display: block;
      width: 100%;
    }

    .bc-native-delivery__instructions-slot .cart-note,
    .bc-native-delivery__instructions-slot .cart-note__inner {
      margin: 0;
    }

    .bc-native-delivery__instructions-slot .cart-note__summary {
      display: block;
      margin: 0 0 8px;
      padding: 0;
      cursor: default;
      list-style: none;
    }

    .bc-native-delivery__instructions-slot .cart-note__summary::-webkit-details-marker,
    .bc-native-delivery__instructions-slot .cart-totals__icon,
    .bc-native-delivery__instructions-slot .icon-plus {
      display: none !important;
    }

    .bc-native-delivery__instructions-slot .cart-note__label {
      display: flex;
      align-items: center;
      margin: 0;
      color: #111;
      font-size: 14px;
      font-weight: 600;
      line-height: 1.2;
    }

    .bc-native-delivery__label-row {
      position: relative;
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
    }

    .bc-native-delivery__label-row .bc-native-delivery__label {
      flex: 1 1 auto;
      min-width: 0;
    }

    .bc-native-delivery__label-actions {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      margin-left: auto;
    }

    .bc-native-delivery__flat-help {
      position: relative;
      flex: 0 0 auto;
      margin: 0 0 0 auto;
    }

    .bc-native-delivery__flat-help-button {
      display: inline-flex;
      align-items: center;
      min-height: 24px;
      padding: 3px 8px;
      border: 1px solid #d6d0ca;
      border-radius: 999px;
      background: #f8f3ee;
      color: #69645f;
      cursor: pointer;
      font-family: Arial, sans-serif;
      font-size: 10px;
      font-weight: 600;
      line-height: 1;
      white-space: nowrap;
    }

    .bc-native-delivery__flat-help-button:hover,
    .bc-native-delivery__flat-help-button:focus-visible,
    .bc-native-delivery__flat-help.is-open .bc-native-delivery__flat-help-button {
      border-color: #8f8983;
      color: #111;
    }

    .bc-native-delivery__flat-help-copy {
      position: absolute;
      right: 0;
      top: calc(100% + 7px);
      z-index: 30;
      display: none;
      width: min(280px, calc(100vw - 64px));
      max-width: calc(100vw - 64px);
      margin: 0;
      padding: 10px 12px;
      border: 1px solid #d6d0ca;
      border-radius: 8px;
      background: #fff;
      box-shadow: 0 8px 20px rgba(17, 17, 17, 0.12);
      color: #4f4a45;
      font-family: Arial, sans-serif;
      font-size: 12px;
      font-weight: 400;
      line-height: 1.4;
      text-align: left;
    }

    .bc-native-delivery__flat-help.is-open .bc-native-delivery__flat-help-copy,
    .bc-native-delivery__flat-help:hover .bc-native-delivery__flat-help-copy,
    .bc-native-delivery__flat-help:focus-within .bc-native-delivery__flat-help-copy {
      display: block;
    }

    .bc-native-delivery__required {
      padding: 3px 7px;
      border-radius: 999px;
      background: #ece9e6;
      color: #69645f;
      font-family: Arial, sans-serif;
      font-size: 10px;
      font-weight: 600;
      line-height: 1;
      text-transform: uppercase;
    }

    .bc-native-delivery__instructions-slot textarea[name="note"] {
      display: block;
      width: 100%;
      min-height: 88px;
      margin: 0 !important;
      padding: 12px 14px;
      border: 1px solid #a5a5a5;
      border-radius: 8px;
      background: #fff !important;
      color: #111;
      font-family: inherit;
      font-size: 16px !important;
      line-height: 1.35 !important;
      resize: vertical;
    }

    .bc-native-delivery__instructions-slot textarea[name="note"][aria-invalid="true"] {
      border-color: #a64a3f;
      box-shadow: 0 0 0 1px #a64a3f;
    }

    .bc-native-delivery__instructions-message {
      min-height: 18px;
      margin: 6px 0 0;
      color: #69645f;
      font-size: 12px;
      line-height: 1.4;
    }

    .bc-native-delivery__instructions-message:empty {
      display: none;
    }

    .bc-native-delivery__instructions-message[data-tone="error"] {
      color: #8b3028;
    }

    html.${NATIVE_DELIVERY_ACTIVE_CLASS} .bc-native-delivery__instructions-message[data-tone="error"] {
      color: #8b3028 !important;
    }

    html.${NATIVE_DELIVERY_ACTIVE_CLASS} .cart__bundle-note:not(:has(cart-note)) {
      display: none !important;
    }
  `;
  document.head.appendChild(style);
}

function enhanceNativeDeliveryInstructions(root = getNativeDeliveryRoot()) {
  const scope = getNativeDeliveryCheckoutScope(root);
  const slot = root?.querySelector('[data-bc-native-delivery-instructions-slot]');

  if (!isNativeDeliveryElement(scope) || !isNativeDeliveryElement(slot)) {
    return;
  }

  const currentNote = slot.querySelector('cart-note');
  const sourceNote = Array.from(scope.querySelectorAll('cart-note')).find((note) => !root.contains(note));

  if (isNativeDeliveryElement(sourceNote)) {
    if (isNativeDeliveryElement(currentNote)) {
      const currentInput = currentNote.querySelector('textarea[name="note"]');
      const sourceInput = sourceNote.querySelector('textarea[name="note"]');

      if (
        isNativeDeliveryTextarea(currentInput) &&
        isNativeDeliveryTextarea(sourceInput) &&
        !currentInput.value.trim() &&
        sourceInput.value.trim()
      ) {
        currentInput.value = sourceInput.value;
      }
      sourceNote.remove();
    } else {
      slot.appendChild(sourceNote);
    }
  }

  const note = slot.querySelector('cart-note');
  const input = note?.querySelector('textarea[name="note"]');

  if (!isNativeDeliveryElement(note) || !isNativeDeliveryTextarea(input)) {
    slot.hidden = true;
    return;
  }

  note.classList.add('bc-native-delivery__instructions-note');
  input.dataset.bcNativeDeliveryInstructions = 'true';
  input.required = true;
  input.setAttribute('aria-required', 'true');
  input.placeholder = 'E.g. leave behind the side gate or with a neighbour';

  note.querySelectorAll('label[for="cart-note"]').forEach((label) => {
    label.textContent = 'Delivery instructions';
  });

  const label = note.querySelector('.cart-note__label');
  if (isNativeDeliveryElement(label)) {
    let labelText = label.querySelector('.cart-note__label-text');
    if (!isNativeDeliveryElement(labelText)) {
      labelText = document.createElement('span');
      labelText.className = 'cart-note__label-text';
    }
    labelText.textContent = 'Delivery instructions';

    let step = label.querySelector('.cart-note__step');
    if (!isNativeDeliveryElement(step)) {
      step = document.createElement('span');
      step.className = 'cart-note__step bc-native-delivery__step';
    } else {
      step.classList.add('bc-native-delivery__step');
    }
    step.textContent = '4';

    let required = label.querySelector('.bc-native-delivery__required');
    if (!isNativeDeliveryElement(required)) {
      required = document.createElement('span');
      required.className = 'bc-native-delivery__required';
      required.textContent = 'Required';
    }

    let labelActions = label.querySelector('.bc-native-delivery__label-actions');
    if (!isNativeDeliveryElement(labelActions)) {
      labelActions = document.createElement('span');
      labelActions.className = 'bc-native-delivery__label-actions';
    }
    labelActions.replaceChildren(required);

    label.replaceChildren(step, labelText, labelActions);
  }

  const details = note.querySelector('details');
  const summary = details?.querySelector('summary');
  if (isNativeDeliveryElement(details)) {
    details.open = true;
  }
  if (isNativeDeliveryElement(summary) && summary.dataset.bcNativeDeliveryStatic !== 'true') {
    summary.dataset.bcNativeDeliveryStatic = 'true';
    summary.setAttribute('aria-disabled', 'true');
    summary.setAttribute('tabindex', '-1');
    summary.addEventListener('click', (event) => event.preventDefault());
  }

  let message = note.querySelector('[data-bc-native-delivery-instructions-message]');
  if (!isNativeDeliveryElement(message)) {
    message = document.createElement('p');
    message.id = `bc-native-delivery-instructions-message-${root.dataset.bcNativeDeliveryContext || 'cart'}`;
    message.className = 'bc-native-delivery__instructions-message';
    message.dataset.bcNativeDeliveryInstructionsMessage = 'true';
    message.setAttribute('role', 'status');
    input.insertAdjacentElement('afterend', message);
  }

  const describedBy = new Set(String(input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
  describedBy.add(message.id);
  input.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
  slot.hidden = false;
  bindNativeDeliveryInstructions(root, input);
  validateNativeDeliveryInstructions(root);
}

function bindNativeDeliveryInstructions(root, input = getNativeDeliveryInstructionsInput(root)) {
  if (!isNativeDeliveryTextarea(input) || nativeDeliveryInstructionInputs.has(input)) {
    return;
  }

  input.addEventListener('input', () => {
    validateNativeDeliveryInstructions(root);
    applyNativeDeliveryCheckoutGuard(root);
  });
  input.addEventListener('blur', () => {
    input.dataset.bcNativeDeliveryInstructionsTouched = 'true';
    validateNativeDeliveryInstructions(root, { showError: true });
    applyNativeDeliveryCheckoutGuard(root);
  });
  nativeDeliveryInstructionInputs.add(input);
  input.dataset.bcNativeDeliveryBound = 'true';
}

function validateNativeDeliveryInstructions(root = getNativeDeliveryRoot(), options) {
  const { showError = false, focus = false } = options || {};
  const input = getNativeDeliveryInstructionsInput(root);
  const message = root?.querySelector('[data-bc-native-delivery-instructions-message]');

  if (!isNativeDeliveryTextarea(input)) {
    return false;
  }

  const complete = Boolean(String(input.value || '').trim());
  if (complete) {
    delete input.dataset.bcNativeDeliveryInstructionsTouched;
  }
  const hasError = !complete && (showError || input.dataset.bcNativeDeliveryInstructionsTouched === 'true');
  input.setAttribute('aria-invalid', hasError ? 'true' : 'false');

  if (isNativeDeliveryElement(message)) {
    message.textContent = hasError
      ? 'Please add delivery instructions before checkout.'
      : '';
    if (hasError) {
      message.dataset.tone = 'error';
    } else {
      delete message.dataset.tone;
    }
  }

  if (hasError && focus && typeof input.focus === 'function') {
    input.focus({ preventScroll: true });
    input.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }

  return complete;
}

function bindNativeDeliveryUi(root) {
  if (!isNativeDeliveryElement(root) || nativeDeliveryBoundRoots.has(root)) {
    return;
  }

  const input = getNativeDeliveryInput(root);
  const dates = root.querySelector('[data-bc-native-delivery-dates]');
  const flatHelpButton = root.querySelector('[data-bc-native-delivery-flat-help-toggle]');

  if (isNativeDeliveryInput(input)) {
    input.addEventListener('input', () => {
      updateNativeDeliveryPostcode(root, { quietInvalid: true });
    });
    input.addEventListener('change', () => {
      updateNativeDeliveryPostcode(root);
    });
    input.addEventListener('blur', () => {
      updateNativeDeliveryPostcode(root);
    });
  }

  if (isNativeDeliveryElement(dates)) {
    dates.addEventListener('click', (event) => {
      const target = isNativeDeliveryElement(event.target) ? event.target : null;
      const button = target?.closest('[data-bc-native-delivery-date]');

      if (!isNativeDeliveryButton(button)) {
        return;
      }

      selectNativeDeliveryDate(root, button.dataset.bcNativeDeliveryDate || '');
    });
    dates.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
        return;
      }

      const target = isNativeDeliveryElement(event.target) ? event.target : null;
      const button = target?.closest('[data-bc-native-delivery-date]');

      if (!isNativeDeliveryButton(button)) {
        return;
      }

      const buttons = Array.from(dates.querySelectorAll('[data-bc-native-delivery-date]'))
        .filter((candidate) => isNativeDeliveryButton(candidate) && !candidate.hidden);
      const currentIndex = buttons.indexOf(button);

      if (currentIndex === -1 || buttons.length < 2) {
        return;
      }

      event.preventDefault();
      const direction = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
      const nextButton = buttons[(currentIndex + direction + buttons.length) % buttons.length];
      const nextDate = nextButton.dataset.bcNativeDeliveryDate || '';
      selectNativeDeliveryDate(root, nextDate);
      window.requestAnimationFrame(() => {
        const currentButtons = Array.from(dates.querySelectorAll('[data-bc-native-delivery-date]'));
        currentButtons.find((candidate) => candidate.dataset.bcNativeDeliveryDate === nextDate)?.focus();
      });
    });
  }

  if (isNativeDeliveryButton(flatHelpButton)) {
    flatHelpButton.addEventListener('click', () => {
      const flatHelp = flatHelpButton.closest('.bc-native-delivery__flat-help');
      if (!isNativeDeliveryElement(flatHelp)) return;
      const isOpen = flatHelp.classList.toggle('is-open');
      flatHelpButton.setAttribute('aria-expanded', String(isOpen));
    });
    flatHelpButton.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const flatHelp = flatHelpButton.closest('.bc-native-delivery__flat-help');
      flatHelp?.classList.remove('is-open');
      flatHelpButton.setAttribute('aria-expanded', 'false');
    });
  }

  nativeDeliveryBoundRoots.add(root);
  root.dataset.bcNativeDeliveryBound = 'true';

  if (isNativeDeliveryInput(input) && input.value.trim()) {
    updateNativeDeliveryPostcode(root, {
      preferredDate: root.dataset.bcNativeDeliveryInitialDate || '',
    });
  } else {
    clearNativeDeliveryDates(root);
  }
}

function updateNativeDeliveryPostcode(
  root = getNativeDeliveryRoot(),
  { quietInvalid = false, preferredDate = '' } = {}
) {
  const input = getNativeDeliveryInput(root);

  if (!isNativeDeliveryElement(root) || !isNativeDeliveryInput(input)) {
    return;
  }

  invalidateNativeDeliveryCheckoutPersistence(root);
  const result = getNativeDeliveryPostcodeResult(input.value);
  root.dataset.bcDeliveryPostcode = result.allowed ? result.normalized : '';
  root.dataset.bcDeliverySector = result.allowed ? result.sectorDisplay : '';
  root.dataset.bcDeliveryDay = result.allowed ? result.deliveryDay : '';
  root.dataset.bcDeliveryDate = '';

  if (!result.allowed) {
    clearNativeDeliveryDates(root);
    syncNativeDeliveryAttributeInputs(root);
    if (shouldSuppressNativeDeliveryPostcodeError(input, result, quietInvalid)) {
      setNativeDeliveryMessage(root, '');
    } else {
      const tone = result.reason === 'unsupported-zone' ? 'unsupported' : result.reason === 'empty' ? '' : 'error';
      setNativeDeliveryMessage(root, result.reason === 'empty' ? '' : result.message, tone);
    }
    applyNativeDeliveryCheckoutGuard(root);
    return;
  }

  renderNativeDeliveryDates(root, result.deliveryDay, preferredDate);
  delete root.dataset.bcNativeDeliveryInitialDate;
  syncNativeDeliveryAttributeInputs(root);
  input.setAttribute('aria-invalid', 'false');
  setNativeDeliveryMessage(root, '');
  applyNativeDeliveryCheckoutGuard(root);
  scheduleNativeDeliveryCartAttributePersist(root);
}

function shouldSuppressNativeDeliveryPostcodeError(input, result, quietInvalid) {
  return Boolean(
    result &&
    !result.allowed &&
    result.reason !== 'empty' &&
    (quietInvalid || document.activeElement === input)
  );
}

function clearNativeDeliveryDates(root) {
  const section = root?.querySelector('[data-bc-native-delivery-date-section]');
  const dates = root?.querySelector('[data-bc-native-delivery-dates]');

  if (isNativeDeliveryElement(section)) {
    section.hidden = false;
    section.dataset.bcNativeDeliveryPending = 'true';
  }

  if (isNativeDeliveryElement(dates)) {
    dates.replaceChildren();
    dates.appendChild(buildNativeDeliveryDefaultCard(null, root));
  }
}

function renderNativeDeliveryDates(root, deliveryDay, selectedDateValue = '') {
  const section = root.querySelector('[data-bc-native-delivery-date-section]');
  const dates = root.querySelector('[data-bc-native-delivery-dates]');

  if (!isNativeDeliveryElement(section) || !isNativeDeliveryElement(dates)) {
    return;
  }

  section.hidden = false;
  delete section.dataset.bcNativeDeliveryPending;
  dates.replaceChildren();

  const deliveryDates = getNativeDeliveryDates(deliveryDay);
  const selectedDate = deliveryDates.find((date) => date.value === selectedDateValue) || deliveryDates[0];

  if (!selectedDate) {
    clearNativeDeliveryDates(root);
    return;
  }

  root.dataset.bcDeliveryDate = selectedDate.value;
  dates.appendChild(buildNativeDeliveryDefaultCard(selectedDate, root));

  const alternatives = document.createElement('details');
  alternatives.className = 'bc-native-delivery__alternatives';
  alternatives.dataset.bcNativeDeliveryAlternatives = '';
  alternatives.open = nativeDeliveryAlternativesOpen.get(root) === true;
  alternatives.addEventListener('toggle', () => {
    if (!alternatives.isConnected) {
      return;
    }
    nativeDeliveryAlternativesOpen.set(root, alternatives.open);
  });

  const summary = document.createElement('summary');
  summary.className = 'bc-native-delivery__link bc-native-delivery__summary';
  const summaryLabel = document.createElement('span');
  summaryLabel.textContent = 'choose another date';
  summary.appendChild(summaryLabel);
  alternatives.appendChild(summary);

  const options = document.createElement('div');
  options.className = 'bc-native-delivery__options';
  options.dataset.bcNativeDeliveryOptions = '';

  deliveryDates.forEach((date) => {
    const isSelected = date.value === selectedDate.value;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'bc-native-delivery__date';
    button.dataset.bcNativeDeliveryDate = date.value;
    button.setAttribute('role', 'radio');
    button.setAttribute('aria-checked', String(isSelected));
    button.setAttribute('aria-label', isSelected ? `${date.label}, selected` : date.label);
    button.tabIndex = isSelected ? 0 : -1;
    button.textContent = date.label;
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (isSelected) {
        return;
      }
      selectNativeDeliveryDate(root, button.dataset.bcNativeDeliveryDate || '');
    });

    options.appendChild(button);
  });

  alternatives.appendChild(options);
  dates.appendChild(alternatives);
}

function getNativeDeliveryDateLead(root) {
  return root?.dataset?.bcNativeDeliveryPurchaseMode === 'one-time'
    ? 'Your delivery'
    : 'Your first delivery';
}

function setNativeDeliveryPurchaseMode(root, purchaseMode) {
  const normalizedPurchaseMode = normalizeNativeDeliveryPurchaseMode(purchaseMode);
  if (!isNativeDeliveryElement(root) || normalizedPurchaseMode === 'unknown') {
    return false;
  }

  if (root.dataset.bcNativeDeliveryPurchaseMode === normalizedPurchaseMode) {
    return false;
  }

  root.dataset.bcNativeDeliveryPurchaseMode = normalizedPurchaseMode;
  const dateLabel = root.querySelector('.bc-native-delivery__default-date');

  if (isNativeDeliveryElement(dateLabel)) {
    const state = getNativeDeliveryState(root);
    const selectedDate = getNativeDeliveryDates(state?.day)
      .find((date) => date.value === state?.date);
    const lead = getNativeDeliveryDateLead(root);
    dateLabel.textContent = selectedDate ? `${lead}: ${selectedDate.fullLabel}` : lead;
  }

  return true;
}

function buildNativeDeliveryDefaultCard(date, root) {
  const card = document.createElement('div');
  card.className = 'bc-native-delivery__default';
  card.setAttribute('aria-disabled', date ? 'false' : 'true');

  const dateLabel = document.createElement('strong');
  dateLabel.className = 'bc-native-delivery__default-date';
  const lead = getNativeDeliveryDateLead(root);
  dateLabel.textContent = date ? `${lead}: ${date.fullLabel}` : lead;

  card.appendChild(dateLabel);
  if (date) {
    const context = document.createElement('span');
    context.className = 'bc-native-delivery__default-context';
    context.textContent = date.context;
    card.appendChild(context);
  } else {
    const context = document.createElement('span');
    context.className = 'bc-native-delivery__default-context';
    context.textContent = 'Enter postcode to see dates';
    card.appendChild(context);
  }
  return card;
}

function getNativeDeliveryDates(deliveryDay, now = new Date()) {
  const targetDow = deliveryDay === 'Sunday' ? 0 : 6;
  const nowParts = getNativeDeliveryLondonParts(now);
  const today = new Date(Date.UTC(nowParts.year, nowParts.month - 1, nowParts.day, 12));
  const todayDow = today.getUTCDay();
  let daysUntil = (targetDow - todayDow + 7) % 7;

  if (daysUntil === 0) {
    daysUntil = 7;
  }

  const weekendCutoffPassed = (todayDow === 5 && nowParts.hour >= 7) || todayDow === 6;
  if (weekendCutoffPassed && daysUntil >= 1 && daysUntil <= 2) {
    daysUntil += 7;
  }

  return Array.from({ length: NATIVE_DELIVERY_DATE_COUNT }, (_, index) => {
    const date = new Date(today);
    date.setUTCDate(today.getUTCDate() + daysUntil + index * 7);
    return {
      value: date.toISOString().slice(0, 10),
      label: new Intl.DateTimeFormat('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
      }).format(date),
      fullLabel: new Intl.DateTimeFormat('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: 'UTC',
      }).format(date),
      context: getNativeDeliveryDateContext(date, today),
    };
  });
}

function getNativeDeliveryDateContext(date, today) {
  const diffDays = Math.round((date.getTime() - today.getTime()) / 86400000);
  const dayCount = Math.max(0, diffDays);
  return `in ${dayCount} ${dayCount === 1 ? 'day' : 'days'}`;
}

function getNativeDeliveryLondonParts(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));

  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    hour: Number(values.hour),
    minute: Number(values.minute),
  };
}

function setNativeDeliveryMessage(root, message, tone = '') {
  const messageNode = root?.querySelector('[data-bc-native-delivery-message]');

  if (!isNativeDeliveryElement(messageNode)) {
    return;
  }

  messageNode.textContent = message || '';

  if (tone) {
    messageNode.dataset.tone = tone;
  } else {
    delete messageNode.dataset.tone;
  }
}

function syncNativeDeliveryAttributeInputs(root = getNativeDeliveryRoot()) {
  if (!isNativeDeliveryElement(root)) {
    return;
  }

  const state = getNativeDeliveryState(root);
  const values = {
    [NATIVE_DELIVERY_ATTRIBUTE_NAMES.postcode]: state?.postcode || '',
    [NATIVE_DELIVERY_ATTRIBUTE_NAMES.date]: state?.date || '',
    [NATIVE_DELIVERY_ATTRIBUTE_NAMES.day]: state?.day || '',
    [NATIVE_DELIVERY_ATTRIBUTE_NAMES.sector]: state?.sector || '',
    [NATIVE_DELIVERY_ATTRIBUTE_NAMES.flareDate]: state?.date || '',
    [NATIVE_DELIVERY_ATTRIBUTE_NAMES.flareProfile]: state?.day ? `${state.day} Profile` : '',
    [NATIVE_DELIVERY_ATTRIBUTE_NAMES.flareProfileName]: state?.day ? `${state.day} Profile` : '',
  };

  Array.from(root.querySelectorAll('[data-bc-native-delivery-attribute]')).forEach((input) => {
    if (!isNativeDeliveryInput(input)) {
      return;
    }

    input.disabled = false;
    input.value = values[input.dataset.bcNativeDeliveryAttribute] || '';
  });
}

function getNativeDeliveryAttributeInputValue(root, attributeName) {
  if (!isNativeDeliveryElement(root) || !attributeName) {
    return '';
  }

  const input = Array.from(root.querySelectorAll('[data-bc-native-delivery-attribute]'))
    .find((candidate) => candidate.dataset.bcNativeDeliveryAttribute === attributeName);

  return isNativeDeliveryInput(input) ? String(input.value || '').trim() : '';
}

function getNativeDeliveryState(root = getNativeDeliveryRoot()) {
  if (!isNativeDeliveryElement(root)) {
    return null;
  }

  const postcode = String(
    root.dataset.bcDeliveryPostcode ||
    getNativeDeliveryAttributeInputValue(root, NATIVE_DELIVERY_ATTRIBUTE_NAMES.postcode)
  ).trim();
  const date = String(
    root.dataset.bcDeliveryDate ||
    getNativeDeliveryAttributeInputValue(root, NATIVE_DELIVERY_ATTRIBUTE_NAMES.date)
  ).trim();
  const day = String(
    root.dataset.bcDeliveryDay ||
    getNativeDeliveryAttributeInputValue(root, NATIVE_DELIVERY_ATTRIBUTE_NAMES.day)
  ).trim();
  const sector = String(
    root.dataset.bcDeliverySector ||
    getNativeDeliveryAttributeInputValue(root, NATIVE_DELIVERY_ATTRIBUTE_NAMES.sector)
  ).trim();

  if (!postcode || !date || !day || !sector || !isNativeDeliveryDateAvailable(day, date)) {
    return null;
  }

  root.dataset.bcDeliveryPostcode = postcode;
  root.dataset.bcDeliveryDate = date;
  root.dataset.bcDeliveryDay = day;
  root.dataset.bcDeliverySector = sector;

  return { postcode, date, day, sector };
}

function isNativeDeliveryDateAvailable(day, dateValue) {
  return getNativeDeliveryDates(day).some((date) => date.value === dateValue);
}

function isNativeDeliveryReady(root = getNativeDeliveryRoot()) {
  return (
    Boolean(getNativeDeliveryEmailValue(root)) &&
    Boolean(getNativeDeliveryState(root)) &&
    Boolean(getNativeDeliveryInstructionsValue(root))
  );
}

function isNativeDeliveryCheckoutCartSettled() {
  if (
    hasPendingBundleAddPipeline ||
    pendingBundleAddPipelineCount > 0 ||
    hasPendingBundleSession() ||
    isPreparingPendingBundleSession ||
    isBundleDrawerLoading
  ) {
    return false;
  }

  if (latestExpectedBundleManifest?.bundleId) {
    return isExpectedBundleCartReady(
      getLatestCartSnapshot(),
      latestExpectedBundleManifest.bundleId,
      latestExpectedBundleManifest.items
    );
  }

  return true;
}

function isNativeDeliveryCheckoutReady(root = getNativeDeliveryRoot()) {
  return isNativeDeliveryReady(root) && isNativeDeliveryCheckoutCartSettled();
}

function isNativeDeliveryVisualStateReady(root = getNativeDeliveryRoot()) {
  const input = getNativeDeliveryInput(root);

  return isNativeDeliveryElement(root) && !root.hidden && isNativeDeliveryInput(input) && isLaidOutVisibleElement(input);
}

function scheduleNativeDeliveryCheckoutGuardReapply(root = getNativeDeliveryRoot()) {
  if (nativeDeliveryCheckoutGuardReapplyTimer) {
    return;
  }

  nativeDeliveryCheckoutGuardReapplyTimer = window.setTimeout(() => {
    nativeDeliveryCheckoutGuardReapplyTimer = 0;
    const deliveryRoot = isNativeDeliveryElement(root) ? root : getNativeDeliveryRoot();

    if (!isNativeDeliveryEnabled()) {
      setNativeDeliveryFlareCheckoutGuardIgnored(nativeDeliveryCheckoutGuardObserverTarget, false);
      return;
    }

    if (!isNativeDeliveryElement(deliveryRoot)) {
      return;
    }

    applyNativeDeliveryCheckoutGuard(deliveryRoot);
  }, 0);
}

function setNativeDeliveryFlareCheckoutGuardIgnored(checkoutButton, ignored) {
  if (!isNativeDeliveryButton(checkoutButton)) {
    return;
  }

  if (ignored) {
    if (checkoutButton.getAttribute(NATIVE_DELIVERY_FLARE_CHECKOUT_IGNORE_ATTR) !== 'true') {
      checkoutButton.setAttribute(NATIVE_DELIVERY_FLARE_CHECKOUT_IGNORE_ATTR, 'true');
    }
    return;
  }

  if (checkoutButton.hasAttribute(NATIVE_DELIVERY_FLARE_CHECKOUT_IGNORE_ATTR)) {
    checkoutButton.removeAttribute(NATIVE_DELIVERY_FLARE_CHECKOUT_IGNORE_ATTR);
  }
}

function setNativeDeliveryFlareCheckoutGuardIgnoredForCurrentSurfaces(ignored) {
  document
    .querySelectorAll('.cart-drawer--bundle-flow #checkout, .cart-page #checkout')
    .forEach((checkoutButton) => {
      setNativeDeliveryFlareCheckoutGuardIgnored(checkoutButton, ignored);
    });
}

function prepareNativeDeliveryCheckoutWithoutRootForCurrentSurfaces() {
  document
    .querySelectorAll('.cart-drawer--bundle-flow #checkout, .cart-page #checkout')
    .forEach((checkoutButton) => {
      if (
        isNativeDeliveryButton(checkoutButton) &&
        !isNativeDeliveryElement(getNativeDeliveryRootForElement(checkoutButton))
      ) {
        void prepareNativeDeliveryCheckoutWithoutRoot(checkoutButton);
      }
    });
}

function releaseNativeDeliveryCheckoutWithoutRoot(checkoutButton) {
  if (!isNativeDeliveryButton(checkoutButton)) {
    return false;
  }

  setNativeDeliveryFlareCheckoutGuardIgnored(checkoutButton, true);
  checkoutButton.dataset.bcDeliveryReady = 'true';
  delete checkoutButton.dataset.bcDeliveryGuardDisabled;
  checkoutButton.disabled = false;
  checkoutButton.removeAttribute('disabled');
  checkoutButton.removeAttribute('aria-disabled');
  checkoutButton.removeAttribute('aria-busy');
  if (checkoutButton.getAttribute('tabindex') === '-1') {
    checkoutButton.removeAttribute('tabindex');
  }
  checkoutButton.style.removeProperty('opacity');
  checkoutButton.style.removeProperty('pointer-events');
  checkoutButton.style.removeProperty('cursor');
  return true;
}

function blockNativeDeliveryCheckoutWithoutRoot(checkoutButton) {
  if (!isNativeDeliveryButton(checkoutButton)) {
    return false;
  }

  delete checkoutButton.dataset.bcDeliveryReady;
  delete checkoutButton.dataset.bcNativeDeliveryStaleCleared;
  delete checkoutButton.dataset.bcNativeDeliveryNoRootCartSignature;
  checkoutButton.disabled = true;
  checkoutButton.setAttribute('disabled', '');
  checkoutButton.setAttribute('aria-disabled', 'true');
  return true;
}

async function prepareNativeDeliveryCheckoutWithoutRoot(checkoutButton) {
  if (
    !isNativeDeliveryEnabled() ||
    !isNativeDeliveryButton(checkoutButton) ||
    isNativeDeliveryElement(getNativeDeliveryRootForElement(checkoutButton))
  ) {
    return false;
  }

  setNativeDeliveryFlareCheckoutGuardIgnored(checkoutButton, true);

  const existingPromise = nativeDeliveryNoRootPreparationPromises.get(checkoutButton);
  if (existingPromise) {
    return existingPromise;
  }

  const latestCart = getLatestCartSnapshot();
  const latestSignature = buildCartSnapshotSignature(latestCart);
  if (
    checkoutButton.dataset.bcNativeDeliveryStaleCleared === 'true' &&
    checkoutButton.dataset.bcNativeDeliveryNoRootCartSignature === latestSignature &&
    Array.isArray(latestCart?.items) &&
    !doesNativeDeliveryCartRequireDelivery(latestCart)
  ) {
    return releaseNativeDeliveryCheckoutWithoutRoot(checkoutButton);
  }

  const preparationPromise = (async () => {
    try {
      const cart = await fetchNativeDeliveryCartState();
      setLatestCartSnapshot(cart);

      if (doesNativeDeliveryCartRequireDelivery(cart)) {
        blockNativeDeliveryCheckoutWithoutRoot(checkoutButton);
        return false;
      }

      await clearNativeDeliveryCartAttributes();
      checkoutButton.dataset.bcNativeDeliveryStaleCleared = 'true';
      checkoutButton.dataset.bcNativeDeliveryNoRootCartSignature = buildCartSnapshotSignature(cart);
      clearNativeDeliveryFallbackCheckoutMessage(checkoutButton);
      return releaseNativeDeliveryCheckoutWithoutRoot(checkoutButton);
    } catch (error) {
      console.error('Native delivery no-delivery checkout preparation failed.', error);
      blockNativeDeliveryCheckoutWithoutRoot(checkoutButton);
      showNativeDeliveryFallbackCheckoutMessage(
        checkoutButton,
        'We could not confirm your delivery details. Please try again.'
      );
      return false;
    }
  })();

  nativeDeliveryNoRootPreparationPromises.set(checkoutButton, preparationPromise);
  void preparationPromise.finally(() => {
    if (nativeDeliveryNoRootPreparationPromises.get(checkoutButton) === preparationPromise) {
      nativeDeliveryNoRootPreparationPromises.delete(checkoutButton);
    }
  });
  return preparationPromise;
}

function shouldReapplyNativeDeliveryCheckoutGuard(checkoutButton) {
  const root = getNativeDeliveryRootForElement(checkoutButton);

  if (!isNativeDeliveryButton(checkoutButton) || !isNativeDeliveryElement(root)) {
    return false;
  }

  if (isNativeDeliveryCheckoutReady(root)) {
    return (
      checkoutButton.disabled ||
      checkoutButton.getAttribute('aria-disabled') !== null ||
      checkoutButton.dataset.bcDeliveryReady !== 'true' ||
      checkoutButton.dataset.bcDeliveryGuardDisabled === 'true'
    );
  }

  if (isNativeDeliveryAwaitingDetails(root)) {
    return (
      checkoutButton.disabled ||
      checkoutButton.getAttribute('aria-disabled') !== null ||
      checkoutButton.dataset.bcDeliveryReady === 'true' ||
      checkoutButton.dataset.bcDeliveryGuardDisabled === 'true'
    );
  }

  return (
    !checkoutButton.disabled ||
    checkoutButton.getAttribute('aria-disabled') !== 'true' ||
    checkoutButton.dataset.bcDeliveryReady === 'true' ||
    checkoutButton.dataset.bcDeliveryGuardDisabled !== 'true'
  );
}

function installNativeDeliveryCheckoutGuardObserver(checkoutButton) {
  if (
    !isNativeDeliveryEnabled() ||
    typeof MutationObserver !== 'function' ||
    !isNativeDeliveryButton(checkoutButton)
  ) {
    return;
  }

  if (nativeDeliveryCheckoutGuardObserver && nativeDeliveryCheckoutGuardObserverTarget === checkoutButton) {
    return;
  }

  nativeDeliveryCheckoutGuardObserver?.disconnect();
  nativeDeliveryCheckoutGuardObserverTarget = checkoutButton;
  nativeDeliveryCheckoutGuardObserver = new MutationObserver(() => {
    if (isNativeDeliveryEnabled() && shouldReapplyNativeDeliveryCheckoutGuard(checkoutButton)) {
      scheduleNativeDeliveryCheckoutGuardReapply(getNativeDeliveryRootForElement(checkoutButton));
    }
  });

  nativeDeliveryCheckoutGuardObserver.observe(checkoutButton, {
    attributes: true,
    attributeFilter: [
      'disabled',
      'aria-disabled',
      'data-bc-delivery-ready',
      'data-bc-delivery-guard-disabled',
    ],
  });
}

function applyNativeDeliveryCheckoutGuard(root = getNativeDeliveryRoot()) {
  const checkoutButton = getNativeDeliveryCheckoutButton(root);
  const checkoutScope = getNativeDeliveryCheckoutScope(root);

  if (
    !isNativeDeliveryEnabled() ||
    !isNativeDeliveryButton(checkoutButton) ||
    !isNativeDeliveryElement(checkoutScope)
  ) {
    return false;
  }

  bindNativeDeliveryCheckoutClickDelegate();
  setNativeDeliveryFlareCheckoutGuardIgnored(checkoutButton, true);
  installNativeDeliveryCheckoutGuardObserver(checkoutButton);

  if (isNativeDeliveryAwaitingDetails(root)) {
    // Wave 1: while details are missing the button stays tappable. The click delegate
    // (handleNativeDeliveryCheckoutSubmit) blocks checkout and points to the missing fields.
    if (checkoutScope.dataset.bcNativeDeliveryState !== 'incomplete') {
      checkoutScope.dataset.bcNativeDeliveryState = 'incomplete';
    }
    if (checkoutButton.disabled) {
      checkoutButton.disabled = false;
    }
    if (checkoutButton.getAttribute('aria-disabled') !== null) {
      checkoutButton.removeAttribute('aria-disabled');
    }
    checkoutButton.removeAttribute('aria-busy');
    delete checkoutButton.dataset.bcDeliveryReady;
    if (checkoutButton.dataset.bcDeliveryGuardDisabled === 'true') {
      delete checkoutButton.dataset.bcDeliveryGuardDisabled;
    }
    updateNativeDeliveryCheckoutHint(root, checkoutButton);
    return true;
  }

  updateNativeDeliveryCheckoutHint(root, checkoutButton, { hide: true });

  if (!isNativeDeliveryCheckoutReady(root)) {
    if (checkoutScope.dataset.bcNativeDeliveryState !== 'blocked') {
      checkoutScope.dataset.bcNativeDeliveryState = 'blocked';
    }
    if (!checkoutButton.disabled) {
      checkoutButton.disabled = true;
    }
    if (checkoutButton.getAttribute('aria-disabled') !== 'true') {
      checkoutButton.setAttribute('aria-disabled', 'true');
    }
    if (checkoutButton.dataset.bcDeliveryGuardDisabled !== 'true') {
      checkoutButton.dataset.bcDeliveryGuardDisabled = 'true';
    }
    if (isNativeDeliveryReady(root) && !isNativeDeliveryCheckoutCartSettled()) {
      checkoutButton.setAttribute('aria-busy', 'true');
    } else {
      checkoutButton.removeAttribute('aria-busy');
    }
    delete checkoutButton.dataset.bcDeliveryReady;
    return true;
  }

  if (checkoutScope.dataset.bcNativeDeliveryState !== 'ready') {
    checkoutScope.dataset.bcNativeDeliveryState = 'ready';
  }
  if (checkoutButton.dataset.bcDeliveryReady !== 'true') {
    checkoutButton.dataset.bcDeliveryReady = 'true';
  }
  checkoutButton.removeAttribute('aria-busy');
  checkoutButton.style.removeProperty('opacity');
  checkoutButton.style.removeProperty('pointer-events');
  checkoutButton.style.removeProperty('cursor');
  if (checkoutButton.getAttribute('aria-disabled') !== null) {
    checkoutButton.removeAttribute('aria-disabled');
  }
  if (checkoutButton.disabled) {
    checkoutButton.disabled = false;
  }

  if (checkoutButton.getAttribute('tabindex') === '-1') {
    checkoutButton.removeAttribute('tabindex');
  }

  if (checkoutButton.dataset.bcDeliveryGuardDisabled === 'true') {
    delete checkoutButton.dataset.bcDeliveryGuardDisabled;
  }

  return false;
}

function isNativeDeliveryAwaitingDetails(root = getNativeDeliveryRoot()) {
  return !isNativeDeliveryReady(root) && isNativeDeliveryCheckoutCartSettled();
}

function getNativeDeliveryMissingDetails(root = getNativeDeliveryRoot()) {
  const missing = [];
  const emailInput = getNativeDeliveryEmailInput(root);
  const postcodeInput = getNativeDeliveryInput(root);

  if (!getNativeDeliveryEmailValue(root)) {
    missing.push({
      key: 'email',
      label: String(emailInput?.value || '').trim() ? 'a valid email' : 'your email',
      field: emailInput,
    });
  }

  if (!getNativeDeliveryState(root)) {
    const postcodeResult = getNativeDeliveryPostcodeResult(postcodeInput?.value);
    if (postcodeResult.allowed) {
      missing.push({
        key: 'date',
        label: 'a delivery date',
        field: Array.from(root?.querySelectorAll('[data-bc-native-delivery-date]:not([disabled])') || [])
          .find((button) => button.getClientRects().length > 0) || postcodeInput,
      });
    } else {
      missing.push({
        key: 'postcode',
        label: postcodeResult.reason === 'empty' ? 'your postcode' : 'a postcode we deliver to',
        field: postcodeInput,
        result: postcodeResult,
      });
    }
  }

  if (!getNativeDeliveryInstructionsValue(root)) {
    missing.push({
      key: 'instructions',
      label: 'delivery instructions',
      field: getNativeDeliveryInstructionsInput(root),
    });
  }

  return missing;
}

function formatNativeDeliveryMissingDetails(missing) {
  const labels = missing.map((item, index) => (
    index > 0 && item.label.startsWith('your ') && missing[index - 1].label.startsWith('your ')
      ? item.label.slice(5)
      : item.label
  ));

  if (labels.length <= 1) {
    return labels.join('');
  }

  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

function updateNativeDeliveryCheckoutHint(root, checkoutButton, { hide = false, tone = '' } = {}) {
  if (!isNativeDeliveryButton(checkoutButton) || !(checkoutButton.parentElement instanceof HTMLElement)) {
    return;
  }

  const hintId = `bc-native-delivery-checkout-hint-${root?.dataset?.bcNativeDeliveryContext || 'cart'}`;
  let hint = checkoutButton.parentElement.querySelector('[data-bc-native-delivery-checkout-hint]');
  const missing = hide ? [] : getNativeDeliveryMissingDetails(root);

  if (!missing.length) {
    if (isNativeDeliveryElement(hint)) {
      hint.hidden = true;
      hint.textContent = '';
      delete hint.dataset.tone;
    }
    const describedBy = String(checkoutButton.getAttribute('aria-describedby') || '')
      .split(/\s+/)
      .filter((id) => id && id !== hintId);
    if (describedBy.length) {
      checkoutButton.setAttribute('aria-describedby', describedBy.join(' '));
    } else {
      checkoutButton.removeAttribute('aria-describedby');
    }
    return;
  }

  if (!isNativeDeliveryElement(hint)) {
    hint = document.createElement('p');
    hint.id = hintId;
    hint.className = 'bc-native-delivery__checkout-hint';
    hint.dataset.bcNativeDeliveryCheckoutHint = 'true';
    hint.setAttribute('aria-live', 'polite');
    checkoutButton.insertAdjacentElement('beforebegin', hint);
  }

  const text = `To continue, add ${formatNativeDeliveryMissingDetails(missing)}.`;
  if (hint.textContent !== text) {
    hint.textContent = text;
  }
  hint.hidden = false;
  if (tone) {
    // An error tone set by a blocked tap stays until every detail is filled in.
    hint.dataset.tone = tone;
  }

  const describedBy = new Set(String(checkoutButton.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
  describedBy.add(hint.id);
  checkoutButton.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
}

function guideNativeDeliveryMissingDetails(root, checkoutButton) {
  const missing = getNativeDeliveryMissingDetails(root);

  if (!missing.length) {
    return false;
  }

  missing.forEach((item) => {
    // Mark fields as touched so the inline messages survive later re-validation (UI re-renders).
    if (item.key === 'email') {
      if (isNativeDeliveryInput(item.field)) {
        item.field.dataset.bcNativeDeliveryEmailTouched = 'true';
      }
      validateNativeDeliveryEmail(root, { showError: true });
    } else if (item.key === 'postcode') {
      if (isNativeDeliveryInput(item.field)) {
        item.field.setAttribute('aria-invalid', 'true');
      }
      if (item.result?.reason === 'empty') {
        setNativeDeliveryMessage(root, 'Please enter your delivery postcode.', 'error');
      } else if (item.result?.reason === 'unsupported-zone') {
        setNativeDeliveryMessage(root, item.result.message, 'unsupported');
      } else {
        setNativeDeliveryMessage(root, item.result?.message || 'Enter a valid UK postcode.', 'error');
      }
    } else if (item.key === 'date') {
      setNativeDeliveryMessage(root, 'Choose an available delivery date before checkout.', 'error');
    } else if (item.key === 'instructions') {
      if (isNativeDeliveryTextarea(item.field)) {
        item.field.dataset.bcNativeDeliveryInstructionsTouched = 'true';
      }
      validateNativeDeliveryInstructions(root, { showError: true });
    }
  });

  updateNativeDeliveryCheckoutHint(root, checkoutButton, { tone: 'error' });

  const firstField = missing.find((item) => isNativeDeliveryElement(item.field))?.field;
  if (isNativeDeliveryElement(firstField)) {
    try {
      firstField.focus({ preventScroll: true });
    } catch (error) {
      firstField.focus?.();
    }
    firstField.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }

  return true;
}

function bindNativeDeliveryCheckoutClickDelegate() {
  if (document.documentElement.dataset.bcNativeDeliveryCheckoutClickBound === 'true') {
    return;
  }

  window.addEventListener('click', (event) => {
    if (!isNativeDeliveryEnabled()) {
      return;
    }

    const target = event.target;
    const checkoutButton = isNativeDeliveryElement(target) && typeof target.closest === 'function'
      ? target.closest('#checkout')
      : null;
    const root = getNativeDeliveryRootForElement(checkoutButton);

    if (!isNativeDeliveryButton(checkoutButton) || !isNativeDeliveryElement(root)) {
      return;
    }

    handleNativeDeliveryCheckoutSubmit(event, root, checkoutButton);
  }, true);
  document.documentElement.dataset.bcNativeDeliveryCheckoutClickBound = 'true';
}

function handleNativeDeliveryCheckoutSubmit(
  event,
  root = getNativeDeliveryRoot(),
  checkoutButton = getNativeDeliveryCheckoutButton(root)
) {
  if (!isNativeDeliveryEnabled()) {
    return false;
  }

  if (!isNativeDeliveryElement(root) || !isNativeDeliveryButton(checkoutButton)) {
    return false;
  }

  const state = getNativeDeliveryState(root);
  const emailComplete = Boolean(getNativeDeliveryEmailValue(root));
  const instructionsComplete = Boolean(getNativeDeliveryInstructionsValue(root));

  if (!emailComplete || !state || !instructionsComplete) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    guideNativeDeliveryMissingDetails(root, checkoutButton);
    applyNativeDeliveryCheckoutGuard(root);
    return true;
  }

  if (!isNativeDeliveryCheckoutCartSettled()) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    applyNativeDeliveryCheckoutGuard(root);
    return true;
  }

  syncNativeDeliveryAttributeInputs(root);

  if (hasNativeDeliveryCheckoutPersistedState(checkoutButton, state)) {
    if (event?.type === 'click') {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      void submitNativeDeliveryCheckoutAfterPersist(
        getNativeDeliveryCheckoutForm(root),
        checkoutButton
      );
      return true;
    }

    return false;
  }

  if (event?.type === 'click' || event?.type === 'submit') {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    void submitNativeDeliveryCheckoutForm(root, checkoutButton);
    return true;
  }

  return false;
}

function handleNativeDeliveryCheckoutWithoutRoot(event, checkoutButton) {
  if (!isNativeDeliveryEnabled() || !isNativeDeliveryButton(checkoutButton)) {
    return false;
  }

  setNativeDeliveryFlareCheckoutGuardIgnored(checkoutButton, true);

  if (isNativeDeliveryElement(getNativeDeliveryRootForElement(checkoutButton))) {
    return false;
  }

  if (checkoutButton.dataset.bcNativeDeliveryStaleCleared === 'true') {
    delete checkoutButton.dataset.bcNativeDeliveryStaleCleared;
    return false;
  }

  if (event?.type === 'click' || event?.type === 'submit') {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    void submitNativeDeliveryCheckoutWithoutRoot(checkoutButton);
    return true;
  }

  return false;
}

async function submitNativeDeliveryCheckoutForm(
  root = getNativeDeliveryRoot(),
  checkoutButton = getNativeDeliveryCheckoutButton(root)
) {
  const form = getNativeDeliveryCheckoutForm(root);
  const state = getNativeDeliveryState(root);

  if (
    !(form instanceof HTMLFormElement) ||
    !isNativeDeliveryButton(checkoutButton) ||
    !getNativeDeliveryEmailValue(root) ||
    !state ||
    !getNativeDeliveryInstructionsValue(root) ||
    !isNativeDeliveryCheckoutCartSettled()
  ) {
    applyNativeDeliveryCheckoutGuard(root);
    return false;
  }

  checkoutButton.disabled = true;
  checkoutButton.setAttribute('aria-disabled', 'true');
  checkoutButton.setAttribute('aria-busy', 'true');

  try {
    nativeDeliveryDeferredPersistRoots.delete(root);
    window.clearTimeout(nativeDeliveryPersistTimers.get(root));
    nativeDeliveryPersistTimers.delete(root);
    await queueNativeDeliveryCartAttributePersist(root, state);
  } catch (error) {
    console.error('Native delivery details could not be saved before checkout.', error);
    checkoutButton.removeAttribute('aria-busy');
    setNativeDeliveryMessage(root, 'We could not save your delivery details. Please try again.', 'error');
    applyNativeDeliveryCheckoutGuard(root);
    return false;
  }

  checkoutButton.dataset.bcNativeDeliveryPersisted = getNativeDeliveryStateSignature(state);
  checkoutButton.removeAttribute('aria-busy');
  applyNativeDeliveryCheckoutGuard(root);

  try {
    submitNativeDeliveryCheckoutAfterPersist(form, checkoutButton);
  } catch (error) {
    console.error('Native delivery checkout handoff failed.', error);
    delete checkoutButton.dataset.bcNativeDeliveryPersisted;
    setNativeDeliveryMessage(root, 'We could not continue to checkout. Please try again.', 'error');
    applyNativeDeliveryCheckoutGuard(root);
    return false;
  }

  return true;
}

function submitNativeDeliveryCheckoutAfterPersist(form, checkoutButton) {
  if (!(form instanceof HTMLFormElement) || !isNativeDeliveryButton(checkoutButton)) {
    return false;
  }

  const drawerDialog = getBundleDrawer()?.querySelector('.cart-drawer__dialog[open]');

  if (drawerDialog instanceof HTMLDialogElement) {
    if (!checkoutGiftFinalizationPromise) {
      const generation = checkoutLifecycleGeneration;
      setCheckoutGiftFinalizationBusy(true);
      const finalizationPromise = finalizeGiftStateBeforeCheckout(generation, {
        nativeFinalSubmit: true,
      });
      checkoutGiftFinalizationPromise = finalizationPromise;
      void finalizationPromise.finally(() => {
        if (checkoutGiftFinalizationPromise === finalizationPromise) {
          checkoutGiftFinalizationPromise = null;
        }
      });
    }

    return true;
  }

  prepareBundleCheckoutProceeding();
  submitNativeDeliveryFinalForm(form, checkoutButton);
  return true;
}

async function submitNativeDeliveryCheckoutWithoutRoot(checkoutButton) {
  const form = checkoutButton?.form || document.getElementById('cart-form');

  if (!(form instanceof HTMLFormElement) || !isNativeDeliveryButton(checkoutButton)) {
    return false;
  }

  checkoutButton.disabled = true;
  checkoutButton.setAttribute('aria-disabled', 'true');
  checkoutButton.setAttribute('aria-busy', 'true');
  clearNativeDeliveryFallbackCheckoutMessage(checkoutButton);

  try {
    const cart = await fetchNativeDeliveryCartState();
    setLatestCartSnapshot(cart);

    if (doesNativeDeliveryCartRequireDelivery(cart)) {
      throw new Error('Native delivery root was missing for a shippable cart.');
    }

    await clearNativeDeliveryCartAttributes();
  } catch (error) {
    console.error('Native delivery stale details could not be cleared before checkout.', error);
    checkoutButton.disabled = false;
    checkoutButton.removeAttribute('aria-disabled');
    checkoutButton.removeAttribute('aria-busy');
    showNativeDeliveryFallbackCheckoutMessage(
      checkoutButton,
      'We could not confirm your delivery details. Please try again.'
    );
    return false;
  }

  checkoutButton.dataset.bcNativeDeliveryStaleCleared = 'true';
  checkoutButton.disabled = false;
  checkoutButton.removeAttribute('aria-disabled');
  checkoutButton.removeAttribute('aria-busy');

  try {
    form.requestSubmit(checkoutButton);
  } catch (error) {
    console.error('Native delivery stale-details checkout resubmit failed.', error);
    delete checkoutButton.dataset.bcNativeDeliveryStaleCleared;
    showNativeDeliveryFallbackCheckoutMessage(
      checkoutButton,
      'We could not continue to checkout. Please try again.'
    );
    return false;
  }

  return true;
}

async function persistNativeDeliveryCartAttributes(state) {
  const shippingProfile = `${state.day} Profile`;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), NATIVE_DELIVERY_CART_UPDATE_TIMEOUT_MS);

  try {
    const response = await getCartMutationFetch()(`${window.Shopify?.routes?.root ?? '/'}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        attributes: {
          [NATIVE_DELIVERY_ATTRIBUTE_NAMES.postcode]: state.postcode,
          [NATIVE_DELIVERY_ATTRIBUTE_NAMES.date]: state.date,
          [NATIVE_DELIVERY_ATTRIBUTE_NAMES.day]: state.day,
          [NATIVE_DELIVERY_ATTRIBUTE_NAMES.sector]: state.sector,
          [NATIVE_DELIVERY_ATTRIBUTE_NAMES.flareDate]: state.date,
          [NATIVE_DELIVERY_ATTRIBUTE_NAMES.flareProfile]: shippingProfile,
          [NATIVE_DELIVERY_ATTRIBUTE_NAMES.flareProfileName]: shippingProfile,
        },
      }),
      keepalive: true,
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Native delivery cart update failed with status ${response.status}`);
    }
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function queueNativeDeliveryCartAttributePersist(root, state) {
  const stateSignature = getNativeDeliveryStateSignature(state);
  const previous = nativeDeliveryPersistChains.get(root) || Promise.resolve();
  const current = previous
    .catch(() => {})
    .then(async () => {
      if (root?.dataset.bcNativeDeliveryPersisted === stateSignature) {
        return;
      }

      await persistNativeDeliveryCartAttributes(state);
      if (isNativeDeliveryElement(root)) {
        root.dataset.bcNativeDeliveryPersisted = stateSignature;
      }

      const checkoutButton = getNativeDeliveryCheckoutButton(root);
      if (isNativeDeliveryButton(checkoutButton)) {
        checkoutButton.dataset.bcNativeDeliveryPersisted = stateSignature;
      }
    });

  nativeDeliveryPersistChains.set(root, current);
  current.then(
    () => {
      if (nativeDeliveryPersistChains.get(root) === current) {
        nativeDeliveryPersistChains.delete(root);
      }
    },
    () => {
      if (nativeDeliveryPersistChains.get(root) === current) {
        nativeDeliveryPersistChains.delete(root);
      }
    }
  );
  return current;
}

async function fetchNativeDeliveryCartState() {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), NATIVE_DELIVERY_STALE_CLEAR_TIMEOUT_MS);

  try {
    const response = await getCartMutationFetch()(`${window.Shopify?.routes?.root ?? '/'}cart.js`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Native delivery cart fetch failed with status ${response.status}`);
    }

    return await response.json();
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function doesNativeDeliveryCartRequireDelivery(cart) {
  if (!Array.isArray(cart?.items)) {
    return true;
  }

  return cart.items.some((item) => item?.requires_shipping !== false && item?.gift_card !== true);
}

function shouldBypassBundleCheckoutGiftGateForNativeNoDeliveryCart(checkoutButton) {
  if (!isNativeDeliveryEnabled() || !isNativeDeliveryButton(checkoutButton)) {
    return false;
  }

  if (isNativeDeliveryElement(getNativeDeliveryRootForElement(checkoutButton))) {
    return false;
  }

  const cart = getLatestCartSnapshot();

  if (!Array.isArray(cart?.items) || Number(cart?.item_count) <= 0) {
    return false;
  }

  if (doesNativeDeliveryCartRequireDelivery(cart)) {
    return false;
  }

  return getTrackedBundleGroupIds(cart).size === 0;
}

async function clearNativeDeliveryCartAttributes() {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), NATIVE_DELIVERY_STALE_CLEAR_TIMEOUT_MS);

  try {
    const response = await getCartMutationFetch()(`${window.Shopify?.routes?.root ?? '/'}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ attributes: nativeDeliveryEmptyAttributes }),
      keepalive: true,
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Native delivery stale attribute clear failed with status ${response.status}`);
    }
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function clearNativeDeliveryFallbackCheckoutMessage(checkoutButton) {
  const parent = checkoutButton?.parentElement;
  parent?.querySelector('[data-bc-native-delivery-fallback-error]')?.remove();
}

function showNativeDeliveryFallbackCheckoutMessage(checkoutButton, messageText) {
  const parent = checkoutButton?.parentElement;

  if (!(parent instanceof HTMLElement)) {
    return;
  }

  let message = parent.querySelector('[data-bc-native-delivery-fallback-error]');

  if (!(message instanceof HTMLElement)) {
    message = document.createElement('p');
    message.dataset.bcNativeDeliveryFallbackError = 'true';
    message.setAttribute('role', 'alert');
    message.style.margin = '8px 0 0';
    message.style.color = '#a53a2d';
    message.style.fontSize = '14px';
    message.style.lineHeight = '1.35';
    message.style.textAlign = 'center';
    checkoutButton.insertAdjacentElement('afterend', message);
  }

  message.textContent = messageText;
}

function scheduleNativeDeliveryCartAttributePersist(root = getNativeDeliveryRoot()) {
  const state = getNativeDeliveryState(root);

  if (!isNativeDeliveryElement(root) || !state) {
    return;
  }

  window.clearTimeout(nativeDeliveryPersistTimers.get(root));
  nativeDeliveryPersistTimers.set(root, window.setTimeout(() => {
    nativeDeliveryPersistTimers.delete(root);
    if (!isNativeDeliveryCheckoutCartSettled()) {
      nativeDeliveryDeferredPersistRoots.add(root);
      return;
    }
    void queueNativeDeliveryCartAttributePersist(root, state).catch((error) => {
      console.warn('Native delivery details will be saved again at checkout.', error);
    });
  }, 100));
}

function flushDeferredNativeDeliveryCartAttributePersist(root = getNativeDeliveryRoot()) {
  if (
    !isNativeDeliveryElement(root) ||
    !nativeDeliveryDeferredPersistRoots.has(root) ||
    !isNativeDeliveryCheckoutCartSettled()
  ) {
    return false;
  }

  nativeDeliveryDeferredPersistRoots.delete(root);
  scheduleNativeDeliveryCartAttributePersist(root);
  return true;
}

function getNativeDeliveryStateSignature(state) {
  return JSON.stringify({
    postcode: state?.postcode || '',
    date: state?.date || '',
    day: state?.day || '',
    sector: state?.sector || '',
  });
}

function hasNativeDeliveryCheckoutPersistedState(checkoutButton, state) {
  const stateSignature = getNativeDeliveryStateSignature(state);
  const root = getNativeDeliveryRootForElement(checkoutButton);
  return Boolean(
    isNativeDeliveryButton(checkoutButton) &&
    (
      checkoutButton.dataset.bcNativeDeliveryPersisted === stateSignature ||
      root?.dataset.bcNativeDeliveryPersisted === stateSignature
    )
  );
}

function invalidateNativeDeliveryCheckoutPersistence(root = getNativeDeliveryRoot()) {
  const checkoutButton = getNativeDeliveryCheckoutButton(root);

  if (isNativeDeliveryElement(root)) {
    delete root.dataset.bcNativeDeliveryPersisted;
  }

  if (isNativeDeliveryButton(checkoutButton)) {
    delete checkoutButton.dataset.bcNativeDeliveryPersisted;
  }
}

function getOrCreateNativeDeliveryCheckoutInput(form) {
  let input = form.querySelector('input[data-bc-native-delivery-checkout-submit]');

  if (input instanceof HTMLInputElement) {
    return input;
  }

  input = document.createElement('input');
  input.type = 'hidden';
  input.name = 'checkout';
  input.dataset.bcNativeDeliveryCheckoutSubmit = 'true';
  form.appendChild(input);
  return input;
}

function buildNativeDeliveryStorefrontCartId(token) {
  const value = String(token || '').trim();
  if (!value) {
    throw new Error('Shopify cart token is unavailable.');
  }
  return value.startsWith('gid://shopify/Cart/') ? value : `gid://shopify/Cart/${value}`;
}

async function syncNativeDeliveryBuyerIdentity(email, postcode) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    NATIVE_DELIVERY_BUYER_IDENTITY_TIMEOUT_MS
  );

  try {
    const cartResponse = await window.fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    if (!cartResponse.ok) {
      throw new Error(`Shopify cart snapshot failed with status ${cartResponse.status}`);
    }

    const cart = await cartResponse.json();
    const response = await window.fetch('/api/2026-07/graphql.json', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        query: 'mutation NativeDeliveryBuyerIdentity($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) { cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) { cart { checkoutUrl } userErrors { field message } } }',
        variables: {
          cartId: buildNativeDeliveryStorefrontCartId(cart?.token),
          buyerIdentity: {
            email,
            countryCode: 'GB',
            deliveryAddressPreferences: [
              {
                deliveryAddress: {
                  country: 'United Kingdom',
                  zip: postcode,
                },
                deliveryAddressValidationStrategy: 'COUNTRY_CODE_ONLY',
                oneTimeUse: true,
              },
            ],
          },
        },
      }),
      signal: controller.signal,
    });
    const payload = await response.json().catch(() => ({}));
    const result = payload?.data?.cartBuyerIdentityUpdate;
    const errors = [...(payload?.errors || []), ...(result?.userErrors || [])];
    if (!response.ok || errors.length || !result?.cart?.checkoutUrl) {
      throw new Error(errors[0]?.message || 'Shopify did not accept the buyer identity.');
    }
    return true;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function identifyNativeDeliveryEmailWithKlaviyo(email) {
  if (!isValidNativeDeliveryEmail(email)) {
    return false;
  }

  const profile = { email: normalizeNativeDeliveryEmail(email) };
  if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
    try {
      const result = window.klaviyo.identify(profile);
      if (result && typeof result.catch === 'function') {
        void result.catch(() => {});
      }
      return true;
    } catch (error) {
      return false;
    }
  }

  window._learnq = window._learnq || [];
  window._learnq.push(['identify', { $email: profile.email }]);
  return true;
}

async function subscribeNativeDeliveryEmailToNewsletter(email) {
  if (!isValidNativeDeliveryEmail(email)) {
    return false;
  }

  const normalizedEmail = normalizeNativeDeliveryEmail(email);
  const url = `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(NATIVE_DELIVERY_KLAVIYO_PUBLIC_KEY)}`;
  const payload = {
    data: {
      type: 'subscription',
      attributes: {
        custom_source: 'Bundle builder delivery setup',
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email: normalizedEmail,
              subscriptions: {
                email: {
                  marketing: {
                    consent: 'SUBSCRIBED',
                  },
                },
              },
            },
          },
        },
      },
      relationships: {
        list: {
          data: {
            type: 'list',
            id: NATIVE_DELIVERY_KLAVIYO_NEWSLETTER_LIST_ID,
          },
        },
      },
    },
  };

  const response = await window.fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/vnd.api+json',
      'Content-Type': 'application/vnd.api+json',
      revision: NATIVE_DELIVERY_KLAVIYO_REVISION,
    },
    body: JSON.stringify(payload),
    keepalive: true,
  });

  if (!response.ok || response.status !== 202) {
    throw new Error('Klaviyo did not accept the newsletter subscription.');
  }

  return true;
}

function submitNativeDeliveryFinalForm(form, checkoutButton) {
  if (!(form instanceof HTMLFormElement) || !isNativeDeliveryButton(checkoutButton)) {
    return false;
  }

  const root = getNativeDeliveryRootForElement(checkoutButton) || getNativeDeliveryRoot();
  const email = getNativeDeliveryEmailValue(root);
  const postcode = getNativeDeliveryState(root)?.postcode || '';
  if (!email) {
    validateNativeDeliveryEmail(root, { showError: true, focus: true });
    return false;
  }

  if (nativeDeliveryFinalSubmitInFlight) {
    return true;
  }

  nativeDeliveryFinalSubmitInFlight = true;
  checkoutButton.disabled = true;
  checkoutButton.setAttribute('aria-disabled', 'true');
  checkoutButton.setAttribute('aria-busy', 'true');
  setNativeDeliveryCheckoutProgress(checkoutButton, true);
  identifyNativeDeliveryEmailWithKlaviyo(email);
  void subscribeNativeDeliveryEmailToNewsletter(email).catch(() => false);

  void (async () => {
    let finalButton = checkoutButton;
    try {
      await syncNativeDeliveryBuyerIdentity(email, postcode).catch(() => false);
      const activeForm = document.getElementById('cart-form');
      const finalForm = activeForm instanceof HTMLFormElement && activeForm.isConnected ? activeForm : form;
      const activeButton = getBundleCheckoutButton();
      finalButton = isNativeDeliveryButton(activeButton) ? activeButton : checkoutButton;
      const checkoutInput = getOrCreateNativeDeliveryCheckoutInput(finalForm);
      checkoutInput.disabled = false;
      checkoutInput.value = 'Checkout';
      HTMLFormElement.prototype.submit.call(finalForm);
    } catch (error) {
      nativeDeliveryFinalSubmitInFlight = false;
      finalButton.disabled = false;
      finalButton.removeAttribute('aria-disabled');
      finalButton.removeAttribute('aria-busy');
      setNativeDeliveryCheckoutProgress(finalButton, false);
      delete finalButton.dataset.bcNativeDeliveryPersisted;
      console.error('Native delivery final checkout submit failed.', error);
    }
  })();

  return true;
}


function enhanceBundleDeliveryUi() {
  const nativeDeliveryEnabled = isNativeDeliveryEnabled();
  setNativeDeliveryFlareCheckoutGuardIgnoredForCurrentSurfaces(nativeDeliveryEnabled);

  if (nativeDeliveryEnabled) {
    enhanceNativeDeliveryUi();
    return;
  }

  syncFlarePostcodePlaceholder();

  const flareBlock = getFlareBlock();

  if (!flareBlock) return;

  applyNativeFlareMinimumSetup(flareBlock);
  installFlareCalendarPortalHandlers(flareBlock);
  installFlareCalendarStateObserver(flareBlock);

  if (isFlareVisualStateReady(flareBlock) && !isBundleCheckoutBlockedForVisiblePrefill(flareBlock)) {
    hideBundleDrawerLoadingState();
  }
}

function applyNativeFlareMinimumSetup(flareBlock) {
  if (!(flareBlock instanceof HTMLElement)) {
    return;
  }

  removeLegacyBundleFlarePresentation(flareBlock);

  const postcodeInput = flareBlock.querySelector('#delivery-zip-code');
  const shipDateInput = flareBlock.querySelector('#ship-date');
  const isCountryReady = selectUnitedKingdom(flareBlock, {
    postcodeInput,
    shipDateInput,
  });

  applyPersistedFlareFieldPrefills(flareBlock, { allowPostcode: isCountryReady });
  syncFlareFieldValueStates(flareBlock);
}

function removeLegacyBundleFlarePresentation(flareBlock) {
  flareBlock.querySelectorAll('.bc-bundle-step-label').forEach((label) => {
    if (!(label instanceof HTMLElement)) return;

    const originalText = label.dataset.bcOriginalText || label.querySelector('.bc-step-label-text')?.textContent || '';
    label.classList.remove('bc-bundle-step-label');
    delete label.dataset.bcOriginalText;
    delete label.dataset.bcStepNumber;

    if (originalText) {
      label.textContent = originalText;
    }
  });

  flareBlock.querySelectorAll('.bc-flare-delivery-heading-hidden').forEach((element) => {
    if (element instanceof HTMLElement) {
      element.classList.remove('bc-flare-delivery-heading-hidden');
    }
  });

  flareBlock.querySelectorAll('.bc-bundle-step-section').forEach((section) => {
    if (!(section instanceof HTMLElement) || section === flareBlock) return;

    while (section.firstChild) {
      section.parentNode?.insertBefore(section.firstChild, section);
    }

    section.remove();
  });
}

// Phase 2 split drawer: Flare appends #flare-date-picker-block to .cart-drawer__summary
// (the stable, never-re-rendered form region). Relocate the block as a whole unit into
// the deterministic anchor at the top of that region so the postcode/date steps (1, 2)
// sit ABOVE the delivery-instructions field (step 3). We move only the Flare container —
// never its internal field nodes — so Flare's own init/state is untouched, and the move
// is idempotent (no-op once the block already lives in the anchor), so a Flare re-append
// is silently corrected on the next enhancement tick.
function placeFlareBlockInForm(flareBlock = getFlareBlock()) {
  if (!(flareBlock instanceof HTMLElement)) {
    return;
  }

  const drawer = getBundleDrawer();
  const anchor = drawer?.querySelector('#bc-bundle-flare-anchor');

  if (!(anchor instanceof HTMLElement)) {
    return;
  }

  // Already the anchor's (only) child — nothing to do.
  if (flareBlock.parentElement === anchor) {
    return;
  }

  anchor.appendChild(flareBlock);
}

function applyBundleFlarePresentation(flareBlock, bundleDrawer = getBundleDrawer()) {
  if (!(flareBlock instanceof HTMLElement)) {
    return;
  }

  applyNativeFlareMinimumSetup(flareBlock);
  return;

  const postcodeInput = flareBlock.querySelector('#delivery-zip-code');
  const shipDateInput = flareBlock.querySelector('#ship-date');
  const timeSlotContainer = flareBlock.querySelector('#time-slot-container');
  const timeSlotVisible = isElementVisible(timeSlotContainer);
  const safePlaceStep = bundleDrawer?.querySelector('.cart-note--bundle-flow .cart-note__step');

  hideFlareDeliveryHeading(flareBlock);
  decorateStepLabel(flareBlock.querySelector('.zip-code-label'), '1');
  decorateStepLabel(flareBlock.querySelector('.date-selector-label'), '2');

  syncFlarePostcodePlaceholder(flareBlock);
  syncFlareFieldValueStates(flareBlock);

  if (timeSlotVisible) {
    decorateStepLabel(timeSlotContainer?.querySelector('.date-selector-label'), '3');
    if (safePlaceStep) safePlaceStep.textContent = '4';
  } else if (safePlaceStep) {
    safePlaceStep.textContent = '3';
  }

  normalizeBundleStepSections(flareBlock, bundleDrawer);

  const isCountryReady = selectUnitedKingdom(flareBlock, {
    postcodeInput,
    shipDateInput,
  });

  applyPersistedFlareFieldPrefills(flareBlock, { allowPostcode: isCountryReady });
  syncFlareFieldValueStates(flareBlock);
}

function hideFlareDeliveryHeading(flareBlock) {
  if (!(flareBlock instanceof HTMLElement)) {
    return;
  }

  Array.from(flareBlock.children).slice(0, 4).forEach((child) => {
    if (!(child instanceof HTMLElement) || child.classList.contains('bc-bundle-step-section')) {
      return;
    }

    const text = normalizeVisibleBundleText(child.textContent ?? '');

    if (/^delivery$/i.test(text)) {
      child.classList.add('bc-flare-delivery-heading-hidden');
      child.setAttribute('aria-hidden', 'true');
    }
  });
}

function installRechargeAddToCartHook() {
  const rechargeBundle = getRechargeBundle();

  if (rechargeBundle.onAddToCart?.__bcBundleCartFlowHook === true) {
    return;
  }

  const existingHandler =
    typeof rechargeBundle.onAddToCart === 'function' ? rechargeBundle.onAddToCart.bind(rechargeBundle) : null;

  rechargeBundle.onAddToCart = (...args) => {
    if (hasActivePendingBundleSession()) {
      if (!isBundleDirectCheckoutPending) {
        scheduleDrawerOpen({ force: true });
      }

      return undefined;
    }

    let result;
    let handlerInvoked = false;
    const hadPrimedPendingSession = hasPrimedPendingBundleSession();

    try {
      isProceedingToCheckout = false;
      window.clearTimeout(checkoutIntentResetTimer);

      // ReCharge can invoke this hook after its /cart/add.js has already landed, so the
      // latest snapshot may include the just-added bundle. Never derive removal updates
      // here — stale-group cleanup happens in canonicalizeTrackedBundleCartAfterAdd,
      // which excludes the newly added group token and so can never delete it.
      if (!hadPrimedPendingSession) {
        const currentCart = getLatestCartSnapshot();
        const cartWithoutTrackedBundle = buildTrackedBundleRemovedCartSnapshot(currentCart) ?? currentCart;
        beginPendingBundleSession({ beforeCartSnapshot: cartWithoutTrackedBundle });
      }

      markPendingBundleSessionActive();
      pendingBundleStateSnapshot = captureRechargeBundleState();
      persistBundleStateSnapshot(pendingBundleStateSnapshot);
      captureAndPersistHybridBundleDraft('add-to-cart', {
        force: true,
        rawSnapshot: pendingBundleStateSnapshot,
        ttlMs: BUNDLE_HYBRID_CHECKOUT_DRAFT_TTL_MS,
      });
      result = existingHandler?.(...args);
      handlerInvoked = true;
    } catch (error) {
      pendingBundleStateSnapshot = null;
      console.error('Bundle state preservation failed during add to cart.', error);

      if (!handlerInvoked) {
        result = existingHandler?.(...args);
      }
    }

    return result;
  };

  rechargeBundle.onAddToCart.__bcBundleCartFlowHook = true;
}

async function refreshBundleDrawer(cartSnapshot = null) {
  const sectionId = getBundleDrawerSectionId();

  if (!sectionId) return false;

  const restoreNativeFlareAppBlockScriptReplay = suppressNativeFlareAppBlockScriptReplay();

  try {
    disconnectFlareCalendarStateObserver();
    await sectionRenderer.renderSection(sectionId, { cache: false });
    lastRenderedDrawerCartSignature = buildCartSnapshotSignature(getLatestCartSnapshot());
    applyBundleDrawerLoadingState();
    if (Array.isArray(cartSnapshot?.items)) {
      applyVerifiedCartSnapshotToBundleDrawer(cartSnapshot);
    }
    bindBundleDrawerCloseHandler();
    dispatchSectionLoad(sectionId);
    return true;
  } catch (error) {
    console.error('Failed to refresh the bundle cart drawer section.', error);
    return false;
  } finally {
    restoreNativeFlareAppBlockScriptReplay();
  }
}

function dispatchSectionLoad(sectionId) {
  const sectionElement = document.getElementById(`shopify-section-${sectionId}`);

  if (!(sectionElement instanceof HTMLElement)) return;

  sectionElement.dispatchEvent(
    new CustomEvent('shopify:section:load', {
      bubbles: true,
      detail: {
        sectionId,
      },
    })
  );
}

function installFetchListener() {
  if (window[FETCH_LISTENER_INSTALL_FLAG]) {
    return;
  }

  cartFlowNativeFetch = window.fetch.bind(window);

  window.fetch = async function bundleCartFlowFetch(input, init) {
    let response;

    try {
      response = await cartFlowNativeFetch(input, init);
    } catch (error) {
      handleFailedCartAddRequest(input, {
        source: 'cart-fetch-error',
        message: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }

    if (!response.ok) {
      handleFailedCartAddRequest(input, {
        source: 'cart-fetch-status',
        status: response.status,
      });
      return response;
    }

    if (isCartAddRequest(input)) {
      try {
        const addResponsePayload = await readCartResponsePayload(response);
        void handleSuccessfulCartAdd(addResponsePayload).catch((error) => {
          console.error('Post-add cart pipeline failed.', error);
        });
      } catch (error) {
        // The caller's add succeeded; never turn a post-add pipeline failure into a
        // rejection of their request.
        console.error('Post-add cart pipeline failed.', error);
      }
    } else if (isCartGiftReconcileMutationRequest(input)) {
      void readCartResponsePayload(response).then((cartPayload) => {
        scheduleBreadBagGiftCartMutationReconcile(cartPayload, { source: 'cart-fetch-mutation' });
      });
    }

    return response;
  };

  window[FETCH_LISTENER_INSTALL_FLAG] = true;
}

function installXhrListener() {
  if (window[XHR_LISTENER_INSTALL_FLAG]) {
    return;
  }

  const nativeOpen = XMLHttpRequest.prototype.open;
  const nativeSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function bundleCartFlowOpen(method, url) {
    this.__bcBundleCartFlowUrl = url;
    // Forward the caller's exact arguments: passing an explicit undefined `async`
    // coerces to false and silently turns third-party requests synchronous.
    return nativeOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function bundleCartFlowSend(body) {
    const handleXhrAddFailure = (source) => {
      handleFailedCartAddRequest(this.__bcBundleCartFlowUrl, {
        source,
        status: this.status,
      });
    };

    this.addEventListener(
      'load',
      async () => {
        if (this.status < 200 || this.status >= 300) {
          handleXhrAddFailure('cart-xhr-status');
          return;
        }

        if (isCartAddRequest(this.__bcBundleCartFlowUrl)) {
          try {
            const addResponsePayload = parseCartResponsePayload(getXhrResponseText(this));
            await handleSuccessfulCartAdd(addResponsePayload);
          } catch (error) {
            console.error('Post-add cart pipeline failed.', error);
          }
        } else if (isCartGiftReconcileMutationRequest(this.__bcBundleCartFlowUrl)) {
          scheduleBreadBagGiftCartMutationReconcile(parseCartResponsePayload(getXhrResponseText(this)), {
            source: 'cart-xhr-mutation',
          });
        }
      },
      { once: true }
    );
    this.addEventListener('error', () => handleXhrAddFailure('cart-xhr-error'), { once: true });
    this.addEventListener('abort', () => handleXhrAddFailure('cart-xhr-abort'), { once: true });
    this.addEventListener('timeout', () => handleXhrAddFailure('cart-xhr-timeout'), { once: true });

    return nativeSend.call(this, body);
  };

  window[XHR_LISTENER_INSTALL_FLAG] = true;
}

function getBundleManifestSellingPlanId(item) {
  const sellingPlan = item?.selling_plan;
  const candidates = [
    item?.selling_plan_allocation?.selling_plan?.id,
    item?.selling_plan_id,
    sellingPlan && typeof sellingPlan === 'object' ? sellingPlan.id : sellingPlan,
    item?.properties?.selling_plan,
    item?.properties?._selling_plan,
  ];

  for (const candidate of candidates) {
    const value = String(candidate ?? '').trim();
    if (/^\d+$/.test(value) && Number(value) > 0) return value;
  }
  return '';
}

function buildBundleManifestQuantityMap(items, { cartItems = false } = {}) {
  return (Array.isArray(items) ? items : []).reduce((map, item) => {
    const variantId = String(cartItems ? item?.variant_id ?? item?.id ?? '' : item?.id ?? item?.variant_id ?? '');
    const quantity = Math.max(0, Number(item?.quantity) || 0);
    if (!variantId || !quantity) return map;

    const key = `${variantId}:${getBundleManifestSellingPlanId(item)}`;
    map.set(key, (map.get(key) || 0) + quantity);
    return map;
  }, new Map());
}

function isExpectedBundleCartReady(cart, expectedBundleId, expectedItems = [], { requireSingleGroup = true } = {}) {
  if (!expectedBundleId || !Array.isArray(cart?.items) || Number(cart?.item_count) <= 0) {
    return false;
  }

  const trackedGroupIds = getTrackedBundleGroupIds(cart);
  if (!trackedGroupIds.has(expectedBundleId) || (requireSingleGroup && trackedGroupIds.size !== 1)) {
    return false;
  }

  const matchingItems = getTrackedBundleItems(cart).filter((item) => (
    item?.properties?._rc_bundle === expectedBundleId
  ));
  const expected = buildBundleManifestQuantityMap(expectedItems);
  const actual = buildBundleManifestQuantityMap(matchingItems, { cartItems: true });

  return expected.size > 0 && expected.size === actual.size && [...expected.entries()].every(([key, quantity]) => (
    actual.get(key) === quantity
  ));
}

async function handleSuccessfulCartAdd(addResponsePayload, {
  verifiedCart = null,
  expectedBundleId = '',
  expectedItems = [],
} = {}) {
  pendingBundleAddPipelineCount += 1;
  hasPendingBundleAddPipeline = true;
  try {
    window.bcCartDebugLog?.('cart-flow:pipeline-start', {
      payloadItems: extractCartAddItems(addResponsePayload).length,
    });
  } catch (error) {}

  try {
    if (expectedBundleId) {
      if (!isExpectedBundleCartReady(verifiedCart, expectedBundleId, expectedItems, { requireSingleGroup: false })) {
        throw new Error('The verified bundle cart proof was missing or invalid.');
      }
      setLatestCartSnapshot(verifiedCart);

      if (isExpectedBundleCartReady(verifiedCart, expectedBundleId, expectedItems)) {
        applyVerifiedCartSnapshotToBundleDrawer(verifiedCart, { preserveGiftSummary: true });
      }
    }

    const hadPendingBundleSession = hasPendingBundleSession();
    const addedItems = extractCartAddItems(addResponsePayload);
    const shouldForceFreshGiftCartAfterAdd = getTrackedBundleIdsFromItems(addedItems).size === 0;
    let finalCartSnapshot = (await canonicalizeTrackedBundleCartAfterAdd({
      addedItems,
      cart: verifiedCart,
    })) ?? getLatestCartSnapshot();
    finalCartSnapshot =
      (await reconcileCottonBreadBagGiftBounded(finalCartSnapshot, {
        source: 'cart-add',
        forceFresh: shouldForceFreshGiftCartAfterAdd,
      })) ?? finalCartSnapshot;

    if (expectedBundleId) {
      const freshCartSnapshot = isExpectedBundleCartReady(finalCartSnapshot, expectedBundleId, expectedItems)
        ? finalCartSnapshot
        : await fetchCartStateForBreadBagGift();
      if (!isExpectedBundleCartReady(freshCartSnapshot, expectedBundleId, expectedItems)) {
        throw new Error('The final bundle cart could not be confirmed. Please try again.');
      }
      finalCartSnapshot = freshCartSnapshot;
      setLatestCartSnapshot(finalCartSnapshot);
      latestExpectedBundleManifest = {
        bundleId: expectedBundleId,
        items: expectedItems.map((item) => ({
          id: item?.id,
          quantity: item?.quantity,
          selling_plan: item?.selling_plan,
          selling_plan_id: item?.selling_plan_id,
        })),
      };
    }

    applyVerifiedCartSnapshotToBundleDrawer(finalCartSnapshot);

    try {
      window.bcCartDebugLog?.('cart-flow:cart-ready', {
        itemCount: Number(finalCartSnapshot?.item_count) || 0,
        trackedGroups: getTrackedBundleGroupIds(finalCartSnapshot).size,
      });
    } catch (error) {}

    const directCheckoutHandled = await maybeHandleBundleDirectCheckoutAfterCartAdd({
      hadPendingBundleSession,
      addedItems,
      finalCartSnapshot,
    });

    if (directCheckoutHandled) {
      commitOptimisticNativeDeliveryRoot();
      isBundleDrawerCartReconciliationPending = false;
      return;
    }

    commitOptimisticNativeDeliveryRoot();

    if (hadPendingBundleSession) {
      clearPendingBundleSession({ cartSnapshot: finalCartSnapshot });
      scheduleDrawerOpen({ force: true, cartSnapshot: finalCartSnapshot });
    } else {
      scheduleDrawerOpen({ cartSnapshot: finalCartSnapshot });
    }
    try {
      window.bcCartDebugLog?.('cart-flow:drawer-scheduled', {
        forced: hadPendingBundleSession,
      });
    } catch (error) {}
  } finally {
    pendingBundleAddPipelineCount = Math.max(0, pendingBundleAddPipelineCount - 1);
    if (pendingBundleAddPipelineCount === 0) {
      hasPendingBundleAddPipeline = false;
    }
  }
}

async function maybeHandleBundleDirectCheckoutAfterCartAdd({
  hadPendingBundleSession,
  addedItems = [],
  finalCartSnapshot,
} = {}) {
  const decision = getBundleCheckoutAbDecision();
  const addedTrackedBundleIds = getTrackedBundleIdsFromItems(addedItems);
  const hasTrackedBundleAfterAdd = addedTrackedBundleIds.size > 0 || getTrackedBundleGroupIds(finalCartSnapshot).size > 0;

  if ((!hadPendingBundleSession && !hasTrackedBundleAfterAdd) || !decision.enabled || decision.cohort !== 'checkout') {
    return false;
  }

  if (decision.dryRun) {
    emitBundleCheckoutAbEvent('would_redirect', {
      cartSignature: buildCartSnapshotSignature(finalCartSnapshot),
      decisionReason: decision.reason,
    });
    return false;
  }

  setBundleDirectCheckoutPending(true);

  const verification = await verifyBundleDirectCheckoutCart(finalCartSnapshot);

  if (!verification.ok) {
    setBundleDirectCheckoutPending(false);
    const fallbackEvent = isDirectCheckoutOnlyBundleFlow() ? 'fallback_cart' : 'fallback_drawer';

    emitBundleCheckoutAbEvent(fallbackEvent, {
      fallbackReason: verification.reason,
      breadBagAction: verification.plan?.action ?? null,
      thirdRewardAction: verification.thirdPlan?.action ?? null,
      guinnessCakesAction: verification.guinnessPlan?.action ?? null,
      cartSignature: buildCartSnapshotSignature(verification.cart ?? finalCartSnapshot),
    });

    if (isDirectCheckoutOnlyBundleFlow()) {
      beginBundleDirectCheckoutFallbackNavigation(verification.cart ?? finalCartSnapshot);
      return true;
    }

    return false;
  }

  beginBundleDirectCheckoutNavigation(verification.cart, decision, verification);
  return true;
}

function getRewardGiftCheckoutIssue(cart) {
  if (!Array.isArray(cart?.items)) {
    return { reason: 'missing-cart-items', rewardSlot: null };
  }

  const firstConfig = readGuinnessCakesGiftConfig(cart);
  const secondConfig = readBreadBagGiftConfig(cart, SECOND_REWARD_CHOICE_SLOT);
  const thirdConfig = readBreadBagGiftConfig(cart, THIRD_REWARD_CHOICE_SLOT);
  const rewardChecks = [
    {
      rewardSlot: FIRST_REWARD_CHOICE_SLOT,
      config: firstConfig,
      mode: getGuinnessCakesGiftSyncMode(firstConfig),
      eligibleSubtotal: getGuinnessCakesEligibleBundleSubtotal(cart, firstConfig),
      giftLines: getMarkedGuinnessCakesGiftLines(cart, firstConfig),
    },
    {
      rewardSlot: SECOND_REWARD_CHOICE_SLOT,
      config: secondConfig,
      mode: getBreadBagGiftSyncMode(secondConfig),
      eligibleSubtotal: getBreadBagEligibleBundleSubtotal(cart, secondConfig),
      giftLines: getMarkedBreadBagGiftLines(cart, secondConfig),
    },
    {
      rewardSlot: THIRD_REWARD_CHOICE_SLOT,
      config: thirdConfig,
      mode: getBreadBagGiftSyncMode(thirdConfig),
      eligibleSubtotal: getBreadBagEligibleBundleSubtotal(cart, thirdConfig),
      giftLines: getMarkedBreadBagGiftLines(cart, thirdConfig),
    },
  ];
  const seenChoiceIds = new Set();
  const seenVariantIds = new Set();

  for (const reward of rewardChecks) {
    const { config, rewardSlot } = reward;
    if (reward.mode === 'off') continue;

    const purchaseMode = getTrackedBundleGiftPurchaseMode(cart, config);
    const qualifies = reward.eligibleSubtotal >= config.thresholdPence;
    if (purchaseMode !== 'subscription' || !qualifies || config.skipped) continue;

    if (config.choiceMode && !config.choiceId) {
      return { reason: 'reward-choice-required', rewardSlot };
    }

    if (reward.giftLines.length !== 1) {
      return { reason: 'reward-gift-line-count', rewardSlot };
    }

    const [giftLine] = reward.giftLines;
    if (Number(giftLine?.quantity) !== 1) {
      return { reason: 'reward-gift-quantity', rewardSlot };
    }

    if (Number(giftLine?.final_line_price) !== 0) {
      return { reason: 'reward-gift-not-free', rewardSlot };
    }

    if (config.choiceId) {
      if (seenChoiceIds.has(config.choiceId)) {
        return { reason: 'reward-gift-choice-duplicate', rewardSlot };
      }
      seenChoiceIds.add(config.choiceId);
    }

    const variantId = Number(giftLine?.variant_id);
    if (Number.isFinite(variantId) && variantId > 0) {
      if (seenVariantIds.has(variantId)) {
        return { reason: 'reward-gift-variant-duplicate', rewardSlot };
      }
      seenVariantIds.add(variantId);
    }
  }

  return null;
}

async function verifyBundleDirectCheckoutCart(initialCart) {
  let currentCart = initialCart?.items ? initialCart : null;
  let lastPlan = null;
  let lastThirdPlan = null;
  let lastGuinnessPlan = null;

  for (let attempt = 1; attempt <= BUNDLE_DIRECT_CHECKOUT_VERIFY_ATTEMPTS; attempt += 1) {
    const freshCart = await fetchCartStateForBreadBagGift();

    if (!Array.isArray(freshCart?.items)) {
      return {
        ok: false,
        reason: 'fresh-cart-fetch-failed',
        cart: currentCart ?? getLatestCartSnapshot(),
        plan: lastPlan,
        thirdPlan: lastThirdPlan,
        guinnessPlan: lastGuinnessPlan,
      };
    }

    currentCart = freshCart;

    if (!Array.isArray(currentCart?.items)) {
      return {
        ok: false,
        reason: 'missing-cart-items',
        cart: currentCart,
        plan: lastPlan,
        thirdPlan: lastThirdPlan,
        guinnessPlan: lastGuinnessPlan,
      };
    }

    const trackedBundleGroupIds = getTrackedBundleGroupIds(currentCart);

    if (trackedBundleGroupIds.size === 0) {
      return {
        ok: false,
        reason: 'missing-tracked-bundle',
        cart: currentCart,
        plan: lastPlan,
        thirdPlan: lastThirdPlan,
        guinnessPlan: lastGuinnessPlan,
      };
    }

    if (trackedBundleGroupIds.size > 1) {
      return {
        ok: false,
        reason: 'duplicate-tracked-bundles',
        cart: currentCart,
        plan: lastPlan,
        thirdPlan: lastThirdPlan,
        guinnessPlan: lastGuinnessPlan,
      };
    }

    lastPlan = buildCottonBreadBagGiftPlan(currentCart, {
      source: `direct-checkout-verify-${attempt}`,
      rewardSlot: SECOND_REWARD_CHOICE_SLOT,
    });
    lastThirdPlan = buildCottonBreadBagGiftPlan(currentCart, {
      source: `direct-checkout-verify-${attempt}`,
      rewardSlot: THIRD_REWARD_CHOICE_SLOT,
    });

    // A disabled promo is not a verification failure: the plan builder still emits
    // remove/normalize actions when the cart needs protecting, and a noop plan means
    // there is nothing to verify for this gift. Config validity only matters when it
    // actually blocked an action ('invalid-config').

    if (lastPlan.action === 'invalid-config') {
      return {
        ok: false,
        reason: 'bread-bag-invalid-config',
        cart: currentCart,
        plan: lastPlan,
        thirdPlan: lastThirdPlan,
        guinnessPlan: lastGuinnessPlan,
      };
    }

    if (lastThirdPlan.action === 'invalid-config') {
      return {
        ok: false,
        reason: 'third-reward-invalid-config',
        cart: currentCart,
        plan: lastPlan,
        thirdPlan: lastThirdPlan,
        guinnessPlan: lastGuinnessPlan,
      };
    }

    if (lastPlan.dryRun) {
      return {
        ok: false,
        reason: 'bread-bag-dry-run',
        cart: currentCart,
        plan: lastPlan,
        thirdPlan: lastThirdPlan,
        guinnessPlan: lastGuinnessPlan,
      };
    }

    if (lastThirdPlan.dryRun) {
      return {
        ok: false,
        reason: 'third-reward-dry-run',
        cart: currentCart,
        plan: lastPlan,
        thirdPlan: lastThirdPlan,
        guinnessPlan: lastGuinnessPlan,
      };
    }

    lastGuinnessPlan = shouldRunGuinnessCakesGiftReconcile(currentCart)
      ? buildGuinnessCakesGiftPlan(currentCart, {
          source: `direct-checkout-verify-${attempt}`,
        })
      : null;

    if (lastGuinnessPlan?.action === 'invalid-config') {
      return {
        ok: false,
        reason: 'guinness-cakes-invalid-config',
        cart: currentCart,
        plan: lastPlan,
        thirdPlan: lastThirdPlan,
        guinnessPlan: lastGuinnessPlan,
      };
    }

    if (lastGuinnessPlan?.dryRun) {
      return {
        ok: false,
        reason: 'guinness-cakes-dry-run',
        cart: currentCart,
        plan: lastPlan,
        thirdPlan: lastThirdPlan,
        guinnessPlan: lastGuinnessPlan,
      };
    }

    if (
      lastPlan.action === 'noop' &&
      lastThirdPlan.action === 'noop' &&
      (!lastGuinnessPlan || lastGuinnessPlan.action === 'noop')
    ) {
      const rewardGiftIssue = getRewardGiftCheckoutIssue(currentCart);
      if (rewardGiftIssue) {
        return {
          ok: false,
          reason: rewardGiftIssue.reason,
          rewardSlot: rewardGiftIssue.rewardSlot,
          cart: currentCart,
          plan: lastPlan,
          thirdPlan: lastThirdPlan,
          guinnessPlan: lastGuinnessPlan,
        };
      }

      return {
        ok: true,
        reason: 'cart-ready',
        cart: currentCart,
        plan: lastPlan,
        thirdPlan: lastThirdPlan,
        guinnessPlan: lastGuinnessPlan,
      };
    }

    if (attempt < BUNDLE_DIRECT_CHECKOUT_VERIFY_ATTEMPTS) {
      currentCart =
        (await reconcileCottonBreadBagGiftBounded(currentCart, {
          source: `direct-checkout-finalize-${attempt}`,
          forceFresh: true,
        })) ?? currentCart;
      await waitForBundleDirectCheckoutRetryDelay();
    }
  }

  return {
    ok: false,
    reason:
      lastPlan?.action !== 'noop'
        ? `bread-bag-plan-${lastPlan?.action ?? 'unknown'}`
        : lastThirdPlan?.action !== 'noop'
          ? `third-reward-plan-${lastThirdPlan?.action ?? 'unknown'}`
          : `guinness-cakes-plan-${lastGuinnessPlan?.action ?? 'unknown'}`,
    cart: currentCart,
    plan: lastPlan,
    thirdPlan: lastThirdPlan,
    guinnessPlan: lastGuinnessPlan,
  };
}

function beginBundleDirectCheckoutFallbackNavigation(cart) {
  clearFlareEnhancementSequence();
  clearFlareUiReadinessCheck();
  window.clearTimeout(openTimer);
  disconnectFlareCalendarStateObserver();
  hideBundleDrawerLoadingState({ force: true });
  clearPendingBundleSession({ cartSnapshot: cart });

  window.setTimeout(() => {
    window.location.assign(`${window.Shopify?.routes?.root ?? '/'}cart`);
  }, BUNDLE_DIRECT_CHECKOUT_REDIRECT_DELAY_MS);
}

function beginBundleDirectCheckoutNavigation(cart, decision, verification) {
  isProceedingToCheckout = true;
  window.clearTimeout(checkoutIntentResetTimer);
  checkoutIntentResetTimer = window.setTimeout(() => {
    isProceedingToCheckout = false;
  }, CHECKOUT_INTENT_RESET_DELAY_MS);

  clearFlareEnhancementSequence();
  clearFlareUiReadinessCheck();
  window.clearTimeout(openTimer);
  window.clearTimeout(breadBagGiftCartMutationReconcileTimer);
  pendingBreadBagGiftCartMutation = null;
  disconnectFlareCalendarStateObserver();
  hideBundleDrawerLoadingState({ force: true });
  captureAndPersistHybridBundleDraft('checkout', {
    force: true,
    rawSnapshot: pendingBundleStateSnapshot,
    ttlMs: BUNDLE_HYBRID_CHECKOUT_DRAFT_TTL_MS,
  });
  clearPendingBundleSession({ cartSnapshot: cart, unlock: false });

  emitBundleCheckoutAbEvent('redirected', {
    decisionReason: decision.reason,
    breadBagAction: verification.plan?.action ?? null,
    breadBagReason: verification.plan?.reason ?? null,
    guinnessCakesAction: verification.guinnessPlan?.action ?? null,
    guinnessCakesReason: verification.guinnessPlan?.reason ?? null,
    cartSignature: buildCartSnapshotSignature(cart),
  });

  window.setTimeout(() => {
    window.location.assign(`${window.Shopify?.routes?.root ?? '/'}checkout`);
  }, BUNDLE_DIRECT_CHECKOUT_REDIRECT_DELAY_MS);
}

function waitForBundleDirectCheckoutRetryDelay() {
  return new Promise((resolve) => {
    window.setTimeout(resolve, BUNDLE_DIRECT_CHECKOUT_VERIFY_RETRY_DELAY_MS);
  });
}

function scheduleBreadBagGiftCartMutationReconcile(cartPayload, { source = 'cart-mutation' } = {}) {
  pendingBreadBagGiftCartMutation = {
    cart: Array.isArray(cartPayload?.items) ? cartPayload : null,
    source,
  };

  window.clearTimeout(breadBagGiftCartMutationReconcileTimer);
  breadBagGiftCartMutationReconcileTimer = window.setTimeout(() => {
    void handleBreadBagGiftCartMutationReconcile();
  }, BREAD_BAG_GIFT_CART_MUTATION_RECONCILE_DELAY_MS);
}

async function handleBreadBagGiftCartMutationReconcile() {
  const pendingMutation = pendingBreadBagGiftCartMutation;
  pendingBreadBagGiftCartMutation = null;

  const baseCart = pendingMutation?.cart ?? null;
  const beforeSignature = buildCartSnapshotSignature(baseCart ?? getLatestCartSnapshot());
  const reconciledCart =
    (await reconcileCottonBreadBagGiftBounded(baseCart, {
      source: pendingMutation?.source ?? 'cart-mutation',
      forceFresh: true,
    })) ??
    baseCart ??
    getLatestCartSnapshot();

  if (reconciledCart?.items) {
    setLatestCartSnapshot(reconciledCart);
  }

  const afterSignature = buildCartSnapshotSignature(reconciledCart ?? getLatestCartSnapshot());
  const syncPlan = breadBagGiftSyncState.lastPlan;
  const didGiftMutationRun =
    syncPlan &&
    !syncPlan.dryRun &&
    ['add', 'replace', 'remove', 'normalize'].includes(syncPlan.action) &&
    !breadBagGiftSyncState.lastError;
  const shouldRefreshDrawer = Boolean(beforeSignature !== afterSignature || didGiftMutationRun);

  if (shouldRefreshDrawer) {
    const didRefreshOpenDrawer = await refreshOpenBundleDrawerAfterBreadBagGiftChange(afterSignature);

    if (!didRefreshOpenDrawer) {
      refreshCartPageAfterBreadBagGiftChange();
    }
  }
}

async function refreshOpenBundleDrawerAfterBreadBagGiftChange(cartSignature = '') {
  const drawer = getBundleDrawer();
  const dialog = getBundleDrawerDialog();

  if (!(drawer instanceof HTMLElement) || !(dialog instanceof HTMLDialogElement) || !dialog.open) {
    return false;
  }

  await refreshBundleDrawer();
  lastRenderedDrawerCartSignature = cartSignature || buildCartSnapshotSignature(getLatestCartSnapshot());
  runFlareEnhancementSequence();
  return true;
}

function refreshCartPageAfterBreadBagGiftChange() {
  if (!isCartPage()) {
    return false;
  }

  window.location.reload();
  return true;
}

function getXhrResponseText(xhr) {
  try {
    return xhr.responseText;
  } catch (error) {
    return '';
  }
}

async function readCartResponsePayload(response) {
  try {
    return await response.clone().json();
  } catch (error) {
    return null;
  }
}

function parseCartResponsePayload(responseText) {
  if (typeof responseText !== 'string' || responseText.trim() === '') {
    return null;
  }

  try {
    return JSON.parse(responseText);
  } catch (error) {
    return null;
  }
}

function extractCartAddItems(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.items)) {
    return payload.items;
  }

  if (payload && typeof payload === 'object') {
    return [payload];
  }

  return [];
}

function handleFailedCartAddRequest(input, { source = 'cart-add-failed', status = null, message = '' } = {}) {
  if (!isCartAddRequest(input) || (!hasPendingBundleSession() && !isBundleDirectCheckoutPending)) {
    return;
  }

  const wasDirectCheckoutPending = isBundleDirectCheckoutPending;

  clearPendingBundleSession({ cartSnapshot: getLatestCartSnapshot() });
  rollbackOptimisticNativeDeliveryRoot();
  void primeCartSnapshot({ force: true });
  // The tap-time shell open masked the items region behind its skeleton; the add failed,
  // so lift it now rather than leaving the customer on a skeleton until the safety window.
  hideBundleDrawerLoadingState({ force: true });

  if (wasDirectCheckoutPending) {
    emitBundleCheckoutAbEvent('fallback_drawer', {
      fallbackReason: 'cart-add-failed',
      source,
      status,
      message,
    });
  }
}

function isCartAddRequest(input) {
  const url = normalizeUrl(input);

  if (!url) return false;

  return CART_ADD_PATHS.has(getCartEndpointPath(url));
}

function isCartGiftReconcileMutationRequest(input) {
  const url = normalizeUrl(input);

  if (!url) return false;

  return CART_GIFT_RECONCILE_MUTATION_PATHS.has(getCartEndpointPath(url));
}

function getCartEndpointPath(url) {
  return getStorefrontPath(url.pathname);
}

function getStorefrontPath(pathname) {
  const normalizedPath = String(pathname ?? '').replace(/\/+$/, '') || '/';
  const rootPath = getShopifyRootPath();

  if (rootPath && rootPath !== '/' && normalizedPath.startsWith(`${rootPath}/`)) {
    return normalizedPath.slice(rootPath.length);
  }

  return normalizedPath;
}

function getShopifyRootPath() {
  try {
    return new URL(window.Shopify?.routes?.root ?? '/', window.location.origin).pathname.replace(/\/+$/, '') || '/';
  } catch (error) {
    return '/';
  }
}

function getRechargeBundle() {
  if (typeof window.RechargeBundle === 'object' && window.RechargeBundle !== null) {
    return window.RechargeBundle;
  }

  window.RechargeBundle = {};
  return window.RechargeBundle;
}

// If ReCharge loads after us and assigns a fresh RechargeBundle object, the
// onAddToCart hook installed on the old object is silently lost — re-install it on
// every reassignment.
function guardRechargeBundleReassignment() {
  let currentValue = window.RechargeBundle;

  try {
    Object.defineProperty(window, 'RechargeBundle', {
      configurable: true,
      get() {
        return currentValue;
      },
      set(nextValue) {
        currentValue = nextValue;

        if (typeof nextValue === 'object' && nextValue !== null) {
          installRechargeAddToCartHook();
        }
      },
    });
  } catch (error) {
    // A non-configurable property means another script already guards it; leave it be.
  }
}

function installBundleDrawerLifecycleHandlers() {
  bindBundleDrawerCloseHandler();

  if (!isBundleSubmitHandlerInstalled) {
    document.addEventListener('submit', handleCheckoutSubmit, true);
    document.addEventListener('click', handleBundlePeekRemoveClick, true);
    isBundleSubmitHandlerInstalled = true;
  }
}

function initializeCartPageBreadBagGiftSync() {
  window.setTimeout(async () => {
    const cart = await fetchCartStateForBreadBagGift();
    const beforeSignature = buildCartSnapshotSignature(cart);
    const reconciledCart =
      (await reconcileCottonBreadBagGiftBounded(cart, {
        source: 'cart-page-load',
      })) ?? cart;

    if (reconciledCart?.items) {
      setLatestCartSnapshot(reconciledCart);
    }

    const afterSignature = buildCartSnapshotSignature(reconciledCart ?? cart);

    if (beforeSignature !== afterSignature) {
      refreshCartPageAfterBreadBagGiftChange();
    }
  }, BREAD_BAG_GIFT_CART_MUTATION_RECONCILE_DELAY_MS);
}

function installFlarePostcodePlaceholderStyle() {
  if (document.getElementById(FLARE_POSTCODE_PLACEHOLDER_STYLE_ID)) {
    return;
  }

  const style = document.createElement('style');
  style.id = FLARE_POSTCODE_PLACEHOLDER_STYLE_ID;
  style.textContent = `
    input.bc-flare-postcode-placeholder::placeholder {
      color: #8c8780;
      opacity: 1;
    }

    .bc-postcode-gate-invalid {
      border-color: #9d2f2f !important;
      box-shadow: none !important;
    }

    [data-bc-postcode-gate-message="true"] {
      color: #9d2f2f;
      display: block;
      font-family: inherit;
      font-size: 14px;
      line-height: 1.35;
      margin: 8px 0 0;
    }

    @media (max-width: 900px) {
      #flare-date-picker-block,
      .cart-drawer--bundle-flow #flare-date-picker-block {
        --flare-block-input-font-size: 16px !important;
        --flare-block-zip-code-font-size: 16px !important;
      }

      #flare-date-picker-block #delivery-zip-code,
      #flare-date-picker-block input.zip-code-input,
      #flare-date-picker-block #input_postcode,
      #flare-date-picker-block input.bc-dc__input,
      #flare-date-picker-block #ship-date,
      #delivery-zip-code,
      input.zip-code-input,
      #input_postcode,
      input.bc-dc__input,
      #ship-date {
        font-size: 16px !important;
        line-height: 1.25 !important;
      }
    }
  `;
  document.head.appendChild(style);
}

function syncFlarePostcodePlaceholder(root = document) {
  if (!(root instanceof Document) && !(root instanceof Element)) {
    return false;
  }

  installFlarePostcodePlaceholderStyle();

  const inputs = root.querySelectorAll('#delivery-zip-code, input.zip-code-input, #input_postcode, input.bc-dc__input');
  let changed = false;

  inputs.forEach((input) => {
    if (!(input instanceof HTMLInputElement)) {
      return;
    }

    if (input.getAttribute('placeholder') !== FLARE_POSTCODE_PLACEHOLDER_TEXT) {
      input.setAttribute('placeholder', FLARE_POSTCODE_PLACEHOLDER_TEXT);
      changed = true;
    }

    if (!input.classList.contains('bc-flare-postcode-placeholder')) {
      input.classList.add('bc-flare-postcode-placeholder');
      changed = true;
    }

    syncFlarePostcodeVisualState(input);
  });

  return changed;
}

function syncFlarePostcodeVisualState(input = document.querySelector('#delivery-zip-code')) {
  if (!(input instanceof HTMLInputElement)) {
    return false;
  }

  const wrapper = input.closest('.zip-code-input-wrapper, .flare-floating-field');

  if (!(wrapper instanceof HTMLElement)) {
    return false;
  }

  const hasValue = Boolean(input.value.trim());
  const isExplicitlyInvalid =
    input.getAttribute('aria-invalid') === 'true' ||
    wrapper.getAttribute('aria-invalid') === 'true' ||
    /\b(error|invalid)\b/i.test(`${input.className} ${wrapper.className}`);
  const hasValidValue = hasValue && input.validity.valid && !isExplicitlyInvalid;

  wrapper.classList.toggle(FLARE_POSTCODE_EMPTY_CLASS, !hasValue);
  wrapper.classList.toggle(FLARE_POSTCODE_VALID_CLASS, hasValidValue);
  syncFlarePlaceholderLayerVisibility(wrapper, [FLARE_POSTCODE_PLACEHOLDER_TEXT], hasValue);

  return hasValidValue;
}

function syncFlareDateVisualState(input = document.querySelector('#ship-date')) {
  if (!(input instanceof HTMLInputElement)) {
    return false;
  }

  const wrapper =
    input.closest('#date-selector-wrapper') || input.closest('.date-selector') || input.closest('.date-content');

  if (!(wrapper instanceof HTMLElement)) {
    return false;
  }

  const hasValue = Boolean(input.value.trim());
  wrapper.classList.toggle(FLARE_DATE_EMPTY_CLASS, !hasValue);
  wrapper.classList.toggle(FLARE_DATE_VALID_CLASS, hasValue);
  syncFlarePlaceholderLayerVisibility(wrapper, ['Select a delivery date'], hasValue);

  return hasValue;
}

function syncFlareFieldValueStates(flareBlock = getFlareBlock()) {
  if (!(flareBlock instanceof HTMLElement)) {
    return false;
  }

  const postcodeSynced = syncFlarePostcodeVisualState(flareBlock.querySelector('#delivery-zip-code'));
  const dateSynced = syncFlareDateVisualState(flareBlock.querySelector('#ship-date'));

  return postcodeSynced || dateSynced;
}

function syncFlarePlaceholderLayerVisibility(container, placeholderTexts, shouldHide) {
  if (!(container instanceof HTMLElement)) {
    return;
  }

  const normalizedPlaceholderTexts = new Set(
    placeholderTexts.map((text) => String(text || '').replace(/\s+/g, ' ').trim()).filter(Boolean)
  );

  if (!normalizedPlaceholderTexts.size) {
    return;
  }

  container.querySelectorAll(`.${FLARE_HIDDEN_PLACEHOLDER_CLASS}`).forEach((element) => {
    if (element instanceof HTMLElement) {
      element.classList.remove(FLARE_HIDDEN_PLACEHOLDER_CLASS);
    }
  });

  if (!shouldHide) {
    return;
  }

  container.querySelectorAll('*').forEach((element) => {
    if (
      !(element instanceof HTMLElement) ||
      element.matches('input, select, textarea, option, button, svg, path')
    ) {
      return;
    }

    const normalizedText = element.textContent?.replace(/\s+/g, ' ').trim();

    if (normalizedText && normalizedPlaceholderTexts.has(normalizedText)) {
      element.classList.add(FLARE_HIDDEN_PLACEHOLDER_CLASS);
    }
  });
}

function parseDeliveryZonePostcode(rawPostcode, options) {
  const compact = String(rawPostcode ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  const allowAmbiguousCorrections = options?.allowAmbiguousCorrections === true;
  const parsed =
    parseDeliveryZonePostcodeCompact(compact) ||
    (allowAmbiguousCorrections ? parseDeliveryZonePostcodeCompact(correctAmbiguousPostcodeDigits(compact), compact) : null);

  if (!parsed) {
    return {
      compact,
      normalized: compact,
      outcode: '',
      incode: '',
      sector: '',
      valid: false,
    };
  }

  return parsed;
}

function parseDeliveryZonePostcodeCompact(compact, originalCompact = compact) {
  const match = String(compact || '').match(/^([A-Z]{1,2}\d[A-Z\d]?)(\d[A-Z]{2})$/);

  if (!match) {
    return null;
  }

  const outcode = match[1];
  const incode = match[2];

  return {
    compact: originalCompact,
    normalized: `${outcode} ${incode}`,
    outcode,
    incode,
    sector: `${outcode}${incode.charAt(0)}`,
    valid: true,
  };
}

function correctAmbiguousPostcodeDigits(compact) {
  return String(compact || '').replace(/[IL]/g, '1').replace(/O/g, '0');
}

function getDeliveryZonePostcodeGateResult(rawPostcode) {
  const hasValue = Boolean(String(rawPostcode ?? '').trim());
  const parsed = parseDeliveryZonePostcode(rawPostcode);

  if (!hasValue) {
    return {
      allowed: false,
      message: POSTCODE_GATE_EMPTY_MESSAGE,
      reason: 'empty',
      ...parsed,
    };
  }

  if (!parsed.valid) {
    return {
      allowed: false,
      message: POSTCODE_GATE_INVALID_MESSAGE,
      reason: 'invalid-format',
      ...parsed,
    };
  }

  const allowed =
    DELIVERY_ZONE_DISTRICTS.has(parsed.outcode) ||
    DELIVERY_ZONE_SECTORS.has(parsed.sector);

  return {
    allowed,
    message: allowed ? '' : POSTCODE_GATE_BLOCKED_MESSAGE,
    reason: allowed ? 'allowed' : 'unsupported-zone',
    ...parsed,
  };
}

function isDeliveryZonePostcodeAllowed(rawPostcode) {
  return getDeliveryZonePostcodeGateResult(rawPostcode).allowed;
}

function clearPostcodeGateFeedback(input) {
  if (!(input instanceof HTMLInputElement)) return false;

  const wrapper = getPostcodeGateFieldWrapper(input);
  const message = getPostcodeGateMessageElement(input, { create: false });
  const messageId = input.dataset.bcPostcodeGateMessageId;

  if (wrapper instanceof HTMLElement) {
    wrapper.classList.remove('bc-postcode-gate-invalid');
    wrapper.removeAttribute('data-bc-postcode-gate-state');
  }

  input.classList.remove('bc-postcode-gate-invalid');
  input.removeAttribute('data-bc-postcode-gate-state');
  input.removeAttribute('aria-invalid');
  if (messageId) {
    const describedBy = String(input.getAttribute('aria-describedby') || '')
      .split(/\s+/)
      .filter(Boolean)
      .filter((id) => id !== messageId)
      .join(' ');
    if (describedBy) {
      input.setAttribute('aria-describedby', describedBy);
    } else {
      input.removeAttribute('aria-describedby');
    }
    delete input.dataset.bcPostcodeGateMessageId;
  }

  if (message) {
    if (message.classList.contains('bc-dc__inline-error')) {
      message.textContent = '';
      message.style.display = 'none';
      message.removeAttribute('role');
      delete message.dataset.bcPostcodeGateMessage;
      return true;
    }

    message.remove();
  }

  return true;
}

function applyPostcodeGateFeedback(input, result) {
  if (!(input instanceof HTMLInputElement)) return false;

  const wrapper = getPostcodeGateFieldWrapper(input);
  const message = getPostcodeGateMessageElement(input, { create: true });
  const state = result?.reason || 'blocked';

  input.setAttribute('aria-invalid', 'true');
  input.dataset.bcPostcodeGateState = state;
  input.classList.add('bc-postcode-gate-invalid');

  if (wrapper instanceof HTMLElement) {
    wrapper.dataset.bcPostcodeGateState = state;
    wrapper.classList.add('bc-postcode-gate-invalid');
  }

  if (message) {
    message.textContent = result?.message || POSTCODE_GATE_BLOCKED_MESSAGE;
    input.setAttribute('aria-describedby', message.id);
  }

  try {
    input.focus({ preventScroll: true });
  } catch (error) {
    input.focus();
  }

  return true;
}

function getPostcodeGateFieldWrapper(input) {
  if (!(input instanceof HTMLInputElement)) return null;

  return input.closest('.zip-code-input-wrapper, .flare-floating-field, .bc-dc__field, .bc-dc__field-row, .bc-dc__input-wrapper') || input;
}

function getPostcodeGateMessageElement(input, { create = false } = {}) {
  if (!(input instanceof HTMLInputElement)) return null;

  const existingId = input.dataset.bcPostcodeGateMessageId;
  if (existingId) {
    const existing = document.getElementById(existingId);
    if (existing instanceof HTMLElement || !create) {
      return existing;
    }
  }

  if (!create) return null;

  const message = document.createElement('p');
  message.id = `${POSTCODE_GATE_MESSAGE_ID}-${Math.random().toString(36).slice(2, 8)}`;
  message.setAttribute('role', 'status');
  message.dataset.bcPostcodeGateMessage = 'true';
  input.dataset.bcPostcodeGateMessageId = message.id;

  const visibleCheckerPanel = input.closest('.bc-dc__panel');
  const visibleCheckerError = visibleCheckerPanel?.querySelector('.bc-dc__inline-error');
  if (visibleCheckerError instanceof HTMLElement) {
    if (!visibleCheckerError.id) {
      visibleCheckerError.id = message.id;
    }
    visibleCheckerError.setAttribute('role', 'status');
    visibleCheckerError.dataset.bcPostcodeGateMessage = 'true';
    visibleCheckerError.style.display = 'block';
    input.dataset.bcPostcodeGateMessageId = visibleCheckerError.id;
    return visibleCheckerError;
  }

  const wrapper = getPostcodeGateFieldWrapper(input);
  const anchor = wrapper instanceof HTMLElement ? wrapper : input;
  anchor.insertAdjacentElement('afterend', message);

  return message;
}

function findPostcodeGateInputForTarget(target) {
  if (!(target instanceof Element)) return null;

  if (target.matches(POSTCODE_GATE_INPUT_SELECTOR)) {
    return target instanceof HTMLInputElement ? target : null;
  }

  const scope = target.closest('.bc-dc-lightbox, #flare-date-picker-block, .cart-drawer__dialog, dialog, form');

  if (!(scope instanceof HTMLElement)) {
    return null;
  }

  const inputs = Array.from(scope.querySelectorAll(POSTCODE_GATE_INPUT_SELECTOR))
    .filter((input) => input instanceof HTMLInputElement);
  const visibleInput = inputs.find((input) => isLaidOutVisibleElement(input));

  return visibleInput || inputs[0] || null;
}

function findPostcodeGateInputForClick(target) {
  if (!(target instanceof Element)) return null;

  const action = target.closest('button, [role="button"], input[type="button"], input[type="submit"], a');
  if (!(action instanceof HTMLElement)) return null;

  const input = findPostcodeGateInputForTarget(action);
  if (!(input instanceof HTMLInputElement)) return null;

  const actionText = normalizePostcodeGateText(action.textContent || action.getAttribute('aria-label') || action.getAttribute('value') || '');
  const actionType = String(action.getAttribute('type') || '').toLowerCase();

  if (
    actionType === 'submit' ||
    /check|continue|checkout|delivery|postcode|date/i.test(actionText)
  ) {
    return input;
  }

  return null;
}

function normalizePostcodeGateText(text) {
  return String(text || '').replace(/\s+/g, ' ').trim();
}

function blockPostcodeGateEvent(event, input, result) {
  event.preventDefault();
  event.stopPropagation();
  if (typeof event.stopImmediatePropagation === 'function') {
    event.stopImmediatePropagation();
  }

  if (openDeliveryCheckerUnsupportedPostcodeWaitlist(input, result)) {
    return;
  }

  applyPostcodeGateFeedback(input, result);
}

function openDeliveryCheckerUnsupportedPostcodeWaitlist(input, result) {
  if (
    !(input instanceof HTMLInputElement) ||
    result?.reason !== 'unsupported-zone'
  ) {
    return false;
  }

  const checker = input.closest('.bc-delivery-checker');

  if (!(checker instanceof HTMLElement)) {
    return false;
  }

  const postcodeLabel = result.normalized || input.value.trim();
  clearPostcodeGateFeedback(input);

  checker
    .querySelectorAll('#bc-dc-fail-postcode, [data-bc-dc-fail-modal-postcode]')
    .forEach((element) => {
      element.textContent = postcodeLabel;
    });

  const failModal = checker.querySelector('#bc-dc-fail-modal, .bc-dc__fail-modal');

  if (failModal instanceof HTMLElement) {
    failModal.classList.add('is-open');
    failModal.setAttribute('aria-hidden', 'false');
    failModal.removeAttribute('hidden');

    window.setTimeout(() => {
      const emailInput = failModal.querySelector('input[type="email"], .bc-dc__waitlist-input');
      if (emailInput instanceof HTMLInputElement) {
        try {
          emailInput.focus({ preventScroll: true });
        } catch (error) {
          emailInput.focus();
        }
      }
    }, 0);

    dispatchDeliveryCheckerUnsupportedPostcodeEvent(checker, input, result);
    return true;
  }

  const formPanel = checker.querySelector('#bc-dc-form');
  const successPanel = checker.querySelector('#bc-dc-success');
  const failPanel = checker.querySelector('#bc-dc-fail');

  if (failPanel instanceof HTMLElement) {
    setDeliveryCheckerPanelVisible(formPanel, false);
    setDeliveryCheckerPanelVisible(successPanel, false);
    setDeliveryCheckerPanelVisible(failPanel, true);
    dispatchDeliveryCheckerUnsupportedPostcodeEvent(checker, input, result);
    return true;
  }

  return false;
}

function setDeliveryCheckerPanelVisible(panel, visible) {
  if (!(panel instanceof HTMLElement)) return;
  panel.classList.toggle('is-visible', Boolean(visible));
  panel.classList.toggle('is-hidden', !visible);
  panel.setAttribute('aria-hidden', visible ? 'false' : 'true');
}

function dispatchDeliveryCheckerUnsupportedPostcodeEvent(checker, input, result) {
  if (!(checker instanceof HTMLElement)) return;

  checker.dispatchEvent(new CustomEvent('bcDc:unsupportedPostcode', {
    bubbles: true,
    detail: {
      input,
      normalized: result?.normalized || '',
      outcode: result?.outcode || '',
      sector: result?.sector || '',
      reason: result?.reason || 'unsupported-zone',
    },
  }));
}

function handleDeliveryPostcodeGateAction(event) {
  if (isNativeDeliveryEnabled()) {
    return;
  }

  const input = event.type === 'submit'
    ? findPostcodeGateInputForTarget(event.target)
    : findPostcodeGateInputForClick(event.target);

  if (!(input instanceof HTMLInputElement)) {
    return;
  }

  const result = getDeliveryZonePostcodeGateResult(input.value);

  if (result.allowed) {
    clearPostcodeGateFeedback(input);
    return;
  }

  blockPostcodeGateEvent(event, input, result);
}

function handleDeliveryPostcodeGateKeydown(event) {
  if (isNativeDeliveryEnabled()) {
    return;
  }

  if (event.key !== 'Enter') return;

  const input = event.target;

  if (!(input instanceof HTMLInputElement) || !input.matches(POSTCODE_GATE_INPUT_SELECTOR)) {
    return;
  }

  const result = getDeliveryZonePostcodeGateResult(input.value);

  if (result.allowed) {
    clearPostcodeGateFeedback(input);
    return;
  }

  blockPostcodeGateEvent(event, input, result);
}

function handleDeliveryPostcodeGateInput(event) {
  if (isNativeDeliveryEnabled()) {
    return;
  }

  const input = event.target;

  if (!(input instanceof HTMLInputElement) || !input.matches(POSTCODE_GATE_INPUT_SELECTOR)) {
    return;
  }

  const result = getDeliveryZonePostcodeGateResult(input.value);

  if (result.allowed || result.reason === 'empty') {
    clearPostcodeGateFeedback(input);
  }
}

function installDeliveryPostcodeGatekeeperDebugHelpers() {
  window.bcDeliveryZoneGatekeeper = {
    parse: parseDeliveryZonePostcode,
    check: getDeliveryZonePostcodeGateResult,
    isAllowed: isDeliveryZonePostcodeAllowed,
    districts: [...DELIVERY_ZONE_DISTRICTS],
    sectors: [...DELIVERY_ZONE_SECTORS],
  };
}

function handleFlarePostcodeVisualStateChange(event) {
  const target = event.target;

  if (
    !(target instanceof HTMLInputElement) ||
    !target.matches('#delivery-zip-code, input.zip-code-input, #input_postcode, input.bc-dc__input, #ship-date')
  ) {
    return;
  }

  const syncTarget = () => {
    if (target.id === 'ship-date') {
      syncFlareDateVisualState(target);
      return;
    }

    syncFlarePostcodeVisualState(target);
  };

  syncTarget();
  window.setTimeout(syncTarget, 80);
  window.setTimeout(syncTarget, 250);
}

function scheduleFlarePostcodePlaceholderSync() {
  flarePostcodePlaceholderTimers.forEach((timer) => window.clearTimeout(timer));
  flarePostcodePlaceholderTimers = [0, 100, 300, 750, 1500].map((delay) => {
    return window.setTimeout(() => {
      syncFlarePostcodePlaceholder();
      syncFlareFieldValueStates();
    }, delay);
  });
}

function installPersistedBundleFieldListeners() {
  document.addEventListener('input', handlePersistedBundleFieldMutation, true);
  document.addEventListener('change', handlePersistedBundleFieldMutation, true);
}

function installFlarePostcodePlaceholderHandlers() {
  syncFlarePostcodePlaceholder();
  document.addEventListener('input', handleFlarePostcodeVisualStateChange, true);
  document.addEventListener('change', handleFlarePostcodeVisualStateChange, true);
  document.addEventListener('blur', handleFlarePostcodeVisualStateChange, true);
  document.addEventListener('click', handleDeliveryPostcodeGateAction, true);
  document.addEventListener('submit', handleDeliveryPostcodeGateAction, true);
  document.addEventListener('keydown', handleDeliveryPostcodeGateKeydown, true);
  document.addEventListener('input', handleDeliveryPostcodeGateInput, true);
  document.addEventListener('change', handleDeliveryPostcodeGateInput, true);
  document.addEventListener('bcDc:lightboxOpening', scheduleFlarePostcodePlaceholderSync, true);
  document.addEventListener('bcDc:lightboxOpened', scheduleFlarePostcodePlaceholderSync, true);
}

function installHybridBundleDraftLifecycleHandlers() {
  if (!isHybridBundlePersistenceEnabled()) {
    return;
  }

  handleHybridBundleStartupCommand();

  document.addEventListener('click', handleHybridBundleDraftInteraction, true);
  document.addEventListener('input', handleHybridBundleDraftInteraction, true);
  document.addEventListener('change', handleHybridBundleDraftInteraction, true);
  document.addEventListener('bc:bundle-builder:before-remount', handleHybridBundleBeforeRemount, true);
  document.addEventListener('bc:bundle-builder:after-remount', handleHybridBundleAfterRemount, true);
  document.addEventListener('bc:bundle-builder:before-reveal', handleHybridBundleBeforeReveal, true);
  document.addEventListener('bc:bundle-builder:revealed', handleHybridBundleRevealed, true);
  document.addEventListener('bcDc:lightboxOpening', handleHybridBundleLightboxOpening, true);
  document.addEventListener('bcDc:lightboxClosing', handleHybridBundleLightboxClosing, true);
  document.addEventListener('bcDc:lightboxClosed', handleHybridBundleLightboxClosed, true);
  window.addEventListener('pagehide', handleHybridBundlePageHide);
  document.addEventListener('visibilitychange', handleHybridBundleVisibilityChange);
}

function handleHybridBundleStartupCommand() {
  if (getHybridBundlePersistenceMode() !== 'clear') {
    return;
  }

  clearHybridBundleDraft('query-clear', { clearLegacy: true });
}

function handleHybridBundleDraftInteraction(event) {
  if (!(event.target instanceof Element) || !event.target.closest('#bundling-app')) {
    return;
  }

  hybridBundleLastUserInteractionAt = Date.now();
  scheduleHybridBundleDraftCapture('interaction');
}

function handleHybridBundleBeforeRemount() {
  captureAndPersistHybridBundleDraft('before-remount', { force: true });
}

function handleHybridBundleAfterRemount() {
  requestHybridBundleDraftRestore('after-remount', { force: true });
}

function handleHybridBundleBeforeReveal() {
  requestHybridBundleDraftRestore('before-reveal', { force: true });
}

function handleHybridBundleRevealed() {
  if (hybridBundleDraftRestoreActive) {
    return;
  }

  requestHybridBundleDraftRestore('revealed', { force: false, allowMask: false });
}

function handleHybridBundleLightboxOpening() {
  scheduleFlarePostcodePlaceholderSync();
  captureAndPersistHybridBundleDraft('lightbox-opening', { force: true });
}

function handleHybridBundleLightboxClosing() {
  captureAndPersistHybridBundleDraft('lightbox-closing', { force: true });
}

function handleHybridBundleLightboxClosed() {
  requestHybridBundleDraftRestore('lightbox-closed', { force: true });
}

function handleHybridBundlePageHide() {
  captureAndPersistHybridBundleDraft('pagehide', { force: true, ttlMs: BUNDLE_HYBRID_CHECKOUT_DRAFT_TTL_MS });
}

function handleHybridBundleVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    captureAndPersistHybridBundleDraft('visibility-hidden', { force: true, ttlMs: BUNDLE_HYBRID_CHECKOUT_DRAFT_TTL_MS });
  }
}

function scheduleHybridBundleDraftCapture(source) {
  window.clearTimeout(hybridBundleDraftCaptureTimer);
  hybridBundleDraftCaptureSettleTimers.forEach((timer) => window.clearTimeout(timer));
  hybridBundleDraftCaptureSettleTimers = [];

  hybridBundleDraftCaptureTimer = window.setTimeout(() => {
    captureAndPersistHybridBundleDraft(source);

    BUNDLE_HYBRID_CAPTURE_SETTLE_DELAYS_MS.forEach((delay) => {
      const timer = window.setTimeout(() => {
        captureAndPersistHybridBundleDraft(`${source}-settled`);
      }, delay);

      hybridBundleDraftCaptureSettleTimers.push(timer);
    });
  }, BUNDLE_HYBRID_CAPTURE_DEBOUNCE_MS);
}

function bindBundleDrawerCloseHandler() {
  const drawer = getBundleDrawer();

  if (!(drawer instanceof HTMLElement) || drawer.dataset.bcDialogCloseBound === 'true') return;

  drawer.dataset.bcDialogCloseBound = 'true';
  drawer.addEventListener('dialog:close', handleBundleDrawerClose);
}

function handleBundleDrawerClose() {
  clearFlareUiReadinessCheck();
  disconnectFlareCalendarStateObserver();
  hideBundleDrawerLoadingState({ force: true });
  captureAndPersistHybridBundleDraft('drawer-close', { force: true });

  if (suppressBundleRollbackOnNextDrawerClose) {
    suppressBundleRollbackOnNextDrawerClose = false;
    return;
  }

  if (checkoutGiftFinalizationPromise) {
    invalidateCheckoutGiftFinalization();
  }

  if (isProceedingToCheckout) return;
  clearPendingBundleSession({ cartSnapshot: getLatestCartSnapshot() });
  restoreStoredBundleStateAfterDrawerClose();
}

function isBundleDrawerOpenWithoutFlareBlock() {
  if (!isBundleDrawerCurrentlyOpen()) {
    return false;
  }

  if (isNativeDeliveryEnabled()) {
    return !(getNativeDeliveryRoot() instanceof HTMLElement);
  }

  return !getFlareBlock();
}

function isBundleDrawerCurrentlyOpen() {
  const drawer = getBundleDrawer();
  const dialog = drawer?.querySelector('.cart-drawer__dialog[open]');

  return dialog instanceof HTMLDialogElement;
}

async function silentlyReopenBundleDrawerAfterMissingFlareBlock() {
  const drawer = getBundleDrawer();

  try {
    suppressBundleRollbackOnNextDrawerClose = true;

    if (typeof drawer?.closeDialog === 'function') {
      await drawer.closeDialog();
    } else if (typeof drawer?.close === 'function') {
      drawer.close();
    }
  } catch (error) {
    suppressBundleRollbackOnNextDrawerClose = false;
  }

  showBundleDrawerLoadingState();
  const cartSnapshot = await primeCartSnapshot({ force: true });
  const cartSignature = buildCartSnapshotSignature(cartSnapshot ?? getLatestCartSnapshot());

  await refreshBundleDrawer();
  lastRenderedDrawerCartSignature = cartSignature;

  const refreshedDrawer = getBundleDrawer();

  if (typeof refreshedDrawer?.open === 'function') {
    refreshedDrawer.open();
    beginBundleDrawerLoadingWindow();
  }

  runFlareEnhancementSequence();
  scheduleFlareUiReadinessCheck(cartSignature);
}

function prepareBundleCheckoutProceeding() {
  captureAndPersistHybridBundleDraft('checkout', { force: true, ttlMs: BUNDLE_HYBRID_CHECKOUT_DRAFT_TTL_MS });
  clearPendingBundleSession({ cartSnapshot: getLatestCartSnapshot() });
  isProceedingToCheckout = true;
  window.clearTimeout(checkoutIntentResetTimer);
  checkoutIntentResetTimer = window.setTimeout(() => {
    isProceedingToCheckout = false;
  }, CHECKOUT_INTENT_RESET_DELAY_MS);
}

function handleCheckoutSubmit(event) {
  const form = event.target;

  if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;

  const submitter = event.submitter;

  if (!(submitter instanceof HTMLElement) || submitter.getAttribute('name') !== 'checkout') {
    return;
  }

  submitter.dataset.bcCheckoutSubmitState = 'submitted';

  const nativeRoot = getNativeDeliveryRootForElement(submitter);

  if (isNativeDeliveryElement(nativeRoot) && handleNativeDeliveryCheckoutSubmit(event, nativeRoot, submitter)) {
    return;
  }

  if (handleNativeDeliveryCheckoutWithoutRoot(event, submitter)) {
    return;
  }

  const drawerDialog = getBundleDrawer()?.querySelector('.cart-drawer__dialog[open]');

  if (!(drawerDialog instanceof HTMLDialogElement)) return;

  if (
    !isBundleCheckoutGiftGateBypassActive &&
    !shouldBypassBundleCheckoutGiftGateForNativeNoDeliveryCart(submitter)
  ) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (!checkoutGiftFinalizationPromise) {
      const generation = checkoutLifecycleGeneration;
      setCheckoutGiftFinalizationBusy(true);
      const finalizationPromise = finalizeGiftStateBeforeCheckout(generation);
      checkoutGiftFinalizationPromise = finalizationPromise;
      void finalizationPromise.finally(() => {
        if (checkoutGiftFinalizationPromise === finalizationPromise) {
          checkoutGiftFinalizationPromise = null;
        }
      });
    }
    return;
  }

  isBundleCheckoutGiftGateBypassActive = false;

  prepareBundleCheckoutProceeding();
}

function setCheckoutGiftFinalizationBusy(isBusy) {
  const checkoutButton = getBundleCheckoutButton();

  if (!(checkoutButton instanceof HTMLButtonElement)) return;

  if (isBusy) {
    clearCheckoutGiftFinalizationMessage(checkoutButton);
    checkoutButton.setAttribute('aria-busy', 'true');
    checkoutButton.dataset.bcCheckoutSubmitState = 'finalizing-gifts';
    setNativeDeliveryCheckoutProgress(checkoutButton, true);
    return;
  }

  if (checkoutButton.getAttribute('aria-busy') !== 'true') {
    return;
  }

  checkoutButton.removeAttribute('aria-busy');
  delete checkoutButton.dataset.bcCheckoutSubmitState;
  setNativeDeliveryCheckoutProgress(checkoutButton, false);
}

function setNativeDeliveryCheckoutProgress(checkoutButton, isBusy) {
  if (!(checkoutButton instanceof HTMLButtonElement)) return;

  const text = checkoutButton.querySelector('.button-text');
  if (!(text instanceof HTMLElement)) return;

  if (isBusy) {
    if (!Object.prototype.hasOwnProperty.call(text.dataset, 'bcCheckoutOriginalText')) {
      text.dataset.bcCheckoutOriginalText = text.textContent || '';
    }
    text.textContent = 'Taking you to checkout…';
    return;
  }

  if (Object.prototype.hasOwnProperty.call(text.dataset, 'bcCheckoutOriginalText')) {
    text.textContent = text.dataset.bcCheckoutOriginalText || 'Checkout';
    delete text.dataset.bcCheckoutOriginalText;
  }
}

function clearCheckoutGiftFinalizationMessage(checkoutButton = getBundleCheckoutButton()) {
  const drawerDialog = checkoutButton?.closest('.cart-drawer__dialog[open]');
  drawerDialog?.querySelector('[data-bc-checkout-gift-error]')?.remove();
}

function showCheckoutGiftFinalizationMessage(checkoutButton = getBundleCheckoutButton()) {
  const drawerDialog = checkoutButton?.closest('.cart-drawer__dialog[open]');

  if (!(drawerDialog instanceof HTMLDialogElement) || !(checkoutButton instanceof HTMLButtonElement)) {
    return;
  }

  let message = drawerDialog.querySelector('[data-bc-checkout-gift-error]');

  if (!(message instanceof HTMLElement)) {
    message = document.createElement('p');
    message.dataset.bcCheckoutGiftError = 'true';
    message.setAttribute('role', 'alert');
    message.style.margin = '8px 0 0';
    message.style.color = '#a53a2d';
    message.style.fontSize = '14px';
    message.style.lineHeight = '1.35';
    message.style.textAlign = 'center';
    checkoutButton.insertAdjacentElement('afterend', message);
  }

  const cart = getLatestCartSnapshot();
  message.textContent = !Array.isArray(cart?.items) || Number(cart?.item_count) <= 0 || Number(cart?.total_price) <= 0
    ? 'We could not confirm your cart. Please close this window and add your bundle again.'
    : 'We could not confirm your free gifts. Please try checkout again.';
}

function invalidateCheckoutGiftFinalization() {
  checkoutLifecycleGeneration += 1;
  checkoutGiftFinalizationPromise = null;
  isBundleCheckoutGiftGateBypassActive = false;
  setCheckoutGiftFinalizationBusy(false);
}

function shouldHoldCheckoutForGiftReconcile() {
  if (pendingBreadBagGiftCartMutation || giftReconcileChainPromise) {
    return true;
  }

  if (breadBagGiftSyncState.lastError || guinnessCakesGiftSyncState.lastError) {
    return true;
  }

  if (breadBagGiftSyncState.lastAction === 'timeout' || guinnessCakesGiftSyncState.lastAction === 'timeout') {
    return true;
  }

  const cart = getLatestCartSnapshot();

  if (
    !Array.isArray(cart?.items) ||
    cart.items.length === 0 ||
    Number(cart?.item_count) <= 0 ||
    Number(cart?.total_price) <= 0
  ) {
    return true;
  }

  if (getTrackedBundleGroupIds(cart).size > 1) {
    return true;
  }

  const breadBagPlan = buildCottonBreadBagGiftPlan(cart, {
    source: 'checkout-gate',
    rewardSlot: SECOND_REWARD_CHOICE_SLOT,
  });
  const thirdRewardPlan = buildCottonBreadBagGiftPlan(cart, {
    source: 'checkout-gate',
    rewardSlot: THIRD_REWARD_CHOICE_SLOT,
  });

  if (['add', 'replace', 'remove', 'normalize'].includes(breadBagPlan.action) && !breadBagPlan.dryRun) {
    return true;
  }

  if (['add', 'replace', 'remove', 'normalize'].includes(thirdRewardPlan.action) && !thirdRewardPlan.dryRun) {
    return true;
  }

  if (shouldRunGuinnessCakesGiftReconcile(cart)) {
    const guinnessPlan = buildGuinnessCakesGiftPlan(cart, { source: 'checkout-gate' });

    if (['add', 'remove', 'normalize'].includes(guinnessPlan.action) && !guinnessPlan.dryRun) {
      return true;
    }
  }

  return false;
}

function hasTerminalGiftReconcileFailure() {
  return Boolean(
    breadBagGiftSyncState.lastError ||
      guinnessCakesGiftSyncState.lastError ||
      breadBagGiftSyncState.lastAction === 'timeout' ||
      guinnessCakesGiftSyncState.lastAction === 'timeout'
  );
}

function startCheckoutReturnGiftReconcileRetry() {
  if (
    checkoutReturnGiftRetryPromise ||
    Date.now() - lastCheckoutReturnGiftRetryAt < CHECKOUT_RETURN_GIFT_RETRY_COOLDOWN_MS
  ) {
    return checkoutReturnGiftRetryPromise;
  }

  lastCheckoutReturnGiftRetryAt = Date.now();
  window.clearTimeout(breadBagGiftCartMutationReconcileTimer);
  pendingBreadBagGiftCartMutation = null;

  const retryPromise = reconcileCottonBreadBagGiftBounded(null, {
    source: 'checkout-return-fallback:retry',
    forceFresh: true,
  })
    .then((cart) => removeDuplicateTrackedBundleGroups(cart ?? getLatestCartSnapshot()))
    .catch((error) => {
      console.error('Checkout-return gift reconciliation retry failed.', error);
      return null;
    })
    .finally(() => {
      if (checkoutReturnGiftRetryPromise === retryPromise) {
        checkoutReturnGiftRetryPromise = null;
      }
    });

  checkoutReturnGiftRetryPromise = retryPromise;
  return retryPromise;
}

function installBundleCheckoutReturnFallbackStatus() {
  window.bcBundleCheckoutReturnCartReady = function bcBundleCheckoutReturnCartReady(cart) {
    if (!Array.isArray(cart?.items) || Number(cart?.item_count) <= 0 || Number(cart?.total_price) <= 0) {
      return false;
    }

    if (getTrackedBundleGroupIds(cart).size !== 1) {
      return false;
    }

    if (!latestExpectedBundleManifest) {
      return false;
    }

    return isExpectedBundleCartReady(
      cart,
      latestExpectedBundleManifest.bundleId,
      latestExpectedBundleManifest.items
    );
  };

  window.bcBundleCheckoutReturnFallbackStatus = function bcBundleCheckoutReturnFallbackStatus() {
    if (
      needsHistoryRestoreCartResync &&
      !historyRestoreCartSnapshotPromise &&
      Date.now() - lastHistoryRestoreCartResyncAttemptAt >= 1000
    ) {
      startHistoryRestoreCartResync();
    }

    const cart = getLatestCartSnapshot();
    const hasCart = Array.isArray(cart?.items);
    const trackedGroupCount = hasCart ? getTrackedBundleGroupIds(cart).size : 0;
    const hasGiftRetryBlockingWork = Boolean(
      checkoutGiftFinalizationPromise ||
        pendingBreadBagGiftCartMutation ||
        giftReconcileChainPromise ||
        checkoutReturnGiftRetryPromise
    );

    if (
      hasCart &&
      trackedGroupCount === 1 &&
      hasTerminalGiftReconcileFailure() &&
      !hasGiftRetryBlockingWork
    ) {
      startCheckoutReturnGiftReconcileRetry();
    }

    const hasPendingWork = Boolean(
      hasPendingBundleAddPipeline ||
        pendingBundleAddPipelineCount > 0 ||
        hasPendingBundleSession() ||
        isPreparingPendingBundleSession ||
        pendingCartSnapshotPromise ||
        latestCartSnapshotPromise ||
        historyRestoreCartSnapshotPromise ||
        needsHistoryRestoreCartResync ||
        isBundleDrawerLoading ||
        checkoutGiftFinalizationPromise ||
        checkoutReturnGiftRetryPromise ||
        pendingBreadBagGiftCartMutation ||
        giftReconcileChainPromise ||
        isBundleDirectCheckoutPending
    );
    const needsGiftReconcile = hasCart ? shouldHoldCheckoutForGiftReconcile() : true;
    const manifestReady = hasCart ? window.bcBundleCheckoutReturnCartReady(cart) : false;

    return {
      ready: manifestReady && !hasPendingWork && !needsGiftReconcile,
      hasCart,
      trackedGroupCount,
      hasPendingWork,
      needsGiftReconcile,
      manifestReady,
    };
  };
}

async function finalizeGiftStateBeforeCheckout(generation, options) {
  options = options || {};
  const nativeFinalSubmit = Boolean(options.nativeFinalSubmit);
  let finalizationError = null;

  try {
    window.clearTimeout(breadBagGiftCartMutationReconcileTimer);
    pendingBreadBagGiftCartMutation = null;

    const freshCart = await fetchCartStateForBreadBagGift();
    if (!Array.isArray(freshCart?.items) || Number(freshCart?.item_count) <= 0 || Number(freshCart?.total_price) <= 0) {
      throw new Error('The cart could not be verified before checkout.');
    }

    const finalCart = await removeDuplicateTrackedBundleGroups(freshCart);
    const verification = await verifyBundleDirectCheckoutCart(finalCart);
    if (!verification.ok) {
      throw new Error(`Gift state could not be verified before checkout (${verification.reason}).`);
    }

    const confirmedCart = verification.cart;
    if (!Array.isArray(confirmedCart?.items) || Number(confirmedCart?.item_count) <= 0 || Number(confirmedCart?.total_price) <= 0) {
      throw new Error('The final cart could not be verified before checkout.');
    }
    setLatestCartSnapshot(confirmedCart);

    const confirmedBundleIds = getTrackedBundleGroupIds(confirmedCart);
    if (confirmedBundleIds.size !== 1) {
      throw new Error('Checkout requires exactly one verified bundle.');
    }
    if (
      latestExpectedBundleManifest &&
      !isExpectedBundleCartReady(
        confirmedCart,
        latestExpectedBundleManifest.bundleId,
        latestExpectedBundleManifest.items
      )
    ) {
      throw new Error('The bundle contents changed before checkout could be verified.');
    }

    const rewardGiftIssue = getRewardGiftCheckoutIssue(confirmedCart);
    if (rewardGiftIssue) {
      throw new Error(`A selected free gift could not be verified before checkout (${rewardGiftIssue.reason}).`);
    }
  } catch (error) {
    finalizationError = error;
    console.error('Pre-checkout gift finalisation failed; checkout remains blocked.', error);
  } finally {
    if (generation !== checkoutLifecycleGeneration) {
      return;
    }

    const form = document.getElementById('cart-form');
    const submitter = getBundleCheckoutButton();
    const drawerDialog = submitter?.closest('.cart-drawer__dialog[open]');

    if (finalizationError) {
      setCheckoutGiftFinalizationBusy(false);
      showCheckoutGiftFinalizationMessage(submitter);
      return;
    }

    if (
      !(form instanceof HTMLFormElement) ||
      !(submitter instanceof HTMLButtonElement) ||
      !(drawerDialog instanceof HTMLDialogElement)
    ) {
      setCheckoutGiftFinalizationBusy(false);
      console.error('Pre-checkout gift finalisation could not find the current checkout form.');
      return;
    }

    setCheckoutGiftFinalizationBusy(false);
    submitter.type = 'submit';
    submitter.name = 'checkout';
    submitter.setAttribute('form', form.id);

    if (submitter.form !== form) {
      console.error('Pre-checkout gift finalisation found a detached checkout button.');
      return;
    }

    if (nativeFinalSubmit && isNativeDeliveryEnabled()) {
      prepareBundleCheckoutProceeding();
      submitNativeDeliveryFinalForm(form, submitter);
      return;
    }

    isBundleCheckoutGiftGateBypassActive = true;

    try {
      form.requestSubmit(submitter);
    } catch (error) {
      console.error('Pre-checkout gift finalisation could not resubmit the current cart form.', error);
    } finally {
      isBundleCheckoutGiftGateBypassActive = false;
    }
  }
}

async function removeDuplicateTrackedBundleGroups(cart) {
  const currentCart = Array.isArray(cart?.items)
    ? cart
    : (await primeCartSnapshot({ force: true })) ?? getLatestCartSnapshot();

  if (getTrackedBundleGroupIds(currentCart).size <= 1) {
    return currentCart;
  }

  // /cart.js lists the most recently added lines first, so the first tracked item
  // belongs to the newest bundle group — keep that one.
  const keepGroupId = getTrackedBundleItems(currentCart)[0]?.properties?._rc_bundle;

  if (!keepGroupId) {
    return currentCart;
  }

  const removalUpdates = buildTrackedBundleRemovalUpdatesExcludingBundleIds(currentCart, new Set([keepGroupId]));

  if (!removalUpdates) {
    return currentCart;
  }

  try {
    const response = await getCartMutationFetch()(`${window.Shopify?.routes?.root ?? '/'}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ updates: removalUpdates }),
    });

    if (!response.ok) {
      throw new Error(`Duplicate bundle cleanup failed with status ${response.status}`);
    }

    const updatedCart = await response.json();
    setLatestCartSnapshot(updatedCart);

    return updatedCart;
  } catch (error) {
    console.error('Failed to remove duplicate bundle groups before checkout.', error);
    return currentCart;
  }
}

async function handleBundlePeekRemoveClick(event) {
  const removeLink =
    event.target instanceof Element
      ? event.target.closest('.bc-cart-peek-remove, .bundle-remove-button, .rc-bundle-item a[href*="/cart/update"], a[href*="/cart/update"]')
      : null;

  if (!(removeLink instanceof HTMLAnchorElement) || !isBundleRemoveLink(removeLink)) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  const updates = extractCartUpdatesFromUrl(removeLink.href);

  if (!updates) {
    window.location.href = removeLink.href;
    return;
  }

  try {
    const response = await getCartMutationFetch()(`${window.Shopify?.routes?.root ?? '/'}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ updates }),
    });

    if (!response.ok) {
      throw new Error(`Bundle removal failed with status ${response.status}`);
    }

    const updatedCart = await response.json();
    const reconciledCart =
      (await reconcileCottonBreadBagGiftBounded(updatedCart, {
        source: 'peek-remove',
        forceFresh: true,
      })) ?? updatedCart;

    setLatestCartSnapshot(reconciledCart);

    if (getTrackedBundleItems(reconciledCart).length === 0) {
      clearHybridBundleDraft('bundle-cart-removed', { clearLegacy: true });
    }

    if (refreshCartPageAfterBreadBagGiftChange()) {
      return;
    }

    await refreshBundleDrawer();
    runFlareEnhancementSequence();
  } catch (error) {
    console.error('Failed to remove item from cart drawer summary.', error);
    window.location.href = removeLink.href;
  }
}

function isBundleRemoveLink(link) {
  if (!(link instanceof HTMLAnchorElement)) {
    return false;
  }

  if (link.matches('.bc-cart-peek-remove, .bundle-remove-button')) {
    return true;
  }

  if (!link.closest('.rc-bundle-item')) {
    if (!isCartPage()) {
      return false;
    }

    const updates = extractCartUpdatesFromUrl(link.href);

    return Boolean(updates) && Object.values(updates).every((quantity) => quantity === 0);
  }

  const updates = extractCartUpdatesFromUrl(link.href);

  return Boolean(updates) && Object.values(updates).every((quantity) => quantity === 0);
}

function extractCartUpdatesFromUrl(urlString) {
  try {
    const url = new URL(urlString, window.location.origin);
    const updates = {};

    url.searchParams.forEach((value, key) => {
      const match = key.match(/^updates\[(.+)\]$/);

      if (!match) {
        return;
      }

      const quantity = Number(value);

      if (!Number.isFinite(quantity)) {
        return;
      }

      updates[match[1]] = quantity;
    });

    return Object.keys(updates).length > 0 ? updates : null;
  } catch (error) {
    return null;
  }
}

function requestHybridBundleDraftRestore(reason = 'unknown', { force = false, allowMask = true, skipCartPrime = false, requestedAt = Date.now() } = {}) {
  if (!isHybridBundlePersistenceEnabled()) {
    return false;
  }

  const draft = readHybridBundleDraft({ allowLegacy: true });

  if (!draft) {
    return false;
  }

  if (!skipCartPrime && shouldPrimeCartBeforeHybridBundleDraftRestore(draft)) {
    beginHybridBundleDraftRestoreWindow({ allowMask, requestedAt });
    const cartPromise = historyRestoreCartSnapshotPromise ?? primeCartSnapshot({ force: true });
    cartPromise.then((cartSnapshot) => {
      historyRestoreCartSnapshotPromise = null;

      if (!cartSnapshot?.items) {
        clearHybridBundleDraftRestoreSequence();
        logHybridBundlePersistenceDebug('restore-skipped-cart-prime-failed', { reason, draft: summarizeHybridBundleDraft(draft) });
        return;
      }

      hybridBundleFreshCartPrimeRequired = false;
      requestHybridBundleDraftRestore(reason, { force, allowMask, skipCartPrime: true, requestedAt });
    }).catch((error) => {
      historyRestoreCartSnapshotPromise = null;
      clearHybridBundleDraftRestoreSequence();
      logHybridBundlePersistenceDebug('restore-skipped-cart-prime-error', {
        reason,
        draft: summarizeHybridBundleDraft(draft),
        message: error instanceof Error ? error.message : String(error),
      });
    });
    return true;
  }

  if (skipCartPrime && !isHybridBundleDraftCartValidationSatisfied(draft)) {
    clearHybridBundleDraftRestoreSequence();
    logHybridBundlePersistenceDebug('restore-skipped-cart-token-unverified', { reason, draft: summarizeHybridBundleDraft(draft) });
    return false;
  }

  const effectiveForce = force || isHybridBundleRecoveryDraft(draft);

  if (hybridBundleDraftRestoredThisLifecycle && !effectiveForce && isCurrentRechargeStateDifferentFromDraft(draft)) {
    logHybridBundlePersistenceDebug('restore-skipped-lifecycle', { reason, draft: summarizeHybridBundleDraft(draft) });
    return false;
  }

  beginHybridBundleDraftRestoreWindow({ allowMask, requestedAt });

  BUNDLE_HYBRID_RESTORE_DELAYS_MS.forEach((delay, index) => {
    const restoreOptions = {
        reason,
        force: effectiveForce,
        isLastAttempt: index === BUNDLE_HYBRID_RESTORE_DELAYS_MS.length - 1,
      };

    if (delay === 0) {
      runHybridBundleDraftRestoreAttempt(draft, restoreOptions);
      return;
    }

    const timer = window.setTimeout(() => {
      runHybridBundleDraftRestoreAttempt(draft, restoreOptions);
    }, delay);

    hybridBundleDraftRestoreTimers.push(timer);
  });

  return true;
}

function beginHybridBundleDraftRestoreWindow({ allowMask = true, requestedAt = Date.now() } = {}) {
  clearHybridBundleDraftRestoreSequence({ unlock: false });
  hybridBundleDraftRestoreActive = true;
  hybridBundleDraftRestoreStartedAt = requestedAt;
  hybridBundleDraftRestoreMaskAllowed = allowMask;

  if (allowMask) {
    hybridBundleDraftRestoreMaskTimer = window.setTimeout(() => {
      if (hybridBundleDraftRestoreActive && isBundlePageVisuallyReadyForUnlock()) {
        setBundleInteractionLocked(true);
      }
    }, BUNDLE_HYBRID_RESTORE_MASK_DELAY_MS);
  }
}

function isHybridBundleRecoveryDraft(draft) {
  const recoverySources = new Set(['checkout', 'add-to-cart', 'pagehide', 'visibility-hidden', 'before-remount', 'lightbox-closing']);
  return recoverySources.has(String(draft?.source || ''));
}

function shouldPrimeCartBeforeHybridBundleDraftRestore(draft) {
  if (hybridBundleFreshCartPrimeRequired) {
    return true;
  }

  const latestCart = getLatestCartSnapshot();

  return Boolean(!latestCart || (draft?.cart?.token && !latestCart.token));
}

function isHybridBundleDraftCartValidationSatisfied(draft) {
  const latestCart = getLatestCartSnapshot();

  if (!latestCart) {
    return false;
  }

  if (draft?.cart?.token) {
    return Boolean(latestCart.token);
  }

  return Array.isArray(latestCart.items) && latestCart.items.length === 0;
}

function runHybridBundleDraftRestoreAttempt(draft, { reason = 'unknown', force = false, isLastAttempt = false } = {}) {
  if (!hybridBundleDraftRestoreActive) {
    return false;
  }

  if (!isHybridBundleDraftCompatible(draft)) {
    clearHybridBundleDraft('incompatible-restore', { clearLegacy: true });
    clearHybridBundleDraftRestoreSequence();
    logHybridBundlePersistenceDebug('restore-incompatible', { reason, draft: summarizeHybridBundleDraft(draft) });
    return false;
  }

  const snapshot = getHybridBundleDraftSnapshot(draft);

  if (!snapshot) {
    clearHybridBundleDraft('missing-snapshot', { clearLegacy: true });
    clearHybridBundleDraftRestoreSequence();
    return false;
  }

  if (force && hybridBundleLastUserInteractionAt > hybridBundleDraftRestoreStartedAt) {
    clearHybridBundleDraftRestoreSequence();
    logHybridBundlePersistenceDebug('restore-cancelled-user-interaction', { reason, draft: summarizeHybridBundleDraft(draft) });
    return false;
  }

  if (hybridBundleDraftRestoreMaskAllowed && isBundlePageVisuallyReadyForUnlock()) {
    setBundleInteractionLocked(true);
  }

  if (hasPersistedBundleStateAlreadyRestored(snapshot)) {
    hybridBundleDraftRestoredThisLifecycle = true;
    hybridBundleDraftEmptyClearSuppressedUntil = 0;
    clearHybridBundleDraftRestoreSequence();
    logHybridBundlePersistenceDebug('restore-already-matched', { reason, draft: summarizeHybridBundleDraft(draft) });
    return true;
  }

  if (!isBundlePageReadyForPersistedStateRestore()) {
    if (isLastAttempt) {
      clearHybridBundleDraftRestoreSequence();
      logHybridBundlePersistenceDebug('restore-timeout-not-ready', { reason, draft: summarizeHybridBundleDraft(draft) });
    }

    return false;
  }

  if (isCurrentRechargeStateDifferentFromDraft(draft) && !force) {
    clearHybridBundleDraftRestoreSequence();
    logHybridBundlePersistenceDebug('restore-skipped-current-state', { reason, draft: summarizeHybridBundleDraft(draft) });
    return false;
  }

  const restored = restoreRechargeBundleState(snapshot);

  if (restored) {
    hybridBundleDraftRestoredThisLifecycle = true;
    hybridBundleDraftEmptyClearSuppressedUntil = 0;
    logHybridBundlePersistenceDebug('restore-applied', { reason, draft: summarizeHybridBundleDraft(draft) });
  }

  if (isLastAttempt) {
    clearHybridBundleDraftRestoreSequence();
  }

  return restored;
}

function clearHybridBundleDraftRestoreSequence({ unlock = true } = {}) {
  window.clearTimeout(hybridBundleDraftRestoreMaskTimer);
  hybridBundleDraftRestoreMaskTimer = 0;
  hybridBundleDraftRestoreMaskAllowed = false;
  hybridBundleDraftRestoreTimers.forEach((timer) => window.clearTimeout(timer));
  hybridBundleDraftRestoreTimers = [];
  hybridBundleDraftRestoreActive = false;
  hybridBundleDraftRestoreStartedAt = 0;

  if (unlock) {
    setBundleInteractionLocked(false);
  }
}

function restoreStoredBundleStateAfterDrawerClose() {
  const storedSnapshot = readRestorableBundleStateSnapshot();
  const currentCart = getLatestCartSnapshot();

  if (!storedSnapshot || (getTrackedBundleItems(currentCart).length === 0 && !readHybridBundleDraft({ allowLegacy: false }))) {
    return;
  }

  pendingBundleStateSnapshot = storedSnapshot;
  requestHybridBundleDraftRestore('drawer-close-restore', { force: true });
}


function schedulePersistedBundleStateRestore() {
  requestHybridBundleDraftRestore('persisted-state', { force: isBundleHistoryRestoreNavigation() });
}

function initializePersistedBundleStateRestore() {
  if (!isHybridBundlePersistenceEnabled()) {
    if (isBundleHistoryRestoreNavigation()) {
      schedulePersistedBundleStateRestore();
      return;
    }

    clearStoredBundleStateSnapshot();
    return;
  }

  const draft = readHybridBundleDraft({ allowLegacy: true });

  if (draft) {
    requestHybridBundleDraftRestore('initial-load', { force: isBundleHistoryRestoreNavigation() });
    return;
  }

  if (!isBundleHistoryRestoreNavigation()) {
    clearStoredBundleStateSnapshot();
  }
}

function isBundleHistoryRestoreNavigation() {
  const navigationEntry = window.performance?.getEntriesByType?.('navigation')?.[0];
  return navigationEntry?.type === 'back_forward';
}


function clearBundleInteractionUnlockSequence() {
  bundleInteractionUnlockTimers.forEach((timer) => {
    window.clearTimeout(timer);
  });

  bundleInteractionUnlockTimers = [];
}

function getVisibleRechargeBundleSummaryElement() {
  const candidates = Array.from(
    document.querySelectorAll('[data-testid="bundle-summary-desktop"], [data-testid="bundle-summary-mobile"]')
  );

  return (
    candidates.find((element) => {
      if (!(element instanceof HTMLElement) || !element.isConnected || !isElementVisible(element)) {
        return false;
      }

      const rect = element.getBoundingClientRect();
      return element.getClientRects().length > 0 && rect.width > 0 && rect.height > 0;
    }) ?? null
  );
}

function normalizeVisibleBundleText(text = '') {
  return String(text)
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function isBundlePageVisuallyReadyForUnlock() {
  const summaryElement = getVisibleRechargeBundleSummaryElement();

  if (!(summaryElement instanceof HTMLElement)) {
    return false;
  }

  const summaryRect = summaryElement.getBoundingClientRect();

  if (summaryElement.getClientRects().length === 0 || summaryRect.width <= 0 || summaryRect.height <= 0) {
    return false;
  }

  const summaryText = normalizeVisibleBundleText(summaryElement.textContent ?? '');

  if (!summaryText) {
    return false;
  }

  return Array.from(summaryElement.querySelectorAll('button, [role="button"]')).some((element) => {
    if (!(element instanceof HTMLElement) || !isElementVisible(element)) {
      return false;
    }

    const rect = element.getBoundingClientRect();
    return element.getClientRects().length > 0 && rect.width > 0 && rect.height > 0;
  });
}

function hasVisibleRechargeBundleProductControls() {
  return Array.from(
    document.querySelectorAll(
      '#bundling-app button[type="button"], #bundling-app button, #bundling-app [data-testid="increment"], #bundling-app [data-testid="decrement"]'
    )
  ).some((element) => {
    return element instanceof HTMLElement && isElementVisible(element);
  });
}

function isBundlePageReadyForPersistedStateRestore() {
  const bundleApp = document.getElementById('bundling-app');

  if (!(bundleApp instanceof HTMLElement) || !bundleApp.isConnected) {
    return false;
  }

  return isBundlePageVisuallyReadyForUnlock() && hasVisibleRechargeBundleProductControls();
}

function hasPersistedBundleStateAlreadyRestored(snapshot) {
  if (!snapshot) {
    return false;
  }

  const bundleStateFiber = findRechargeBundleStateFiber();

  if (!bundleStateFiber) {
    return false;
  }

  return doesBundleStateMatchSnapshot(bundleStateFiber, snapshot);
}

function installBundlePageResumeHandler() {
  window.addEventListener('pageshow', handleBundlePageShow);
}

function handleBundlePageShow(event) {
  const navigationEntry = window.performance?.getEntriesByType?.('navigation')?.[0];
  const isHistoryRestore = event.persisted || navigationEntry?.type === 'back_forward';

  if (isHistoryRestore) {
    nativeDeliveryFinalSubmitInFlight = false;
    invalidateCheckoutGiftFinalization();
    setNativeDeliveryCheckoutProgress(getBundleCheckoutButton(), false);
    isProceedingToCheckout = false;
    clearPendingBundleSession({ cartSnapshot: getLatestCartSnapshot() });
    dismissBundleDrawer();
    needsHistoryRestoreCartResync = true;
    hybridBundleFreshCartPrimeRequired = true;
    startHistoryRestoreCartResync();
  }

  requestHybridBundleDraftRestore(isHistoryRestore ? 'pageshow-history' : 'pageshow', { force: isHistoryRestore });
}

function startHistoryRestoreCartResync() {
  if (historyRestoreCartSnapshotPromise) {
    return historyRestoreCartSnapshotPromise;
  }

  lastHistoryRestoreCartResyncAttemptAt = Date.now();
  const historySnapshotPromise = primeCartSnapshot({ force: true });
  historyRestoreCartSnapshotPromise = historySnapshotPromise;
  void historySnapshotPromise
    .then((cart) => {
      if (historyRestoreCartSnapshotPromise === historySnapshotPromise && Array.isArray(cart?.items)) {
        needsHistoryRestoreCartResync = false;
        if (isNativeDeliveryEnabled()) {
          applyNativeDeliveryCheckoutGuard(getNativeDeliveryRoot());
        }
      }
    })
    .finally(() => {
      if (historyRestoreCartSnapshotPromise === historySnapshotPromise) {
        historyRestoreCartSnapshotPromise = null;
      }
    });

  return historySnapshotPromise;
}

function dismissBundleDrawer() {
  clearFlareUiReadinessCheck();
  disconnectFlareCalendarStateObserver();
  hideBundleDrawerLoadingState({ force: true });
  const drawer = getBundleDrawer();

  if (typeof drawer?.close === 'function') {
    drawer.close();
  }
}

async function fetchCartState() {
  try {
    const response = await fetchBreadBagGiftCartRequest(`${window.Shopify?.routes?.root ?? '/'}cart.js`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) return null;

    return response.json();
  } catch (error) {
    return null;
  }
}

function setLatestCartSnapshot(cart) {
  if (!Array.isArray(cart?.items)) {
    return false;
  }

  latestCartSnapshot = cloneCartState(cart);
  latestCartSnapshotVersion += 1;
  return true;
}

function primeCartSnapshot({ force = false } = {}) {
  if (latestCartSnapshotPromise && !force) {
    return latestCartSnapshotPromise;
  }

  const versionAtRequest = latestCartSnapshotVersion;
  const nextSnapshotPromise = fetchCartState()
    .then((cart) => {
      if (cart?.items) {
        // Only accept this response if nothing newer landed while it was in flight,
        // so a slow /cart.js read can never roll back a fresher mutation result.
        if (latestCartSnapshotVersion === versionAtRequest) {
          setLatestCartSnapshot(cart);
        }

        return getLatestCartSnapshot();
      }

      return force ? null : latestCartSnapshot;
    })
    .finally(() => {
      if (latestCartSnapshotPromise === nextSnapshotPromise) {
        latestCartSnapshotPromise = null;
      }
    });

  latestCartSnapshotPromise = nextSnapshotPromise;
  return nextSnapshotPromise;
}

function getLatestCartSnapshot() {
  return cloneCartState(latestCartSnapshot);
}

function beginPendingBundleSession({ beforeCartSnapshot = null } = {}) {
  pendingBundleSessionState = 'primed';
  setBundleInteractionLocked(true);
  pendingBeforeCartSnapshot = cloneCartState(beforeCartSnapshot);
  pendingCartSnapshotPromise = Promise.resolve(pendingBeforeCartSnapshot);

  window.clearTimeout(primedPendingBundleSessionResetTimer);
  primedPendingBundleSessionResetTimer = window.setTimeout(() => {
    if (!hasPrimedPendingBundleSession()) {
      return;
    }

    clearPendingBundleSession();
    primeCartSnapshot({ force: true });
  }, PRIMED_SESSION_RESET_DELAY_MS);
}

function markPendingBundleSessionActive() {
  if (!hasPendingBundleSession()) return;

  window.clearTimeout(primedPendingBundleSessionResetTimer);
  pendingBundleSessionState = 'active';
}

function hasPendingBundleSession() {
  return pendingBundleSessionState !== 'idle';
}

function hasPrimedPendingBundleSession() {
  return pendingBundleSessionState === 'primed';
}

function hasActivePendingBundleSession() {
  return pendingBundleSessionState === 'active';
}

function clearPendingBundleSession({ cartSnapshot = null, unlock = true } = {}) {
  window.clearTimeout(primedPendingBundleSessionResetTimer);
  clearBundleInteractionUnlockSequence();
  pendingBundleSessionState = 'idle';
  pendingBeforeCartSnapshot = null;
  pendingCartSnapshotPromise = null;
  pendingBundleStateSnapshot = null;

  if (cartSnapshot?.items) {
    setLatestCartSnapshot(cartSnapshot);
  }

  if (!isProceedingToCheckout) {
    setBundleDirectCheckoutPending(false);
  }

  if (unlock) {
    setBundleInteractionLocked(false);
  }
}

function setBundleInteractionLocked(locked) {
  const bundleApp = document.getElementById('bundling-app');

  if (!(bundleApp instanceof HTMLElement)) return;

  if (locked) {
    activateBundleMotionMask();
    bundleApp.style.minHeight = `${bundleApp.offsetHeight}px`;
    bundleApp.style.overflow = 'hidden';
    bundleApp.setAttribute('data-bc-session-pending', 'true');
  } else {
    bundleApp.style.removeProperty('min-height');
    bundleApp.style.removeProperty('overflow');
    bundleApp.removeAttribute('data-bc-session-pending');
    deactivateBundleMotionMask();
  }
}

function captureRechargeBundleState() {
  const bundleStateFiber = findRechargeBundleStateFiber();

  if (!bundleStateFiber) return null;

  const stateHook = getHookAt(bundleStateFiber, 0);
  const variantHook = getHookAt(bundleStateFiber, 1);
  const sellingPlanHook = getHookAt(bundleStateFiber, 2);
  const productsHook = getHookAt(bundleStateFiber, 3);
  const activeFiltersHook = getHookAt(bundleStateFiber, 5);

  if (
    typeof stateHook?.queue?.dispatch !== 'function' ||
    typeof variantHook?.queue?.dispatch !== 'function' ||
    typeof sellingPlanHook?.queue?.dispatch !== 'function' ||
    typeof productsHook?.queue?.dispatch !== 'function'
  ) {
    return null;
  }

  const selectedProducts = cloneStateValue(productsHook.memoizedState);

  if (!Array.isArray(selectedProducts) || selectedProducts.length === 0) {
    return null;
  }

  return {
    state: cloneStateValue(stateHook.memoizedState),
    selectedVariantId: cloneStateValue(variantHook.memoizedState),
    selectedSellingPlanId: cloneStateValue(sellingPlanHook.memoizedState),
    selectedProducts,
    activeFilters: cloneStateValue(activeFiltersHook?.memoizedState ?? {}),
  };
}

function restoreRechargeBundleState(snapshot) {
  if (!snapshot) return false;

  const bundleStateFiber = findRechargeBundleStateFiber();

  if (!bundleStateFiber) return false;

  if (doesBundleStateMatchSnapshot(bundleStateFiber, snapshot)) {
    return true;
  }

  const stateHook = getHookAt(bundleStateFiber, 0);
  const variantHook = getHookAt(bundleStateFiber, 1);
  const sellingPlanHook = getHookAt(bundleStateFiber, 2);
  const productsHook = getHookAt(bundleStateFiber, 3);
  const activeFiltersHook = getHookAt(bundleStateFiber, 5);

  const requiredDispatches = [
    dispatchHookState(stateHook, cloneStateValue(snapshot.state)),
    dispatchHookState(variantHook, cloneStateValue(snapshot.selectedVariantId)),
    dispatchHookState(sellingPlanHook, cloneStateValue(snapshot.selectedSellingPlanId)),
    dispatchHookState(productsHook, cloneStateValue(snapshot.selectedProducts)),
  ];
  const optionalFiltersDispatched =
    typeof activeFiltersHook?.queue?.dispatch !== 'function' ||
    dispatchHookState(activeFiltersHook, cloneStateValue(snapshot.activeFilters));

  return requiredDispatches.every(Boolean) && optionalFiltersDispatched;
}

function dispatchHookState(hook, value) {
  if (typeof hook?.queue?.dispatch !== 'function') return false;

  try {
    hook.queue.dispatch(value);
    return true;
  } catch (error) {
    console.error('Failed to dispatch Recharge bundle state.', error);
    return false;
  }
}

function doesBundleStateMatchSnapshot(bundleStateFiber, snapshot) {
  const stateHook = getHookAt(bundleStateFiber, 0);
  const variantHook = getHookAt(bundleStateFiber, 1);
  const sellingPlanHook = getHookAt(bundleStateFiber, 2);
  const productsHook = getHookAt(bundleStateFiber, 3);
  const activeFiltersHook = getHookAt(bundleStateFiber, 5);

  return (
    areEquivalentStateValues(stateHook?.memoizedState, snapshot.state) &&
    areEquivalentStateValues(variantHook?.memoizedState, snapshot.selectedVariantId) &&
    areEquivalentStateValues(sellingPlanHook?.memoizedState, snapshot.selectedSellingPlanId) &&
    serializeStateValue(productsHook?.memoizedState) === serializeStateValue(snapshot.selectedProducts) &&
    serializeStateValue(activeFiltersHook?.memoizedState ?? {}) === serializeStateValue(snapshot.activeFilters)
  );
}

function findRechargeBundleStateFiber() {
  const rootFiber = getRechargeRootFiber();

  if (!rootFiber) return null;

  const stack = [rootFiber];
  const visited = new Set();

  while (stack.length > 0) {
    const fiber = stack.pop();

    if (!fiber || visited.has(fiber)) continue;

    visited.add(fiber);

    if (isRechargeBundleStateFiber(fiber)) {
      return fiber;
    }

    if (fiber.alternate) stack.push(fiber.alternate);
    if (fiber.sibling) stack.push(fiber.sibling);
    if (fiber.child) stack.push(fiber.child);
  }

  return null;
}

function getRechargeRootFiber() {
  const bundlingApp = document.getElementById('bundling-app');

  if (!(bundlingApp instanceof HTMLElement)) return null;

  const reactKey = Object.keys(bundlingApp).find((key) => {
    return key.startsWith('__reactContainer$') || key.startsWith('__reactFiber$');
  });

  if (!reactKey) return null;

  const reactNode = bundlingApp[reactKey];

  return reactNode?.current ?? reactNode ?? null;
}

function isRechargeBundleStateFiber(fiber) {
  const stateHook = getHookAt(fiber, 0);
  const variantHook = getHookAt(fiber, 1);
  const sellingPlanHook = getHookAt(fiber, 2);
  const productsHook = getHookAt(fiber, 3);
  const activeFiltersHook = getHookAt(fiber, 5);

  return (
    typeof stateHook?.queue?.dispatch === 'function' &&
    typeof variantHook?.queue?.dispatch === 'function' &&
    typeof sellingPlanHook?.queue?.dispatch === 'function' &&
    typeof productsHook?.queue?.dispatch === 'function' &&
    typeof stateHook.memoizedState === 'string' &&
    isNumericIdentifier(variantHook.memoizedState) &&
    isNumericIdentifier(sellingPlanHook.memoizedState) &&
    isRechargeSelectedProductsArray(productsHook.memoizedState) &&
    (
      activeFiltersHook == null ||
      (
        typeof activeFiltersHook.queue?.dispatch === 'function' &&
        activeFiltersHook.memoizedState != null &&
        typeof activeFiltersHook.memoizedState === 'object' &&
        !Array.isArray(activeFiltersHook.memoizedState)
      )
    )
  );
}

function isNumericIdentifier(value) {
  return typeof value === 'string' && /^\d+$/.test(value);
}

function isRechargeSelectedProductsArray(value) {
  return (
    Array.isArray(value) &&
    (
      value.length === 0 ||
      value.every((item) => {
        const normalizedItem = normalizeRechargeBundleSelectedProduct(item);

        return Boolean(normalizedItem?.productId && normalizedItem?.variantId && normalizedItem.quantity > 0);
      })
    )
  );
}

function getHookAt(fiber, index) {
  let hook = fiber?.memoizedState ?? null;
  let currentIndex = 0;

  while (hook && currentIndex < index) {
    hook = hook.next;
    currentIndex += 1;
  }

  return hook;
}

function cloneStateValue(value) {
  if (value == null) return value;
  if (typeof value !== 'object') return value;

  if (typeof window.structuredClone === 'function') {
    try {
      return window.structuredClone(value);
    } catch (error) {
      // Fall through to JSON cloning.
    }
  }

  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    return value;
  }
}

function serializeStateValue(value) {
  try {
    return JSON.stringify(value);
  } catch (error) {
    return '';
  }
}

function areEquivalentStateValues(currentValue, snapshotValue) {
  if (currentValue === snapshotValue) {
    return true;
  }

  return serializeStateValue(currentValue) === serializeStateValue(snapshotValue);
}

function getFlareBlock() {
  return getBundleDrawer()?.querySelector('#flare-date-picker-block') || null;
}

function getUnitedKingdomCountryOption(countrySelect) {
  if (!(countrySelect instanceof HTMLSelectElement)) {
    return null;
  }

  return Array.from(countrySelect.options).find((option) => {
    return GB_COUNTRY_MATCHER.test(option.value) || GB_COUNTRY_LABEL_MATCHER.test(option.textContent ?? '');
  }) || null;
}

function canPrefillFlarePostcode(flareBlock = getFlareBlock()) {
  if (!(flareBlock instanceof HTMLElement)) {
    return true;
  }

  const countryContainer = flareBlock.querySelector('.country-container');
  const countrySelect = flareBlock.querySelector('#delivery-country');

  if (!(countryContainer instanceof HTMLElement) || !(countrySelect instanceof HTMLSelectElement)) {
    return true;
  }

  const countryOption = getUnitedKingdomCountryOption(countrySelect);

  if (!countryOption) {
    return true;
  }

  return countrySelect.value === countryOption.value;
}

function isFlarePostcodePrefillReady(flareBlock = getFlareBlock()) {
  if (!(flareBlock instanceof HTMLElement)) {
    return false;
  }

  const dateAnchor = getFlareDateSelectorAnchor(flareBlock);
  const shipDateInput = flareBlock.querySelector('#ship-date');

  return isLaidOutVisibleElement(dateAnchor) && shipDateInput instanceof HTMLInputElement;
}

function selectUnitedKingdom(flareBlock, { postcodeInput, shipDateInput } = {}) {
  const countryContainer = flareBlock.querySelector('.country-container');
  const countrySelect = flareBlock.querySelector('#delivery-country');

  if (!(countryContainer instanceof HTMLElement) || !(countrySelect instanceof HTMLSelectElement)) {
    return true;
  }

  const countryOption = getUnitedKingdomCountryOption(countrySelect);

  if (!countryOption) {
    return true;
  }

  if (hasFlareUserInput(postcodeInput, shipDateInput)) {
    flareBlock.dataset.bcCountryAutoSelectionLocked = 'true';
  }

  if (
    countrySelect.value !== countryOption.value &&
    countrySelect.dataset.bcCountryAutoSelected !== 'true' &&
    flareBlock.dataset.bcCountryAutoSelectionLocked !== 'true'
  ) {
    countrySelect.dataset.bcCountryAutoSelected = 'true';
    countrySelect.value = countryOption.value;
    countrySelect.dispatchEvent(new Event('input', { bubbles: true }));
    countrySelect.dispatchEvent(new Event('change', { bubbles: true }));
    return false;
  }

  if (countrySelect.value === countryOption.value) {
    countryContainer.style.display = 'none';
    return true;
  }

  return false;
}

function hasFlareUserInput(postcodeInput, shipDateInput) {
  return (
    Boolean(postcodeInput?.value?.trim()) ||
    Boolean(shipDateInput?.value?.trim()) ||
    document.activeElement === postcodeInput ||
    document.activeElement === shipDateInput
  );
}

function decorateStepLabel(label, stepNumber) {
  if (!(label instanceof HTMLElement)) return;

  const originalText = label.dataset.bcOriginalText ?? sanitizeStepLabelText(label.textContent);

  if (originalText) {
    label.dataset.bcOriginalText = originalText;
  }

  label.classList.add('bc-bundle-step-label');
  label.dataset.bcStepNumber = stepNumber;

  const existingStep = label.querySelector('.step-number');
  const existingText = label.querySelector('.bc-step-label-text');

  if (existingStep instanceof HTMLElement && existingText instanceof HTMLElement) {
    const isAlreadyDecorated = existingStep.textContent === stepNumber && existingText.textContent === originalText;

    if (isAlreadyDecorated) {
      return;
    }
  }

  let step = existingStep;

  if (!(step instanceof HTMLElement)) {
    step = document.createElement('span');
    step.className = 'step-number';
  }

  step.textContent = stepNumber;

  let labelText = existingText;

  if (!(labelText instanceof HTMLElement)) {
    labelText = document.createElement('span');
    labelText.className = 'bc-step-label-text';
  }

  labelText.textContent = originalText;

  if (existingStep instanceof HTMLElement && existingStep !== step) {
    existingStep.remove();
  }

  if (existingText instanceof HTMLElement && existingText !== labelText) {
    existingText.remove();
  }

  if (
    label.childElementCount === 2 &&
    label.firstElementChild === step &&
    label.lastElementChild === labelText &&
    labelText.textContent === originalText
  ) {
    return;
  }

  label.replaceChildren(step, labelText);
}

function normalizeBundleStepSections(flareBlock, bundleDrawer = getBundleDrawer()) {
  if (!(flareBlock instanceof HTMLElement)) {
    return;
  }

  ensureBundleStepSection(flareBlock, [flareBlock.querySelector('.zip-code-container')], 'bc-bundle-step-section--postcode');

  const dateLabel = flareBlock.querySelector(':scope > .date-selector-label') || flareBlock.querySelector('.bc-bundle-step-section--date .date-selector-label');
  const dateAnchor = getFlareDateSelectorAnchor(flareBlock);
  ensureBundleStepSection(flareBlock, [dateLabel, dateAnchor], 'bc-bundle-step-section--date');

  const timeSlotContainer = flareBlock.querySelector('#time-slot-container');
  if (timeSlotContainer instanceof HTMLElement && isElementVisible(timeSlotContainer)) {
    ensureBundleStepSection(flareBlock, [timeSlotContainer], 'bc-bundle-step-section--time');
  }

  const bundleNote = bundleDrawer?.querySelector('.cart__bundle-note');
  if (bundleNote instanceof HTMLElement) {
    bundleNote.classList.add('bc-bundle-step-section', 'bc-bundle-step-section--safe-place');
  }
}

function ensureBundleStepSection(parent, nodes, sectionClassName) {
  if (!(parent instanceof HTMLElement)) {
    return null;
  }

  const elements = Array.from(
    new Set(nodes.filter((node) => node instanceof HTMLElement))
  );

  if (!elements.length) {
    return null;
  }

  const existingSection = parent.querySelector(`:scope > .${sectionClassName}`);

  if (existingSection instanceof HTMLElement) {
    const movableElements = elements.filter((element) => {
      return element !== existingSection && !existingSection.contains(element) && !element.contains(existingSection);
    });

    const needsMove = movableElements.some((element) => element.parentElement !== existingSection);

    if (needsMove) {
      movableElements.forEach((element) => existingSection.appendChild(element));
    }

    existingSection.classList.add('bc-bundle-step-section', sectionClassName);
    return existingSection;
  }

  if (elements.length === 1) {
    elements[0].classList.add('bc-bundle-step-section', sectionClassName);
    return elements[0];
  }

  const firstElement = elements[0];
  const section = document.createElement('div');
  section.className = `bc-bundle-step-section ${sectionClassName}`;
  parent.insertBefore(section, firstElement);
  elements.forEach((element) => section.appendChild(element));
  return section;
}

function sanitizeStepLabelText(rawLabelText = '') {
  return rawLabelText
    .replace(/^\s*\d+\s*[\.\):-]?\s*/, '')
    .replace(/\s*\*+\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function isElementVisible(element) {
  if (!(element instanceof HTMLElement)) return false;

  const styles = window.getComputedStyle(element);
  return styles.display !== 'none' && styles.visibility !== 'hidden';
}

function isLaidOutVisibleElement(element) {
  if (!(element instanceof HTMLElement) || !isElementVisible(element)) {
    return false;
  }

  if (element.getAttribute('aria-disabled') === 'true') {
    return false;
  }

  if ('disabled' in element && element.disabled) {
    return false;
  }

  const rect = element.getBoundingClientRect();
  return element.getClientRects().length > 0 && rect.width > 0 && rect.height > 0;
}

function isFlareDeliveryUiReady() {
  if (isNativeDeliveryEnabled()) {
    return isNativeDeliveryVisualStateReady();
  }

  const flareBlock = getFlareBlock();
  const postcodeLabel = flareBlock?.querySelector('.zip-code-label');
  const postcodeInput = flareBlock?.querySelector('#delivery-zip-code');
  const dateLabel = flareBlock?.querySelector('.date-selector-label');
  const dateAnchor = getFlareDateSelectorAnchor(flareBlock);

  return (
    isLaidOutVisibleElement(postcodeLabel) &&
    isLaidOutVisibleElement(postcodeInput) &&
    isLaidOutVisibleElement(dateLabel) &&
    isLaidOutVisibleElement(dateAnchor)
  );
}

function getBundleCheckoutButton(bundleDrawer = getBundleDrawer()) {
  return bundleDrawer?.querySelector('#checkout') || null;
}

function getFlareTimeSlotSelect(flareBlock = getFlareBlock()) {
  return flareBlock?.querySelector('#time-slot-select') || flareBlock?.querySelector('.time-slot-select-wrapper select') || null;
}

function isFlareTimeSlotSelectionPending(flareBlock = getFlareBlock()) {
  if (!(flareBlock instanceof HTMLElement)) {
    return false;
  }

  const timeSlotContainer = flareBlock.querySelector('#time-slot-container');
  const timeSlotSelect = getFlareTimeSlotSelect(flareBlock);

  if (!(timeSlotContainer instanceof HTMLElement) || !(timeSlotSelect instanceof HTMLSelectElement)) {
    return false;
  }

  if (!isElementVisible(timeSlotContainer)) {
    return false;
  }

  if (timeSlotSelect.getAttribute('aria-disabled') === 'true' || timeSlotSelect.disabled) {
    return false;
  }

  const hasSelectableOption = Array.from(timeSlotSelect.options).some((option) => {
    return !option.disabled && String(option.value ?? '').trim();
  });

  if (!hasSelectableOption) {
    return false;
  }

  return !String(timeSlotSelect.value ?? '').trim();
}

function hasVisibleFlareDateSelection(flareBlock = getFlareBlock()) {
  const dateAnchor = getFlareDateSelectorAnchor(flareBlock);

  if (!(dateAnchor instanceof HTMLElement) || !isLaidOutVisibleElement(dateAnchor)) {
    return false;
  }

  const dateText = normalizeVisibleBundleText(dateAnchor.textContent ?? '');

  if (!dateText) {
    return false;
  }

  return !/^select\s+a\s+date$/i.test(dateText);
}

function isBundleCheckoutBlockedForVisiblePrefill(flareBlock = getFlareBlock(), bundleDrawer = getBundleDrawer()) {
  if (isNativeDeliveryEnabled()) {
    return false;
  }

  if (!(flareBlock instanceof HTMLElement)) {
    return false;
  }

  const checkoutButton = getBundleCheckoutButton(bundleDrawer);
  const postcodeInput = flareBlock.querySelector('#delivery-zip-code');
  const shipDateInput = flareBlock.querySelector('#ship-date');
  const dateAnchor = getFlareDateSelectorAnchor(flareBlock);
  const hasShipDateValue = shipDateInput instanceof HTMLInputElement && Boolean(shipDateInput.value.trim());
  const hasVisibleDateSelection = hasShipDateValue || hasVisibleFlareDateSelection(flareBlock);

  if (!(checkoutButton instanceof HTMLButtonElement) || !checkoutButton.disabled) {
    return false;
  }

  if (!(postcodeInput instanceof HTMLInputElement)) {
    return false;
  }

  if (!isLaidOutVisibleElement(postcodeInput) || !isLaidOutVisibleElement(dateAnchor)) {
    return false;
  }

  if (isFlareTimeSlotSelectionPending(flareBlock)) {
    return false;
  }

  return Boolean(postcodeInput.value.trim()) && hasVisibleDateSelection;
}

function hasFlareFallbackScaffold(flareBlock = getFlareBlock()) {
  if (!(flareBlock instanceof HTMLElement)) {
    return false;
  }

  const postcodeContainer = flareBlock.querySelector('.zip-code-container');
  const dateLabel = flareBlock.querySelector('.date-selector-label');
  const dateAnchor = getFlareDateSelectorAnchor(flareBlock);
  const safePlaceInput = document.getElementById('cart-note');

  return (
    postcodeContainer instanceof HTMLElement &&
    dateLabel instanceof HTMLElement &&
    dateAnchor instanceof HTMLElement &&
    safePlaceInput instanceof HTMLTextAreaElement
  );
}

function isFlareVisualStateReady(flareBlock = getFlareBlock()) {
  if (isNativeDeliveryEnabled()) {
    return isNativeDeliveryVisualStateReady();
  }

  if (!(flareBlock instanceof HTMLElement)) {
    return false;
  }

  const postcodeLabel = flareBlock.querySelector('.zip-code-label');
  const postcodeInput = flareBlock.querySelector('#delivery-zip-code');
  const dateLabel = flareBlock.querySelector('.date-selector-label');
  const dateAnchor = getFlareDateSelectorAnchor(flareBlock);

  if (
    !isLaidOutVisibleElement(postcodeLabel) ||
    !isLaidOutVisibleElement(postcodeInput) ||
    !isLaidOutVisibleElement(dateLabel) ||
    !isLaidOutVisibleElement(dateAnchor)
  ) {
    return false;
  }

  if (isBundleCheckoutBlockedForVisiblePrefill(flareBlock)) {
    return false;
  }

  const calendar = flareBlock.querySelector('.flatpickr-calendar');

  if (!(calendar instanceof HTMLElement) || !isElementVisible(calendar)) {
    return true;
  }

  return hasRenderedFlareCalendar(calendar);
}

function installFlareCalendarStateObserver(flareBlock = getFlareBlock()) {
  if (!(flareBlock instanceof HTMLElement)) {
    disconnectFlareCalendarStateObserver();
    return;
  }

  if (flareCalendarStateObserver) {
    syncFlareCalendarPresentation(flareBlock);
    return;
  }

  flareCalendarStateObserver = new MutationObserver(() => {
    window.requestAnimationFrame(() => {
      applyBundleFlarePresentation(flareBlock);
      syncFlareCalendarPresentation(flareBlock);
    });
  });

  flareCalendarStateObserver.observe(flareBlock, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'aria-hidden'],
  });

  syncFlareCalendarPresentation(flareBlock);
}

function disconnectFlareCalendarStateObserver() {
  flareCalendarStateObserver?.disconnect();
  flareCalendarStateObserver = null;
  flareCalendarObservedNode = null;
  teardownFlareCalendarPopover();

  const drawer = getBundleDrawer();

  if (drawer instanceof HTMLElement) {
    drawer.removeAttribute('data-bc-calendar-open');
  }
}

function syncFlareCalendarPresentation(flareBlock = getFlareBlock()) {
  const drawer = getBundleDrawer();

  if (!(drawer instanceof HTMLElement)) {
    return;
  }

  const calendar =
    flareBlock?.querySelector('.flatpickr-calendar') ||
    flareCalendarPortaledNode ||
    document.querySelector('.flatpickr-calendar.open');
  const calendarOpen = hasRenderedFlareCalendar(calendar);

  if (calendarOpen) {
    drawer.setAttribute('data-bc-calendar-open', 'true');
    presentFlareCalendarPopover(flareBlock, calendar);
  } else {
    drawer.removeAttribute('data-bc-calendar-open');
    teardownFlareCalendarPopover();
  }
}

function installFlareCalendarPortalHandlers() {
  if (!flareCalendarPortalDocumentHandlerInstalled) {
    document.addEventListener('click', handleFlareCalendarPortalTrigger, true);
    document.addEventListener('focusin', handleFlareCalendarPortalTrigger, true);
    flareCalendarPortalDocumentHandlerInstalled = true;
  }
}

function handleFlareCalendarPortalTrigger(event) {
  const target = event.target;
  const drawer = getBundleDrawer();

  if (!(target instanceof Element) || !(drawer instanceof HTMLElement) || !drawer.contains(target)) {
    return;
  }

  if (!target.closest('#date-selector-wrapper, .date-selector, .date-dropdown, #ship-date')) {
    return;
  }

  scheduleFlareCalendarPortalSync();
}

function scheduleFlareCalendarPortalSync() {
  [0, 50, 150, 300, 600, 1000, 1500].forEach((delay) => {
    window.setTimeout(() => {
      const flareBlock = getFlareBlock();
      const calendar =
        flareBlock?.querySelector('.flatpickr-calendar.open') ||
        document.querySelector('.flatpickr-calendar.open');

      if (calendar instanceof HTMLElement && hasRenderedFlareCalendar(calendar)) {
        presentFlareCalendarPopover(flareBlock, calendar);
        return;
      }

      syncFlareCalendarPresentation(flareBlock);
    }, delay);
  });
}

function hasRenderedFlareCalendar(calendar) {
  if (!(calendar instanceof HTMLElement) || !isElementVisible(calendar)) {
    return false;
  }

  const dayCount = calendar.querySelectorAll('.flatpickr-day').length;
  const weekdayCount = calendar.querySelectorAll('.flatpickr-weekday').length;

  return dayCount >= 7 && weekdayCount >= 7;
}

function presentFlareCalendarPopover(flareBlock = getFlareBlock(), calendar = flareBlock?.querySelector('.flatpickr-calendar')) {
  const dialog = getBundleDrawerDialog();
  const anchor = getFlareDateSelectorAnchor(flareBlock);

  if (!(dialog instanceof HTMLElement) || !(anchor instanceof HTMLElement) || !(calendar instanceof HTMLElement)) {
    return;
  }

  if (flareCalendarPortaledNode !== calendar) {
    teardownFlareCalendarPopover({ preserveObserver: true });
  }

  if (!(flareCalendarPortalHost instanceof HTMLElement)) {
    flareCalendarPortalHost = document.createElement('div');
    flareCalendarPortalHost.className = 'bc-flare-calendar-popover';
    dialog.appendChild(flareCalendarPortalHost);
  } else if (flareCalendarPortalHost.parentElement !== dialog) {
    dialog.appendChild(flareCalendarPortalHost);
  }

  if (flareCalendarPortaledNode !== calendar) {
    flareCalendarOriginalParent = calendar.parentNode;
    flareCalendarOriginalNextSibling = calendar.nextSibling;
    flareCalendarPortaledNode = calendar;
    flareCalendarPortaledNode.classList.add('bc-flare-calendar--popover');
    flareCalendarPortalHost.appendChild(flareCalendarPortaledNode);
  }

  observeFlareCalendarNode(flareCalendarPortaledNode);
  flareCalendarPortalHost.hidden = false;
  installFlareCalendarPositionListeners();
  requestFlareCalendarPopoverPosition();
}

function teardownFlareCalendarPopover({ preserveObserver = false } = {}) {
  teardownFlareCalendarPositionListeners();

  if (flareCalendarPortaledNode instanceof HTMLElement) {
    flareCalendarPortaledNode.classList.remove('bc-flare-calendar--popover');

    if (flareCalendarOriginalParent instanceof Node && flareCalendarOriginalParent.isConnected) {
      if (flareCalendarOriginalNextSibling instanceof Node && flareCalendarOriginalNextSibling.parentNode === flareCalendarOriginalParent) {
        flareCalendarOriginalParent.insertBefore(flareCalendarPortaledNode, flareCalendarOriginalNextSibling);
      } else {
        flareCalendarOriginalParent.appendChild(flareCalendarPortaledNode);
      }
    } else {
      flareCalendarPortaledNode.remove();
    }
  }

  flareCalendarPortaledNode = null;
  flareCalendarOriginalParent = null;
  flareCalendarOriginalNextSibling = null;

  if (flareCalendarPortalHost instanceof HTMLElement) {
    flareCalendarPortalHost.remove();
  }

  flareCalendarPortalHost = null;

  if (!preserveObserver) {
    flareCalendarObservedNode = null;
  }
}

function observeFlareCalendarNode(calendar) {
  if (!(flareCalendarStateObserver instanceof MutationObserver) || !(calendar instanceof HTMLElement)) {
    return;
  }

  if (flareCalendarObservedNode === calendar) {
    return;
  }

  flareCalendarStateObserver.observe(calendar, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'aria-hidden'],
  });

  flareCalendarObservedNode = calendar;
}

function getFlareDateSelectorAnchor(flareBlock = getFlareBlock()) {
  const scope = flareBlock instanceof HTMLElement ? flareBlock : getBundleDrawer();

  if (!(scope instanceof HTMLElement)) {
    return null;
  }

  return (
    scope.querySelector('#flare-date-picker-block #date-selector-wrapper') ||
    scope.querySelector('#flare-date-picker-block .date-content') ||
    scope.querySelector('#flare-date-picker-block .date-selector')
  );
}

function installFlareCalendarPositionListeners() {
  const content = getBundleDrawerContent();

  if (content instanceof HTMLElement && flareCalendarScrollContainer !== content) {
    teardownFlareCalendarPositionListeners();
    flareCalendarScrollContainer = content;
    flareCalendarScrollContainer.addEventListener('scroll', requestFlareCalendarPopoverPosition, { passive: true });
    window.addEventListener('resize', requestFlareCalendarPopoverPosition, { passive: true });
  }
}

function teardownFlareCalendarPositionListeners() {
  if (flareCalendarScrollContainer instanceof HTMLElement) {
    flareCalendarScrollContainer.removeEventListener('scroll', requestFlareCalendarPopoverPosition);
  }

  flareCalendarScrollContainer = null;
  window.removeEventListener('resize', requestFlareCalendarPopoverPosition);
  window.cancelAnimationFrame(flareCalendarPositionRaf);
  flareCalendarPositionRaf = 0;
}

function requestFlareCalendarPopoverPosition() {
  if (flareCalendarPositionRaf) {
    return;
  }

  flareCalendarPositionRaf = window.requestAnimationFrame(() => {
    flareCalendarPositionRaf = 0;
    positionFlareCalendarPopover();
  });
}

function positionFlareCalendarPopover() {
  const dialog = getBundleDrawerDialog();
  const flareBlock = getFlareBlock();
  const anchor = getFlareDateSelectorAnchor(flareBlock);
  const host = flareCalendarPortalHost;
  const calendar = flareCalendarPortaledNode;

  if (!(dialog instanceof HTMLElement) || !(anchor instanceof HTMLElement) || !(host instanceof HTMLElement) || !(calendar instanceof HTMLElement)) {
    return;
  }

  const dialogRect = dialog.getBoundingClientRect();
  const anchorRect = anchor.getBoundingClientRect();
  const horizontalPadding = 16;
  const verticalPadding = 16;
  const maxWidth = Math.max(280, dialogRect.width - horizontalPadding * 2);
  const popoverWidth = Math.min(360, maxWidth);
  const relativeAnchorCenter = anchorRect.left - dialogRect.left + anchorRect.width / 2;
  const minLeft = horizontalPadding;
  const maxLeft = Math.max(horizontalPadding, dialogRect.width - popoverWidth - horizontalPadding);
  const left = clampNumber(relativeAnchorCenter - popoverWidth / 2, minLeft, maxLeft);

  host.style.width = `${popoverWidth}px`;
  host.style.left = `${left}px`;
  host.style.visibility = 'hidden';
  host.style.display = 'block';

  const measuredHeight = host.offsetHeight || calendar.offsetHeight || 336;
  const relativeAnchorTop = anchorRect.top - dialogRect.top;
  const relativeAnchorBottom = anchorRect.bottom - dialogRect.top;
  const availableBelow = dialogRect.height - relativeAnchorBottom - verticalPadding;
  const availableAbove = relativeAnchorTop - verticalPadding;
  const placeAbove = availableAbove > availableBelow && availableAbove >= Math.min(measuredHeight, 240);
  const top = placeAbove
    ? Math.max(verticalPadding, relativeAnchorTop - measuredHeight - 10)
    : Math.min(dialogRect.height - measuredHeight - verticalPadding, relativeAnchorBottom + 10);

  host.dataset.placement = placeAbove ? 'top' : 'bottom';
  host.style.top = `${Math.max(verticalPadding, top)}px`;
  host.style.visibility = '';
}

function clampNumber(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function captureHybridBundleDeliveryState() {
  if (isNativeDeliveryEnabled()) {
    const root = getNativeDeliveryRoot();
    const state = getNativeDeliveryState(root);
    const input = getNativeDeliveryInput(root);
    const safePlaceInput = document.getElementById('cart-note');

    return {
      postcode: state?.postcode || (input instanceof HTMLInputElement ? normalizePostcode(input.value) : ''),
      country: 'GB',
      shipDate: state?.date || '',
      timeSlot: '',
      safePlace: safePlaceInput instanceof HTMLTextAreaElement ? String(safePlaceInput.value || '').trim().slice(0, 280) : readCookie(STORED_SAFE_PLACE_COOKIE_NAME),
    };
  }

  const flareBlock = getFlareBlock();
  const postcodeInput = flareBlock?.querySelector('#delivery-zip-code');
  const countrySelect = flareBlock?.querySelector('#delivery-country');
  const shipDateInput = flareBlock?.querySelector('#ship-date');
  const timeSlotSelect = getFlareTimeSlotSelect(flareBlock);
  const safePlaceInput = document.getElementById('cart-note');

  return {
    postcode: postcodeInput instanceof HTMLInputElement ? normalizePostcode(postcodeInput.value) : readCookie(STORED_POSTCODE_COOKIE_NAME),
    country: countrySelect instanceof HTMLSelectElement ? countrySelect.value : '',
    shipDate: shipDateInput instanceof HTMLInputElement ? String(shipDateInput.value || '').trim() : '',
    timeSlot: timeSlotSelect instanceof HTMLSelectElement ? timeSlotSelect.value : '',
    safePlace: safePlaceInput instanceof HTMLTextAreaElement ? String(safePlaceInput.value || '').trim().slice(0, 280) : readCookie(STORED_SAFE_PLACE_COOKIE_NAME),
  };
}

function restoreHybridBundleDeliveryState(delivery = {}) {
  if (!delivery || typeof delivery !== 'object') {
    return false;
  }

  if (isNativeDeliveryEnabled()) {
    const root = getNativeDeliveryRoot();
    const input = getNativeDeliveryInput(root);
    let didRestore = false;

    if (root instanceof HTMLElement && input instanceof HTMLInputElement) {
      didRestore = setFieldValueIfNeeded(input, delivery.postcode) || didRestore;
      updateNativeDeliveryPostcode(root);

      if (delivery.shipDate && root.dataset.bcDeliveryDay) {
        const dateValue = String(delivery.shipDate);

        if (isNativeDeliveryDateAvailable(root.dataset.bcDeliveryDay, dateValue)) {
          root.dataset.bcDeliveryDate = dateValue;
          renderNativeDeliveryDates(root, root.dataset.bcDeliveryDay, dateValue);
          syncNativeDeliveryAttributeInputs(root);
        }
      }
    }

    const safePlaceInput = document.getElementById('cart-note');
    didRestore = setFieldValueIfNeeded(safePlaceInput, delivery.safePlace) || didRestore;
    return didRestore;
  }

  const flareBlock = getFlareBlock();
  let didRestore = false;

  if (flareBlock instanceof HTMLElement) {
    const countrySelect = flareBlock.querySelector('#delivery-country');
    const postcodeInput = flareBlock.querySelector('#delivery-zip-code');
    const shipDateInput = flareBlock.querySelector('#ship-date');
    const timeSlotSelect = getFlareTimeSlotSelect(flareBlock);

    didRestore = setSelectValueIfNeeded(countrySelect, delivery.country) || didRestore;
    didRestore = setFieldValueIfNeeded(postcodeInput, delivery.postcode) || didRestore;
    didRestore = setFieldValueIfNeeded(shipDateInput, delivery.shipDate) || didRestore;
    didRestore = setSelectValueIfNeeded(timeSlotSelect, delivery.timeSlot) || didRestore;
    syncFlareFieldValueStates(flareBlock);
  }

  const safePlaceInput = document.getElementById('cart-note');
  didRestore = setFieldValueIfNeeded(safePlaceInput, delivery.safePlace) || didRestore;

  return didRestore;
}

function handlePersistedBundleFieldMutation(event) {
  if (!isNativeDeliveryElement(event.target)) {
    return;
  }

  if (isNativeDeliveryInput(event.target) && event.target.id === 'delivery-zip-code') {
    persistFlarePostcode(event.target.value);
    return;
  }

  if (isNativeDeliveryInput(event.target) && event.target.matches('[data-bc-native-delivery-postcode]')) {
    const result = getNativeDeliveryPostcodeResult(event.target.value);

    if (result.allowed) {
      persistFlarePostcode(result.normalized);
    }
    return;
  }

  if (event.target instanceof HTMLTextAreaElement && event.target.id === 'cart-note') {
    persistSafePlace(event.target.value);
  }
}

function applyPersistedFlareFieldPrefills(flareBlock = getFlareBlock(), { allowPostcode = true } = {}) {
  const postcodeInput = flareBlock?.querySelector('#delivery-zip-code');
  const safePlaceInput = document.getElementById('cart-note');

  const didPrefillPostcode =
    allowPostcode && isFlarePostcodePrefillReady(flareBlock) ? prefillStoredFlarePostcode(postcodeInput) : false;
  const didPrefillSafePlace = prefillStoredSafePlace(safePlaceInput);

  syncFlareFieldValueStates(flareBlock);

  return didPrefillPostcode || didPrefillSafePlace;
}

function requestPersistedFlareFieldSync(flareBlock = getFlareBlock()) {
  window.cancelAnimationFrame(flareFieldSyncRaf);
  flareFieldSyncRaf = window.requestAnimationFrame(() => {
    flareFieldSyncRaf = 0;
    applyPersistedFlareFieldPrefills(flareBlock, { allowPostcode: canPrefillFlarePostcode(flareBlock) });
  });
}

function prefillStoredFlarePostcode(postcodeInput) {
  if (!(postcodeInput instanceof HTMLInputElement)) {
    return false;
  }

  if (!isLaidOutVisibleElement(postcodeInput)) {
    return false;
  }

  if (postcodeInput.value.trim() || document.activeElement === postcodeInput) {
    return false;
  }

  const storedPostcode = readCookie(STORED_POSTCODE_COOKIE_NAME);

  if (!storedPostcode) {
    return false;
  }

  return setFieldValueIfNeeded(postcodeInput, storedPostcode);
}

function persistFlarePostcode(rawPostcode) {
  const normalizedPostcode = normalizePostcode(rawPostcode);

  if (!normalizedPostcode) {
    return false;
  }

  if (!isDeliveryZonePostcodeAllowed(normalizedPostcode)) {
    clearCookie(STORED_POSTCODE_COOKIE_NAME);
    return false;
  }

  try {
    document.cookie = `${STORED_POSTCODE_COOKIE_NAME}=${encodeURIComponent(normalizedPostcode)}; path=/; max-age=31536000; SameSite=Lax`;
    return true;
  } catch (error) {
    return false;
  }
}

function prefillStoredSafePlace(safePlaceInput) {
  if (!(safePlaceInput instanceof HTMLTextAreaElement)) {
    return false;
  }

  if (safePlaceInput.value.trim() || document.activeElement === safePlaceInput) {
    return false;
  }

  const storedSafePlace = readCookie(STORED_SAFE_PLACE_COOKIE_NAME);

  if (!storedSafePlace) {
    return false;
  }

  return setFieldValueIfNeeded(safePlaceInput, storedSafePlace);
}

function persistSafePlace(rawSafePlace) {
  const normalizedSafePlace = String(rawSafePlace ?? '').trim().slice(0, 280);

  if (!normalizedSafePlace) {
    clearCookie(STORED_SAFE_PLACE_COOKIE_NAME);
    return false;
  }

  try {
    document.cookie = `${STORED_SAFE_PLACE_COOKIE_NAME}=${encodeURIComponent(normalizedSafePlace)}; path=/; max-age=31536000; SameSite=Lax`;
    return true;
  } catch (error) {
    return false;
  }
}

function readCookie(name) {
  try {
    const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  } catch (error) {
    return '';
  }
}

function clearCookie(name) {
  try {
    document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
  } catch (error) {
    return false;
  }

  return true;
}

function normalizePostcode(rawPostcode) {
  const normalizedPostcode = String(rawPostcode ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toUpperCase();

  return normalizedPostcode;
}

function setFieldValueIfNeeded(element, nextValue) {
  if (!(element instanceof HTMLInputElement) && !(element instanceof HTMLTextAreaElement)) {
    return false;
  }

  const normalizedNextValue = String(nextValue ?? '');

  if (!normalizedNextValue.trim()) {
    return false;
  }

  if (element.value === normalizedNextValue) {
    return false;
  }

  const valueDescriptor = Object.getOwnPropertyDescriptor(
    element instanceof HTMLTextAreaElement ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype,
    'value'
  );

  try {
    if (typeof valueDescriptor?.set === 'function') {
      valueDescriptor.set.call(element, normalizedNextValue);
    } else {
      element.value = normalizedNextValue;
    }
  } catch (error) {
    element.value = normalizedNextValue;
  }

  element.dispatchEvent(new Event('input', { bubbles: true }));
  element.dispatchEvent(new Event('change', { bubbles: true }));

  return true;
}

function setSelectValueIfNeeded(element, nextValue) {
  if (!(element instanceof HTMLSelectElement)) {
    return false;
  }

  const normalizedNextValue = String(nextValue ?? '');

  if (!normalizedNextValue.trim() || element.value === normalizedNextValue) {
    return false;
  }

  const hasOption = Array.from(element.options).some((option) => option.value === normalizedNextValue);

  if (!hasOption) {
    return false;
  }

  element.value = normalizedNextValue;
  element.dispatchEvent(new Event('input', { bubbles: true }));
  element.dispatchEvent(new Event('change', { bubbles: true }));

  return true;
}

function buildCartSnapshotSignature(cart) {
  if (!Array.isArray(cart?.items)) {
    return '';
  }

  const itemSignature = cart.items
    .map((item) => {
      const sellingPlanAllocation = item?.selling_plan_allocation;
      const sellingPlanId = sellingPlanAllocation?.selling_plan?.id ?? '';
      const lineDiscountSignature = (item?.line_level_discount_allocations || [])
        .map((allocation) => `${allocation?.discount_application?.key || allocation?.discount_application?.title || ''}:${Number(allocation?.amount) || 0}`)
        .sort()
        .join(',');
      const giftMarker = item?.properties?._bc_free_gift ?? '';
      return `${item?.key ?? ''}:${Number(item?.quantity) || 0}:${Number(item?.final_line_price) || 0}:${sellingPlanId}:${Number(sellingPlanAllocation?.price) || 0}:${Number(sellingPlanAllocation?.compare_at_price) || 0}:${giftMarker}:${lineDiscountSignature}`;
    })
    .sort()
    .join('|');

  const discountSignature = Array.isArray(cart?.cart_level_discount_applications)
    ? cart.cart_level_discount_applications
        .map((discount) => {
          const application = discount?.discount_application || discount;
          const amount = Number(discount?.amount ?? discount?.total_allocated_amount ?? application?.total_allocated_amount) || 0;
          return `${application?.title ?? ''}:${amount}`;
        })
        .sort()
        .join('|')
    : '';

  return `${itemSignature}::${Number(cart?.total_price) || 0}:${Number(cart?.original_total_price) || 0}::${discountSignature}`;
}

function formatBundleDrawerCartTotal(totalPence) {
  if (totalPence === null || totalPence === undefined || totalPence === '') {
    return '';
  }

  const normalizedPence = Number(totalPence);

  if (!Number.isFinite(normalizedPence) || normalizedPence < 0) {
    return '';
  }

  const amount = normalizedPence / 100;
  const currency = String(window.Shopify?.currency?.active || 'GBP').toUpperCase();

  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    return `£${amount.toFixed(2)}`;
  }
}

function calculateOptimisticSubscriptionDiscountTotal(detail) {
  const subtotalPence = Number(detail?.subtotalPence);
  const preservedCartTotalPence = Number(detail?.preservedCartTotalPence ?? 0);
  const firstOrderOfferPence = Number(detail?.firstOrderOfferPence);
  if (
    detail?.optimisticPriceEligible !== true ||
    !Number.isFinite(subtotalPence) ||
    subtotalPence < 0 ||
    !Number.isFinite(preservedCartTotalPence) ||
    preservedCartTotalPence < 0
  ) {
    return null;
  }

  if (
    detail?.purchaseMode === 'subscription' &&
    Number.isFinite(firstOrderOfferPence) &&
    firstOrderOfferPence > 0
  ) {
    return preservedCartTotalPence + Math.max(0, Math.round(firstOrderOfferPence));
  }

  const discountCode = String(detail?.discountCode || '').trim().toUpperCase();
  if (
    detail?.purchaseMode === 'subscription' &&
    STANDARD_SUBSCRIPTION_DISCOUNT_CODES.has(discountCode)
  ) {
    return preservedCartTotalPence + Math.max(0, Math.round(subtotalPence * (1 - STANDARD_SUBSCRIPTION_DISCOUNT_RATE)));
  }

  return preservedCartTotalPence + subtotalPence;
}

function renderBundleDrawerDiscountSummary({ discountCodes = [], discountSavingsPence = 0, giftCount = 0 } = {}, { preserveRenderedGifts = false } = {}) {
  const drawer = getBundleDrawer();
  const wrapper = drawer?.querySelector('.cart__bundle-discount-summary');

  if (!(wrapper instanceof HTMLElement)) {
    return false;
  }

  const seenCodes = new Set();
  const codes = discountCodes.reduce((result, code) => {
    const normalizedCode = String(code || '').trim();
    const normalizedKey = normalizedCode.toUpperCase();
    if (!normalizedCode || /^[-–—]+$/.test(normalizedCode) || seenCodes.has(normalizedKey)) return result;
    seenCodes.add(normalizedKey);
    result.push(normalizedCode);
    return result;
  }, []);
  const normalizedSavings = Math.max(0, Number(discountSavingsPence) || 0);
  const normalizedGiftCount = Math.max(0, Number(giftCount) || 0);
  const root = wrapper.querySelector('.cart-discount--bundle-peek');
  const codeBenefit = wrapper.querySelector('.cart-discount__bundle-code-benefit');
  const codeGroup = wrapper.querySelector('.cart-discount__bundle-code-group');
  const codeText = wrapper.querySelector('.cart-discount__bundle-code-text');
  const additional = wrapper.querySelector('.cart-discount__bundle-code-caption');
  const savings = wrapper.querySelector('.cart-discount__bundle-savings');
  const gifts = wrapper.querySelector('.cart-discount__bundle-gifts');

  if (![root, codeBenefit, codeGroup, codeText, additional, savings, gifts].every((node) => node instanceof HTMLElement)) {
    return false;
  }

  const keepExistingGifts = preserveRenderedGifts && gifts.hidden === false;
  const showGifts = normalizedGiftCount > 0 || keepExistingGifts;
  const showCode = codes.length > 0 && normalizedSavings > 0;
  root.classList.toggle('cart-discount--has-code', showCode);
  codeBenefit.hidden = !showCode;
  codeGroup.hidden = !showCode;
  codeText.textContent = showCode ? codes[0] : '';
  additional.textContent = showCode && codes.length > 1 ? `+${codes.length - 1} more` : '';
  additional.hidden = !showCode || codes.length <= 1;
  savings.textContent = showCode ? `Extra ${formatBundleDrawerCartTotal(normalizedSavings)} off` : '';
  savings.hidden = !showCode;

  if (normalizedGiftCount > 0) {
    gifts.textContent = normalizedGiftCount === 1 ? '1 free gift included' : `${normalizedGiftCount} free gifts included`;
  }
  gifts.hidden = !showGifts;

  const hasBenefits = showCode || showGifts;
  root.hidden = !hasBenefits;
  wrapper.hidden = !hasBenefits;
  return true;
}

function readVerifiedBundleDrawerDiscountSummary(cart) {
  const discountCodes = [];
  let discountSavingsPence = 0;
  let automaticSavingsPence = 0;
  let subscriptionSavingsPence = 0;
  let oneTimeAnchorPence = 0;
  let giftCount = 0;
  let giftValuePence = 0;
  const cartLevelDiscountKeys = new Set();

  (cart?.cart_level_discount_applications || []).forEach((discount) => {
    const application = discount?.discount_application || discount;
    if (application?.type !== 'discount_code') return;
    const amount = Number(discount?.amount ?? discount?.total_allocated_amount ?? application?.total_allocated_amount) || 0;
    if (amount <= 0) return;
    const title = String(application.title || '').trim();
    const identity = String(application.key || `${application.type}:${title.toUpperCase()}`);
    if (title) discountCodes.push(title);
    cartLevelDiscountKeys.add(identity);
    discountSavingsPence += amount;
  });

  (cart?.items || []).forEach((item) => {
    const properties = item?.properties || {};
    const rewardSlot = Number(properties._bc_reward_slot) || 0;
    const isManagedGift = String(properties._bc_free_gift || '').startsWith('bundle-choice:') &&
      properties._bc_free_gift_source === 'bundle-gift-picker' &&
      [3000, 4000, 5000].includes(rewardSlot) &&
      Number(item?.final_line_price) === 0;

    (item?.line_level_discount_allocations || []).forEach((allocation) => {
      const application = allocation?.discount_application;
      const amount = Number(allocation.amount) || 0;
      if (amount <= 0) return;
      if (application?.type === 'automatic' && !isManagedGift) {
        automaticSavingsPence += amount;
        return;
      }
      if (application?.type !== 'discount_code') return;
      const title = String(application.title || '').trim();
      const identity = String(application.key || `${application.type}:${title.toUpperCase()}`);
      if (cartLevelDiscountKeys.has(identity)) return;
      if (title) discountCodes.push(title);
      discountSavingsPence += amount;
    });


    if (!isManagedGift && Number(item?.final_line_price) > 0) {
      const allocation = item?.selling_plan_allocation;
      const compareAtPrice = Number(allocation?.compare_at_price);
      const sellingPlanPrice = Number(allocation?.price);
      const quantity = Math.max(0, Number(item?.quantity) || 0);
      const originalLinePrice = Math.max(0, Number(item?.original_line_price) || 0);

      if (Number.isFinite(compareAtPrice) && compareAtPrice > 0 && quantity > 0) {
        oneTimeAnchorPence += compareAtPrice * quantity;
      } else {
        oneTimeAnchorPence += originalLinePrice;
      }

      if (Number.isFinite(compareAtPrice) && Number.isFinite(sellingPlanPrice) && compareAtPrice > sellingPlanPrice) {
        subscriptionSavingsPence += (compareAtPrice - sellingPlanPrice) * quantity;
      }
    }

    if (isManagedGift) {
      giftCount += Math.max(1, Number(item.quantity) || 1);
      giftValuePence += Number(item.original_line_price) || 0;
    }
  });

  return { discountCodes, discountSavingsPence, automaticSavingsPence, subscriptionSavingsPence, oneTimeAnchorPence, giftCount, giftValuePence };
}

function applyOptimisticCartTotalToBundleDrawer(totalPricePence, detail = {}) {
  const formattedTotal = formatBundleDrawerCartTotal(totalPricePence);
  const optimisticSubscriptionSavingsPence = Math.max(0, Number(detail.subscriptionSavingsPence) || 0);
  const optimisticCodeSavingsPence = Math.max(
    0,
    Number(detail.subtotalPence) + Number(detail.preservedCartTotalPence ?? 0) - Number(totalPricePence)
  );
  const optimisticOriginalTotalPence = Number(totalPricePence) + optimisticCodeSavingsPence + optimisticSubscriptionSavingsPence;
  const formattedOriginalTotal = formatBundleDrawerCartTotal(optimisticOriginalTotalPence);
  const drawer = getBundleDrawer();

  if (!(drawer instanceof HTMLElement)) {
    return false;
  }

  const presenter = drawer.querySelector('[data-bc-cart-price-presenter]');
  const peekPrices = Array.from(
    drawer.querySelectorAll('.bc-bundle-peek__summary-price:not(.bc-bundle-peek__summary-price--original)')
  );

  if (!formattedTotal || !(presenter instanceof HTMLElement) || peekPrices.length === 0) {
    return false;
  }

  drawer.querySelectorAll('[data-cart-subtotal]').forEach((node) => {
    node.textContent = formattedTotal;
    node.setAttribute('value', formattedTotal);
  });
  peekPrices.forEach((node) => {
    node.textContent = formattedTotal;
  });
  drawer.querySelectorAll('.bc-bundle-peek__summary-price--original').forEach((node) => {
    node.textContent = formattedOriginalTotal;
    node.setAttribute('aria-label', `One-time price ${formattedOriginalTotal}`);
    node.hidden = !formattedOriginalTotal || (optimisticCodeSavingsPence + optimisticSubscriptionSavingsPence) <= 0;
  });
  peekPrices.forEach((node) => node.setAttribute('aria-label', `Your total ${formattedTotal}`));
  renderBundleDrawerDiscountSummary({
    discountCodes: optimisticCodeSavingsPence > 0 ? [detail.discountCode] : [],
    discountSavingsPence: optimisticCodeSavingsPence,
    giftCount: detail.giftCount,
    giftValuePence: detail.giftValuePence,
  });
  drawer.querySelectorAll('.bc-bundle-peek__details[open]').forEach((details) => {
    details.removeAttribute('open');
  });
  drawer.setAttribute('data-bc-cart-price-source', 'estimate');
  drawer.setAttribute('data-bc-cart-snapshot-ready', 'true');
  presenter.hidden = false;

  return true;
}

function applyVerifiedCartSnapshotToBundleDrawer(cart, { preserveGiftSummary = false } = {}) {
  if (!Array.isArray(cart?.items)) {
    return false;
  }

  const drawer = getBundleDrawer();

  if (!(drawer instanceof HTMLElement)) {
    return false;
  }

  const formattedTotal = formatBundleDrawerCartTotal(cart.total_price);
  const discountSummary = readVerifiedBundleDrawerDiscountSummary(cart);
  const verifiedCodeSavingsPence = Math.max(0, Number(discountSummary.discountSavingsPence) || 0);
  const verifiedAutomaticSavingsPence = Math.max(0, Number(discountSummary.automaticSavingsPence) || 0);
  const verifiedSubscriptionSavingsPence = Math.max(0, Number(discountSummary.subscriptionSavingsPence) || 0);
  const verifiedOneTimeAnchorPence = Math.max(0, Number(discountSummary.oneTimeAnchorPence) || 0);
  const verifiedOriginalTotalPence = verifiedOneTimeAnchorPence > Number(cart.total_price)
    ? verifiedOneTimeAnchorPence
    : Number(cart.total_price) + verifiedCodeSavingsPence + verifiedAutomaticSavingsPence + verifiedSubscriptionSavingsPence;
  const formattedOriginalTotal = formatBundleDrawerCartTotal(verifiedOriginalTotalPence);
  const presenter = drawer.querySelector('[data-bc-cart-price-presenter]');
  const peekPrices = Array.from(
    drawer.querySelectorAll('.bc-bundle-peek__summary-price:not(.bc-bundle-peek__summary-price--original)')
  );

  if (!formattedTotal || !(presenter instanceof HTMLElement) || peekPrices.length === 0) {
    return false;
  }

  drawer.querySelectorAll('[data-cart-subtotal]').forEach((node) => {
    node.textContent = formattedTotal;
    node.setAttribute('value', formattedTotal);
  });
  peekPrices.forEach((node) => {
    node.textContent = formattedTotal;
  });
  drawer.querySelectorAll('.bc-bundle-peek__summary-price--original').forEach((node) => {
    node.textContent = formattedOriginalTotal;
    node.setAttribute('aria-label', `One-time price ${formattedOriginalTotal}`);
    node.hidden = verifiedOriginalTotalPence <= Number(cart.total_price);
  });
  peekPrices.forEach((node) => node.setAttribute('aria-label', `Your total ${formattedTotal}`));
  renderBundleDrawerDiscountSummary(discountSummary, {
    preserveRenderedGifts:
      preserveGiftSummary && drawer.getAttribute('data-bc-cart-price-source') === 'estimate',
  });
  drawer.setAttribute('data-bc-cart-price-source', 'snapshot');
  presenter.hidden = false;
  drawer.setAttribute('data-bc-cart-snapshot-ready', 'true');

  return true;
}

function normalizeUrl(input) {
  try {
    if (typeof input === 'string') {
      return new URL(input, window.location.origin);
    }

    if (input instanceof URL) {
      return input;
    }

    if (input instanceof Request) {
      return new URL(input.url, window.location.origin);
    }

    if (input && typeof input === 'object' && 'url' in input && typeof input.url === 'string') {
      return new URL(input.url, window.location.origin);
    }
  } catch (error) {
    return null;
  }

  return null;
}

function installRechargeSubmitClickInterceptor() {
  document.addEventListener('click', handleRechargeSubmitClick, true);
}

// Instant visual feedback. The cart otherwise looks frozen for ~2s after the customer taps
// Add to cart because the drawer isn't opened until the whole add + gift-reconcile pipeline
// finishes (handleSuccessfulCartAdd -> scheduleDrawerOpen). This pops the Phase 2 split
// drawer the moment the tap registers: the delivery form (already in the DOM, preserved
// across morphs by data-skip-subtree-update) is usable at once while the volatile items
// region stays masked behind its skeleton and is filled by that same post-add render — so
// there's no stale-cart flash. It is purely additive: it does NOT preventDefault, so the
// native ReCharge add proceeds exactly as before. Registered before the submit interceptor
// so it still runs even if that path ever starts calling stopImmediatePropagation.
function installBundleAddToCartInstantFeedback() {
  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const control = event.target.closest('button, [role="button"], a');

      if (!isBundleAddToCartControl(control)) {
        return;
      }

      // The custom builder emits a richer pending-add event with the complete local
      // price state. Let that event paint the drawer before opening it; opening here
      // would expose the old cart for one frame and cause the later comparison-price
      // insertion to visibly jump. Legacy ReCharge surfaces still use this fast path.
      if (control.closest('[data-bc-custom-bundle-mvp]')) {
        return;
      }

      const builderMode = control.closest('[data-bc-custom-bundle-mvp]')
        ?.getAttribute('data-bc-mvp-purchase-mode');
      openBundleDrawerShellImmediately({ purchaseMode: builderMode });
    },
    true
  );
}

function isBundleAddToCartControl(control) {
  if (!(control instanceof HTMLElement)) {
    return false;
  }

  if (control.disabled || control.getAttribute('aria-disabled') === 'true') {
    return false;
  }

  // Scope to the same bundle CTA containers the submit interceptor watches.
  const inSummary =
    control.closest('[data-testid="bundle-summary-mobile"]') ||
    control.closest('[data-testid="bundle-summary-desktop"]') ||
    control.closest('.rb-sticky-container');

  if (!inSummary) {
    return false;
  }

  // Use innerText (not textContent): the button renders "Add" / "to" / "cart" as separate
  // CSS-spaced blocks, so textContent collapses to "Addtocart" and a \s-based matcher
  // misses it; innerText keeps the whitespace. The label also reflects bundle validity —
  // an incomplete bundle shows a different label, so this only fires on a real add.
  const label = (control.innerText || control.textContent || '').replace(/\s+/g, ' ').trim();

  return RECHARGE_FINAL_ADD_TO_CART_TEXT_MATCHER.test(label);
}

function handleRechargeSubmitClick(event) {
  const rechargeSubmitButton = findRechargeSubmitButton(event.target);

  if (!(rechargeSubmitButton instanceof HTMLElement)) {
    return;
  }

  if (allowNativeRechargeSubmitClick) {
    allowNativeRechargeSubmitClick = false;
    return;
  }

  if (isPreparingPendingBundleSession) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    return;
  }

  if (hasPendingBundleSession()) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (!isBundleDirectCheckoutPending) {
      scheduleDrawerOpen({ force: true });
    }

    return;
  }

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  isPreparingPendingBundleSession = true;
  captureAndPersistHybridBundleDraft('submit-click', { force: true, ttlMs: BUNDLE_HYBRID_CHECKOUT_DRAFT_TTL_MS });

  const checkoutAbDecision = getBundleCheckoutAbDecision();

  if (checkoutAbDecision.enabled && checkoutAbDecision.cohort === 'checkout') {
    emitBundleCheckoutAbEvent('exposed', {
      dryRun: checkoutAbDecision.dryRun,
      decisionReason: checkoutAbDecision.reason,
    });

    if (!checkoutAbDecision.dryRun) {
      setBundleDirectCheckoutPending(true);
    }
  }

  preparePendingBundleSession(rechargeSubmitButton).finally(() => {
    isPreparingPendingBundleSession = false;
  });
}

function findRechargeSubmitButton(target) {
  if (!(target instanceof Element)) return null;

  return (
    getRechargeSubmitButtons().find((button) => {
      return button === target || button.contains(target);
    }) ?? null
  );
}

function getRechargeSubmitButtons() {
  const candidates = [
    ...document.querySelectorAll('[data-testid="bundle-summary-desktop"] button'),
    ...document.querySelectorAll('[data-testid="bundle-summary-mobile"] button'),
    ...document.querySelectorAll('.rb-sticky-container button'),
    ...document.querySelectorAll('.rb-sticky-container .rb-btn'),
  ];

  const seen = new Set();

  return candidates.filter((button) => {
    if (!(button instanceof HTMLButtonElement) && !(button instanceof HTMLElement)) {
      return false;
    }

    if (seen.has(button)) return false;
    seen.add(button);

    if (!isElementVisible(button)) return false;

    const label = button.textContent.trim();

    return RECHARGE_FINAL_ADD_TO_CART_TEXT_MATCHER.test(label);
  });
}

function cloneCartState(cart) {
  if (!cart || typeof cart !== 'object') return null;

  return cloneStateValue(cart);
}

function captureAndPersistHybridBundleDraft(source = 'unknown', { force = false, rawSnapshot = null, ttlMs = null } = {}) {
  if (!isHybridBundlePersistenceEnabled()) {
    return null;
  }

  if (!force && hybridBundleDraftRestoreActive) {
    return null;
  }

  const snapshot = rawSnapshot ?? captureRechargeBundleState();

  if (!snapshot) {
    if (shouldPreserveHybridBundleDraftOnEmptyCapture(source)) {
      const preservedDraft = readHybridBundleDraft({ allowLegacy: false, logInvalid: false });
      logHybridBundlePersistenceDebug('empty-capture-preserved', { source, draft: summarizeHybridBundleDraft(preservedDraft) });
      return preservedDraft;
    }

    if (isBundlePageReadyForPersistedStateRestore()) {
      clearHybridBundleDraft('empty-interaction', { clearLegacy: true });
    }

    return null;
  }

  if (shouldPreserveHybridBundleDraftOverSnapshot(source, snapshot)) {
    const preservedDraft = readHybridBundleDraft({ allowLegacy: false, logInvalid: false });
    logHybridBundlePersistenceDebug('snapshot-capture-preserved', { source, draft: summarizeHybridBundleDraft(preservedDraft) });
    return preservedDraft;
  }

  const draft = buildHybridBundleDraft(snapshot, source, { ttlMs });

  if (!draft) {
    return null;
  }

  const signature = buildHybridBundleDraftSignature(draft);

  if (!force && signature === hybridBundleDraftLastCaptureSignature) {
    return draft;
  }

  hybridBundleDraftLastCaptureSignature = signature;

  if (persistHybridBundleDraft(draft)) {
    updateHybridBundleEmptyClearSuppression(source);
    logHybridBundlePersistenceDebug('draft-captured', { source, draft: summarizeHybridBundleDraft(draft) });
  }

  return draft;
}

function shouldPreserveHybridBundleDraftOnEmptyCapture(source) {
  const normalizedSource = String(source || '');

  if (!isHybridBundleDraftPreservationSource(normalizedSource)) {
    return false;
  }

  const draft = readHybridBundleDraft({ allowLegacy: false, logInvalid: false });

  if (!draft || !isHybridBundleRecoveryDraft(draft)) {
    return false;
  }

  if (isHybridBundleDraftCheckoutNavigationSource(draft.source)) {
    if (/^drawer-close/.test(normalizedSource)) {
      return true;
    }

    if (/^lightbox-(opening|closing)$/.test(normalizedSource)) {
      return true;
    }

    if (/^(checkout|pagehide|visibility-hidden)$/.test(normalizedSource)) {
      return isProceedingToCheckout || isBundleDrawerCurrentlyOpen() || hasActivePendingBundleSession();
    }

    if (isBundleDrawerCurrentlyOpen() || hasActivePendingBundleSession()) {
      return true;
    }
  }

  return Date.now() < hybridBundleDraftEmptyClearSuppressedUntil;
}

function shouldPreserveHybridBundleDraftOverSnapshot(source, snapshot) {
  const normalizedSource = String(source || '');

  if (!isHybridBundleDraftPreservationSource(normalizedSource)) {
    return false;
  }

  const draft = readHybridBundleDraft({ allowLegacy: false, logInvalid: false });

  if (!isHybridBundleDraftCheckoutNavigationSource(draft?.source)) {
    return false;
  }

  if (/^drawer-close/.test(normalizedSource)) {
    return true;
  }

  if (/^lightbox-(opening|closing)$/.test(normalizedSource)) {
    return true;
  }

  if (/^(checkout|pagehide|visibility-hidden)$/.test(normalizedSource)) {
    return isProceedingToCheckout || isBundleDrawerCurrentlyOpen() || hasActivePendingBundleSession();
  }

  if (!isBundleDrawerCurrentlyOpen() && !hasActivePendingBundleSession()) {
    return false;
  }

  const draftItemCount = Array.isArray(draft.rawRechargeState?.selectedProducts)
    ? draft.rawRechargeState.selectedProducts.length
    : 0;
  const snapshotItemCount = Array.isArray(snapshot?.selectedProducts)
    ? snapshot.selectedProducts.length
    : 0;

  return snapshotItemCount <= draftItemCount;
}

function isHybridBundleDraftPreservationSource(source) {
  return /^(interaction|drawer-close)(-settled)?$/.test(source) ||
    /^(checkout|pagehide|visibility-hidden)$/.test(source) ||
    /^lightbox-(opening|closing)$/.test(source);
}

function isHybridBundleDraftCheckoutNavigationSource(source) {
  return /^(add-to-cart|checkout)$/.test(String(source || ''));
}

function updateHybridBundleEmptyClearSuppression(source) {
  if (String(source || '') === 'add-to-cart') {
    hybridBundleDraftEmptyClearSuppressedUntil = Date.now() + BUNDLE_HYBRID_EMPTY_CLEAR_SUPPRESS_MS;
  }
}

function buildHybridBundleDraft(snapshot, source, { ttlMs = null } = {}) {
  if (!snapshot || !Array.isArray(snapshot.selectedProducts) || snapshot.selectedProducts.length === 0) {
    return null;
  }

  const now = Date.now();
  const previousDraft = readHybridBundleDraft({ allowLegacy: false, logInvalid: false });
  const selectedTtlMs =
    Number.isFinite(ttlMs) && ttlMs > 0
      ? ttlMs
      : source === 'checkout'
        ? BUNDLE_HYBRID_CHECKOUT_DRAFT_TTL_MS
        : BUNDLE_HYBRID_DRAFT_TTL_MS;

  return {
    schemaVersion: BUNDLE_HYBRID_SCHEMA_VERSION,
    featureVersion: BUNDLE_HYBRID_FEATURE_VERSION,
    source,
    tabId: getHybridBundleDraftTabId(),
    createdAt: Number(previousDraft?.createdAt) || now,
    updatedAt: now,
    expiresAt: now + selectedTtlMs,
    productHandle: getCurrentBundleProductHandle(),
    path: getStorefrontPath(window.location.pathname),
    rawRechargeState: cloneStateValue(snapshot),
    normalizedSelection: normalizeRechargeBundleSelection(snapshot),
    delivery: captureHybridBundleDeliveryState(),
    cart: {
      token: typeof getLatestCartSnapshot()?.token === 'string' ? getLatestCartSnapshot().token : null,
      signature: buildCartSnapshotSignature(getLatestCartSnapshot()),
      trackedBundleIds: getTrackedBundleItems(getLatestCartSnapshot())
        .map((item) => item?.properties?._rc_bundle)
        .filter(Boolean),
    },
  };
}

function persistHybridBundleDraft(draft) {
  if (!isHybridBundleDraftEnvelope(draft)) {
    return false;
  }

  try {
    window.sessionStorage.setItem(BUNDLE_HYBRID_DRAFT_SESSION_STORAGE_KEY, JSON.stringify(draft));
    return true;
  } catch (error) {
    return false;
  }
}

function readHybridBundleDraft({ allowLegacy = true, logInvalid = true } = {}) {
  const sessionDraft = readHybridBundleDraftFromStorage(getBrowserStorage('sessionStorage'), BUNDLE_HYBRID_DRAFT_SESSION_STORAGE_KEY);
  const draft = sessionDraft;

  if (draft) {
    if (isHybridBundleDraftCompatible(draft)) {
      return draft;
    }

    if (logInvalid) {
      logHybridBundlePersistenceDebug('draft-invalid', { draft: summarizeHybridBundleDraft(draft) });
    }

    clearHybridBundleDraft('invalid-draft', { clearLegacy: true });
  }

  if (!allowLegacy || !isBundleHistoryRestoreNavigation() || window.BC_BUNDLE_PERSISTENCE?.allowLegacy !== true) {
    return null;
  }

  const legacySnapshot = readStoredBundleStateSnapshot();

  if (!legacySnapshot) {
    return null;
  }

  return buildLegacyHybridBundleDraft(legacySnapshot);
}

function readHybridBundleDraftFromStorage(storage, key) {
  try {
    const rawDraft = storage?.getItem(key);

    if (!rawDraft) {
      return null;
    }

    const parsedDraft = JSON.parse(rawDraft);
    return isHybridBundleDraftEnvelope(parsedDraft) ? parsedDraft : null;
  } catch (error) {
    return null;
  }
}

function getBrowserStorage(storageName) {
  try {
    return window[storageName] ?? null;
  } catch (error) {
    return null;
  }
}

function buildLegacyHybridBundleDraft(snapshot) {
  const now = Date.now();

  return {
    schemaVersion: BUNDLE_HYBRID_SCHEMA_VERSION,
    featureVersion: 'legacy',
    source: 'legacy',
    tabId: getHybridBundleDraftTabId(),
    createdAt: now,
    updatedAt: now,
    expiresAt: now + BUNDLE_HYBRID_DRAFT_TTL_MS,
    productHandle: getCurrentBundleProductHandle(),
    path: getStorefrontPath(window.location.pathname),
    rawRechargeState: cloneStateValue(snapshot),
    normalizedSelection: normalizeRechargeBundleSelection(snapshot),
    delivery: captureHybridBundleDeliveryState(),
    cart: {
      token: typeof getLatestCartSnapshot()?.token === 'string' ? getLatestCartSnapshot().token : null,
      signature: buildCartSnapshotSignature(getLatestCartSnapshot()),
      trackedBundleIds: getTrackedBundleItems(getLatestCartSnapshot())
        .map((item) => item?.properties?._rc_bundle)
        .filter(Boolean),
    },
  };
}

function clearHybridBundleDraft(reason = 'unknown', { clearLegacy = false } = {}) {
  try {
    window.sessionStorage.removeItem(BUNDLE_HYBRID_DRAFT_SESSION_STORAGE_KEY);
    window.localStorage.removeItem(BUNDLE_HYBRID_DRAFT_LOCAL_STORAGE_KEY);
  } catch (error) {
    // Ignore storage cleanup failures.
  }

  hybridBundleDraftLastCaptureSignature = '';
  hybridBundleDraftEmptyClearSuppressedUntil = 0;

  if (clearLegacy) {
    clearStoredBundleStateSnapshot();
  }

  logHybridBundlePersistenceDebug('draft-cleared', { reason });
}

function readRestorableBundleStateSnapshot() {
  const hybridDraft = readHybridBundleDraft({ allowLegacy: true });
  return getHybridBundleDraftSnapshot(hybridDraft);
}

function getHybridBundleDraftSnapshot(draft) {
  if (!draft?.rawRechargeState) {
    return null;
  }

  const snapshot = draft.rawRechargeState;

  if (!Array.isArray(snapshot.selectedProducts) || snapshot.selectedProducts.length === 0) {
    return null;
  }

  return snapshot;
}

function isHybridBundleDraftEnvelope(draft) {
  return Boolean(
    draft &&
      typeof draft === 'object' &&
      Number(draft.schemaVersion) === BUNDLE_HYBRID_SCHEMA_VERSION &&
      draft.rawRechargeState &&
      Array.isArray(draft.rawRechargeState.selectedProducts)
  );
}

function isHybridBundleDraftCompatible(draft) {
  if (!isHybridBundleDraftEnvelope(draft)) {
    return false;
  }

  const now = Date.now();

  if (!Number.isFinite(Number(draft.expiresAt)) || Number(draft.expiresAt) <= now) {
    return false;
  }

  if (!TARGET_PRODUCT_HANDLES.has(draft.productHandle)) {
    return false;
  }

  if (draft.path && !TARGET_PATHS.has(getStorefrontPath(draft.path))) {
    return false;
  }

  const latestCartToken = getLatestCartSnapshot()?.token;
  const latestCartItems = getLatestCartSnapshot()?.items;

  if (draft.cart?.token && latestCartToken && draft.cart.token !== latestCartToken) {
    return false;
  }

  if (!draft.cart?.token && Array.isArray(latestCartItems) && latestCartItems.length > 0) {
    return false;
  }

  return getCurrentBundleProductHandle() === draft.productHandle;
}

function isCurrentRechargeStateDifferentFromDraft(draft) {
  const snapshot = getHybridBundleDraftSnapshot(draft);

  if (!snapshot) {
    return false;
  }

  const currentSnapshot = captureRechargeBundleState();

  if (!currentSnapshot) {
    return false;
  }

  return !areEquivalentStateValues(currentSnapshot.selectedVariantId, snapshot.selectedVariantId) ||
    !areEquivalentStateValues(currentSnapshot.selectedSellingPlanId, snapshot.selectedSellingPlanId) ||
    serializeStateValue(currentSnapshot.selectedProducts) !== serializeStateValue(snapshot.selectedProducts);
}

function buildHybridBundleDraftSignature(draft) {
  return serializeStateValue({
    productHandle: draft?.productHandle,
    selectedVariantId: draft?.normalizedSelection?.selectedVariantId,
    selectedSellingPlanId: draft?.normalizedSelection?.selectedSellingPlanId,
    items: draft?.normalizedSelection?.items,
    delivery: draft?.delivery,
  });
}

function summarizeHybridBundleDraft(draft) {
  if (!draft) {
    return null;
  }

  return {
    schemaVersion: draft.schemaVersion,
    featureVersion: draft.featureVersion,
    source: draft.source,
    productHandle: draft.productHandle,
    path: draft.path,
    updatedAt: draft.updatedAt,
    expiresAt: draft.expiresAt,
    itemCount: Array.isArray(draft.normalizedSelection?.items) ? draft.normalizedSelection.items.length : 0,
    selectedVariantId: draft.normalizedSelection?.selectedVariantId ?? draft.rawRechargeState?.selectedVariantId ?? null,
    selectedSellingPlanId: draft.normalizedSelection?.selectedSellingPlanId ?? draft.rawRechargeState?.selectedSellingPlanId ?? null,
    cartToken: draft.cart?.token ? 'present' : 'missing',
    cartSignature: draft.cart?.signature ?? '',
  };
}

function normalizeRechargeBundleSelection(snapshot) {
  const selectedProducts = Array.isArray(snapshot?.selectedProducts) ? snapshot.selectedProducts : [];

  return {
    selectedVariantId: normalizeNumericId(snapshot?.selectedVariantId),
    selectedSellingPlanId: normalizeNumericId(snapshot?.selectedSellingPlanId),
    items: selectedProducts.map(normalizeRechargeBundleSelectedProduct).filter(Boolean),
  };
}

function normalizeRechargeBundleSelectedProduct(product) {
  if (!product || typeof product !== 'object') {
    return null;
  }

  const variantId = normalizeNumericId(
    product.variantId ??
      product.variant_id ??
      product.selectedVariantId ??
      product.selected_variant_id ??
      product.externalVariantId ??
      product.external_variant_id ??
      product.variant?.id
  );
  const productId = normalizeNumericId(product.productId ?? product.product_id ?? product.externalProductId ?? product.external_product_id ?? product.id);
  const quantity = Number(product.quantity ?? product.qty ?? product.count ?? 1);
  const title = String(product.title ?? product.productTitle ?? product.product_title ?? product.name ?? '').trim();

  return {
    productId,
    variantId,
    quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
    title,
  };
}

function normalizeNumericId(value) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : null;
}

function getCurrentBundleProductHandle() {
  const normalizedPath = getStorefrontPath(window.location.pathname);
  const handle = normalizedPath.split('/').filter(Boolean).pop();
  return TARGET_PRODUCT_HANDLES.has(handle) ? handle : 'morning-bundle';
}

function getHybridBundleDraftTabId() {
  try {
    const existingTabId = window.sessionStorage.getItem(BUNDLE_HYBRID_DRAFT_TAB_ID_STORAGE_KEY);

    if (existingTabId) {
      return existingTabId;
    }

    const nextTabId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem(BUNDLE_HYBRID_DRAFT_TAB_ID_STORAGE_KEY, nextTabId);
    return nextTabId;
  } catch (error) {
    return 'unknown';
  }
}

function persistBundleStateSnapshot(snapshot) {
  if (!snapshot) {
    return false;
  }

  try {
    window.sessionStorage.setItem(BUNDLE_STATE_SNAPSHOT_STORAGE_KEY, JSON.stringify(snapshot));
    return true;
  } catch (error) {
    return false;
  }
}

function clearStoredBundleStateSnapshot() {
  try {
    window.sessionStorage.removeItem(BUNDLE_STATE_SNAPSHOT_STORAGE_KEY);
    return true;
  } catch (error) {
    return false;
  }
}

function readStoredBundleStateSnapshot() {
  try {
    const storedSnapshot = window.sessionStorage.getItem(BUNDLE_STATE_SNAPSHOT_STORAGE_KEY);

    if (!storedSnapshot) {
      return null;
    }

    const parsedSnapshot = JSON.parse(storedSnapshot);

    if (!Array.isArray(parsedSnapshot?.selectedProducts) || parsedSnapshot.selectedProducts.length === 0) {
      clearStoredBundleStateSnapshot();
      return null;
    }

    return parsedSnapshot;
  } catch (error) {
    return null;
  }
}

async function preparePendingBundleSession(rechargeSubmitButton) {
  try {
    const currentCart = await resolvePendingBundleSessionCartSnapshot();
    logBundleReplacementDebug('preparePendingBundleSession:currentCart', {
      buttonLabel: rechargeSubmitButton?.textContent?.trim() ?? null,
      cart: summarizeBundleReplacementCart(currentCart),
    });
    const cartWithoutTrackedBundle = await resetTrackedBundleCartState(currentCart);
    logBundleReplacementDebug('preparePendingBundleSession:cartWithoutTrackedBundle', {
      cart: summarizeBundleReplacementCart(cartWithoutTrackedBundle),
    });

    beginPendingBundleSession({ beforeCartSnapshot: cartWithoutTrackedBundle });
  } catch (error) {
    logBundleReplacementDebug('preparePendingBundleSession:error', {
      message: error instanceof Error ? error.message : String(error),
    });
    console.error('Failed to prepare the bundle cart session before add to cart.', error);
    beginPendingBundleSession({ beforeCartSnapshot: getLatestCartSnapshot() });
  } finally {
    logBundleReplacementDebug('preparePendingBundleSession:finally', {
      allowNativeRechargeSubmitClick,
    });
    allowNativeRechargeSubmitClick = true;
    rechargeSubmitButton.click();
  }
}

async function resolvePendingBundleSessionCartSnapshot() {
  let currentCart = null;

  if (historyRestoreCartSnapshotPromise) {
    currentCart = (await historyRestoreCartSnapshotPromise) ?? null;
  }

  if (!currentCart?.items) {
    currentCart = (await primeCartSnapshot({ force: true })) ?? getLatestCartSnapshot();
  }

  if (needsHistoryRestoreCartResync && !buildTrackedBundleRemovalUpdates(currentCart)) {
    currentCart = (await primeCartSnapshot({ force: true })) ?? currentCart;
  }

  historyRestoreCartSnapshotPromise = null;
  needsHistoryRestoreCartResync = false;

  return currentCart ?? getLatestCartSnapshot();
}

async function resetTrackedBundleCartState(cart) {
  const resetPayload = buildTrackedBundleCartResetPayload(cart);
  logBundleReplacementDebug('resetTrackedBundleCartState:payload', {
    cart: summarizeBundleReplacementCart(cart),
    resetPayload,
  });

  if (!resetPayload) {
    logBundleReplacementDebug('resetTrackedBundleCartState:noPayload', {
      cart: summarizeBundleReplacementCart(cart ?? getLatestCartSnapshot()),
    });
    return cart ?? getLatestCartSnapshot();
  }

  const response = await getCartMutationFetch()(`${window.Shopify?.routes?.root ?? '/'}cart/update.js`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(resetPayload),
  });

  if (!response.ok) {
    logBundleReplacementDebug('resetTrackedBundleCartState:responseError', {
      status: response.status,
      resetPayload,
    });
    throw new Error(`Existing bundle replacement failed with status ${response.status}`);
  }

  const updatedCart = await response.json();
  logBundleReplacementDebug('resetTrackedBundleCartState:updatedCart', {
    resetPayload,
    cart: summarizeBundleReplacementCart(updatedCart),
  });

  setLatestCartSnapshot(updatedCart);

  return updatedCart;
}

function buildTrackedBundleCartResetPayload(cart) {
  const removalUpdates = buildTrackedBundleRemovalUpdates(cart);

  if (!removalUpdates) {
    return null;
  }

  return {
    updates: removalUpdates,
  };
}

function buildTrackedBundleRemovalUpdates(cart) {
  const trackedBundleItems = getTrackedBundleItems(cart);

  if (isBundleReplacementDebugEnabled()) {
    logBundleReplacementDebug('buildTrackedBundleRemovalUpdates:trackedItems', {
      trackedBundleItems: trackedBundleItems.map((item) => ({
        key: item?.key ?? null,
        quantity: Number(item?.quantity) || 0,
        title: item?.title ?? null,
        bundleParent: item?.properties?._rc_bundle_parent ?? null,
        bundleId: item?.properties?._rc_bundle ?? null,
      })),
    });
  }

  if (trackedBundleItems.length === 0) {
    logBundleReplacementDebug('buildTrackedBundleRemovalUpdates:noTrackedItems', {
      cart: summarizeBundleReplacementCart(cart),
    });
    return null;
  }

  const updates = trackedBundleItems.reduce((updates, item) => {
    if (item?.key) {
      updates[item.key] = 0;
    }

    return updates;
  }, {});

  logBundleReplacementDebug('buildTrackedBundleRemovalUpdates:updates', {
    updates,
  });

  return updates;
}

function buildTrackedBundleRemovedCartSnapshot(cart) {
  if (!Array.isArray(cart?.items)) {
    return cloneCartState(cart);
  }

  const nextCart = cloneCartState(cart);
  const trackedBundleKeys = new Set(getTrackedBundleItems(cart).map((item) => item?.key).filter(Boolean));

  if (trackedBundleKeys.size === 0 || !Array.isArray(nextCart?.items)) {
    return nextCart;
  }

  nextCart.items = nextCart.items.filter((item) => !trackedBundleKeys.has(item?.key));

  return nextCart;
}

function getTrackedBundleItems(cart) {
  if (!Array.isArray(cart?.items)) return [];

  return cart.items.filter((item) => {
    const bundleHandle = item?.properties?._rc_bundle_parent;
    const bundleId = item?.properties?._rc_bundle;

    return TARGET_PRODUCT_HANDLES.has(bundleHandle) && Boolean(bundleId);
  });
}

function getTrackedBundleGroupIds(cart) {
  return new Set(
    getTrackedBundleItems(cart)
      .map((item) => item?.properties?._rc_bundle)
      .filter(Boolean)
  );
}

function getTrackedBundleIdsFromItems(items) {
  return new Set(
    items
      .map((item) => {
        const bundleHandle = item?.properties?._rc_bundle_parent;
        const bundleId = item?.properties?._rc_bundle;

        if (!TARGET_PRODUCT_HANDLES.has(bundleHandle) || !bundleId) {
          return null;
        }

        return bundleId;
      })
      .filter(Boolean)
  );
}

function buildTrackedBundleRemovalUpdatesExcludingBundleIds(cart, retainedBundleIds) {
  if (!(retainedBundleIds instanceof Set) || retainedBundleIds.size === 0) {
    return null;
  }

  const trackedBundleItems = getTrackedBundleItems(cart).filter((item) => {
    return !retainedBundleIds.has(item?.properties?._rc_bundle);
  });

  if (trackedBundleItems.length === 0) {
    return null;
  }

  return trackedBundleItems.reduce((updates, item) => {
    if (item?.key) {
      updates[item.key] = 0;
    }

    return updates;
  }, {});
}

async function canonicalizeTrackedBundleCartAfterAdd({ addedItems = [], cart = null } = {}) {
  const addedBundleIds = getTrackedBundleIdsFromItems(addedItems);

  if (addedBundleIds.size === 0) {
    return cart ?? null;
  }

  // One atomic /cart/update.js removes every tracked bundle line that does not belong
  // to the just-added group: this is both the "replace the previous bundle on re-add"
  // behaviour and the duplicate-group cleanup. The added group ids are excluded by
  // construction, so this call can never remove the bundle the customer just added.
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const currentCart =
      attempt === 0 && cart?.items ? cart : (await primeCartSnapshot({ force: true })) ?? getLatestCartSnapshot();
    const removalUpdates = buildTrackedBundleRemovalUpdatesExcludingBundleIds(currentCart, addedBundleIds);

    if (!removalUpdates) {
      return currentCart;
    }

    try {
      const response = await getCartMutationFetch()(`${window.Shopify?.routes?.root ?? '/'}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ updates: removalUpdates }),
      });

      if (!response.ok) {
        throw new Error(`Post-add bundle canonicalization failed with status ${response.status}`);
      }

      const updatedCart = await response.json();
      setLatestCartSnapshot(updatedCart);

      return updatedCart;
    } catch (error) {
      console.error('Failed to canonicalize duplicate bundle groups after add to cart.', error);
    }
  }

  return (await primeCartSnapshot({ force: true })) ?? getLatestCartSnapshot();
}
